import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

const API_BASE_URL = getApiBaseUrl();

const AUTH_STORAGE_KEY = 'docxToolAuthSession';
const USER_LOGIN_ACCESS_CODE = process.env.REACT_APP_USER_LOGIN_ACCESS_CODE || 'uf-9K2m-Q7xL-2026';
const USER_LOGIN_PATH = `/user/login/${USER_LOGIN_ACCESS_CODE}`;
const ADMIN_LOGIN_ACCESS_CODE = process.env.REACT_APP_ADMIN_LOGIN_ACCESS_CODE || 'af-6P8r-M3vN-2026';
const ADMIN_LOGIN_PATH = `/admin/login/${ADMIN_LOGIN_ACCESS_CODE}`;
const ARTICLE_FORMATTING_LABEL = 'Article Formatting Rules';
const DOCUMENT_FORMATTING_LABEL = 'Document Formatting Rules';
const APPLY_ARTICLE_FORMATTING_LABEL = ' Article Formatting ';
const APPLY_DOCUMENT_FORMATTING_LABEL = ' Document Formatting ';
const MANAGE_ARTICLE_FORMATTING_LABEL = 'Manage Article Formatting Rules';
const MANAGE_DOCUMENT_FORMATTING_LABEL = 'Manage Document Formatting Rules';
const DOCUMENT_UPLOAD_PAGE = 'upload-document-format';
const DOCUMENT_MANAGE_PAGE = 'manage-document-rules';
const DOCUMENT_APPLY_PAGE = 'apply-document-rules';

// "hi this is testing"

function getApiBaseUrl() {
  const configuredUrl = process.env.REACT_APP_API_BASE_URL;
  if (configuredUrl) {
    const normalizedUrl = configuredUrl.replace(/\/+$/, '');
    return normalizedUrl.endsWith('/api') ? normalizedUrl : `${normalizedUrl}/api`;
  }

  if (typeof window !== 'undefined') {
    const localHosts = new Set(['localhost', '127.0.0.1', '::1']);
    if (localHosts.has(window.location.hostname)) {
      return 'http://127.0.0.1:5000/api';
    }

    if (['3000', '3001'].includes(window.location.port)) {
      return `${window.location.protocol}//${window.location.hostname}:5000/api`;
    }
  }

  return '/api';
}

const RULE_SET_LABELS = {
  rule_set_1: 'Rule Set 1',
  rule_set_2: 'Rule Set 2',
  rule_set_3: 'Rule Set 3',
};

const RULE_SET_ORDER = ['rule_set_1', 'rule_set_2', 'rule_set_3'];

const FIELD_ORDER = {
  rule_set_1: ['title', 'author_name', 'author_details', 'abstract', 'keywords'],
  rule_set_2: ['heading_1', 'heading_2', 'heading_3', 'content'],
  rule_set_3: ['table', 'table_title', 'figure', 'figure_title', 'references'],
};

const NEW_UPLOAD_FIELD_ORDER = {
  rule_set_1: ['title', 'abstract', 'keywords'],
  rule_set_2: ['heading_1', 'heading_2', 'heading_3', 'content'],
  rule_set_3: ['table', 'table_title', 'figure', 'figure_title', 'references'],
};

const FIELD_LABELS = {
  title: 'Title',
  author_name: 'Author Name',
  author_details: 'Author Details',
  abstract: 'Abstract',
  keywords: 'Keywords',
  heading_1: 'Heading 1',
  heading_2: 'Heading 2',
  heading_3: 'Heading 3',
  equation: 'Equation',
  content: 'Content',
  table: 'Table',
  table_title: 'Table Title',
  figure: 'Figure',
  figure_title: 'Figure Title',
  references: 'References',
  header: 'Header',
  footer: 'Footer',
};

const APPLIED_RULE_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'author_name', label: 'Author Name' },
  { value: 'author_details', label: 'Author Details' },
  { value: 'abstract', label: 'Abstract' },
  { value: 'keywords', label: 'Keywords' },
  { value: 'heading_1', label: 'H1' },
  { value: 'heading_2', label: 'H2' },
  { value: 'heading_3', label: 'H3' },
  { value: 'content', label: 'Content' },
  { value: 'table_title', label: 'Table Title' },
  { value: 'figure_title', label: 'Figure Title' },
];

const DETECTION_RULE_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'author_name', label: 'Author Name' },
  { value: 'author_details', label: 'Author Details' },
  { value: 'abstract', label: 'Abstract' },
  { value: 'keywords', label: 'Keywords' },
  { value: 'heading_1', label: 'Heading 1' },
  { value: 'heading_2', label: 'Heading 2' },
  { value: 'heading_3', label: 'Heading 3' },
  { value: 'content', label: 'Content' },
  { value: 'table', label: 'Table' },
  { value: 'table_title', label: 'Table Title' },
  { value: 'figure', label: 'Figure' },
  { value: 'figure_title', label: 'Figure Title' },
  { value: 'references', label: 'References' },
];

const DOCUMENT_DETECTION_RULE_OPTIONS = [
  { value: 'title', label: 'Chapter Title' },
  { value: 'abstract', label: 'Abstract' },
  { value: 'keywords', label: 'Keywords' },
  { value: 'heading_1', label: 'Heading 1' },
  { value: 'heading_2', label: 'Heading 2' },
  { value: 'heading_3', label: 'Heading 3' },
  { value: 'content', label: 'Content' },
  { value: 'table', label: 'Table' },
  { value: 'table_title', label: 'Table Title' },
  { value: 'figure', label: 'Figure' },
  { value: 'figure_title', label: 'Figure Title' },
  { value: 'references', label: 'References' },
  { value: 'header', label: 'Header' },
  { value: 'footer', label: 'Footer' },
];

const DOCUMENT_EXCLUDED_RULE_TYPES = new Set(['author_name', 'author_details']);

const DETECTION_RULE_DOCS = {
  title: {
    summary: 'First non-empty paragraph is treated as title when no earlier rule overrides it.',
    order: [
      'Runs after special labels like table title, figure title, references, abstract, and keywords are checked. (Yeh tab apply hota hai jab pehle koi special section rule match na ho.)',
      'If the paragraph is the first meaningful text block, it becomes `title`. (Document ka pehla proper text aksar title maana jata hai.)',
    ],
    patterns: [
      'No strict text pattern is required.',
      'Position in the document is the main rule here.',
    ],
    styles: [
      'No dedicated style check is required for title fallback.',
      'Saved format styling is later applied to whatever is classified as title.',
    ],
    signatures: [
      'No signature comparison is used for title fallback detection.',
    ],
    examples: [
      'My Research Paper -> title',
      'ANALYSIS OF SMART GRID SYSTEMS -> title',
    ],
    codeRefs: [
      {
        name: '_classify_block_type',
        detail: 'Yeh main decision function hai. Har paragraph yahin aata hai aur function top-to-bottom checks chala kar decide karta hai ki isko title, abstract, heading, content ya kisi aur block type mein rakhna hai.',
      },
      {
        name: 'non_empty_index == 0',
        detail: 'Agar paragraph first meaningful non-empty block hai, aur usse pehle koi special rule match nahi hua, to function usko title mark kar deta hai.',
      },
    ],
  },
  author_name: {
    summary: 'Article formatting flow maps an early paragraph to author name only when it looks like an author line.',
    order: [
      'Only applies in the article formatting flow. (Yeh rule sirf article formatting flow me use hota hai.)',
      'If the second non-empty block looks like an author name, it becomes `author_name`; otherwise it stays out of author fields. (Title ke baad author name na ho to next content ko author nahi banaya jata.)',
    ],
    patterns: [
      'Author-style names such as Dr./Prof. lines, multiple names, initials, or affiliation markers.',
      'Rejects section labels and content-like lines such as abstract, introduction, methodology, study, or chapter text.',
    ],
    styles: [
      'No required DOCX style.',
    ],
    signatures: [
      'No signature match is used.',
    ],
    examples: [
      'Dr. A. Kumar -> author_name',
      'John Smith, Jane Lee -> author_name',
    ],
    codeRefs: [
      {
        name: '_classify_block_type',
        detail: 'Author detection main classifier mein hota hai. Yeh pehle special headings aur labels check karta hai, phir author-name pattern validate karta hai.',
      },
      {
        name: '_looks_like_author_name_line',
        detail: 'Second non-empty paragraph author_name tabhi banta hai jab yeh helper usko real author line jaisa validate kare.',
      },
    ],
  },
  author_details: {
    summary: 'Article formatting flow maps author details only when an author/detail cue is present.',
    order: [
      'Only applies in the article formatting flow. (Yeh bhi sirf article formatting flow ke liye hai.)',
      'Author detail lines are not created from normal body content when the author block is missing. (Author block missing ho to neeche ka content author details nahi banega.)',
    ],
    patterns: [
      'Corresponding author, email, contact, phone, mobile, or university/college detail cues.',
      'Normal abstract/body/heading content is skipped.',
    ],
    styles: [
      'No required style.',
    ],
    signatures: [
      'No signature match is used.',
    ],
    examples: [
      'Department of Computer Science -> author_details',
      'ABC University, India -> author_details',
    ],
    codeRefs: [
      {
        name: '_classify_block_type',
        detail: 'Author details ke liye classifier explicit detail cues check karta hai; broad early-paragraph fallback use nahi hota.',
      },
      {
        name: '_looks_like_corresponding_author_detail',
        detail: 'Yeh helper sirf author/contact/affiliation cue wali lines ko author_details allow karta hai.',
      },
    ],
  },
  abstract: {
    summary: 'Abstract is detected either from an explicit Abstract label or by matching the saved abstract text.',
    order: [
      'If text equals `Abstract` or starts with `Abstract:`, abstract mode starts. (Abstract label milte hi app abstract section shuru maan leti hai.)',
      'If a paragraph matches the saved abstract body, it can also enter abstract mode. (Saved format ke abstract text se match hone par bhi yeh abstract maana ja sakta hai.)',
      'Abstract mode continues until keywords or a heading-style boundary is detected. (Abstract section tab tak chalta hai jab tak next clear section start na ho.)',
    ],
    patterns: [
      '`Abstract`',
      '`Abstract:`',
      'A paragraph body already saved as abstract in the selected format',
    ],
    styles: [
      'Ending abstract mode can depend on heading-style boundaries.',
    ],
    signatures: [
      'No signature-based abstract detection is used.',
    ],
    examples: [
      'Abstract: This study proposes... -> abstract',
      'Abstract -> starts abstract section',
    ],
    codeRefs: [
      {
        name: '_starts_abstract_section',
        detail: 'Yeh function check karta hai ki text exactly `Abstract` hai ya `Abstract:` se start ho raha hai. Match milte hi abstract mode on ho jata hai.',
      },
      {
        name: '_matches_saved_abstract',
        detail: 'Agar selected format mein abstract pehle se saved hai, to incoming paragraph ko normalized text se compare karke abstract body ke roop mein pehchana ja sakta hai.',
      },
      {
        name: '_ends_abstract_section',
        detail: 'Abstract mode ko band karne ke liye yeh function keywords ya heading-style boundary dekhता hai. Isse abstract ke baad ka content alag classify hota hai.',
      },
    ],
  },
  keywords: {
    summary: 'Keywords are detected from explicit `Keywords` or `Index Terms` labels.',
    order: [
      'When a paragraph starts with `Keywords` or `Index Terms`, it is classified as keywords. (Keywords label se start hone wala text keywords maana jata hai.)',
      'After keywords are seen, heading detection becomes active for body content. (Iske baad app body sections aur headings ko detect karna start karti hai.)',
    ],
    patterns: [
      '`Keywords:`',
      '`Keyword`',
      '`Keyword:`',
      '`Keywords`',
      '`Index Term`',
      '`Index Term:`',
      '`Index Terms:`',
    ],
    styles: [
      'No required style.',
    ],
    signatures: [
      'No signature match is used.',
    ],
    examples: [
      'Keywords: AI, automation, parsing -> keywords',
      'Index Terms: machine learning -> keywords',
    ],
    codeRefs: [
      {
        name: '_classify_block_type',
        detail: 'Keywords detection bhi main classifier mein inline hota hai. Yahan lowercase text par direct prefix checks hote hain.',
      },
      {
        name: 'lower_text.startswith("keywords")',
        detail: 'Text agar `Keywords` ya `Index Terms` se start karta hai to block ko keywords type de diya jata hai. Isi ke baad body heading detection active hoti hai.',
      },
    ],
  },
  heading_1: {
    summary: 'Heading 1 can come from saved text match, saved formatting signature, Word style match, or text-pattern inference.',
    order: [
      'Saved heading text lookup is checked first. (Sabse pehle yeh dekha jata hai ki paragraph ka text pehle se saved heading texts se match karta hai ya nahi.)',
      'Then saved formatting signature is compared. (Agar text match na ho to saved formatting se compare kiya jata hai.)',
      'Then DOCX style `Heading1` is checked. (Uske baad Word ka Heading 1 style dekha jata hai.)',
      'Then text patterns are inferred. (Phir numbering ya heading-jaisa text pattern samjha jata hai.)',
    ],
    patterns: [
      'Roman numeral style like `I. Introduction` can infer heading_1.',
      'Saved format text like `INTRODUCTION` can force heading_1.',
    ],
    styles: [
      'DOCX style `Heading 1` or `Heading1` after keywords/body context.',
    ],
    signatures: [
      'Compares paragraph formatting with saved heading_1 rule signature.',
      'Main compared fields: bold, italic, underline, font_family, align.',
    ],
    examples: [
      'I. Introduction -> heading_1',
      'Heading 1 style + `METHODOLOGY` -> heading_1',
      'Saved rule text `INTRODUCTION` + uploaded `INTRODUCTION` -> heading_1',
    ],
    codeRefs: [
      {
        name: '_build_saved_heading_lookup',
        detail: 'Yeh selected saved format ke heading texts ka normalized lookup banata hai. Matlab agar saved rules mein `INTRODUCTION` heading_1 ke andar hai, to uploaded doc ka same normalized text direct heading_1 ho sakta hai.',
      },
      {
        name: '_match_heading_level_from_rule_signature',
        detail: 'Yeh paragraph ke formatting signature ko saved heading rule signature se compare karta hai. Compare hone wale main fields hain: bold, italic, underline, font_family aur align. Agar text alag ho lekin style same ho, to bhi heading_1 detect ho sakta hai.',
      },
      {
        name: '_infer_heading_level_from_text',
        detail: 'Agar saved text aur signature dono se match na mile, to yeh text pattern infer karta hai. Roman numeral style jaise `I.`, `II.` ya kuch heading-like patterns ko dekh kar heading level decide karta hai.',
      },
    ],
  },
  heading_2: {
    summary: 'Heading 2 is usually detected from saved heading text/signature, Heading 2 style, or decimal/alphabetic numbering patterns.',
    order: [
      'Saved text match and signature match run before generic pattern inference. (Pehle saved text aur style matching try hoti hai.)',
      'DOCX style `Heading2` also maps directly. (Agar Word style Heading 2 ho to direct heading_2 ban sakta hai.)',
      'If numbering pattern looks like one or two decimal levels, it typically becomes heading_2. (Jaise `1.` ya `1.1` wali numbering aksar heading_2 hoti hai.)',
    ],
    patterns: [
      '`1. Overview`',
      '`1.1 Scope`',
      '`A. Problem Statement`',
    ],
    styles: [
      'DOCX style `Heading 2` or `Heading2`.',
    ],
    signatures: [
      'Same signature comparison path as heading_1 using saved heading_2 formatting.',
    ],
    examples: [
      '3. Overview -> heading_2',
      '1.1 Scope -> heading_2',
      'Heading 2 style + `RELATED WORK` -> heading_2',
    ],
    codeRefs: [
      {
        name: '_infer_heading_level_from_text',
        detail: 'Heading_2 ke liye yeh function decimal ya alphabetic numbering patterns padhta hai. Yeh check karta hai ki text heading-jaisa lag raha hai ya nahi.',
      },
      {
        name: 'decimal numbering depth <= 2',
        detail: 'Agar numbering `1.` ya `1.1` jaisi ho, to code uski depth count karta hai. Depth 1 ya 2 hone par yeh heading_2 side push hota hai.',
      },
    ],
  },
  heading_3: {
    summary: 'Heading 3 is detected when saved rules/style match or when numbering depth looks deeper than heading_2.',
    order: [
      'Saved text/signature matches can assign heading_3. (Saved text ya formatting match ho to yeh heading_3 ban sakta hai.)',
      'DOCX style `Heading3` maps directly. (Word ka Heading 3 style direct use ho sakta hai.)',
      'Deeper decimal numbering falls back to heading_3. (Jaise `1.1.1` jaisi deeper numbering heading_3 me ja sakti hai.)',
    ],
    patterns: [
      '`1.1.1 Detailed Flow`',
      'Run-in heading style like `Result: ...` may be converted into heading_3 in extractor-side saved rules.',
    ],
    styles: [
      'DOCX style `Heading 3` or `Heading3`.',
    ],
    signatures: [
      'Saved heading_3 formatting signature can drive detection.',
    ],
    examples: [
      '1.1.1 Dataset Preparation -> heading_3',
      'Heading 3 style + `Implementation Notes` -> heading_3',
    ],
    codeRefs: [
      {
        name: '_infer_heading_level_from_text',
        detail: 'Heading_3 inference bhi isi function mein hoti hai, lekin yahan focus deeper numbering par hota hai.',
      },
      {
        name: 'depth > 2',
        detail: 'Agar numbering `1.1.1` ya usse deeper structure dikhaye, to code usko lower-level heading maan kar heading_3 classify karta hai.',
      },
    ],
  },
  content: {
    summary: 'If no earlier rule matches, the paragraph falls back to content.',
    order: [
      'Content is the final fallback after title, abstract, keywords, headings, references, table title, and figure title checks. (Jab kuch aur match nahi hota, tab block content ban jata hai.)',
      'In the document formatting flow, the article-flow author-field fallback is skipped, so more early body text can become content there. (Document formatting flow me kuch early text bhi seedha content ban sakta hai.)',
    ],
    patterns: [
      'No special pattern required.',
    ],
    styles: [
      'No specific style required.',
    ],
    signatures: [
      'No content-specific signature detection is used.',
    ],
    examples: [
      'This section explains the overall workflow... -> content',
      'The experiment was conducted on... -> content',
    ],
    codeRefs: [
      {
        name: '_classify_block_type',
        detail: 'Saare specific checks fail hone ke baad wahi main classifier final fallback choose karta hai.',
      },
      {
        name: 'fallback',
        detail: 'Fallback ka matlab hai paragraph kisi known pattern, style rule, special label, heading rule, ya positional rule mein fit nahi hua. Tab usko content bana diya jata hai.',
      },
    ],
  },
  table: {
    summary: 'Table blocks come from real DOCX tables or from adjacent tab-separated rows that the parser treats as a synthetic table.',
    order: [
      'Real DOCX table nodes are captured directly as table blocks. (Agar document me actual Word table hai, to parser usko seedha table block bana deta hai.)',
      'Paragraph rows that look tab-separated can also be grouped into one table. (Agar multiple paragraph rows tab-separated table jaise lagte hain, to unhe bhi ek synthetic table maana ja sakta hai.)',
    ],
    patterns: [
      'Real inserted DOCX table',
      'Tab-separated rows like `Name\\tValue\\tScore`',
      'Multiple adjacent rows with 3 or more tab-separated cells',
    ],
    styles: [
      'No paragraph style match is required for real tables.',
      'Synthetic paragraph-table detection depends on row structure, not heading style.',
    ],
    signatures: [
      'No formatting-signature comparison is used for table detection.',
    ],
    examples: [
      'Actual Word table -> table',
      '`Metric\\tValue\\tUnit` + next tab-separated rows -> table',
    ],
    codeRefs: [
      {
        name: '_collect_tabular_paragraph_rows',
        detail: 'Yeh helper adjacent paragraph rows ko dekh kar check karta hai ki kya wo tab-separated table jaise lag rahe hain. Agar haan, to un rows ko ek synthetic table block me collect kar leta hai.',
      },
      {
        name: '_append_or_merge_table_block',
        detail: 'Yeh helper collected rows ko final table block me convert karta hai aur matching adjacent table rows ko merge bhi kar sakta hai.',
      },
    ],
  },
  table_title: {
    summary: 'Table title is detected from explicit table-caption markers, including Roman numerals and two-line captions placed next to a real table or table-like rows.',
    order: [
      'This check runs very early in classification. (Table title pattern ko shuru me hi pakadne ki koshish hoti hai.)',
      'If a paragraph is only a marker like `TABLE V` and the next paragraph looks like the caption text, both lines can be merged into one table title. (Agar pehli line sirf caption marker ho aur next line title ho, to dono ko jod kar ek hi table title banaya ja sakta hai.)',
      'The multiline caption path is only used when an adjacent block actually looks like a table or tab-separated table rows, whether that table appears before or after the caption. (Yeh merge tabhi hota hai jab caption ke bagal me actual table ya table-jaisi rows milen, chahe table pehle ho ya baad me.)',
    ],
    patterns: [
      '`Table 1:`',
      '`Table 2 - Results`',
      '`Table 3. Sample Output`',
      '`TABLE III` + next line caption text',
      '`TABLE VIII Behavior Classification Method Comparison`',
    ],
    styles: [
      'No style requirement.',
    ],
    signatures: [
      'No signature match is used.',
    ],
    examples: [
      'Table 1: Dataset Statistics -> table_title',
      'TABLE III + `Training Progress Across Epochs` -> merged table_title',
      'TABLE VII Comparison with State-of-the-Art Detection Methods -> table_title',
    ],
    codeRefs: [
      {
        name: '_looks_like_table_title_text',
        detail: 'Classifier text ke start par `Table` marker dekhta hai aur numeric ke saath Roman numeral captions bhi allow karta hai. Yeh normal prose sentence aur real caption mein difference karne ki koshish karta hai.',
      },
      {
        name: '_resolve_multiline_table_caption',
        detail: 'Yeh helper `TABLE V` jaise marker ko next caption line ke saath merge karta hai, lekin sirf tab jab uske aas-paas actual table ya tab-separated table rows milen. Matlab table caption se pehle bhi ho sakta hai aur baad me bhi.',
      },
    ],
  },
  figure: {
    summary: 'Figure blocks come from actual images embedded inside DOCX paragraphs.',
    order: [
      'The parser first scans each paragraph for embedded drawing/image elements. (Parser paragraph ke andar actual drawing ya image elements dekhta hai.)',
      'Every discovered image becomes a figure block in preview data. (Jo bhi image milti hai woh figure block ban jati hai.)',
      'Figure title detection stays separate and only links to nearby figure captions. (Figure block aur figure caption alag detect hote hain; caption nearby figure se link hota hai.)',
    ],
    patterns: [
      'Embedded image in a DOCX paragraph',
      'Inline drawing/image content',
    ],
    styles: [
      'Figure detection does not depend on paragraph text style.',
      'Alignment is taken from the paragraph that contains the image.',
    ],
    signatures: [
      'No text signature or formatting-signature comparison is used for figure detection.',
    ],
    examples: [
      'Inline DOCX image -> figure',
      'Paragraph containing embedded drawing -> figure',
    ],
    codeRefs: [
      {
        name: '_extract_paragraph_image_blocks',
        detail: 'Yeh helper paragraph ke andar drawing nodes scan karta hai aur har embedded image ko preview ke liye figure block me convert karta hai.',
      },
      {
        name: '_paragraph_contains_image',
        detail: 'Yeh lightweight check batata hai ki paragraph me actual image/drawing hai ya nahi. Isi tarah ka signal figure caption adjacency validate karne me bhi use hota hai.',
      },
    ],
  },
  figure_title: {
    summary: 'Figure title is detected from explicit figure-caption markers, including multiline captions placed next to a real figure.',
    order: [
      'This check also runs very early in classification. (Figure title pattern bhi shuruat me hi detect kiya jata hai.)',
      'A figure-caption line is only accepted when a real figure is placed just before or just after it. (Figure caption tabhi valid maana jata hai jab uske just pehle ya just baad actual figure ho.)',
      'If a paragraph is only a marker like `FIGURE III` and the next paragraph looks like the caption text, both lines can be merged into one figure title. (Agar pehli line sirf figure marker ho aur next line title ho, to dono ko jod kar ek hi figure title banaya ja sakta hai.)',
    ],
    patterns: [
      '`Figure 1:`',
      '`Figure 2 - Model Architecture`',
      '`Fig. 3 Sample Screen`',
      '`FIGURE III` + next line caption text',
    ],
    styles: [
      'No style requirement.',
    ],
    signatures: [
      'No signature match is used.',
    ],
    examples: [
      'Figure 1: Proposed Workflow -> figure_title',
      'Fig. 2 System Layout -> figure_title',
      'FIGURE III + `Model Output Overview` -> merged figure_title',
    ],
    codeRefs: [
      {
        name: '_resolve_figure_caption',
        detail: 'Yeh helper single-line aur multiline dono figure captions ko handle karta hai. Caption tabhi accept hota hai jab uske just pehle ya just baad actual figure/image mile.',
      },
      {
        name: '_has_adjacent_figure_item',
        detail: 'Yeh check karta hai ki caption ke adjacent paragraph me actual image drawing hai ya nahi. Isse normal prose me likha `Figure 1` galti se figure_title nahi banega.',
      },
    ],
  },
  references: {
    summary: 'References are detected from explicit references headings and then references mode keeps later entries in the references block.',
    order: [
      'If the text is `References`, `Reference`, or `Bibliography`, references mode starts. (In labels me se koi milte hi references section start maana jata hai.)',
      'While references mode is active, later paragraphs are also treated as references. (Uske baad ke related lines bhi references ke andar jaati hain.)',
    ],
    patterns: [
      '`References`',
      '`Bibliography`',
      'Numbered reference lines after the references heading',
    ],
    styles: [
      'No style requirement.',
    ],
    signatures: [
      'No signature match is used for the heading itself.',
    ],
    examples: [
      'References -> references heading',
      '[1] Author name, paper title... -> references entry',
    ],
    codeRefs: [
      {
        name: '_is_references_heading',
        detail: 'Yeh function normalized text ko `references`, `reference`, `bibliography` jaise labels se compare karta hai. Match hone par references section start mana jata hai.',
      },
      {
        name: 'references_mode',
        detail: 'Ek baar references heading mil gayi to internal `references_mode` flag on ho jata hai. Uske baad aane wali later paragraphs ko references block ke roop mein treat kiya ja sakta hai.',
      },
    ],
  },
  header: {
    summary: 'Header formatting is taken from the saved document-formatting rule and applied to the DOCX section header.',
    order: [
      'Header rules are extracted from the source document header area when a document-formatting format is created. (Yeh body paragraph detection nahi hai, actual DOCX header se aata hai.)',
      'When formatting is applied, the saved header rule is rendered before page body content is finalized. (Final DOCX ke section header me yeh rule apply hota hai.)',
    ],
    patterns: [
      'Saved header text',
      'Optional right-side header text',
      'Optional header image or header layout',
    ],
    styles: [
      'Font family, size, bold, italic, underline, color, and alignment can be stored with the header rule.',
      'Header distance from top and image size can also be configured.',
    ],
    signatures: [
      'Header/footer rules are structured formatting configs, not paragraph detection signatures.',
    ],
    examples: [
      'University Name + department line -> header',
      'Logo above title text -> header',
    ],
    codeRefs: [
      {
        name: '_populate_header_footer',
        detail: 'Extractor source document ke section header se text, image, layout, aur distance collect karta hai.',
      },
      {
        name: '_render_header_story',
        detail: 'Formatter saved header config ko final DOCX header story me render karta hai.',
      },
    ],
  },
  footer: {
    summary: 'Footer formatting is taken from the saved document-formatting rule and applied to the DOCX section footer.',
    order: [
      'Footer rules are extracted from the source document footer area when a document-formatting format is created. (Yeh body paragraph classifier ka hissa nahi hai.)',
      'When formatting is applied, saved footer text and page-number settings are rendered into the final document footer. (Page number bhi footer rule se aa sakta hai.)',
    ],
    patterns: [
      'Saved footer text',
      'Optional dynamic page number',
      'Optional first-page footer variant',
    ],
    styles: [
      'Font family, size, bold, italic, underline, color, and alignment can be stored with the footer rule.',
      'Footer distance from bottom can also be configured.',
    ],
    signatures: [
      'Footer rules are structured formatting configs, not paragraph detection signatures.',
    ],
    examples: [
      'Page 1 -> footer',
      'Journal name + dynamic page number -> footer',
    ],
    codeRefs: [
      {
        name: '_populate_header_footer',
        detail: 'Extractor source document ke section footer se text, page settings, aur distance collect karta hai.',
      },
      {
        name: '_render_footer_story',
        detail: 'Formatter saved footer config aur dynamic page number ko final DOCX footer me render karta hai.',
      },
    ],
  },
};

const DOCUMENT_DETECTION_RULE_DOCS = {
  ...DETECTION_RULE_DOCS,
  title: {
    summary: 'Chapter title is the first main document title block in the document-formatting flow.',
    order: [
      'Runs in the document-formatting flow after explicit labels like table title, figure title, references, abstract, and keywords are checked. (Pehle special section rules check hote hain.)',
      'If the paragraph is the first meaningful text block and no stronger rule matched, it becomes `chapter title`. (Document ka pehla proper title chapter title maana jata hai.)',
      'Author name and author details fallback is not used in document formatting. (Article wali author detection yahan skip hoti hai.)',
    ],
    patterns: [
      'No strict text pattern is required.',
      'Position in the document is the main rule for chapter title.',
    ],
    styles: [
      'No dedicated style check is required for chapter title fallback.',
      'Saved document-formatting title styling is later applied to the block classified as chapter title.',
    ],
    signatures: [
      'No signature comparison is used for chapter title fallback detection.',
    ],
    examples: [
      'CHAPTER 1 INTRODUCTION -> chapter title',
      'INTRODUCTION -> chapter title',
    ],
    codeRefs: [
      {
        name: '_classify_block_type',
        detail: 'Document-formatting classifier paragraph ko top-to-bottom checks se classify karta hai. Jab first meaningful paragraph kisi special rule se match nahi hota, to title field document flow me chapter title ki tarah use hoti hai.',
      },
      {
        name: 'is_new_upload_format',
        detail: 'Document formatting format me author_name/author_details fields nahi hote, isliye title rule chapter title ke role me use hota hai.',
      },
    ],
  },
};

const HEADING_FIELD_KEYS = new Set(['heading_1', 'heading_2', 'heading_3']);
const NUMBERING_FIELD_KEYS = new Set(['heading_1', 'heading_2', 'heading_3', 'references']);
const HEADER_FOOTER_FIELD_KEYS = new Set(['header', 'footer']);
const SINGLE_INSTANCE_BLOCK_TYPES = new Set(['title', 'abstract', 'keywords', 'references', 'header', 'footer']);

function isSingleInstanceDetectionRule(fieldKey) {
  return SINGLE_INSTANCE_BLOCK_TYPES.has(fieldKey);
}

const NUMBERING_STYLE_OPTIONS = [
  { value: 'none', label: 'No numbering' },
  { value: 'decimal', label: '1. 2. 3.' },
  { value: 'decimal-bracket', label: '[1] [2] [3]', referencesOnly: true },
  { value: 'decimal-nested', label: '1.1 1.2 1.3' },
  { value: 'decimal-paren', label: '1) 2) 3)' },
  { value: 'upper-roman', label: 'I. II. III.' },
  { value: 'upper-alpha', label: 'A. B. C.' },
  { value: 'lower-alpha-paren', label: 'a) b) c)' },
  { value: 'lower-alpha', label: 'a. b. c.' },
  { value: 'lower-roman', label: 'i. ii. iii.' },
  { value: 'custom', label: 'Custom text' },
];

const LIST_BULLET_STYLE_OPTIONS = [
  { value: 'disc', label: '• Bullet' },
  { value: 'circle', label: '○ Circle' },
  { value: 'square', label: '■ Square' },
];

const LIST_NUMBER_STYLE_OPTIONS = [
  { value: 'decimal', label: '1. 2. 3.' },
  { value: 'decimal-paren', label: '1) 2) 3)' },
  { value: 'upper-alpha', label: 'A. B. C.' },
  { value: 'lower-alpha', label: 'a. b. c.' },
  { value: 'lower-alpha-paren', label: 'a) b) c)' },
  { value: 'upper-roman', label: 'I. II. III.' },
  { value: 'lower-roman', label: 'i. ii. iii.' },
];

const ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
  { value: 'justify', label: 'Justify' },
];

const PAGE_NUMBER_OPTIONS = [
  { value: 'decimal', label: '1, 2, 3' },
  { value: 'upper-roman', label: 'I, II, III' },
  { value: 'lower-roman', label: 'i, ii, iii' },
  { value: 'upper-alpha', label: 'A, B, C' },
  { value: 'lower-alpha', label: 'a, b, c' },
];

const PAGE_NUMBER_ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const TITLE_POSITION_OPTIONS = [
  { value: 'above', label: 'Above' },
  { value: 'below', label: 'Below' },
];

const TEXT_TRANSFORM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'lowercase', label: 'lower case' },
  { value: 'sentence_case', label: 'sentence case' },
  { value: 'capitalize_words', label: 'Capitalized Each Word' },
];

const HEADER_LAYOUT_OPTIONS = [
  { value: 'text_only', label: 'Text Only' },
  { value: 'text_left_text_right', label: 'Text Left, Text Right' },
  { value: 'image_only', label: 'Image Only' },
  { value: 'image_top_text_bottom', label: 'Image Top, Text Bottom' },
  { value: 'text_top_image_bottom', label: 'Text Top, Image Bottom' },
  { value: 'image_left_text_right', label: 'Image Left, Text Right' },
  { value: 'text_left_image_right', label: 'Text Left, Image Right' },
];

const HEADER_FOOTER_PAGE_MODE_OPTIONS = [
  { value: 'all_pages_same', label: 'Same on all pages' },
  { value: 'first_page_different', label: 'First page different' },
];

const SPECIAL_COLUMN_COUNT_OPTIONS = [
  { value: 1, label: '1 Column' },
  { value: 2, label: '2 Columns' },
  { value: 3, label: '3 Columns' },
];

const SPECIAL_COLUMN_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'text_image', label: 'Text + Image' },
];

const DEFAULT_FIELD_RULES = {
  title: {
    font_family: 'Times New Roman',
    font_size: '18',
    bold: true,
    italic: false,
    underline: false,
    alignment: 'center',
    line_spacing: '1.3',
    spacing_before: '0',
    spacing_after: '8',
    text_transform: 'none',
  },
  author_name: {
    font_family: 'Times New Roman',
    font_size: '12',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'center',
    line_spacing: '1.15',
    spacing_before: '0',
    spacing_after: '4',
    text_transform: 'capitalize_words',
  },
  author_details: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'center',
    line_spacing: '1.15',
    spacing_before: '0',
    spacing_after: '10',
    text_transform: 'capitalize_words',
  },
  abstract: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.35',
    spacing_before: '0',
    spacing_after: '6',
    text_transform: 'none',
  },
  keywords: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: false,
    italic: true,
    underline: false,
    alignment: 'left',
    line_spacing: '1.2',
    spacing_before: '0',
    spacing_after: '8',
    text_transform: 'none',
  },
  heading_1: {
    font_family: 'Times New Roman',
    font_size: '14',
    bold: true,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.2',
    spacing_before: '0',
    spacing_after: '6',
    text_transform: 'none',
  },
  heading_2: {
    font_family: 'Times New Roman',
    font_size: '12',
    bold: true,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.2',
    spacing_before: '0',
    spacing_after: '6',
    text_transform: 'none',
  },
  heading_3: {
    font_family: 'Times New Roman',
    font_size: '11',
    bold: true,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.2',
    spacing_before: '0',
    spacing_after: '6',
    text_transform: 'none',
  },
  equation: {
    font_family: 'Cambria Math',
    font_size: '11',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'center',
    line_spacing: '1.2',
    spacing_before: '6',
    spacing_after: '6',
    text_transform: 'none',
  },
  content: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'justify',
    line_spacing: '1.35',
    spacing_before: '0',
    spacing_after: '6',
    text_transform: 'none',
  },
  table_title: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: true,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.15',
    spacing_before: '0',
    spacing_after: '6',
    title_position: 'above',
    text_transform: 'none',
  },
  figure_title: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: true,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.15',
    spacing_before: '0',
    spacing_after: '6',
    title_position: 'below',
    text_transform: 'none',
  },
  references: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.2',
    spacing_before: '0',
    spacing_after: '4',
    text_transform: 'none',
  },
  table: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'left',
    line_spacing: '1.15',
    spacing_before: '0',
    spacing_after: '8',
    text_transform: 'none',
  },
  figure: {
    font_family: 'Times New Roman',
    font_size: '10',
    bold: false,
    italic: false,
    underline: false,
    alignment: 'center',
    line_spacing: '1.15',
    spacing_before: '0',
    spacing_after: '8',
    text_transform: 'none',
  },
};

function App() {
  const [authSession, setAuthSession] = useState(loadAuthSession);
  const externalLoginAttemptedCodeRef = useRef('');
  const [routePanel, setRoutePanel] = useState(getPanelFromHash());
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({
    name: '',
    identifier: '',
    email: '',
    password: '',
    secret_key: '',
  });
  const [authLoading, setAuthLoading] = useState(false);
  const [formats, setFormats] = useState([]);
  const [selectedFormatId, setSelectedFormatId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedNewUploadFile, setSelectedNewUploadFile] = useState(null);
  const [rawSelectedFile, setRawSelectedFile] = useState(null);
  const [optimizeSelectedFile, setOptimizeSelectedFile] = useState(null);
  const [indexSelectedFile, setIndexSelectedFile] = useState(null);
  const [indexPreviewDocument, setIndexPreviewDocument] = useState(null);
  const [indexPreviewHistory, setIndexPreviewHistory] = useState([]);
  const [rules, setRules] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [previewDocument, setPreviewDocument] = useState(null);
  const [selectedPreviewBlockId, setSelectedPreviewBlockId] = useState('');
  const [selectedPreviewBlockIds, setSelectedPreviewBlockIds] = useState([]);
  const skipNextPreviewSelectRef = useRef('');
  const [previewHistory, setPreviewHistory] = useState([]);
  const [optimizePreviewDocument, setOptimizePreviewDocument] = useState(null);
  const [selectedOptimizeBlockId, setSelectedOptimizeBlockId] = useState('');
  const [optimizeSpreadIndex, setOptimizeSpreadIndex] = useState(0);
  const [page, setPage] = useState(getPageFromHash());
  const [specialHeaderConfigDrafts, setSpecialHeaderConfigDrafts] = useState({});
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [selectedDetectionRuleKey, setSelectedDetectionRuleKey] = useState('title');
  const [selectedDocumentDetectionRuleKey, setSelectedDocumentDetectionRuleKey] = useState('title');
  const [selectedDetectionRuleScope, setSelectedDetectionRuleScope] = useState('article');
  const [selectedDownloadDate, setSelectedDownloadDate] = useState(getCurrentDateInputValue);
  const [registeredUsersSearchTerm, setRegisteredUsersSearchTerm] = useState('');
  const [showAppliedRulesTable, setShowAppliedRulesTable] = useState(false);
  const [pendingAppliedRuleChange, setPendingAppliedRuleChange] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [resettingUserId, setResettingUserId] = useState('');
  const [resetPasswordModalUser, setResetPasswordModalUser] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingNewFormat, setUploadingNewFormat] = useState(false);
  const [applyingRules, setApplyingRules] = useState(false);
  const [optimizingDoc, setOptimizingDoc] = useState(false);
  const [generatingIndex, setGeneratingIndex] = useState(false);
  const [downloadingIndexDoc, setDownloadingIndexDoc] = useState(false);
  const [downloadingOptimizedDoc, setDownloadingOptimizedDoc] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingFormatId, setTogglingFormatId] = useState('');
  const [activeRow, setActiveRow] = useState('');
  const [message, setMessage] = useState('');
  const [isMessageDismissing, setIsMessageDismissing] = useState(false);
  const [error, setError] = useState('');
  const classicFormats = useMemo(() => {
    const pinnedFormatRank = (format) => {
      const label = String(format?.format_name || format?.document_name || '').trim().toLowerCase();
      if (label.includes('springer')) return 0;
      if (label.includes('ieee')) return 1;
      return 2;
    };

    return formats
      .filter((format) => !isNewUploadFormat(format))
      .map((format, originalIndex) => ({ format, originalIndex }))
      .sort((left, right) => (
        pinnedFormatRank(left.format) - pinnedFormatRank(right.format)
        || left.originalIndex - right.originalIndex
      ))
      .map(({ format }) => format);
  }, [formats]);
  const newUploadFormats = useMemo(() => formats.filter((format) => isNewUploadFormat(format)), [formats]);
  const activeSpecialHeaderConfig = useMemo(
    () => getSpecialHeaderConfigForFormat(specialHeaderConfigDrafts, selectedFormatId, rules),
    [specialHeaderConfigDrafts, selectedFormatId, rules]
  );
  const isSpecialHeaderConfigSaved = useMemo(
    () => isSpecialHeaderConfigPersisted(activeSpecialHeaderConfig, rules),
    [activeSpecialHeaderConfig, rules]
  );
  void selectedOptimizeBlockId;
  void setSelectedOptimizeBlockId;
  void optimizeSpreadIndex;
  void setOptimizeSpreadIndex;
  void downloadingOptimizedDoc;
  void setDownloadingOptimizedDoc;

  const currentRole = authSession?.user?.role || '';
  const authToken = authSession?.token || '';

  useEffect(() => {
    if (authToken) {
      loadRules();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    if (currentRole === 'admin' && page === 'registered-users') {
      loadRegisteredUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole, page, selectedDownloadDate]);

  useEffect(() => {
    setPreviewDocument((currentDocument) => reconcilePreviewDocument(currentDocument, rules));
  }, [rules]);

  useEffect(() => {
    function syncPage() {
      setPage(getPageFromHash());
      setRoutePanel(getPanelFromHash());
    }

    window.addEventListener('hashchange', syncPage);
    window.addEventListener('popstate', syncPage);
    return () => {
      window.removeEventListener('hashchange', syncPage);
      window.removeEventListener('popstate', syncPage);
    };
  }, []);

  useEffect(() => {
    if (authSession?.token) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authSession));
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [authSession]);

  useEffect(() => {
    if (routePanel !== 'external-login') {
      return;
    }

    async function completeExternalLogin() {
      const code = getExternalLoginCode();
      if (code && externalLoginAttemptedCodeRef.current === code) {
        return;
      }

      externalLoginAttemptedCodeRef.current = code;
      setAuthLoading(true);
      setError('');
      setMessage('Checking your abcdindex session...');

      if (!code) {
        setAuthLoading(false);
        setMessage('');
        setError('External login code is missing. Please open this link from abcdindex again.');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/external-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });
        const data = await readJsonResponse(response);

        if (!response.ok) {
          throw new Error(data.error || 'External login failed.');
        }

        setAuthSession(data);
        setMessage('Logged in from abcdindex.');
        setError('');
        navigateTo('apply');
      } catch (requestError) {
        setMessage('');
        setError(getFriendlyErrorMessage(requestError));
      } finally {
        setAuthLoading(false);
      }
    }

    completeExternalLogin();
  }, [routePanel]);

  useEffect(() => {
    setPreviewDocument((currentDocument) => {
      if (!currentDocument) {
        return currentDocument;
      }

      const nextSpecialConfig = hasSpecialHeaderFooterConfig(activeSpecialHeaderConfig)
        ? normalizeSpecialHeaderConfig(activeSpecialHeaderConfig)
        : null;

      if (JSON.stringify(currentDocument.special_header_config || null) === JSON.stringify(nextSpecialConfig)) {
        return currentDocument;
      }

      return {
        ...currentDocument,
        special_header_config: nextSpecialConfig,
      };
    });
  }, [activeSpecialHeaderConfig]);

  useEffect(() => {
    if (!message) {
      setIsMessageDismissing(false);
      return;
    }

    setIsMessageDismissing(false);
    const dismissTimer = window.setTimeout(() => {
      setIsMessageDismissing(true);
    }, 1600);
    const clearTimer = window.setTimeout(() => {
      setMessage('');
      setIsMessageDismissing(false);
    }, 2000);

    return () => {
      window.clearTimeout(dismissTimer);
      window.clearTimeout(clearTimer);
    };
  }, [message]);

  const previewRows = useMemo(() => flattenRuleRows(rules, drafts, false), [rules, drafts]);
  const manageRows = useMemo(() => {
    if ((page === DOCUMENT_MANAGE_PAGE || page === DOCUMENT_APPLY_PAGE) && !isNewUploadFormat(rules)) {
      return [];
    }

    if ((page === 'manage' || page === 'apply') && isNewUploadFormat(rules)) {
      return [];
    }

    return flattenRuleRows(rules, drafts, true);
  }, [rules, drafts, page]);

  async function authFetch(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    });

    if (response.status === 401) {
      setAuthSession(null);
      setMessage('');
      setError('Session expired. Please login again.');
    }

    return response;
  }

  async function handleAuthSubmit(event) {
    event.preventDefault();
    setAuthLoading(true);
    setError('');
    setMessage('');

    const wantsAdmin = routePanel === 'admin';
    const endpoint = wantsAdmin
      ? '/auth/admin/login'
      : authMode === 'register'
        ? '/auth/user/register'
        : '/auth/user/login';
    const payload = wantsAdmin
      ? { identifier: authForm.identifier, password: authForm.password, secret_key: authForm.secret_key }
      : authMode === 'register'
        ? { name: authForm.name, email: authForm.email, password: authForm.password }
        : { email: authForm.email, password: authForm.password };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Login failed.');
      }

      setAuthSession(data);
      setAuthForm({ name: '', identifier: '', email: '', password: '', secret_key: '' });
      setMessage(wantsAdmin ? 'Admin login successful.' : 'User login successful.');
      navigateTo(wantsAdmin ? 'home' : 'apply');
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setAuthLoading(false);
    }
  }

  function handleLogout() {
    const wasAdmin = currentRole === 'admin';
    const shouldReturnToAbcdindex = authSession?.user?.external_provider === 'abcdindex';
    setAuthSession(null);
    setRules(null);
    setDrafts({});
    setFormats([]);
    setRegisteredUsers([]);
    setSelectedDownloadDate(getCurrentDateInputValue());
    setRegisteredUsersSearchTerm('');
    setResetPasswordModalUser(null);
    setResetPasswordForm({ password: '', confirmPassword: '' });
    setSelectedFormatId('');
    setSpecialHeaderConfigDrafts({});
    setPreviewDocument(null);
    setSelectedPreviewBlockId('');
    setSelectedPreviewBlockIds([]);
    setOptimizePreviewDocument(null);
    setIndexSelectedFile(null);
    setIndexPreviewDocument(null);
    setMessage('Logged out successfully.');
    setError('');

    if (shouldReturnToAbcdindex) {
      window.location.assign('https://abcdindex.com/');
      return;
    }

    navigateTo(wasAdmin ? 'admin-login' : 'user-login');
  }

  function buildFormatOption(payload, fallbackId = '') {
    if (!payload) {
      return null;
    }

    const formatId = payload.format_id || fallbackId || '';
    if (!formatId) {
      return null;
    }

    return {
      format_id: formatId,
      format_name: payload.format_name || payload.document_name || 'Untitled Format',
      document_name: payload.document_name || '',
      template_columns: payload.template_columns === 2 ? 2 : 1,
      updated_at: payload.updated_at || '',
      source: payload.source || '',
      is_enabled: payload.is_enabled !== false,
    };
  }

  function getFormatLabel(format) {
    return format?.format_name || format?.document_name || 'Untitled Format';
  }

  function normalizeFormatOptions(list) {
    const seenIds = new Set();
    const seenLabels = new Set();

    return (Array.isArray(list) ? list : []).filter((format) => {
      if (!format?.format_id) {
        return false;
      }

      const labelKey = getFormatLabel(format).trim().toLowerCase();
      if (seenIds.has(format.format_id) || seenLabels.has(labelKey)) {
        return false;
      }

      seenIds.add(format.format_id);
      seenLabels.add(labelKey);
      return true;
    });
  }

  function getFormatsForCurrentPage(list) {
    const nextFormats = Array.isArray(list) ? list : [];

    if (page === DOCUMENT_MANAGE_PAGE || page === DOCUMENT_APPLY_PAGE) {
      return nextFormats.filter((format) => isNewUploadFormat(format));
    }

    if (page === 'manage' || page === 'apply') {
      return nextFormats.filter((format) => !isNewUploadFormat(format));
    }

    return nextFormats;
  }

  function getFormatTemplateColumns(format) {
    if (format?.format_id && format.format_id === selectedFormatId && rules?.template_columns) {
      return rules.template_columns;
    }

    return format?.template_columns === 2 ? 2 : 1;
  }

  async function handleManageFormat(formatId) {
    if (!formatId) {
      return;
    }

    if (formatId !== selectedFormatId) {
      await loadRulesByFormat(formatId);
    }

    navigateTo('manage');
  }

  async function handleManageNewFormat(formatId) {
    if (!formatId) {
      return;
    }

    openPageInNewTab(DOCUMENT_MANAGE_PAGE, { formatId });
  }

  async function openClassicManagePage() {
    if (!isNewUploadFormat(rules)) {
      navigateTo('manage');
      return;
    }

    const fallbackFormatId = classicFormats[0]?.format_id || '';
    if (fallbackFormatId) {
      await loadRulesByFormat(fallbackFormatId);
    }
    navigateTo('manage');
  }

  async function openNewManagePage() {
    const fallbackFormatId = isNewUploadFormat(rules) ? selectedFormatId : newUploadFormats[0]?.format_id || '';
    openPageInNewTab(DOCUMENT_MANAGE_PAGE, { formatId: fallbackFormatId });
  }

  async function openClassicApplyPage() {
    if (!isNewUploadFormat(rules)) {
      navigateTo('apply');
      return;
    }

    const fallbackFormatId = classicFormats[0]?.format_id || '';
    if (fallbackFormatId) {
      await loadRulesByFormat(fallbackFormatId);
    }
    navigateTo('apply');
  }

  async function openNewApplyPage() {
    const fallbackFormatId = isNewUploadFormat(rules) ? selectedFormatId : newUploadFormats[0]?.format_id || '';
    openPageInNewTab(DOCUMENT_APPLY_PAGE, { formatId: fallbackFormatId });
  }

  async function handleLayoutSettingsFormat(formatId) {
    if (!formatId) {
      return;
    }

    if (formatId !== selectedFormatId) {
      await loadRulesByFormat(formatId);
    }

    navigateTo('special-layout-setting');
  }

  async function loadRules() {
    setLoading(true);

    try {
      const response = await authFetch(`${API_BASE_URL}/rules`);
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load rules.');
      }

      setRules(data);
      setDrafts(createDraftMap(data));
      const fallbackFormatId = data.format_id || data.document_name || 'current-format';
      setSelectedFormatId(fallbackFormatId);
      setSpecialHeaderConfigDraftForFormat(fallbackFormatId, data.special_header_config);
      const fallbackOption = buildFormatOption(data, fallbackFormatId);
      setFormats(normalizeFormatOptions(fallbackOption ? [fallbackOption] : []));
      const requestedFormatId = getRequestedFormatId();
      const refreshedFormatId = await refreshFormats(requestedFormatId || fallbackFormatId);
      if (refreshedFormatId && refreshedFormatId !== fallbackFormatId) {
        await loadRulesByFormat(refreshedFormatId);
      }
    } catch {
      setRules(null);
      setDrafts({});
      setSelectedFormatId('');
      setFormats([]);
    } finally {
      setLoading(false);
    }
  }

  async function refreshFormats(preferredFormatId = '') {
    try {
      const response = await authFetch(`${API_BASE_URL}/formats`);
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load formats.');
      }

      const nextFormats = Array.isArray(data.formats) ? data.formats : [];
      if (nextFormats.length === 0) {
        return preferredFormatId || '';
      }

      const scopedFormats = getFormatsForCurrentPage(nextFormats);
      const preferredFormat = scopedFormats.find((format) => format.format_id === preferredFormatId);
      const activeFormat = scopedFormats.find((format) => format.format_id === data.active_format_id);
      const resolvedFormatId = preferredFormat?.format_id || activeFormat?.format_id || scopedFormats[0]?.format_id || preferredFormatId || '';
      setFormats(normalizeFormatOptions(nextFormats));
      setSelectedFormatId(resolvedFormatId);
      return resolvedFormatId;
    } catch {
      return preferredFormatId || '';
    }
  }

  async function loadRulesByFormat(formatId) {
    if (!formatId) {
      setRules(null);
      setDrafts({});
      setSpecialHeaderConfigDrafts({});
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/rules?format_id=${encodeURIComponent(formatId)}`);
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load rules.');
      }

      setRules(data);
      setDrafts(createDraftMap(data));
      const fallbackFormatId = data.format_id || formatId || data.document_name || 'selected-format';
      setSelectedFormatId(fallbackFormatId);
      setSpecialHeaderConfigDraftForFormat(fallbackFormatId, data.special_header_config);
      const fallbackOption = buildFormatOption(data, fallbackFormatId);
      setFormats((currentFormats) => {
        if (!fallbackOption) {
          return normalizeFormatOptions(currentFormats);
        }

        const filteredFormats = currentFormats.filter((format) => format.format_id !== fallbackOption.format_id);
        return normalizeFormatOptions([fallbackOption, ...filteredFormats]);
      });
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  async function handleFormatSelectionChange(event) {
    const nextFormatId = event.target.value;
    setPreviewDocument(null);
    setSelectedPreviewBlockId('');
    setSelectedPreviewBlockIds([]);
    setPreviewHistory([]);
    await loadRulesByFormat(nextFormatId);
  }

  function setSpecialHeaderConfigDraftForFormat(formatId, config) {
    if (!formatId) {
      return;
    }

    setSpecialHeaderConfigDrafts((currentDrafts) => ({
      ...currentDrafts,
      [formatId]: hasSpecialHeaderFooterConfig(config)
        ? normalizeSpecialHeaderConfig(config)
        : createDefaultSpecialHeaderConfig(),
    }));
  }

  function updateActiveSpecialHeaderConfig(nextConfig) {
    setSpecialHeaderConfigDrafts((currentDrafts) => {
      const targetFormatId = selectedFormatId || rules?.format_id || '';
      if (!targetFormatId) {
        return currentDrafts;
      }

      return {
        ...currentDrafts,
        [targetFormatId]: normalizeSpecialHeaderConfig(nextConfig),
      };
    });
  }

  async function handleSaveSpecialHeaderConfig() {
    if (!rules) {
      setError('Select a saved format before saving special header/footer.');
      setMessage('');
      return;
    }

    await persistSpecialHeaderConfig(
      normalizeSpecialHeaderConfig(activeSpecialHeaderConfig),
      isSpecialHeaderConfigSaved ? 'Special header/footer config updated and saved.' : 'Special header/footer config saved.'
    );
  }

  async function handleClearSpecialHeaderConfig() {
    if (!rules) {
      setError('Select a saved format before clearing special header/footer.');
      setMessage('');
      return;
    }

    await persistSpecialHeaderConfig(null, 'Special header/footer config cleared.');
  }

  async function persistSpecialHeaderConfig(nextConfig, successMessage) {
    if (!rules) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const normalizedConfig = hasSpecialHeaderFooterConfig(nextConfig)
        ? normalizeSpecialHeaderConfig(nextConfig)
        : null;
      const nextRules = {
        ...rules,
        special_header_config: normalizedConfig,
        header_separator: normalizeHeaderSeparator(rules.header_separator),
        footer_separator: normalizeFooterSeparator(rules.footer_separator),
      };
      const requestUrl = selectedFormatId
        ? `${API_BASE_URL}/rules/update?format_id=${encodeURIComponent(selectedFormatId)}`
        : `${API_BASE_URL}/rules/update`;
      const response = await authFetch(requestUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(nextRules),
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Special header/footer save failed.');
      }

      const fallbackFormatId = data.format_id || selectedFormatId || rules.format_id || '';
      setRules(data);
      setDrafts(createDraftMap(data));
      setSpecialHeaderConfigDraftForFormat(fallbackFormatId, data.special_header_config);
      setPreviewDocument((currentDocument) =>
        currentDocument
          ? {
              ...currentDocument,
              special_header_config: normalizedConfig,
              header_separator: normalizeHeaderSeparator(rules.header_separator),
              footer_separator: normalizeFooterSeparator(rules.footer_separator),
            }
          : currentDocument
      );
      setMessage(successMessage);
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleFormat(formatId, isEnabled) {
    if (!formatId) {
      return;
    }

    setTogglingFormatId(formatId);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/formats/${encodeURIComponent(formatId)}/enabled`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_enabled: isEnabled }),
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || `Unable to ${isEnabled ? 'enable' : 'disable'} format.`);
      }

      setFormats((currentFormats) => currentFormats.map((format) => (
        format.format_id === formatId
          ? { ...format, is_enabled: data.is_enabled }
          : format
      )));
      setMessage(`Format ${isEnabled ? 'enabled' : 'disabled'} successfully.`);
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setTogglingFormatId('');
    }
  }

  async function handleUpload(event) {
    event.preventDefault();
    if (!selectedFile) {
      setError('Choose a DOCX file before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('source', 'upload');

    setUploading(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/upload-docx`, {
        method: 'POST',
        body: formData,
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed.');
      }

      setRules(data);
      setDrafts(createDraftMap(data));
      const fallbackFormatId = data.format_id || data.document_name || selectedFile.name || 'uploaded-format';
      setSelectedFormatId(fallbackFormatId);
      setSpecialHeaderConfigDraftForFormat(fallbackFormatId, data.special_header_config);
      const fallbackOption = buildFormatOption(data, fallbackFormatId);
      setFormats((currentFormats) => {
        if (!fallbackOption) {
          return normalizeFormatOptions(currentFormats);
        }

        const filteredFormats = currentFormats.filter((format) => format.format_id !== fallbackOption.format_id);
        return normalizeFormatOptions([fallbackOption, ...filteredFormats]);
      });
      await refreshFormats(fallbackFormatId);
      setPreviewDocument(null);
      setSelectedPreviewBlockId('');
      setSelectedPreviewBlockIds([]);
      setPreviewHistory([]);
      setMessage(`Extracted rules from ${data.document_name || selectedFile.name}.`);
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setUploading(false);
    }
  }

  async function persistDrafts(nextDrafts, successMessage, rowKey = '') {
    if (!rules) {
      return;
    }

    setSaving(true);
    setActiveRow(rowKey);
    setError('');
    setMessage('');

    try {
      const nextRules = buildPayloadFromDrafts(rules, nextDrafts);
      const requestUrl = selectedFormatId
        ? `${API_BASE_URL}/rules/update?format_id=${encodeURIComponent(selectedFormatId)}`
        : `${API_BASE_URL}/rules/update`;
      const response = await authFetch(requestUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(nextRules),
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Save failed.');
      }

      setRules(data);
      setDrafts(createDraftMap(data));
      setMessage(successMessage);
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setSaving(false);
      setActiveRow('');
    }
  }

  async function handleSaveAll() {
    await persistDrafts(drafts, 'Rules saved to the file-backed database.');
  }

  function updateRulesLayoutSettings(patch) {
    setRules((currentRules) => {
      if (!currentRules) {
        return currentRules;
      }

      const templateColumns = patch.template_columns === 2 ? 2 : currentRules.template_columns === 2 ? 2 : 1;
      const nextDocumentLayout = normalizeOptimizeLayout(
        {
          ...(currentRules.document_layout || {}),
          ...(patch.document_layout || {}),
        },
        templateColumns
      );

      return {
        ...currentRules,
        template_columns: templateColumns,
        document_layout: nextDocumentLayout,
        front_matter_lines: normalizeFrontMatterLines(
          patch.front_matter_lines || currentRules.front_matter_lines
        ),
        header_separator: normalizeHeaderSeparator(
          patch.header_separator || currentRules.header_separator
        ),
        footer_separator: normalizeFooterSeparator(
          patch.footer_separator || currentRules.footer_separator
        ),
      };
    });
  }

  async function handleSaveLayoutSettings() {
    if (!rules) {
      return;
    }

    const normalizedLayout = normalizeOptimizeLayout(rules.document_layout, rules.template_columns);
    if (rules.template_columns === 2 && !isDoubleColumnLayoutInRange(normalizedLayout)) {
      setError('Left margin, right margin, and column gap must be between 0.5 cm and 5 cm.');
      setMessage('');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const nextRules = {
        ...rules,
        template_columns: rules.template_columns === 2 ? 2 : 1,
        document_layout: normalizedLayout,
        front_matter_lines: normalizeFrontMatterLines(rules.front_matter_lines),
        header_separator: normalizeHeaderSeparator(rules.header_separator),
        footer_separator: normalizeFooterSeparator(rules.footer_separator),
      };
      const requestUrl = selectedFormatId
        ? `${API_BASE_URL}/rules/update?format_id=${encodeURIComponent(selectedFormatId)}`
        : `${API_BASE_URL}/rules/update`;
      const response = await authFetch(requestUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(nextRules),
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Layout settings save failed.');
      }

      setRules(data);
      setDrafts(createDraftMap(data));
      setFormats((currentFormats) =>
        normalizeFormatOptions(
          currentFormats.map((format) =>
            format.format_id === selectedFormatId
              ? { ...format, template_columns: data.template_columns === 2 ? 2 : 1 }
              : format
          )
        )
      );
      setMessage('Layout settings saved successfully.');
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }

  async function handleRawUpload(event) {
    event.preventDefault();
    if (!rawSelectedFile) {
      setError('Choose a raw DOCX file before applying rules.');
      return;
    }

    const formData = new FormData();
    formData.append('file', rawSelectedFile);
    if (selectedFormatId) {
      formData.append('format_id', selectedFormatId);
    }
    if (page === DOCUMENT_APPLY_PAGE) {
      formData.append('preserve_embedded_preview', '1');
    }

    setApplyingRules(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/preview/apply`, {
        method: 'POST',
        body: formData,
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Unable to apply saved rules.');
      }

      const normalizedPreviewDocument = data?.embedded_preview_reused
        ? data
        : reconcilePreviewDocument(
            getPreviewDocumentWithSpecialOverride(data, activeSpecialHeaderConfig),
            rules
          );
      setPreviewDocument(normalizedPreviewDocument);
      setSelectedPreviewBlockId(normalizedPreviewDocument?.blocks?.[0]?.id || '');
      setSelectedPreviewBlockIds(normalizedPreviewDocument?.blocks?.[0]?.id ? [normalizedPreviewDocument.blocks[0].id] : []);
      setPreviewHistory([]);
      setMessage(`Preview generated for ${data.document_name || rawSelectedFile.name}.`);
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setApplyingRules(false);
    }
  }

  function updatePreviewBlock(blockId, patch, options = {}) {
    setPreviewDocument((currentDocument) => {
      if (!currentDocument) {
        return currentDocument;
      }

      if (Array.isArray(patch?.__replaceBlocks)) {
        const nextDocument = reconcilePreviewDocument({
          ...currentDocument,
          blocks: currentDocument.blocks.flatMap((block) =>
            block.id === blockId ? patch.__replaceBlocks.map(normalizePreviewHeadingBlock) : [block]
          ),
        }, rules, patch.__selectBlockId || blockId, { disableCascade: options.disableCascade || patch.__disableCascade });

        if (JSON.stringify(nextDocument) !== JSON.stringify(currentDocument)) {
          setPreviewHistory((currentHistory) => [
            ...currentHistory.slice(-99),
            {
              document: currentDocument,
              selectedBlockId: selectedPreviewBlockId,
            },
          ]);
        }

        if (patch.__selectBlockId) {
          setSelectedPreviewBlockId(patch.__selectBlockId);
          setSelectedPreviewBlockIds([patch.__selectBlockId]);
        }

        return nextDocument;
      }

      const nextDocument = reconcilePreviewDocument({
        ...currentDocument,
        blocks: currentDocument.blocks.map((block) =>
          block.id === blockId
            ? normalizePreviewHeadingBlock({
                ...block,
                ...patch,
                format: {
                  ...block.format,
                  ...(patch.format || {}),
                  numbering: {
                    ...block.format.numbering,
                    ...(patch.format?.numbering || {}),
                  },
                },
              })
            : block
        ),
      }, rules, blockId, { disableCascade: options.disableCascade });

      if (JSON.stringify(nextDocument) !== JSON.stringify(currentDocument)) {
        setPreviewHistory((currentHistory) => [
          ...currentHistory.slice(-99),
          {
            document: currentDocument,
            selectedBlockId: selectedPreviewBlockId,
          },
        ]);
      }

      return nextDocument;
    });
  }

  function updatePreviewBlocks(blockIds, patchFactory, options = {}) {
    const normalizedIds = Array.from(new Set((blockIds || []).filter(Boolean)));
    if (normalizedIds.length === 0) {
      return;
    }

    setPreviewDocument((currentDocument) => {
      if (!currentDocument) {
        return currentDocument;
      }

      const blockIdSet = new Set(normalizedIds);
      const nextDocument = reconcilePreviewDocument({
        ...currentDocument,
        blocks: currentDocument.blocks.map((block) => {
          if (!blockIdSet.has(block.id)) {
            return block;
          }

          const nextPatch = typeof patchFactory === 'function' ? patchFactory(block) : patchFactory;
          if (!nextPatch) {
            return block;
          }

          return normalizePreviewHeadingBlock({
            ...block,
            ...nextPatch,
            format: {
              ...block.format,
              ...(nextPatch.format || {}),
              numbering: {
                ...block.format.numbering,
                ...(nextPatch.format?.numbering || {}),
              },
            },
          });
        }),
      }, rules, options.selectedBlockId || normalizedIds[0], { disableCascade: options.disableCascade });

      if (JSON.stringify(nextDocument) !== JSON.stringify(currentDocument)) {
        setPreviewHistory((currentHistory) => [
          ...currentHistory.slice(-99),
          {
            document: currentDocument,
            selectedBlockId: selectedPreviewBlockId,
          },
        ]);
      }

      return nextDocument;
    });

    if (options.selectedBlockId) {
      setSelectedPreviewBlockId(options.selectedBlockId);
    }
    if (Array.isArray(options.selectedBlockIds)) {
      setSelectedPreviewBlockIds(options.selectedBlockIds);
    }
  }

  function mergeSelectedPreviewBlockWithPrevious() {
    const activeBlockId = selectedPreviewBlockId || selectedPreviewBlockIds[0] || '';
    if (!activeBlockId) {
      return;
    }

    setPreviewDocument((currentDocument) => {
      if (!currentDocument?.blocks?.length) {
        return currentDocument;
      }

      const currentBlocks = currentDocument.blocks.map((block) => {
        const blockElement = getPreviewBlockElementById(block.id);
        if (!blockElement) {
          return block;
        }

        return {
          ...block,
          ...getNormalizedBlockContentFromElement(block, blockElement, currentDocument.blocks),
        };
      });
      const selectedIndex = currentBlocks.findIndex((block) => block.id === activeBlockId);
      if (selectedIndex <= 0) {
        return currentDocument;
      }

      const previousBlock = currentBlocks[selectedIndex - 1];
      const selectedBlock = currentBlocks[selectedIndex];
      const mergedBlock = normalizePreviewHeadingBlock({
        ...previousBlock,
        containsKeywords: hasPreviewKeywordMarker(previousBlock) || hasPreviewKeywordMarker(selectedBlock),
        text: joinPreviewBlockText(getPreviewBlockMergeText(previousBlock), getPreviewBlockMergeText(selectedBlock)),
        html: joinPreviewBlockHtml(getPreviewBlockMergeHtml(previousBlock), getPreviewBlockMergeHtml(selectedBlock)),
      });
      const nextDocument = reconcilePreviewDocument({
        ...currentDocument,
        blocks: [
          ...currentBlocks.slice(0, selectedIndex - 1),
          mergedBlock,
          ...currentBlocks.slice(selectedIndex + 1),
        ],
      }, rules, previousBlock.id, { disableCascade: true });

      if (JSON.stringify(nextDocument) !== JSON.stringify(currentDocument)) {
        setPreviewHistory((currentHistory) => [
          ...currentHistory.slice(-99),
          {
            document: currentDocument,
            selectedBlockId: selectedPreviewBlockId,
          },
        ]);
      }

      setSelectedPreviewBlockId(previousBlock.id);
      setSelectedPreviewBlockIds([previousBlock.id]);
      return nextDocument;
    });
  }

  function removePreviewBlock(blockId) {
    if (!blockId) {
      return;
    }

    setPreviewDocument((currentDocument) => {
      if (!currentDocument?.blocks?.length) {
        return currentDocument;
      }

      const currentBlocks = currentDocument.blocks.map((block) => {
        const blockElement = getPreviewBlockElementById(block.id);
        if (!blockElement) {
          return block;
        }

        return {
          ...block,
          ...getNormalizedBlockContentFromElement(block, blockElement, currentDocument.blocks),
        };
      });
      const removedIndex = currentBlocks.findIndex((block) => block.id === blockId);
      if (removedIndex === -1) {
        return currentDocument;
      }

      const nextBlocks = currentBlocks.filter((block) => block.id !== blockId);
      const nextSelectedBlockId = nextBlocks[Math.min(removedIndex, nextBlocks.length - 1)]?.id || '';
      const nextDocument = reconcilePreviewDocument({
        ...currentDocument,
        blocks: nextBlocks,
      }, rules, nextSelectedBlockId, { disableCascade: true });

      setPreviewHistory((currentHistory) => [
        ...currentHistory.slice(-99),
        {
          document: currentDocument,
          selectedBlockId: selectedPreviewBlockId,
        },
      ]);
      setSelectedPreviewBlockId(nextSelectedBlockId);
      setSelectedPreviewBlockIds(nextSelectedBlockId ? [nextSelectedBlockId] : []);
      return nextDocument;
    });
  }

  function addPreviewSectionBelow(blockId, nextType = 'content') {
    if (!blockId) return;
    setPreviewDocument((currentDocument) => {
      if (!currentDocument?.blocks?.length) return currentDocument;
      const index = currentDocument.blocks.findIndex((block) => block.id === blockId);
      if (index < 0) return currentDocument;
      const newBlock = {
        id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        text: '',
        html: '',
        ...buildPreviewRuleTypePatch({ type: nextType, text: '', html: '' }, nextType, rules),
      };
      const nextBlocks = [...currentDocument.blocks.slice(0, index + 1), newBlock, ...currentDocument.blocks.slice(index + 1)];
      const nextDocument = reconcilePreviewDocument({ ...currentDocument, blocks: nextBlocks }, rules, newBlock.id, { disableCascade: true });
      setPreviewHistory((history) => [...history.slice(-99), { document: currentDocument, selectedBlockId: selectedPreviewBlockId }]);
      setSelectedPreviewBlockId(newBlock.id);
      setSelectedPreviewBlockIds([newBlock.id]);
      return nextDocument;
    });
  }

  async function handleAdminSectionChange(nextSection) {
    if (!nextSection) {
      return;
    }

    if (nextSection === 'manage') {
      await openClassicManagePage();
      return;
    }

    if (nextSection === DOCUMENT_MANAGE_PAGE) {
      await openNewManagePage();
      return;
    }

    navigateTo(nextSection);
  }

  function getAdminSectionValue() {
    if ([
      'manage',
      DOCUMENT_MANAGE_PAGE,
      'special-header-footer',
      'special-layout-setting',
      'registered-users',
      'detection-rules',
    ].includes(page)) {
      return page;
    }

    return '';
  }

  function undoPreviewChange() {
    setPreviewHistory((currentHistory) => {
      if (currentHistory.length === 0) {
        return currentHistory;
      }

      const previousEntry = currentHistory[currentHistory.length - 1];
      setPreviewDocument(previousEntry.document);
      const nextSelectedBlockId = previousEntry.selectedBlockId || previousEntry.document.blocks?.[0]?.id || '';
      setSelectedPreviewBlockId(nextSelectedBlockId);
      setSelectedPreviewBlockIds(nextSelectedBlockId ? [nextSelectedBlockId] : []);
      return currentHistory.slice(0, -1);
    });
  }

  function handleSelectPreviewBlock(nextBlockId, options = {}) {
    if (!previewDocument) {
      setSelectedPreviewBlockId(nextBlockId);
      setSelectedPreviewBlockIds(nextBlockId ? [nextBlockId] : []);
      return;
    }

    if (
      !options.toggle
      && selectedPreviewBlockId === nextBlockId
      && selectedPreviewBlockIds.length === (nextBlockId ? 1 : 0)
      && (!nextBlockId || selectedPreviewBlockIds[0] === nextBlockId)
    ) {
      return;
    }

    if (options.toggle) {
      skipNextPreviewSelectRef.current = nextBlockId;
      window.setTimeout(() => {
        if (skipNextPreviewSelectRef.current === nextBlockId) {
          skipNextPreviewSelectRef.current = '';
        }
      }, 0);

      setSelectedPreviewBlockIds((currentIds) => {
        const exists = currentIds.includes(nextBlockId);
        const nextIds = exists
          ? currentIds.filter((id) => id !== nextBlockId)
          : [...currentIds, nextBlockId];

        if (!exists || selectedPreviewBlockId === nextBlockId || !selectedPreviewBlockId) {
          setSelectedPreviewBlockId(nextBlockId);
        } else if (exists && currentIds.length === 1) {
          setSelectedPreviewBlockId(nextBlockId);
        } else if (exists && selectedPreviewBlockId === nextBlockId) {
          setSelectedPreviewBlockId(nextIds[0] || '');
        }

        return nextIds;
      });
      return;
    }

    if (skipNextPreviewSelectRef.current === nextBlockId) {
      skipNextPreviewSelectRef.current = '';
      return;
    }

    if (!options.skipSync && selectedPreviewBlockId && selectedPreviewBlockId !== nextBlockId) {
      const previousBlock = previewDocument.blocks.find((block) => block.id === selectedPreviewBlockId);
      const previousElement = getPreviewBlockElementById(selectedPreviewBlockId);
      if (previousBlock && previousElement) {
        updatePreviewBlock(
          selectedPreviewBlockId,
          getNormalizedBlockContentFromElement(previousBlock, previousElement, previewDocument.blocks),
          { disableCascade: true }
        );
      }
    }

    setSelectedPreviewBlockId(nextBlockId);
    setSelectedPreviewBlockIds(nextBlockId ? [nextBlockId] : []);
  }

  function handlePreviewOutsideClick(event) {
    if (!previewDocument || (!selectedPreviewBlockId && selectedPreviewBlockIds.length === 0)) {
      return;
    }

    const targetElement = event.target instanceof Element ? event.target : null;
    if (targetElement?.closest('.preview-block-card')) {
      return;
    }

    if (selectedPreviewBlockId) {
      const previousBlock = previewDocument.blocks.find((block) => block.id === selectedPreviewBlockId);
      const previousElement = getPreviewBlockElementById(selectedPreviewBlockId);
      if (previousBlock && previousElement) {
        updatePreviewBlock(
          selectedPreviewBlockId,
          getNormalizedBlockContentFromElement(previousBlock, previousElement, previewDocument.blocks),
          { disableCascade: true }
        );
      }
    }

    setSelectedPreviewBlockId('');
    setSelectedPreviewBlockIds([]);
  }

  async function downloadPreviewDocx() {
    if (!previewDocument) {
      return;
    }

    const exportPayload = {
      ...getPreviewDocumentWithSpecialOverride(previewDocument, activeSpecialHeaderConfig),
      format_id: previewDocument.format_id || selectedFormatId || rules?.format_id || '',
      source: previewDocument.source || rules?.source || '',
    };

    setDownloadingDoc(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/preview/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(exportPayload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Unable to generate DOCX.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${(exportPayload.document_name || 'formatted-document').replace(/\.docx$/i, '')}-formatted.docx`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Formatted DOCX downloaded successfully.');
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setDownloadingDoc(false);
    }
  }

  function requestAppliedRuleChange(row, nextBlockType) {
    if (!row || !nextBlockType || row.ruleType === nextBlockType) {
      return;
    }

    setPendingAppliedRuleChange({ row, nextBlockType });
  }

  function applyPendingAppliedRuleChange(applyToSimilarPattern) {
    if (!pendingAppliedRuleChange) {
      return;
    }

    const { row, nextBlockType } = pendingAppliedRuleChange;
    const targetRows = applyToSimilarPattern
      ? getAppliedRuleTableRows(previewDocument)
          .filter((candidateRow) => {
            // The row the user explicitly changed must always remain a
            // target.  The list guard below only protects other similar
            // rows from accidental bulk promotion.
            if (candidateRow.id === row.id) {
              return true;
            }
            // A numbered DOCX list is content, even when its first bold
            // phrase looks like a heading.  Never let Apply to All promote
            // those list items (for example, “Data Collection Module”).
            if (nextBlockType?.startsWith('heading_') && isPreviewNumberedListRow(candidateRow, previewDocument)) {
              return false;
            }
            return areAppliedRuleRowsSimilarForChange(candidateRow, row, nextBlockType);
          })
      : [row];
    const normalizedTargetRows = dedupeAppliedRuleRows(resolveAppliedRuleRowsForBlocks(targetRows, previewDocument?.blocks || [], nextBlockType));
    const targetBlockIds = Array.from(new Set(normalizedTargetRows.map((targetRow) => targetRow.blockId).filter(Boolean)));

    setPreviewDocument((currentDocument) => {
      if (!currentDocument?.blocks?.length || normalizedTargetRows.length === 0) {
        return currentDocument;
      }

      if (nextBlockType === 'content') {
        const mergedBackDocument = mergeManualSplitHeadingsBackToContent(
          currentDocument,
          normalizedTargetRows,
          rules,
          row.blockId
        );
        if (mergedBackDocument) {
          if (JSON.stringify(mergedBackDocument) !== JSON.stringify(currentDocument)) {
            setPreviewHistory((currentHistory) => [
              ...currentHistory.slice(-99),
              {
                document: currentDocument,
                selectedBlockId: selectedPreviewBlockId,
              },
            ]);
          }

          return mergedBackDocument;
        }
      }

      const rowsByBlockId = normalizedTargetRows.reduce((rowMap, targetRow) => {
        if (!targetRow?.blockId) {
          return rowMap;
        }

        return {
          ...rowMap,
          [targetRow.blockId]: [...(rowMap[targetRow.blockId] || []), targetRow],
        };
      }, {});

      let nextBlocks = currentDocument.blocks.flatMap((block) => {
        const blockRows = rowsByBlockId[block.id] || [];
        if (blockRows.length === 0) {
          return [block];
        }

        return applyAppliedRuleRowsToBlock(block, blockRows, nextBlockType, rules);
      });
      if (nextBlockType === 'content') {
        nextBlocks = mergeConvertedManualSplitGroups(nextBlocks, new Set(targetBlockIds), rules);
      }
      const nextDocument = reconcilePreviewDocument({
        ...currentDocument,
        blocks: nextBlocks,
      }, rules, row.blockId, { disableCascade: true });

      if (JSON.stringify(nextDocument) !== JSON.stringify(currentDocument)) {
        setPreviewHistory((currentHistory) => [
          ...currentHistory.slice(-99),
          {
            document: currentDocument,
            selectedBlockId: selectedPreviewBlockId,
          },
        ]);
      }

      return nextDocument;
    });

    const contentMergeSelectedId = nextBlockType === 'content'
      ? (getManualSplitBlockIdParts(row.blockId)?.baseId || row.blockId || targetBlockIds[0] || '')
      : (row.blockId || targetBlockIds[0] || '');
    setSelectedPreviewBlockId(contentMergeSelectedId);
    setSelectedPreviewBlockIds(contentMergeSelectedId ? [contentMergeSelectedId] : targetBlockIds);
    setPendingAppliedRuleChange(null);
  }

  async function handleOptimizeDocx(event) {
    event.preventDefault();
    if (!optimizeSelectedFile) {
      setError('Choose a DOCX file before optimizing.');
      return;
    }

    const formData = new FormData();
    formData.append('file', optimizeSelectedFile);

    setOptimizingDoc(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/optimize-docx`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await readJsonResponse(response);
        throw new Error(data.error || 'Unable to optimize DOCX.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${optimizeSelectedFile.name.replace(/\.docx$/i, '')}-optimized.docx`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Optimized DOCX downloaded successfully.');
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setOptimizingDoc(false);
    }
  }

  async function handleNewFormatUpload(event) {
    event.preventDefault();
    if (!selectedNewUploadFile) {
      setError('Choose a DOCX file before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedNewUploadFile);
    formData.append('source', 'new_upload');

    setUploadingNewFormat(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/upload-docx`, {
        method: 'POST',
        body: formData,
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed.');
      }

      setRules(data);
      setDrafts(createDraftMap(data));
      const fallbackFormatId = data.format_id || data.document_name || selectedNewUploadFile.name || 'uploaded-format';
      setSelectedFormatId(fallbackFormatId);
      setSpecialHeaderConfigDraftForFormat(fallbackFormatId, data.special_header_config);
      const fallbackOption = buildFormatOption(data, fallbackFormatId);
      setFormats((currentFormats) => {
        if (!fallbackOption) {
          return normalizeFormatOptions(currentFormats);
        }

        const filteredFormats = currentFormats.filter((format) => format.format_id !== fallbackOption.format_id);
        return normalizeFormatOptions([fallbackOption, ...filteredFormats]);
      });
      await refreshFormats(fallbackFormatId);
      setPreviewDocument(null);
      setSelectedPreviewBlockId('');
      setSelectedPreviewBlockIds([]);
      setPreviewHistory([]);
      setMessage(`Extracted rules from ${data.document_name || selectedNewUploadFile.name}.`);
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setUploadingNewFormat(false);
    }
  }

  async function loadRegisteredUsers() {
    setUsersLoading(true);

    try {
      const dateQuery = selectedDownloadDate ? `?date=${encodeURIComponent(selectedDownloadDate)}` : '';
      const response = await authFetch(`${API_BASE_URL}/admin/users${dateQuery}`);
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load registered users.');
      }

      setRegisteredUsers(Array.isArray(data.users) ? data.users : []);
    } catch (requestError) {
      setRegisteredUsers([]);
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setUsersLoading(false);
    }
  }

  function openResetPasswordModal(user) {
    if (!user?.id) {
      return;
    }

    if (user.external_provider) {
      setError('External login users must reset their password in the connected platform.');
      return;
    }
    setResetPasswordModalUser(user);
    setResetPasswordForm({ password: '', confirmPassword: '' });
    setError('');
    setMessage('');
  }

  function closeResetPasswordModal(forceClose = false) {
    if (resettingUserId && !forceClose) {
      return;
    }

    setResetPasswordModalUser(null);
    setResetPasswordForm({ password: '', confirmPassword: '' });
  }

  async function handleResetUserPassword() {
    if (!resetPasswordModalUser?.id) {
      return;
    }

    const password = resetPasswordForm.password;
    const confirmPassword = resetPasswordForm.confirmPassword;
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Password and confirm password must match.');
      return;
    }

    setResettingUserId(String(resetPasswordModalUser.id));
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(resetPasswordModalUser.id)}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });
      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Unable to reset password.');
      }

      const userLabel = data?.user?.email || data?.user?.name || resetPasswordModalUser.email || `user #${resetPasswordModalUser.id}`;
      setMessage(`Password reset successful for ${userLabel}.`);
      closeResetPasswordModal(true);
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setResettingUserId('');
    }
  }

  async function handleIndexDocx(event) {
    event.preventDefault();
    if (!indexSelectedFile) {
      setError('Choose a DOCX file before creating the index preview.');
      return;
    }

    const formData = new FormData();
    formData.append('file', indexSelectedFile);

    setGeneratingIndex(true);
    setError('');
    setMessage('');

    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => {
      abortController.abort();
    }, 180000);

    try {
      const response = await authFetch(`${API_BASE_URL}/index/preview`, {
        method: 'POST',
        body: formData,
        signal: abortController.signal,
      });

      const data = await readJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.error || 'Unable to create index preview.');
      }

      setIndexPreviewDocument(data);
      setIndexPreviewHistory([]);
      setMessage('Index preview generated successfully.');
    } catch (requestError) {
      setError(
        requestError?.name === 'AbortError'
          ? 'Index preview is taking too long. Please try again after closing Microsoft Word documents, or upload a smaller DOCX.'
          : getFriendlyErrorMessage(requestError)
      );
    } finally {
      window.clearTimeout(timeoutId);
      setGeneratingIndex(false);
    }
  }

  async function downloadIndexDocx() {
    if (!indexPreviewDocument) {
      setError('Generate an index preview before downloading.');
      return;
    }

    setDownloadingIndexDoc(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/index/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(indexPreviewDocument),
      });

      if (!response.ok) {
        const data = await readJsonResponse(response);
        throw new Error(data.error || 'Unable to download index DOCX.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${(indexPreviewDocument.document_name || indexSelectedFile?.name || 'index-document').replace(/\.docx$/i, '')}-index.docx`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Index DOCX downloaded successfully.');
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setDownloadingIndexDoc(false);
    }
  }

  function updateIndexPreviewSection(sectionKey, rowIndex, fieldKey, value) {
    if (!indexPreviewDocument) {
      return;
    }

    const currentRows = indexPreviewDocument[sectionKey] || [];
    const currentEntry = currentRows[rowIndex];
    if (!currentEntry || String(currentEntry[fieldKey] ?? '') === String(value ?? '')) {
      return;
    }

    setIndexPreviewHistory((currentHistory) => [
      ...currentHistory.slice(-49),
      cloneIndexPreviewDocument(indexPreviewDocument),
    ]);
    setIndexPreviewDocument({
      ...indexPreviewDocument,
      [sectionKey]: currentRows.map((entry, index) =>
        index === rowIndex ? { ...entry, [fieldKey]: value } : entry
      ),
    });
  }

  function deleteIndexPreviewRow(sectionKey, rowIndex) {
    if (!indexPreviewDocument) {
      return;
    }

    const currentRows = indexPreviewDocument[sectionKey] || [];
    if (rowIndex < 0 || rowIndex >= currentRows.length) {
      return;
    }

    setIndexPreviewHistory((currentHistory) => [
      ...currentHistory.slice(-49),
      cloneIndexPreviewDocument(indexPreviewDocument),
    ]);
    setIndexPreviewDocument({
      ...indexPreviewDocument,
      [sectionKey]: currentRows.filter((_, index) => index !== rowIndex),
    });
  }

  function undoIndexPreviewChange() {
    setIndexPreviewHistory((currentHistory) => {
      if (currentHistory.length === 0) {
        return currentHistory;
      }

      const previousDocument = currentHistory[currentHistory.length - 1];
      setIndexPreviewDocument(previousDocument);
      return currentHistory.slice(0, -1);
    });
  }

  function updateOptimizeBlock(blockId, patch) {
    setOptimizePreviewDocument((currentDocument) => {
      if (!currentDocument) {
        return currentDocument;
      }

      return normalizeOptimizePreviewDocument({
        ...currentDocument,
        blocks: currentDocument.blocks.map((block) =>
          block.id === blockId
            ? {
                ...block,
                ...patch,
                format: {
                  ...block.format,
                  ...(patch.format || {}),
                },
              }
            : block
        ),
      });
    });
  }

  function updateOptimizeLayout(patch) {
    setOptimizePreviewDocument((currentDocument) => {
      if (!currentDocument) {
        return currentDocument;
      }

      return normalizeOptimizePreviewDocument({
        ...currentDocument,
        document_layout: {
          ...(currentDocument.document_layout || {}),
          ...patch,
        },
      });
    });
  }

  function moveOptimizeBlock(blockId, direction) {
    setOptimizePreviewDocument((currentDocument) => {
      if (!currentDocument) {
        return currentDocument;
      }

      const currentIndex = currentDocument.blocks.findIndex((block) => block.id === blockId);
      if (currentIndex === -1) {
        return currentDocument;
      }

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= currentDocument.blocks.length) {
        return currentDocument;
      }

      const nextBlocks = [...currentDocument.blocks];
      const [movedBlock] = nextBlocks.splice(currentIndex, 1);
      nextBlocks.splice(targetIndex, 0, movedBlock);

      return normalizeOptimizePreviewDocument({
        ...currentDocument,
        blocks: nextBlocks,
      });
    });
  }

  async function downloadOptimizedDocx() {
    if (!optimizePreviewDocument) {
      return;
    }

    setDownloadingOptimizedDoc(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE_URL}/optimize/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(optimizePreviewDocument),
      });

      if (!response.ok) {
        const data = await readJsonResponse(response);
        throw new Error(data.error || 'Unable to export optimized DOCX.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${(optimizePreviewDocument.document_name || optimizeSelectedFile?.name || 'optimized-document').replace(/\.docx$/i, '')}-optimized.docx`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Optimized DOCX downloaded successfully.');
    } catch (requestError) {
      setError(getFriendlyErrorMessage(requestError));
    } finally {
      setDownloadingOptimizedDoc(false);
    }
  }
  void updateOptimizeBlock;
  void updateOptimizeLayout;
  void moveOptimizeBlock;
  void downloadOptimizedDocx;

  async function handleRowUpdate(ruleSetKey, fieldKey) {
    await persistDrafts(
      drafts,
      `${FIELD_LABELS[fieldKey] || fieldKey} updated successfully.`,
      `${ruleSetKey}.${fieldKey}`
    );
  }

  async function handleRowDelete(ruleSetKey, fieldKey, originalValue) {
    const draftKey = `${ruleSetKey}.${fieldKey}`;
    const nextDrafts = {
      ...drafts,
      [draftKey]: serializeValue(createEmptyValue(originalValue)),
    };

    setDrafts(nextDrafts);
    await persistDrafts(
      nextDrafts,
      `${FIELD_LABELS[fieldKey] || fieldKey} cleared successfully.`,
      draftKey
    );
  }

  function updateField(ruleSetKey, fieldKey, rawValue) {
    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [`${ruleSetKey}.${fieldKey}`]: rawValue,
    }));
  }

  async function handleHeadingNumberingUpdate(ruleSetKey, fieldKey, numberingStyle) {
    const draftKey = `${ruleSetKey}.${fieldKey}`;
    if (!isHeadingField(fieldKey)) {
      const ruleDraftKey = `field_rules.${fieldKey}`;
      const currentRuleConfig = getFieldRuleDraftValue(
        rules,
        fieldKey,
        drafts[ruleDraftKey] ?? JSON.stringify(getPersistedFieldRule(rules, fieldKey), null, 2)
      );
      const nextDrafts = {
        ...drafts,
        [ruleDraftKey]: JSON.stringify(
          {
            ...currentRuleConfig,
            numbering: {
              raw: '',
              style: numberingStyle,
              enabled: numberingStyle !== 'none',
            },
          },
          null,
          2
        ),
      };

      setDrafts(nextDrafts);
      await persistDrafts(
        nextDrafts,
        `${FIELD_LABELS[fieldKey] || fieldKey} numbering updated successfully.`,
        draftKey
      );
      return;
    }

    const sourceValue = drafts[draftKey] ?? serializeEditableValue(rules, fieldKey, rules?.rule_sets?.[ruleSetKey]?.[fieldKey] ?? []);
    const currentEntries = normalizeHeadingEntries(parseStructuredDraft(fieldKey, sourceValue));
    const seedEntries = currentEntries.length > 0 ? currentEntries : [createHeadingEntry()];
    const nextEntries = seedEntries.map((entry) => ({
      ...entry,
      numbering: {
        raw: '',
        style: numberingStyle,
        enabled: numberingStyle !== 'none',
      },
    }));
    const nextDrafts = {
      ...drafts,
      [draftKey]: JSON.stringify(nextEntries, null, 2),
    };

    setDrafts(nextDrafts);
    await persistDrafts(
      nextDrafts,
      `${FIELD_LABELS[fieldKey] || fieldKey} numbering updated successfully.`,
      draftKey
    );
  }

  if (routePanel === 'public') {
    return (
      <LandingPage currentRole={currentRole} />
    );
  }

  if (routePanel === 'external-login') {
    return <ExternalLoginPage loading={authLoading} error={error} message={message} />;
  }

  if (routePanel === 'admin' && currentRole !== 'admin' && !isAllowedAdminLoginRoute()) {
    return <RestrictedLoginPage />;
  }

  if (routePanel === 'user' && currentRole !== 'user' && !isAllowedUserLoginRoute()) {
    return <RestrictedLoginPage onAdminOpen={() => navigateTo('admin-login')} />;
  }

  if ((routePanel === 'admin' && currentRole !== 'admin') || (routePanel === 'user' && currentRole !== 'user')) {
    return (
      <AuthPage
        panel={routePanel}
        mode={authMode}
        form={authForm}
        loading={authLoading}
        error={error}
        message={message}
        onModeChange={setAuthMode}
        onFormChange={setAuthForm}
        onSubmit={handleAuthSubmit}
      />
    );
  }

  return (
    <main className="page-shell">
      {page === 'apply' && pendingAppliedRuleChange && (
        <AppliedRuleConfirmPopup
          pendingChange={pendingAppliedRuleChange}
          onApplyAll={() => applyPendingAppliedRuleChange(true)}
          onApplySelected={() => applyPendingAppliedRuleChange(false)}
          onCancel={() => setPendingAppliedRuleChange(null)}
        />
      )}

      <header className="topbar">
        <div className="brand-lockup">
          <h1 className="brand-title">AutoFormatter</h1>
        </div>

        <nav className="page-switcher">
          {currentRole === 'admin' ? (
            <>
              <label className="page-mode-switcher" htmlFor="admin-upload-page-switcher">
                <span className="page-mode-switcher-label">Upload Flow</span>
                <select
                  id="admin-upload-page-switcher"
                  className="page-mode-switcher-select"
                  value={page === DOCUMENT_UPLOAD_PAGE ? DOCUMENT_UPLOAD_PAGE : 'home'}
                  onChange={(event) => {
                    if (event.target.value === DOCUMENT_UPLOAD_PAGE) {
                      openPageInNewTab(DOCUMENT_UPLOAD_PAGE);
                      return;
                    }
                    navigateTo(event.target.value);
                  }}
                >
                  <option value="home">Article Formatting</option>
                  <option value={DOCUMENT_UPLOAD_PAGE}>Document Formatting</option>
                </select>
              </label>
              <label className="page-mode-switcher" htmlFor="admin-section-switcher">
                <span className="page-mode-switcher-label">Admin Section</span>
                <select
                  id="admin-section-switcher"
                  className="page-mode-switcher-select"
                  value={getAdminSectionValue()}
                  onChange={(event) => handleAdminSectionChange(event.target.value)}
                >
                  <option value="">Select Admin Section</option>
                  <option value="manage">{MANAGE_ARTICLE_FORMATTING_LABEL}</option>
                  <option value={DOCUMENT_MANAGE_PAGE}>{MANAGE_DOCUMENT_FORMATTING_LABEL}</option>
                  <option value="special-header-footer">Header/Footer</option>
                  <option value="special-layout-setting">Layout Settings</option>
                  <option value="registered-users">Registered Users</option>
                  <option value="detection-rules">Detection Rules</option>
                </select>
              </label>
            </>
          ) : (
            <>
              <button
                className={page === 'apply' ? 'nav-button active' : 'nav-button'}
                type="button"
                onClick={openClassicApplyPage}
              >
                {APPLY_ARTICLE_FORMATTING_LABEL}
              </button>
              <button
                className={page === DOCUMENT_APPLY_PAGE ? 'nav-button active' : 'nav-button'}
                type="button"
                onClick={openNewApplyPage}
              >
                {APPLY_DOCUMENT_FORMATTING_LABEL}
              </button>
              <button
                className={page === 'optimize' ? 'nav-button active' : 'nav-button'}
                type="button"
                onClick={() => navigateTo('optimize')}
              >
                Optimize
              </button>
              <button
                className={page === 'index' ? 'nav-button active' : 'nav-button'}
                type="button"
                onClick={() => navigateTo('index')}
              >
                Index
              </button>
            </>
          )}
          <button className="ghost-button" type="button" onClick={handleLogout}>
            Logout
          </button>
        </nav>
      </header>

      {(message || error) && (
        <section
          className={`status-banner ${error ? 'error' : 'success'} ${isMessageDismissing && !error ? 'dismissing' : ''}`}
        >
          {error || message}
        </section>
      )}

      {resetPasswordModalUser && (
        <ResetPasswordModal
          user={resetPasswordModalUser}
          form={resetPasswordForm}
          loading={resettingUserId === String(resetPasswordModalUser.id)}
          onChange={setResetPasswordForm}
          onClose={closeResetPasswordModal}
          onSubmit={handleResetUserPassword}
        />
      )}

      {page === 'home' ? (
        <>
          <section className="hero-panel">
           
            <form className="upload-card" onSubmit={handleUpload}>
              <label className="file-picker" htmlFor="docx-file">
                <span>Upload Article Format DOCX</span>
                <input
                  id="docx-file"
                  type="file"
                  accept=".docx"
                  onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                />
              </label>

              <button className="primary-button" type="submit" disabled={uploading}>
                {uploading ? 'Extracting...' : 'Upload as Article Format'}
              </button>

              <button
                className="secondary-button"
                type="button"
                onClick={handleSaveAll}
                disabled={!rules || saving}
              >
                {saving && !activeRow ? 'Saving...' : 'Save Rules'}
              </button>

              
              {selectedFile && <p className="helper-text">Selected file: {selectedFile.name}</p>}
            </form>
          </section>

          <section className="meta-strip">
            
        
            <article className="meta-strip-wide">
              <span>Uploaded Article Formats</span>
              {classicFormats.length > 0 ? (
                <div className="format-name-list">
                  {classicFormats.map((format) => (
                    <div className="format-name-item" key={format.format_id}>
                      <div className="format-name-content">
                        <strong>{getFormatLabel(format)}</strong>
                        <div className="format-name-meta">
                          <span className="format-column-badge">
                            {getTemplateColumnsLabel(getFormatTemplateColumns(format))}
                          </span>
                          <span className={`format-status-badge ${format.is_enabled === false ? 'disabled' : 'enabled'}`}>
                            {format.is_enabled === false ? 'Disabled' : 'Enabled'}
                          </span>
                        </div>
                      </div>
                      <div className="format-name-actions">
                        <button
                          className="mini-button secondary-button"
                          type="button"
                          onClick={() => handleManageFormat(format.format_id)}
                          disabled={togglingFormatId === format.format_id}
                        >
                          {MANAGE_ARTICLE_FORMATTING_LABEL}
                        </button>
                        <button
                          className="mini-button secondary-button"
                          type="button"
                          onClick={() => handleLayoutSettingsFormat(format.format_id)}
                          disabled={togglingFormatId === format.format_id}
                        >
                          Layout Setting
                        </button>
                        <button
                          className={`mini-button ${format.is_enabled === false ? 'enable-button' : 'disable-button'}`}
                          type="button"
                          onClick={() => handleToggleFormat(format.format_id, format.is_enabled === false)}
                          disabled={togglingFormatId === format.format_id}
                        >
                          {togglingFormatId === format.format_id
                            ? 'Updating...'
                            : format.is_enabled === false ? 'Enable' : 'Disable'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <strong>No formats uploaded yet</strong>
              )}
            </article>
          </section>

     
        </>
      ) : page === DOCUMENT_UPLOAD_PAGE ? (
        <>
          <section className="hero-panel">
            <div className="hero-copy">
              <span className="eyebrow">{DOCUMENT_FORMATTING_LABEL}</span>
              <h3>Upload a DOCX for the document formatting</h3>
              
            </div>

            <form className="upload-card" onSubmit={handleNewFormatUpload}>
              <label className="file-picker" htmlFor="new-docx-file">
                <span>Upload Document Format DOCX</span>
                <input
                  id="new-docx-file"
                  type="file"
                  accept=".docx"
                  onChange={(event) => setSelectedNewUploadFile(event.target.files?.[0] || null)}
                />
              </label>

              <button className="primary-button" type="submit" disabled={uploadingNewFormat}>
                {uploadingNewFormat ? 'Extracting...' : 'Upload Document Format'}
              </button>

              {selectedNewUploadFile && <p className="helper-text">Selected file: {selectedNewUploadFile.name}</p>}
             
            </form>
          </section>

          <section className="meta-strip">
            <article className="meta-strip-wide">
              <span>Uploaded Document Formats</span>
              {newUploadFormats.length > 0 ? (
                <div className="format-name-list">
                  {newUploadFormats.map((format) => (
                    <div className="format-name-item" key={format.format_id}>
                      <div className="format-name-content">
                        <strong>{getFormatLabel(format)}</strong>
                        <div className="format-name-meta">
                          <span className="format-column-badge">
                            {getTemplateColumnsLabel(getFormatTemplateColumns(format))}
                          </span>
                          <span className={`format-status-badge ${format.is_enabled === false ? 'disabled' : 'enabled'}`}>
                            {format.is_enabled === false ? 'Disabled' : 'Enabled'}
                          </span>
                        </div>
                      </div>
                      <div className="format-name-actions">
                        <button
                          className="mini-button secondary-button"
                          type="button"
                          onClick={() => handleManageNewFormat(format.format_id)}
                          disabled={togglingFormatId === format.format_id}
                        >
                          {MANAGE_DOCUMENT_FORMATTING_LABEL}
                        </button>
                        <button
                          className="mini-button secondary-button"
                          type="button"
                          onClick={() => handleLayoutSettingsFormat(format.format_id)}
                          disabled={togglingFormatId === format.format_id}
                        >
                          Layout Setting
                        </button>
                        <button
                          className={`mini-button ${format.is_enabled === false ? 'enable-button' : 'disable-button'}`}
                          type="button"
                          onClick={() => handleToggleFormat(format.format_id, format.is_enabled === false)}
                          disabled={togglingFormatId === format.format_id}
                        >
                          {togglingFormatId === format.format_id
                            ? 'Updating...'
                            : format.is_enabled === false ? 'Enable' : 'Disable'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <strong>No document-format uploads yet</strong>
              )}
            </article>
          </section>
        </>
      ) : page === 'manage' ? (
        <section className="table-panel manage-panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">{ARTICLE_FORMATTING_LABEL}</span>
            </div>
            <button className="secondary-button" type="button" onClick={handleSaveAll} disabled={!rules || saving}>
              {saving && !activeRow ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>

          <div className="format-selector-row">
            <label className="file-picker" htmlFor="saved-format-manage">
              <span>Select Article Format</span>
              <select
                id="saved-format-manage"
                className="format-select"
                value={selectedFormatId}
                onChange={handleFormatSelectionChange}
                disabled={classicFormats.length === 0}
              >
                <option value="">Select saved article format</option>
                {classicFormats.map((format) => (
                  <option key={format.format_id} value={format.format_id}>
                    {getFormatLabel(format)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="empty-state">Loading rules...</div>
          ) : manageRows.length === 0 ? (
            <div className="empty-state">No extracted data available yet.</div>
          ) : (
            <ManageTable
              rows={manageRows}
              saving={saving}
              activeRow={activeRow}
              onFieldChange={updateField}
              onHeadingNumberingUpdate={handleHeadingNumberingUpdate}
              onUpdate={handleRowUpdate}
              onDelete={handleRowDelete}
            />
          )}
        </section>
      ) : page === DOCUMENT_MANAGE_PAGE ? (
        <section className="table-panel manage-panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">{DOCUMENT_FORMATTING_LABEL}</span>
              <h2>Manage rules for the document formatting </h2>
              
            </div>
            <button className="secondary-button" type="button" onClick={handleSaveAll} disabled={!rules || saving}>
              {saving && !activeRow ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>

          <div className="format-selector-row">
            <label className="file-picker" htmlFor="saved-format-manage-document">
              <span>Select Document Format</span>
              <select
                id="saved-format-manage-document"
                className="format-select"
                value={selectedFormatId}
                onChange={handleFormatSelectionChange}
                disabled={newUploadFormats.length === 0}
              >
                <option value="">Select saved document format</option>
                {newUploadFormats.map((format) => (
                  <option key={format.format_id} value={format.format_id}>
                    {getFormatLabel(format)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="empty-state">Loading rules...</div>
          ) : manageRows.length === 0 ? (
            <div className="empty-state">No extracted data available yet.</div>
          ) : (
            <ManageTable
              rows={manageRows}
              saving={saving}
              activeRow={activeRow}
              onFieldChange={updateField}
              onHeadingNumberingUpdate={handleHeadingNumberingUpdate}
              onUpdate={handleRowUpdate}
              onDelete={handleRowDelete}
            />
          )}
        </section>
      ) : page === 'special-header-footer' ? (
        <SpecialHeaderFooterPage
          rules={rules}
          config={activeSpecialHeaderConfig}
          onChange={updateActiveSpecialHeaderConfig}
          onRulesChange={updateRulesLayoutSettings}
          onSave={handleSaveSpecialHeaderConfig}
          onClear={handleClearSpecialHeaderConfig}
          isSaved={isSpecialHeaderConfigSaved}
          saving={saving}
          formats={formats}
          selectedFormatId={selectedFormatId}
          onFormatChange={handleFormatSelectionChange}
          getFormatLabel={getFormatLabel}
        />
      ) : page === 'special-layout-setting' ? (
        <SpecialLayoutSettingPage
          rules={rules}
          saving={saving}
          formats={formats}
          selectedFormatId={selectedFormatId}
          onFormatChange={handleFormatSelectionChange}
          onChange={updateRulesLayoutSettings}
          onSave={handleSaveLayoutSettings}
          getFormatLabel={getFormatLabel}
        />
      ) : page === 'registered-users' ? (
        <section className="table-panel manage-panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">Admin Users</span>
              <h2>All registered users</h2>
            </div>
            <button className="secondary-button" type="button" onClick={loadRegisteredUsers} disabled={usersLoading}>
              {usersLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div className="registered-users-filter-row">
            <label className="file-picker registered-users-filter-field" htmlFor="registered-users-date">
              <span>Select Date</span>
              <input
                id="registered-users-date"
                className="format-select"
                type="date"
                value={selectedDownloadDate}
                onChange={(event) => setSelectedDownloadDate(event.target.value)}
              />
            </label>

            <label className="file-picker registered-users-filter-field" htmlFor="registered-users-search">
              <span>Search User</span>
              <input
                id="registered-users-search"
                className="format-select"
                type="text"
                value={registeredUsersSearchTerm}
                onChange={(event) => setRegisteredUsersSearchTerm(event.target.value)}
                placeholder="Search by ID, name, email, or login type"
              />
            </label>
          </div>

          {usersLoading ? (
            <div className="empty-state">Loading registered users...</div>
          ) : registeredUsers.length === 0 ? (
            <div className="empty-state">No registered users found.</div>
          ) : (
            <RegisteredUsersTable
              users={registeredUsers}
              resettingUserId={resettingUserId}
              searchTerm={registeredUsersSearchTerm}
              selectedDownloadDate={selectedDownloadDate}
              onResetPassword={openResetPasswordModal}
            />
          )}
        </section>
      ) : page === 'detection-rules' ? (
        <DetectionRulesPage
          selectedArticleKey={selectedDetectionRuleKey}
          selectedDocumentKey={selectedDocumentDetectionRuleKey}
          selectedScope={selectedDetectionRuleScope}
          onSelectArticleKey={(nextKey) => {
            setSelectedDetectionRuleKey(nextKey);
            setSelectedDetectionRuleScope('article');
          }}
          onSelectDocumentKey={(nextKey) => {
            setSelectedDocumentDetectionRuleKey(nextKey);
            setSelectedDetectionRuleScope('document');
          }}
        />
      ) : page === 'optimize' ? (
        <section className="table-panel manage-panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">Optimize DOCX</span>
              <h2>Upload a DOCX and download a non-destructively optimized version</h2>
            </div>
          </div>

          <form className="upload-card optimize-card optimize-simple-card" onSubmit={handleOptimizeDocx}>
            <label className="file-picker" htmlFor="optimize-docx-file">
              <span>Upload DOCX File</span>
              <input
                id="optimize-docx-file"
                type="file"
                accept=".docx"
                onChange={(event) => setOptimizeSelectedFile(event.target.files?.[0] || null)}
              />
            </label>

            

            {optimizeSelectedFile && (
              <p className="helper-text">Selected file: {optimizeSelectedFile.name}</p>
            )}

            <button className="primary-button" type="submit" disabled={!optimizeSelectedFile || optimizingDoc}>
              {optimizingDoc ? 'Optimizing...' : 'Download Optimized Document'}
            </button>
          </form>
        </section>
      ) : page === 'index' ? (
        <section className="table-panel manage-panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">Index DOCX</span>
              <h2>Upload, preview, edit, and download index pages</h2>
            </div>
            <div className="panel-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={downloadIndexDocx}
                disabled={!indexPreviewDocument || downloadingIndexDoc}
              >
                {downloadingIndexDoc ? 'Downloading...' : 'Download Index DOCX'}
              </button>
            </div>
          </div>

          <div className="index-workspace">
            <aside className="index-sidebar">
              <form className="upload-card optimize-card optimize-simple-card" onSubmit={handleIndexDocx}>
                <div className="index-upload-card-header">
                  <button
                    className="index-undo-button"
                    type="button"
                    onClick={undoIndexPreviewChange}
                    disabled={!indexPreviewDocument || indexPreviewHistory.length === 0}
                    aria-label="Undo last index change"
                    title="Undo"
                  >
                    ↶
                  </button>
                </div>
                <label className="file-picker" htmlFor="index-docx-file">
                  <span>Upload DOCX File</span>
                  <input
                    id="index-docx-file"
                    type="file"
                    accept=".docx"
                    onChange={(event) => {
                      setIndexSelectedFile(event.target.files?.[0] || null);
                      setIndexPreviewDocument(null);
                      setIndexPreviewHistory([]);
                    }}
                  />
                </label>

                {indexSelectedFile && (
                  <p className="helper-text">Selected file: {indexSelectedFile.name}</p>
                )}

                <button className="primary-button" type="submit" disabled={!indexSelectedFile || generatingIndex}>
                  {generatingIndex ? 'Creating Preview...' : 'Generate Preview'}
                </button>
              </form>
            </aside>

            <div className="index-preview-panel">
              {generatingIndex ? (
                <div className="empty-state preview-empty-state index-preview-loading">
                  <div className="index-preview-loader" aria-hidden="true" />
                  <div>Generating index preview...</div>
                </div>
              ) : !indexPreviewDocument ? (
                <div className="empty-state preview-empty-state">
                  Upload a DOCX and generate preview to edit index pages.
                </div>
              ) : (
                <IndexPreviewEditor
                  preview={indexPreviewDocument}
                  onChange={updateIndexPreviewSection}
                  onDelete={deleteIndexPreviewRow}
                />
              )}
            </div>
          </div>
        </section>
      ) : page === 'apply' || page === DOCUMENT_APPLY_PAGE ? (
        <section className="table-panel manage-panel">
          <div className="panel-header">
            <div>
              <span className="eyebrow">{page === DOCUMENT_APPLY_PAGE ? APPLY_DOCUMENT_FORMATTING_LABEL : APPLY_ARTICLE_FORMATTING_LABEL}</span>
              
            </div>
            <div className="panel-actions">
              
              {page === 'apply' && (
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setShowAppliedRulesTable(true)}
                  disabled={!previewDocument}
                >
                  Rule Data
                </button>
              )}
             
              <button
                className="secondary-button"
                type="button"
                onClick={downloadPreviewDocx}
                disabled={!previewDocument || downloadingDoc}
              >
                {downloadingDoc ? 'Downloading...' : 'Download'}
              </button>
            </div>
          </div>

          {page === 'apply' && showAppliedRulesTable && previewDocument && (
            <AppliedRulesTableModal
              previewDocument={previewDocument}
              onRuleChange={requestAppliedRuleChange}
              onClose={() => setShowAppliedRulesTable(false)}
            />
          )}

          <div className="apply-layout">
            <aside className="apply-sidebar">
              {previewDocument && (
                <PreviewToolbar
                  rules={rules}
                  previewDocument={previewDocument}
                  selectedBlockId={selectedPreviewBlockId}
                  selectedBlockIds={selectedPreviewBlockIds}
                  onBlockChange={updatePreviewBlock}
                  onBlocksChange={updatePreviewBlocks}
                  onMergeSelectedBlock={mergeSelectedPreviewBlockWithPrevious}
                  onSelectBlock={handleSelectPreviewBlock}
                  onUndo={undoPreviewChange}
                  canUndo={previewHistory.length > 0}
                />
              )}

              <form className="upload-card raw-upload-card" onSubmit={handleRawUpload}>
                <label className="file-picker" htmlFor="saved-format-apply">
                  <span>{page === DOCUMENT_APPLY_PAGE ? 'Select Document Format' : 'Select Article Format'}</span>
                  <select
                    id="saved-format-apply"
                    className="format-select"
                    value={selectedFormatId}
                    onChange={handleFormatSelectionChange}
                    disabled={(page === DOCUMENT_APPLY_PAGE ? newUploadFormats : classicFormats).length === 0}
                  >
                    <option value="">{page === DOCUMENT_APPLY_PAGE ? 'Select saved document format' : 'Select saved article format'}</option>
                    {(page === DOCUMENT_APPLY_PAGE ? newUploadFormats : classicFormats).map((format) => (
                      <option key={format.format_id} value={format.format_id}>
                        {getFormatLabel(format)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="file-picker" htmlFor="raw-docx-file">
                  <span>Select Raw DOCX File</span>
                  <input
                    id="raw-docx-file"
                    type="file"
                    accept=".docx"
                    onChange={(event) => setRawSelectedFile(event.target.files?.[0] || null)}
                  />
                </label>

                <button className="primary-button" type="submit" disabled={applyingRules}>
                  {applyingRules ? 'Applying Rules...' : 'Apply Saved Rules'}
                </button>

                {rawSelectedFile && <p className="helper-text">Selected raw file: {rawSelectedFile.name}</p>}
              </form>
            </aside>

            <div className="apply-preview-panel" onMouseDown={handlePreviewOutsideClick}>
              {applyingRules ? (
                <div className="empty-state preview-empty-state apply-preview-loading">
                  <div className="index-preview-loader" aria-hidden="true" />
                  <strong>Generating preview...</strong>
                  <span>Please wait while the DOCX formatting rules are applied.</span>
                </div>
              ) : !previewDocument ? (
                <div className="empty-state preview-empty-state">
                  {page === DOCUMENT_APPLY_PAGE
                    ? 'First select the document format, then upload the docs and generate a preview.'
                    : 'First select the article format, then upload the  docs and generate a preview.'}
                </div>
              ) : (
                <PreviewEditor
                  rules={rules}
                  previewDocument={previewDocument}
                  onBlockChange={updatePreviewBlock}
                  onBlocksChange={updatePreviewBlocks}
                  onBlockRemove={removePreviewBlock}
                  onAddSectionBelow={addPreviewSectionBelow}
                  onRuleChange={requestAppliedRuleChange}
                  selectedBlockId={selectedPreviewBlockId}
                  selectedBlockIds={selectedPreviewBlockIds}
                  onSelectBlock={handleSelectPreviewBlock}
                />
              )}
            </div>
          </div>
        </section>
      ) : null}
    </main>
  );
}

function IndexPreviewEditor({ preview, onChange, onDelete }) {
  return (
    <div className="index-preview-pages">
      <IndexPreviewTable
        title="TABLE OF CONTENTS"
        sectionKey="contents"
        rows={preview.contents || []}
        columns={[
          { key: 'description', label: 'Description' },
          { key: 'page_no', label: 'Page.No', compact: true },
        ]}
        onChange={onChange}
        onDelete={onDelete}
      />
      <IndexPreviewTable
        title="LIST OF FIGURES"
        sectionKey="figures"
        rows={preview.figures || []}
        columns={[
          { key: 'figure_no', label: 'Figure No.', compact: true },
          { key: 'name', label: 'Name of Figure' },
          { key: 'page_no', label: 'Page No.', compact: true },
        ]}
        onChange={onChange}
        onDelete={onDelete}
      />
      <IndexPreviewTable
        title="LIST OF Tables"
        sectionKey="tables"
        rows={preview.tables || []}
        columns={[
          { key: 'table_no', label: 'Table No.', compact: true },
          { key: 'name', label: 'Name of Tables' },
          { key: 'page_no', label: 'Page No.', compact: true },
        ]}
        onChange={onChange}
        onDelete={onDelete}
      />
    </div>
  );
}

function RegisteredUsersTable({ users, resettingUserId, searchTerm, selectedDownloadDate, onResetPassword }) {
  const USERS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [users, selectedDownloadDate, searchTerm]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredUsers = users.filter((user) => {
    if (!normalizedSearchTerm) {
      return true;
    }

    const searchableValue = [
      user.id,
      user.name,
      user.email,
      user.external_provider ? `External (${user.external_provider})` : 'Direct',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableValue.includes(normalizedSearchTerm);
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + USERS_PER_PAGE);

  return (
    <div className="registered-users-table-shell">
      <div className="table-wrap">
        <table className="rules-table registered-users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Login Type</th>
              <th>Created At</th>
              <th>Formatted Downloads</th>
              <th>{selectedDownloadDate ? `Downloads On ${selectedDownloadDate}` : 'Downloads On Date'}</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 ? (
              <tr>
                <td className="registered-users-empty-row" colSpan={8}>
                  No users matched your search.
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.name || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>{user.external_provider ? `External (${user.external_provider})` : 'Direct'}</td>
                  <td>{formatAdminDate(user.created_at)}</td>
                  <td>{user.formatted_download_count ?? 0}</td>
                  <td>{user.formatted_download_count_for_date ?? 0}</td>
                  <td>
                    <button
                      className="mini-button secondary-button"
                      type="button"
                      onClick={() => onResetPassword(user)}
                      disabled={Boolean(user.external_provider) || resettingUserId === String(user.id)}
                    >
                      {resettingUserId === String(user.id) ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-pagination">
        <span className="table-pagination-summary">
          Showing {filteredUsers.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length}
        </span>
        <div className="table-pagination-actions">
          <button
            className="mini-button secondary-button"
            type="button"
            onClick={() => setCurrentPage((pageNumber) => Math.max(1, pageNumber - 1))}
            disabled={safeCurrentPage === 1}
          >
            Previous
          </button>
          <span className="table-pagination-page">Page {safeCurrentPage} of {totalPages}</span>
          <button
            className="mini-button secondary-button"
            type="button"
            onClick={() => setCurrentPage((pageNumber) => Math.min(totalPages, pageNumber + 1))}
            disabled={safeCurrentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, form, loading, onChange, onClose, onSubmit }) {
  const userLabel = user?.name || user?.email || `User #${user?.id || ''}`;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-password-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <span className="eyebrow">Reset Password</span>
            <h2 id="reset-password-title">Set a new password for {userLabel}</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose} disabled={loading}>
            Close
          </button>
        </div>

        <div className="modal-form-grid">
          <label className="auth-field" htmlFor="reset-password-input">
            <span>New Password</span>
            <input
              id="reset-password-input"
              type="password"
              value={form.password}
              onChange={(event) => onChange((current) => ({ ...current, password: event.target.value }))}
              placeholder="Enter new password"
              disabled={loading}
            />
          </label>

          <label className="auth-field" htmlFor="reset-password-confirm-input">
            <span>Confirm Password</span>
            <input
              id="reset-password-confirm-input"
              type="password"
              value={form.confirmPassword}
              onChange={(event) => onChange((current) => ({ ...current, confirmPassword: event.target.value }))}
              placeholder="Re-enter new password"
              disabled={loading}
            />
          </label>
        </div>

        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="primary-button" type="button" onClick={onSubmit} disabled={loading}>
            {loading ? 'Resetting...' : 'Save New Password'}
          </button>
        </div>
      </section>
    </div>
  );
}

function DetectionRulesPage({
  selectedArticleKey,
  selectedDocumentKey,
  selectedScope,
  onSelectArticleKey,
  onSelectDocumentKey,
}) {
  const selectedKey = selectedScope === 'document' ? selectedDocumentKey : selectedArticleKey;
  const docsByScope = selectedScope === 'document' ? DOCUMENT_DETECTION_RULE_DOCS : DETECTION_RULE_DOCS;
  const selectedDoc = docsByScope[selectedKey] || docsByScope.title;
  const isSingleInstance = isSingleInstanceDetectionRule(selectedKey);
  const selectedLabel = selectedScope === 'document' && selectedKey === 'title'
    ? 'Chapter Title'
    : FIELD_LABELS[selectedKey] || selectedKey;

  return (
    <section className="table-panel manage-panel detection-rules-page">
      <div className="panel-header">
        <div>
          <span className="eyebrow">Admin Guide</span>
          <h2>Detection rules explorer</h2>
          <p className="helper-text manage-page-note">
            Select any detected type to see how the app classifies it using order, patterns, styles, signatures, and examples.
          </p>
        </div>
      </div>

      <div className="format-selector-row">
        <label className="file-picker detection-rule-picker" htmlFor="article-detection-rule-select">
          <span>Article Formatting</span>
          <select
            id="article-detection-rule-select"
            className="format-select"
            value={selectedArticleKey}
            onChange={(event) => onSelectArticleKey(event.target.value)}
          >
            {DETECTION_RULE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="file-picker detection-rule-picker" htmlFor="document-detection-rule-select">
          <span>Document Formatting</span>
          <select
            id="document-detection-rule-select"
            className="format-select"
            value={selectedDocumentKey}
            onChange={(event) => onSelectDocumentKey(event.target.value)}
          >
            {DOCUMENT_DETECTION_RULE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="detection-rule-grid">
        <article className="detection-rule-card detection-rule-summary-card">
          <span className="eyebrow">Summary</span>
          <h3>{selectedLabel}</h3>
          <p>{selectedDoc.summary}</p>
        </article>

        <article className="detection-rule-card">
          <span className="eyebrow">Occurrence Rule</span>
          <ul className="detection-rule-list">
            {isSingleInstance ? (
              <>
                <li>This type is treated as a single-instance section in one document.</li>
                <li>If you manually mark another `{selectedLabel}`, older matching blocks are reset to `content`.</li>
              </>
            ) : (
              <>
                <li>This type can appear multiple times in the same document.</li>
                <li>No single-instance restriction is enforced for this detection type.</li>
              </>
            )}
          </ul>
        </article>

        <article className="detection-rule-card">
          <span className="eyebrow">Detection Order</span>
          <ul className="detection-rule-list">
            {selectedDoc.order.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>

        <article className="detection-rule-card">
          <span className="eyebrow">Patterns</span>
          <ul className="detection-rule-list">
            {selectedDoc.patterns.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>

        <article className="detection-rule-card">
          <span className="eyebrow">Style Rules</span>
          <ul className="detection-rule-list">
            {selectedDoc.styles.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>

        <article className="detection-rule-card">
          <span className="eyebrow">Signature Rules</span>
          <ul className="detection-rule-list">
            {selectedDoc.signatures.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>

        <article className="detection-rule-card">
          <span className="eyebrow">Examples</span>
          <ul className="detection-rule-list detection-rule-code-list">
            {selectedDoc.examples.map((item) => (
              <li key={item}>
                <code>{item}</code>
              </li>
            ))}
          </ul>
        </article>

        <article className="detection-rule-card detection-rule-code-card">
          <span className="eyebrow">Code References</span>
          <div className="detection-code-reference-list">
            {selectedDoc.codeRefs.map((item) => {
              const codeRef = typeof item === 'string' ? { name: item, detail: '' } : item;
              return (
                <article className="detection-code-reference-item" key={codeRef.name}>
                  <code>{codeRef.name}</code>
                  {codeRef.detail && <p>{codeRef.detail}</p>}
                </article>
              );
            })}
          </div>
        </article>
      </div>
    </section>
  );
}


function IndexPreviewTable({ title, sectionKey, rows, columns, onChange, onDelete }) {
  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <section className="index-preview-page">
      <h3>{title}</h3>
      <table className="index-preview-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.compact ? 'compact' : ''}>
                {column.label}
              </th>
            ))}
            <th className="index-preview-action-header" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <Fragment key={`${sectionKey}-${rowIndex}`}>
              {sectionKey === 'contents' && row.is_chapter && rowIndex > 0 && (
                <tr className="index-preview-spacer-row">
                  {columns.map((column) => (
                    <td key={column.key} className={column.compact ? 'compact' : ''} />
                  ))}
                  <td className="index-preview-action-cell" />
                </tr>
              )}
              <tr className={row.is_chapter ? 'index-preview-chapter-row' : ''}>
                {columns.map((column) => (
                  <td key={column.key} className={column.compact ? 'compact' : ''}>
                    <input
                      className="index-preview-input"
                      type={column.key === 'page_no' ? 'number' : 'text'}
                      min={column.key === 'page_no' ? '1' : undefined}
                      disabled={sectionKey === 'contents' && row.is_chapter && column.key === 'page_no'}
                      value={row[column.key] ?? ''}
                      onChange={(event) => onChange(sectionKey, rowIndex, column.key, event.target.value)}
                    />
                  </td>
                ))}
                <td className="index-preview-action-cell">
                  <button
                    className="index-row-delete-button"
                    type="button"
                    aria-label="Delete row"
                    title="Delete row"
                    onClick={() => onDelete(sectionKey, rowIndex)}
                  >
                    ×
                  </button>
                </td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function getTemplateColumnsLabel(templateColumns) {
  return templateColumns === 2 ? 'Double Column' : 'Single Column';
}

function normalizeOptimizeLayout(layout, templateColumns) {
  const isDoubleColumn = templateColumns === 2;
  const paginationMode = ['strict', 'balanced', 'compact'].includes(layout?.pagination_mode)
    ? layout.pagination_mode
    : 'balanced';
  return {
    left_margin_cm: String(layout?.left_margin_cm || '2'),
    right_margin_cm: String(layout?.right_margin_cm || '2'),
    column_gap_cm: String(layout?.column_gap_cm || (isDoubleColumn ? '1' : '0')),
    top_margin_cm: String(layout?.top_margin_cm || '1.8'),
    bottom_margin_cm: String(layout?.bottom_margin_cm || '1.8'),
    pagination_mode: paginationMode,
    auto_fit_images: layout?.auto_fit_images !== false,
    max_figure_height_percent: String(layout?.max_figure_height_percent || '30'),
    keep_small_table_rows: String(layout?.keep_small_table_rows || '3'),
    allow_large_table_split: layout?.allow_large_table_split !== false,
    repeat_table_header: layout?.repeat_table_header !== false,
  };
}

function normalizeFrontMatterLines(value) {
  const color = /^#[0-9a-f]{6}$/i.test(String(value?.color || ''))
    ? String(value.color).toUpperCase()
    : '#17365D';
  const parsedWidth = Number.parseFloat(value?.width_pt);
  const width = Number.isFinite(parsedWidth) ? Math.max(0.5, Math.min(4, parsedWidth)) : 1.5;
  const normalizeMargin = (rawValue) => {
    const parsed = Number.parseFloat(rawValue);
    return String(Number.isFinite(parsed) ? Math.max(0, Math.min(36, parsed)) : 6);
  };
  return {
    above_abstract: Boolean(value?.above_abstract),
    below_keywords: Boolean(value?.below_keywords),
    color,
    width_pt: String(width),
    above_line_outer_margin_pt: normalizeMargin(value?.above_line_outer_margin_pt),
    above_line_inner_margin_pt: normalizeMargin(value?.above_line_inner_margin_pt),
    below_line_inner_margin_pt: normalizeMargin(value?.below_line_inner_margin_pt),
    below_line_outer_margin_pt: normalizeMargin(value?.below_line_outer_margin_pt),
    box_enabled: Boolean(value?.box_enabled),
    box_style: ['single', 'double', 'dotted', 'dashed'].includes(value?.box_style) ? value.box_style : 'single',
    box_corner_radius_pt: normalizeMargin(value?.box_corner_radius_pt ?? 0),
    box_padding_pt: normalizeMargin(value?.box_padding_pt),
  };
}

function normalizeHeaderSeparator(value) {
  const color = /^#[0-9a-f]{6}$/i.test(String(value?.color || ''))
    ? String(value.color).toUpperCase()
    : '#7F1D1D';
  const parsedWidth = Number.parseFloat(value?.width_pt);
  const normalizedWidth = String(Number.isFinite(parsedWidth) ? Math.max(0.5, Math.min(4, parsedWidth)) : 1.5);
  const normalizeWidth = (rawValue) => {
    const parsed = Number.parseFloat(rawValue);
    return String(Number.isFinite(parsed) ? Math.max(0.5, Math.min(4, parsed)) : normalizedWidth);
  };
  const normalizeMargin = (rawValue, fallback) => {
    const parsed = Number.parseFloat(rawValue);
    return String(Number.isFinite(parsed) ? Math.max(0, Math.min(36, parsed)) : fallback);
  };
  return {
    enabled: Boolean(value?.enabled),
    style: ['single', 'double', 'dotted', 'dashed'].includes(value?.style) ? value.style : 'single',
    page_scope: ['all_pages', 'first_page_only', 'remaining_pages'].includes(value?.page_scope) ? value.page_scope : 'all_pages',
    tone: ['dark', 'faded'].includes(value?.tone) ? value.tone : 'dark',
    double_gap_pt: normalizeMargin(value?.double_gap_pt, 2),
    color,
    width_pt: normalizedWidth,
    upper_width_pt: normalizeWidth(value?.upper_width_pt),
    lower_width_pt: normalizeWidth(value?.lower_width_pt),
    margin_above_pt: normalizeMargin(value?.margin_above_pt, 4),
    margin_below_pt: normalizeMargin(value?.margin_below_pt, 6),
  };
}

function normalizeFooterSeparator(value) {
  const base = normalizeHeaderSeparator(value);
  const alignment = ['left', 'center', 'right'].includes(value?.text_align) ? value.text_align : 'center';
  const parsedFontSize = Number.parseFloat(value?.font_size);
  const normalizeMargin = (rawValue, fallback) => {
    const parsed = Number.parseFloat(rawValue);
    return String(Number.isFinite(parsed) ? Math.max(0, Math.min(36, parsed)) : fallback);
  };
  return {
    ...base,
    margin_above_pt: normalizeMargin(value?.margin_above_pt, 6),
    margin_below_pt: normalizeMargin(value?.margin_below_pt, 4),
    text: String(value?.text || ''),
    text_align: alignment,
    font_size: String(Number.isFinite(parsedFontSize) ? Math.max(6, Math.min(24, parsedFontSize)) : 9),
    bold: Boolean(value?.bold),
  };
}

function isDoubleColumnLayoutInRange(layout) {
  return ['left_margin_cm', 'right_margin_cm', 'column_gap_cm'].every((fieldKey) => {
    const value = Number.parseFloat(layout?.[fieldKey]);
    return Number.isFinite(value) && value >= 0.5 && value <= 5;
  });
}

function getLayoutPreviewStyle(layout, templateColumns) {
  if (templateColumns !== 2) {
    return undefined;
  }

  const normalizedLayout = normalizeOptimizeLayout(layout, templateColumns);
  const leftMargin = Number.parseFloat(normalizedLayout.left_margin_cm) || 2;
  const rightMargin = Number.parseFloat(normalizedLayout.right_margin_cm) || 2;
  const columnGap = Number.parseFloat(normalizedLayout.column_gap_cm) || 1;
  const pxPerCm = 24;

  return {
    paddingLeft: `${Math.max(12, leftMargin * pxPerCm)}px`,
    paddingRight: `${Math.max(12, rightMargin * pxPerCm)}px`,
    columnGap: `${Math.max(12, columnGap * pxPerCm)}px`,
  };
}

function paginateOptimizeDocument(document) {
  if (!document?.blocks?.length) {
    return [];
  }

  const layout = normalizeOptimizeLayout(document.document_layout, document.template_columns);
  const pageCapacity = Math.max(
    280,
    getOptimizePageCapacity(layout, document.template_columns) - estimateHeaderFooterWeight(document)
  );
  const pages = [];
  let currentBlocks = [];
  let currentWeight = 0;

  getOptimizeBlockUnits(document.blocks).forEach((unit) => {
    const unitWeight = unit.reduce((sum, block) => sum + estimateOptimizeBlockWeight(block, document.template_columns), 0);
    if (currentBlocks.length > 0 && currentWeight + unitWeight > pageCapacity) {
      pages.push(currentBlocks);
      currentBlocks = [];
      currentWeight = 0;
    }

    currentBlocks.push(...unit);
    currentWeight += unitWeight;
  });

  if (currentBlocks.length > 0) {
    pages.push(currentBlocks);
  }

  return pages.map((blocks, index) => ({
    pageNumber: index + 1,
    blocks,
  }));
}

function getOptimizeBlockUnits(blocks) {
  const units = [];
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  let index = 0;

  while (index < safeBlocks.length) {
    const current = safeBlocks[index];
    const next = safeBlocks[index + 1];

    if (isOptimizeTitleForMedia(current, next)) {
      units.push([current, next]);
      index += 2;
      continue;
    }

    if (isOptimizeMediaWithFollowingTitle(current, next)) {
      units.push([current, next]);
      index += 2;
      continue;
    }

    units.push([current]);
    index += 1;
  }

  return units;
}

function isOptimizeTitleForMedia(current, next) {
  return Boolean(
    (current?.type === 'table_title' && next?.type === 'table')
      || (current?.type === 'figure_title' && next?.type === 'figure')
  );
}

function isOptimizeMediaWithFollowingTitle(current, next) {
  return Boolean(
    (current?.type === 'table' && next?.type === 'table_title')
      || (current?.type === 'figure' && next?.type === 'figure_title')
  );
}

function getOptimizePageCapacity(layout, templateColumns) {
  const top = Number.parseFloat(layout.top_margin_cm || '1.8') || 1.8;
  const bottom = Number.parseFloat(layout.bottom_margin_cm || '1.8') || 1.8;
  const left = Number.parseFloat(layout.left_margin_cm || '2.0') || 2.0;
  const right = Number.parseFloat(layout.right_margin_cm || '2.0') || 2.0;
  const gutter = templateColumns === 2 ? (Number.parseFloat(layout.column_gap_cm || '1') || 1) : 0;
  const marginPenalty = ((top + bottom) * 35) + ((left + right + gutter) * 24);
  const base = templateColumns === 2 ? 560 : 700;
  return Math.max(360, base - marginPenalty);
}

function estimateHeaderFooterWeight(document) {
  const headerEntry = normalizeHeaderFooterEntries('header', document?.header_rules || [])[0];
  const footerEntry = normalizeHeaderFooterEntries('footer', document?.footer_rules || [])[0];

  const estimateEntry = (entry, isFooter = false) => {
    if (!entry) {
      return 0;
    }

    const textLines = String(entry.text || '').split('\n').filter((line) => line.trim()).length;
    const rightLines = String(entry.right_text || '').split('\n').filter((line) => line.trim()).length;
    const imageWeight = entry.image?.src ? 90 : 0;
    const pageNumberWeight = isFooter && entry.dynamic_page_number?.enabled ? 22 : 0;
    return (Math.max(textLines, 1) * 18) + (rightLines * 12) + imageWeight + pageNumberWeight + 18;
  };

  return estimateEntry(headerEntry, false) + estimateEntry(footerEntry, true);
}

function estimateOptimizeBlockWeight(block, templateColumns) {
  if (!block) {
    return 0;
  }

  if (block.type === 'figure') {
    const height = Number(block.height_emu || 2000000);
    const scaledHeight = height * ((Number(block.image_scale_percent || '100') || 100) / 100);
    const spacing = (Number(block.format?.spacing_before || 0) + Number(block.format?.spacing_after || 0)) * 2;
    return Math.max(130, (scaledHeight / 18000) + spacing);
  }

  if (block.type === 'table') {
    const rowCount = Array.isArray(block.rows) ? block.rows.length : 0;
    return 160 + (rowCount * 36);
  }

  const rawText = String(block.text || block.html || '');
  const textWeight = rawText.replace(/<[^>]+>/g, ' ').trim().length;
  const paragraphCount = Math.max(1, String(block.html || block.text || '').split(/<br\s*\/?>\s*<br\s*\/?>|\n{2,}/i).filter(Boolean).length);
  const lineSpacing = Number(block.format?.line_spacing || 1.2) || 1.2;
  const spacing = (Number(block.format?.spacing_before || 0) + Number(block.format?.spacing_after || 0)) * 2;
  const typeBoost = block.type?.startsWith('heading_') ? 40 : block.type === 'title' ? 65 : 90;
  const columnCompaction = templateColumns === 2 ? 0.88 : 1;

  return (typeBoost + ((textWeight / 9) + (paragraphCount * 18) + (lineSpacing * 20) + spacing)) * columnCompaction;
}

function getOptimizePageLayoutGroups(allBlocks, pageBlocks, templateColumns) {
  if (templateColumns !== 2) {
    return {
      singleColumnGroups: groupOptimizePreviewBlocks(pageBlocks),
      twoColumnGroups: [],
    };
  }

  const firstBlockId = pageBlocks[0]?.id;
  const fullKeywordIndex = Array.isArray(allBlocks) ? allBlocks.findIndex((block) => block.type === 'keywords') : -1;
  const firstBlockIndex = Array.isArray(allBlocks) ? allBlocks.findIndex((block) => block.id === firstBlockId) : -1;
  const pageKeywordIndex = pageBlocks.findIndex((block) => block.type === 'keywords');

  if (fullKeywordIndex !== -1 && firstBlockIndex > fullKeywordIndex) {
    return {
      singleColumnGroups: [],
      twoColumnGroups: groupOptimizePreviewBlocks(pageBlocks),
    };
  }

  if (pageKeywordIndex !== -1 && pageKeywordIndex < pageBlocks.length - 1) {
    return {
      singleColumnGroups: groupOptimizePreviewBlocks(pageBlocks.slice(0, pageKeywordIndex + 1)),
      twoColumnGroups: groupOptimizePreviewBlocks(pageBlocks.slice(pageKeywordIndex + 1)),
    };
  }

  return {
    singleColumnGroups: groupOptimizePreviewBlocks(pageBlocks),
    twoColumnGroups: [],
  };
}

function groupOptimizePreviewBlocks(blocks) {
  return getOptimizeBlockUnits(blocks).map((unit) => ({
    type: unit[0]?.type || 'content',
    blocks: unit,
  }));
}

function splitOptimizeGroupsIntoColumns(groups) {
  const leftColumn = [];
  const rightColumn = [];
  let leftWeight = 0;
  let rightWeight = 0;

  groups.forEach((group) => {
    const groupWeight = (group.blocks || []).reduce((sum, block) => sum + estimateOptimizeBlockWeight(block, 2), 0);
    if (leftWeight <= rightWeight) {
      leftColumn.push(group);
      leftWeight += groupWeight;
    } else {
      rightColumn.push(group);
      rightWeight += groupWeight;
    }
  });

  return [leftColumn, rightColumn];
}

function PreviewToolbar({
  rules,
  previewDocument,
  selectedBlockId,
  selectedBlockIds = [],
  onBlockChange,
  onBlocksChange,
  onMergeSelectedBlock,
  onSelectBlock,
  onUndo,
  canUndo,
}) {
  const isNewUploadRules = isNewUploadFormat(rules);
  const selectedBlock = previewDocument.blocks.find((block) => block.id === selectedBlockId) || previewDocument.blocks[0];
  const normalizedSelectedBlockIds = selectedBlockIds.length > 0
    ? selectedBlockIds
    : (selectedBlock?.id ? [selectedBlock.id] : []);
  const isBulkSelection = normalizedSelectedBlockIds.length > 1;
  const selectedBlockIndex = previewDocument.blocks.findIndex((block) => block.id === selectedBlock?.id);
  const canMergeSelectedBlock = Boolean(onMergeSelectedBlock && selectedBlock && selectedBlockIndex > 0 && !isBulkSelection);
  const presetGroups = getRulePresetGroups(rules);
  const [openPresetGroupId, setOpenPresetGroupId] = useState(presetGroups[0]?.id || '');
  const [selectedBulletStyle, setSelectedBulletStyle] = useState('disc');
  const [selectedListNumberStyle, setSelectedListNumberStyle] = useState('upper-alpha');

  useEffect(() => {
    function rememberPreviewSelection() {
      getSelectionInfo();
    }

    window.document.addEventListener('selectionchange', rememberPreviewSelection);
    return () => window.document.removeEventListener('selectionchange', rememberPreviewSelection);
  }, []);

  function applyAlignment(align) {
    const selectionInfo = getSelectionInfo();
    if (selectionInfo.blockId === selectedBlock?.id) {
      const paragraphElement = getSelectedParagraphElement();
      if (paragraphElement) {
        paragraphElement.style.textAlign = align;
        syncSelectedEditableBlock();
        return;
      }
    }

    if (selectedBlock) {
      onBlockChange(selectedBlock.id, { format: { align } });
    }
  }

  function syncSelectedEditableBlock() {
    const blockElement = getSelectedBlockElement() || getPreviewBlockElementById(selectedBlock?.id);
    if (!blockElement) {
      return;
    }

    syncEditableBlockElement(blockElement);
  }

  function syncEditableBlockElement(blockElement) {
    if (!blockElement) {
      return;
    }

    const blockId = blockElement.dataset.blockId;
    if (!blockId) {
      return;
    }

    const currentBlock = previewDocument.blocks.find((block) => block.id === blockId);
    if (!currentBlock) {
      return;
    }

    onBlockChange(blockId, getNormalizedBlockContentFromElement(currentBlock, blockElement, previewDocument.blocks));
  }

  function handleToolbarAction(action, sync = false) {
    return (event) => {
      event.preventDefault();
      action();
      if (sync) {
        syncSelectedEditableBlock();
      }
    };
  }

  function handlePresetGroupPointerDown(event) {
    event.preventDefault();
    event.stopPropagation();
    getSelectionInfo({ allowStored: true });
  }

  function handlePresetGroupToggle(groupId) {
    const selectionInfo = getSelectionInfo({ allowStored: true });
    setOpenPresetGroupId((currentId) => (currentId === groupId ? '' : groupId));
    window.requestAnimationFrame(() => restorePreviewSelection(selectionInfo));
  }

  function toggleInlineTextStyle(command, fallbackStyleMap) {
    const selectionInfo = getSelectionInfo({ allowStored: true });
    const activeBlock = selectionInfo.blockId
      ? previewDocument.blocks.find((block) => block.id === selectionInfo.blockId)
      : selectedBlock;
    if (!activeBlock) {
      return;
    }

    if (!selectionInfo.hasSelection || selectionInfo.blockId !== activeBlock.id || isFullBlockSelection(selectionInfo, activeBlock.id)) {
      toggleBlockTextStyle(activeBlock, command);
      return;
    }

    restorePreviewSelection(selectionInfo);
    const didToggleInline = toggleInlineStyleOnSelection(command, fallbackStyleMap);
    if (didToggleInline) {
      syncSelectedEditableBlock();
      return;
    }

    toggleBlockTextStyle(activeBlock, command);
  }

  function toggleBlockTextStyle(block, command) {
    const key = command === 'bold' ? 'bold' : command === 'italic' ? 'italic' : command === 'underline' ? 'underline' : '';
    if (!key) {
      return;
    }
    onBlockChange(block.id, {
      format: {
        [key]: !Boolean(block.format?.[key]),
      },
    });
  }

  function applyListFormatting(listPatch) {
    if (normalizedSelectedBlockIds.length === 0) {
      return;
    }

    const selectionInfo = getSelectionInfo({ allowStored: true });
    const selectionBlock = selectionInfo.blockId
      ? previewDocument.blocks.find((block) => block.id === selectionInfo.blockId)
      : null;
    const selectionBlockId = selectionBlock?.id || selectedBlock?.id || '';
    const isPartialSelection = !isBulkSelection
      && selectionBlock
      && isSelectionWithinCurrentBlock(selectionInfo, selectionBlockId)
      && !isFullBlockSelection(selectionInfo, selectionBlockId);

    if (isPartialSelection) {
      const splitPatch = createPartialSelectionListSplit(selectionBlock, selectionInfo, listPatch, rules);
      if (splitPatch) {
        onBlockChange(selectionBlock.id, splitPatch);
        lastPreviewSelectionInfo = null;
        return;
      }

      // If the user selected text inside a block, never fall back to formatting
      // the whole section. A failed split should leave the content unchanged.
      return;
    }

    const targetBlockIds = selectionBlock?.id ? [selectionBlock.id] : normalizedSelectedBlockIds;

    onBlocksChange(
      targetBlockIds,
      (block) => {
        return {
          source: 'manual',
          format: {
            ...block.format,
            list: listPatch,
          },
        };
      },
      {
        selectedBlockId: targetBlockIds[targetBlockIds.length - 1],
        selectedBlockIds: targetBlockIds,
        disableCascade: true,
      }
    );
  }

  function applyPreset(preset) {
    const selectionInfo = getSelectionInfo({ allowStored: true });
    const selectionBlock = selectionInfo.blockId
      ? previewDocument.blocks.find((block) => block.id === selectionInfo.blockId)
      : null;
    const activeBlock = selectionBlock || selectedBlock;
    const isHeadingPreset = preset.blockType === 'heading_1' || preset.blockType === 'heading_2' || preset.blockType === 'heading_3';
    const isNumberingPreset = isNumberingField(preset.blockType);
    if (isBulkSelection && preset.blockType) {
      onBlocksChange(
        normalizedSelectedBlockIds,
        (block) => {
          const patch = {
            type: preset.blockType,
            level: getHeadingLevel(preset.blockType),
            label: formatBlockLabel(preset.blockType),
            source: 'manual',
            ...(preset.blockFormat
              ? {
                  format: {
                    ...preset.blockFormat,
                    ...(isNumberingPreset ? { numbering: getConfiguredNumbering(rules, preset.blockType) } : {}),
                  },
                }
              : {}),
          };

          if (isHeadingPreset) {
            patch.text = sanitizeHeadingText(block.text || block.html || '');
            patch.html = sanitizeHeadingHtml(block.html || block.text || '');
          }

          return patch;
        },
        {
          selectedBlockId: normalizedSelectedBlockIds[normalizedSelectedBlockIds.length - 1],
          selectedBlockIds: normalizedSelectedBlockIds,
        }
      );
      return;
    }

    const isPartialSelection = activeBlock
      && isSelectionWithinCurrentBlock(selectionInfo, activeBlock.id)
      && !isFullBlockSelection(selectionInfo, activeBlock.id);

    if (isPartialSelection) {
      if (preset.blockType && activeBlock) {
        const splitPatch = createPartialSelectionRuleSplit(activeBlock, selectionInfo, preset, rules);
        if (splitPatch) {
          onBlockChange(activeBlock.id, splitPatch);
          lastPreviewSelectionInfo = null;
        }
        return;
      }

      const didApplyInlineOnly = applyInlineStyleToSelection(preset.inlineStyle);
      if (didApplyInlineOnly) {
        syncSelectedEditableBlock();
      }
      return;
    }

    const didApplyInline = isHeadingPreset ? false : applyInlineStyleToSelection(preset.inlineStyle);
    if (activeBlock) {
      const blockElement = getPreviewBlockElementById(activeBlock.id);
      const currentRenderedPrefix = getHeadingRenderPrefix(previewDocument.blocks, activeBlock);
      const renderedBlockText = blockElement
        ? stripRenderedPrefix(blockElement.innerText, currentRenderedPrefix).trim()
        : '';
      const renderedBlockHtml = blockElement
        ? stripRenderedPrefix(blockElement.innerHTML, currentRenderedPrefix).trim()
        : '';
      const currentBlockText = renderedBlockText || activeBlock.text || '';
      const currentBlockHtml = renderedBlockHtml || activeBlock.html || buildHeadingHtmlFromText(activeBlock.text || '');
      const patch = {
        ...(preset.blockType
          ? {
              type: preset.blockType,
              level: getHeadingLevel(preset.blockType),
              label: formatBlockLabel(preset.blockType),
              source: 'manual',
            }
          : {}),
        ...(preset.blockFormat
          ? {
              format: {
                ...preset.blockFormat,
                ...(isNumberingPreset ? { numbering: getConfiguredNumbering(rules, preset.blockType) } : {}),
              },
            }
          : {}),
      };

      if (isHeadingPreset) {
        patch.text = sanitizeHeadingText(currentBlockText);
        patch.html = sanitizeHeadingHtml(currentBlockHtml);
      } else if (activeBlock.type?.startsWith('heading_') && preset.blockType === 'content') {
        const normalizedText = normalizeHeadingConvertedToContentText(currentBlockText || activeBlock.text || '');
        patch.text = normalizedText;
        patch.html = textToPreviewHtml(normalizedText);
      }

      onBlockChange(activeBlock.id, patch);
    }
    if (didApplyInline) {
      syncSelectedEditableBlock();
    }
  }

  return (
    <section className="preview-toolbar-panel">
      <div className="panel-header preview-editor-header">
        <div>
          <span className="eyebrow">Preview Editor</span>
        </div>
      </div>

      {isBulkSelection && (
        <div className="bulk-selection-row">
          <button
            className="ghost-button bulk-selection-clear"
            type="button"
            onMouseDown={handleToolbarAction(() => onSelectBlock(selectedBlockId))}
          >
            Clear Multi-Select
          </button>
        </div>
      )}

      <div className="preview-toolbar">
        <button className="ghost-button preview-icon-button" type="button" onMouseDown={handleToolbarAction(() => onUndo())} disabled={!canUndo} aria-label="Undo" title="Undo">
          <span aria-hidden="true">↶</span>
        </button>
        <button className="ghost-button preview-icon-button" type="button" onMouseDown={handleToolbarAction(() => toggleInlineTextStyle('bold', { fontWeight: '700' }))} aria-label="Bold" title="Bold">
          <span className="preview-icon-bold" aria-hidden="true">B</span>
        </button>
        <button className="ghost-button preview-icon-button" type="button" onMouseDown={handleToolbarAction(() => toggleInlineTextStyle('italic', { fontStyle: 'italic' }))} aria-label="Italic" title="Italic">
          <span className="preview-icon-italic" aria-hidden="true">I</span>
        </button>
        <button className="ghost-button preview-icon-button" type="button" onMouseDown={handleToolbarAction(() => toggleInlineTextStyle('underline', { textDecoration: 'underline' }))} aria-label="Underline" title="Underline">
          <span className="preview-icon-underline" aria-hidden="true">U</span>
        </button>
        <button className="ghost-button preview-icon-button" type="button" onMouseDown={handleToolbarAction(() => applyAlignment('left'))} aria-label="Align left" title="Align left">
          <span className="preview-align-icon preview-align-left" aria-hidden="true"><span /><span /><span /></span>
        </button>
        <button className="ghost-button preview-icon-button" type="button" onMouseDown={handleToolbarAction(() => applyAlignment('center'))} aria-label="Align center" title="Align center">
          <span className="preview-align-icon preview-align-center" aria-hidden="true"><span /><span /><span /></span>
        </button>
        <button className="ghost-button preview-icon-button" type="button" onMouseDown={handleToolbarAction(() => applyAlignment('right'))} aria-label="Align right" title="Align right">
          <span className="preview-align-icon preview-align-right" aria-hidden="true"><span /><span /><span /></span>
        </button>
        <span className="preview-toolbar-break" aria-hidden="true" />
        {!isNewUploadRules && (
          <>
            <select
              className="heading-select preview-toolbar-select"
              value={selectedBulletStyle}
              onChange={(event) => setSelectedBulletStyle(event.target.value)}
            >
              {LIST_BULLET_STYLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              className="ghost-button preview-toolbar-action"
              type="button"
              onMouseDown={handleToolbarAction(() => applyListFormatting({ enabled: true, type: 'bullet', style: selectedBulletStyle }))}
            >
              Bullets
            </button>
            <select
              className="heading-select preview-toolbar-select"
              value={selectedListNumberStyle}
              onChange={(event) => setSelectedListNumberStyle(event.target.value)}
            >
              {LIST_NUMBER_STYLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              className="ghost-button preview-toolbar-action"
              type="button"
              onMouseDown={handleToolbarAction(() => applyListFormatting({ enabled: true, type: 'number', style: selectedListNumberStyle }))}
            >
              Numbering
            </button>
            <button
              className="ghost-button"
              type="button"
              onMouseDown={handleToolbarAction(() => applyListFormatting({ enabled: false, type: 'bullet', style: 'disc' }))}
            >
              Clear List
            </button>
            <button
              className="ghost-button"
              type="button"
              onMouseDown={handleToolbarAction(() => onMergeSelectedBlock())}
              disabled={!canMergeSelectedBlock}
            >
              Merge
            </button>
          </>
        )}
      </div>

      {presetGroups.length > 0 && (
        <div className="rule-preset-groups">
          <div className="rule-preset-toggles">
            {presetGroups.map((group) => (
              <button
                key={group.id}
                className={openPresetGroupId === group.id ? 'rule-preset-toggle active' : 'rule-preset-toggle'}
                type="button"
                onMouseDown={handlePresetGroupPointerDown}
                onClick={() => handlePresetGroupToggle(group.id)}
              >
                {group.label}
              </button>
            ))}
          </div>

          {presetGroups
            .filter((group) => group.id === openPresetGroupId)
            .map((group) => (
              <section className="rule-preset-group" key={group.id}>
                <div className="rule-preset-list">
                  {group.presets.map((preset) => (
                    <button
                      key={preset.id}
                      className="ghost-button rule-preset-button"
                      type="button"
                      onMouseDown={handleToolbarAction(() => applyPreset(preset))}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </section>
            ))}
        </div>
      )}

    </section>
  );
}

function PreviewEditor({
  rules,
  previewDocument,
  onBlockChange,
  onBlocksChange,
  onBlockRemove,
  onAddSectionBelow,
  onRuleChange,
  selectedBlockId,
  selectedBlockIds = [],
  onSelectBlock,
}) {
  const templateColumns = previewDocument.template_columns === 2 ? 2 : 1;
  const frontMatterBox = normalizeFrontMatterLines(rules?.front_matter_lines);
  const previewLayout = getPreviewLayoutGroups(previewDocument.blocks, templateColumns, frontMatterBox.box_enabled);
  const specialHeaderConfig = previewDocument.special_header_config || null;
  const layoutPreviewStyle = getLayoutPreviewStyle(previewDocument.document_layout, templateColumns);
  const appliedRuleRows = getAppliedRuleTableRows(previewDocument);

  return (
    <section className="preview-editor-shell">
      <PreviewHeaderFooterChrome
        type="header"
        entries={previewDocument.header_rules}
        specialConfig={specialHeaderConfig}
        separator={previewDocument.header_separator}
      />
      {previewLayout.singleColumnGroups.length > 0 && (
        <div className="preview-block-list" style={layoutPreviewStyle}>
          {previewLayout.singleColumnGroups.map((group, groupIndex) => (
            <PreviewSectionGroup
              key={`single-${group.type}-${groupIndex}`}
              group={group}
              allBlocks={previewDocument.blocks}
              rules={rules}
              onChange={onBlockChange}
              onBlocksChange={onBlocksChange}
              onBlockRemove={onBlockRemove}
              onAddSectionBelow={onAddSectionBelow}
              onRuleChange={onRuleChange}
              appliedRuleRows={appliedRuleRows}
              selectedBlockId={selectedBlockId}
              selectedBlockIds={selectedBlockIds}
              onSelect={onSelectBlock}
              registerRef={() => {}}
            />
          ))}
        </div>
      )}
      {previewLayout.twoColumnGroups.length > 0 && (
        <div className="preview-block-list two-column-preview preview-block-list-after-keywords" style={layoutPreviewStyle}>
          {previewLayout.twoColumnGroups.map((group, groupIndex) => (
            <PreviewSectionGroup
              key={`double-${group.type}-${groupIndex}`}
              group={group}
              allBlocks={previewDocument.blocks}
              rules={rules}
              onChange={onBlockChange}
              onBlocksChange={onBlocksChange}
              onBlockRemove={onBlockRemove}
              onAddSectionBelow={onAddSectionBelow}
              onRuleChange={onRuleChange}
              appliedRuleRows={appliedRuleRows}
              selectedBlockId={selectedBlockId}
              selectedBlockIds={selectedBlockIds}
              onSelect={onSelectBlock}
              registerRef={() => {}}
            />
          ))}
        </div>
      )}
      <PreviewHeaderFooterChrome
        type="footer"
        entries={previewDocument.footer_rules}
        specialConfig={specialHeaderConfig}
        footerSeparator={previewDocument.footer_separator}
      />
    </section>
  );
}

function AppliedRuleConfirmPopup({ pendingChange, onApplyAll, onApplySelected, onCancel }) {
  const selectedLabel = getAppliedRuleName(pendingChange?.nextBlockType);
  const patternStyle = pendingChange?.row?.patternStyle || 'Normal';

  return (
    <div className="rule-confirm-popover" role="dialog" aria-modal="true" aria-label="Confirm rule change">
      <div className="rule-confirm-card">
        <div>
          <span className="eyebrow">Confirm Rule Change</span>
          <h3>{selectedLabel}</h3>
          <p className="helper-text">
            Same exact heading style: {patternStyle}
          </p>
        </div>
        <div className="rule-confirm-actions">
          <button className="primary-button" type="button" onClick={onApplyAll}>
            Apply to All
          </button>
          <button className="secondary-button" type="button" onClick={onApplySelected}>
            Only Selected Text
          </button>
          <button className="ghost-button" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function AppliedRulesTableModal({ previewDocument, onRuleChange, onClose }) {
  const rows = getAppliedRuleTableRows(previewDocument);

  return (
    <div className="modal-backdrop applied-rules-backdrop" role="dialog" aria-modal="true" aria-label="Applied rules data">
      <div className="modal-card applied-rules-modal-card">
        <div className="modal-header">
          <div>
            <span className="eyebrow">Applied Rules</span>
            <h2>Rule data from formatted preview</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="table-wrap applied-rules-table-wrap">
          <table className="rules-table applied-rules-table">
            <thead>
              <tr>
                <th>Rule Name</th>
                <th>Text</th>
                <th>Text Pattern Style</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="3" className="registered-users-empty-row">No rule data available.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <select
                        className="applied-rules-rule-select"
                        value={row.ruleType}
                        onChange={(event) => onRuleChange(row, event.target.value)}
                      >
                        {APPLIED_RULE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={row.patternStyle.includes('Bold') ? 'applied-rules-text applied-rules-text-bold' : 'applied-rules-text'}>
                      {row.text}
                    </td>
                    <td>{row.patternStyle}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OptimizeLayoutPanel({
  optimizeDocument,
  selectedBlockId,
  onSelectBlock,
  onBlockChange,
  onMoveBlock,
  onLayoutChange,
}) {
  const selectedBlock = optimizeDocument.blocks.find((block) => block.id === selectedBlockId) || optimizeDocument.blocks[0];
  const layout = normalizeOptimizeLayout(optimizeDocument.document_layout, optimizeDocument.template_columns);

  return (
    <section className="preview-toolbar-panel optimize-controls-panel">
      <div className="panel-header preview-editor-header">
        <div>
          <span className="eyebrow">Optimize Controls</span>
        </div>
        <div className="helper-text">Spacing, margins, image size, and reflow controls.</div>
      </div>

      <div className="preview-format-toolbar optimize-layout-grid">
        <label className="heading-editor-field">
          <span>Left Margin (cm)</span>
          <input
            className="heading-input"
            type="text"
            value={layout.left_margin_cm}
            onChange={(event) => onLayoutChange({ left_margin_cm: event.target.value })}
          />
        </label>
        <label className="heading-editor-field">
          <span>Right Margin (cm)</span>
          <input
            className="heading-input"
            type="text"
            value={layout.right_margin_cm}
            onChange={(event) => onLayoutChange({ right_margin_cm: event.target.value })}
          />
        </label>
        <label className="heading-editor-field">
          <span>Top Margin (cm)</span>
          <input
            className="heading-input"
            type="text"
            value={layout.top_margin_cm}
            onChange={(event) => onLayoutChange({ top_margin_cm: event.target.value })}
          />
        </label>
        <label className="heading-editor-field">
          <span>Bottom Margin (cm)</span>
          <input
            className="heading-input"
            type="text"
            value={layout.bottom_margin_cm}
            onChange={(event) => onLayoutChange({ bottom_margin_cm: event.target.value })}
          />
        </label>
      </div>

      {selectedBlock && (
        <>
          <div className="optimize-selected-meta">
            <strong>{formatBlockLabel(selectedBlock.type)}</strong>
            <select
              className="heading-select"
              value={selectedBlock.id}
              onChange={(event) => onSelectBlock(event.target.value)}
            >
              {optimizeDocument.blocks.map((block, index) => (
                <option key={block.id} value={block.id}>
                  {index + 1}. {formatBlockLabel(block.type)}
                </option>
              ))}
            </select>
          </div>

          <div className="preview-format-toolbar optimize-layout-grid">
            <label className="heading-editor-field">
              <span>Spacing Before (pt)</span>
              <input
                className="heading-input"
                type="text"
                value={selectedBlock.format?.spacing_before || '0'}
                onChange={(event) => onBlockChange(selectedBlock.id, { format: { spacing_before: event.target.value } })}
              />
            </label>
            <label className="heading-editor-field">
              <span>Spacing After (pt)</span>
              <input
                className="heading-input"
                type="text"
                value={selectedBlock.format?.spacing_after || '0'}
                onChange={(event) => onBlockChange(selectedBlock.id, { format: { spacing_after: event.target.value } })}
              />
            </label>
            <label className="heading-editor-field">
              <span>Line Spacing</span>
              <input
                className="heading-input"
                type="text"
                value={selectedBlock.format?.line_spacing || '1.2'}
                onChange={(event) => onBlockChange(selectedBlock.id, { format: { line_spacing: event.target.value } })}
              />
            </label>
            <label className="heading-editor-field">
              <span>Align</span>
              <select
                className="heading-select"
                value={selectedBlock.format?.align || 'left'}
                onChange={(event) => onBlockChange(selectedBlock.id, { format: { align: event.target.value } })}
              >
                {ALIGNMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {selectedBlock.type === 'figure' && (
              <label className="heading-editor-field">
                <span>Image Scale (%)</span>
                <input
                  className="heading-input"
                  type="text"
                  value={selectedBlock.image_scale_percent || '100'}
                  onChange={(event) => onBlockChange(selectedBlock.id, { image_scale_percent: event.target.value })}
                />
              </label>
            )}
          </div>

          <div className="optimize-action-row">
            <button className="ghost-button" type="button" onClick={() => onMoveBlock(selectedBlock.id, 'up')}>
              Move Up
            </button>
            <button className="ghost-button" type="button" onClick={() => onMoveBlock(selectedBlock.id, 'down')}>
              Move Down
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function OptimizePreviewWorkspace({
  optimizeDocument,
  selectedBlockId,
  onSelectBlock,
  spreadIndex,
  onSpreadChange,
  onDownload,
  downloading,
}) {
  const pages = paginateOptimizeDocument(optimizeDocument);
  const totalSpreads = Math.max(1, Math.ceil(pages.length / 2));
  const safeSpreadIndex = Math.min(spreadIndex, totalSpreads - 1);
  const startPageIndex = safeSpreadIndex * 2;
  const visiblePages = pages.slice(startPageIndex, startPageIndex + 2);
  const isLastSpread = safeSpreadIndex >= totalSpreads - 1;

  return (
    <section className="preview-editor-shell optimize-preview-shell">
      <div className="preview-toolbar-panel optimize-book-toolbar">
        <div className="panel-header preview-editor-header">
          <div>
            <span className="eyebrow">Book Preview</span>
          </div>
          <div className="helper-text">
            Showing pages {startPageIndex + 1}-{Math.min(startPageIndex + visiblePages.length, pages.length)} of {pages.length}
          </div>
        </div>

        <div className="optimize-action-row">
          <button
            className="ghost-button"
            type="button"
            onClick={() => onSpreadChange(Math.max(0, safeSpreadIndex - 1))}
            disabled={safeSpreadIndex === 0}
          >
            Previous
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={() => onSpreadChange(Math.min(totalSpreads - 1, safeSpreadIndex + 1))}
            disabled={isLastSpread}
          >
            Next
          </button>
          {isLastSpread && (
            <button className="secondary-button" type="button" onClick={onDownload} disabled={downloading}>
              {downloading ? 'Downloading...' : 'Download'}
            </button>
          )}
        </div>
      </div>

      <div className="optimize-book-spread">
        {visiblePages.map((pageData) => (
          <OptimizePreviewPage
            key={pageData.pageNumber}
            pageData={pageData}
            optimizeDocument={optimizeDocument}
            selectedBlockId={selectedBlockId}
            onSelectBlock={onSelectBlock}
          />
        ))}
        {visiblePages.length === 1 && <div className="optimize-page-card optimize-page-card-empty" />}
      </div>
    </section>
  );
}

function OptimizePreviewPage({ pageData, optimizeDocument, selectedBlockId, onSelectBlock }) {
  const pageDocument = {
    ...optimizeDocument,
    blocks: pageData.blocks,
  };
  const templateColumns = pageDocument.template_columns === 2 ? 2 : 1;
  const previewLayout = getOptimizePageLayoutGroups(optimizeDocument.blocks, pageDocument.blocks, templateColumns);
  const twoColumnPageGroups = splitOptimizeGroupsIntoColumns(previewLayout.twoColumnGroups);

  return (
    <article className="optimize-page-card">
      <div className="optimize-page-number">Page {pageData.pageNumber}</div>
      <PreviewHeaderFooterChrome type="header" entries={pageDocument.header_rules} specialConfig={pageDocument.special_header_config || null} separator={pageDocument.header_separator} />
      {previewLayout.singleColumnGroups.length > 0 && (
        <div className="optimize-page-body">
          <div className="preview-block-list optimize-block-list">
            {previewLayout.singleColumnGroups.map((group, groupIndex) => (
              <OptimizeSectionGroup
                key={`opt-single-${pageData.pageNumber}-${group.type}-${groupIndex}`}
                group={group}
                selectedBlockId={selectedBlockId}
                onSelect={onSelectBlock}
              />
            ))}
          </div>
        </div>
      )}
      {previewLayout.twoColumnGroups.length > 0 && (
        <div className="optimize-page-body">
          <div className="preview-block-list optimize-block-list preview-block-list-after-keywords">
            <div className="optimize-two-column-grid">
              {twoColumnPageGroups.map((columnGroups, columnIndex) => (
                <div className="optimize-two-column-track" key={`opt-col-${pageData.pageNumber}-${columnIndex}`}>
                  {columnGroups.map((group, groupIndex) => (
                    <OptimizeSectionGroup
                      key={`opt-double-${pageData.pageNumber}-${columnIndex}-${group.type}-${groupIndex}`}
                      group={group}
                      selectedBlockId={selectedBlockId}
                      onSelect={onSelectBlock}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <PreviewHeaderFooterChrome type="footer" entries={pageDocument.footer_rules} specialConfig={pageDocument.special_header_config || null} footerSeparator={pageDocument.footer_separator} />
    </article>
  );
}

function OptimizeSectionGroup({ group, selectedBlockId, onSelect }) {
  return (
    <section className="preview-section-group">
      <div className="preview-block-top">
        <div className="preview-block-type">{formatBlockLabel(group.type)}</div>
      </div>
      <div className="preview-section-body">
        {group.blocks.map((block) => (
          <OptimizeBlockCard
            key={block.id}
            block={block}
            isSelected={selectedBlockId === block.id}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  );
}

function OptimizeBlockCard({ block, isSelected, onSelect }) {
  const cardClassName = isSelected ? 'preview-block-card selected optimize-block-card' : 'preview-block-card optimize-block-card';
  const cardStyle = buildPreviewBlockCardStyle(block.format);

  if (block.type === 'figure') {
    return (
      <article className={cardClassName} style={cardStyle} onClick={() => onSelect(block.id)}>
        <PreviewFigureBlock
          block={block}
          onSelect={onSelect}
        />
      </article>
    );
  }

  if (block.type === 'table') {
    return (
      <article className={cardClassName} style={cardStyle} onClick={() => onSelect(block.id)}>
        <OptimizeTableBlock block={block} />
      </article>
    );
  }

  const renderedPrefix = block.type?.startsWith('heading_') ? `${block.renderPrefix || ''} ` : '';
  const listPrefix = isListFormattingEnabled(block) ? `${getStandaloneListRenderPrefix(block)} ` : '';
  return (
    <article className={cardClassName} style={cardStyle} onClick={() => onSelect(block.id)}>
      <div
        className="preview-block-text optimize-static-block"
        style={buildPreviewInlineStyle(block.format, { includeSpacing: false })}
        dangerouslySetInnerHTML={{ __html: `${escapeHtml(renderedPrefix || listPrefix)}${renderPreviewBlockHtml(block)}` }}
      />
    </article>
  );
}

function OptimizeTableBlock({ block }) {
  const rows = Array.isArray(block.rows) ? block.rows : [];
  return (
    <div className="table-wrap">
      <table className="rules-table optimize-inline-table">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`opt-row-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`opt-cell-${rowIndex}-${cellIndex}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
void OptimizeLayoutPanel;
void OptimizePreviewWorkspace;

function PreviewHeaderFooterChrome({ type, entries, specialConfig, separator, footerSeparator }) {
  const normalizedSeparator = normalizeHeaderSeparator(separator);
  const normalizedFooterSeparator = normalizeFooterSeparator(footerSeparator);
  const toCssBorderStyle = (style) => ({ single: 'solid', double: 'double', dotted: 'dotted', dashed: 'dashed' }[style] || 'solid');
  const fadeColor = (color, tone) => {
    if (tone !== 'faded') return color;
    const channels = [1, 3, 5].map((index) => Number.parseInt(color.slice(index, index + 2), 16));
    return `rgb(${channels.map((channel) => Math.round(channel + (255 - channel) * 0.55)).join(',')})`;
  };
  const headerLineColor = fadeColor(normalizedSeparator.color, normalizedSeparator.tone);
  const footerLineColor = fadeColor(normalizedFooterSeparator.color, normalizedFooterSeparator.tone);
  const activeSeparator = type === 'header' ? normalizedSeparator : normalizedFooterSeparator;
  const activeLineColor = type === 'header' ? headerLineColor : footerLineColor;
  const separatorStyle = undefined;
  const separatorLines = activeSeparator.enabled
    ? <PreviewSeparatorLines type={type} config={activeSeparator} color={activeLineColor} cssStyle={toCssBorderStyle} />
    : null;
  if (hasSpecialHeaderFooterConfig(specialConfig)) {
    const specialSection = normalizeSpecialHeaderConfig(specialConfig)[type];
    if (hasSpecialSectionContent(specialSection)) {
      return (
        <div style={separatorStyle}>
          {type === 'footer' && separatorLines}
          <SpecialPreviewHeaderFooterChrome type={type} section={specialSection} />
          {type === 'header' && separatorLines}
        </div>
      );
    }
  }

  const normalizedEntries = normalizeHeaderFooterEntries(type, Array.isArray(entries) && entries.length ? entries : [{}]);
  const entry = normalizedEntries[0];

  if (!entry) {
    return null;
  }

  const headerLayout = entry.layout || 'text_only';
  const showDualHeaderText = type === 'header' && headerLayout === 'text_left_text_right';
  const showHeaderImage = type === 'header'
    && (entry.image.name || entry.image.src)
    && headerLayout !== 'text_only'
    && headerLayout !== 'text_left_text_right';
  const showHeaderText = type !== 'header'
    ? Boolean(entry.text)
    : Boolean(entry.text) && headerLayout !== 'image_only';
  const showHeaderRightText = showDualHeaderText && Boolean(entry.right_text);
  const firstPageEntry = entry.page_mode === 'first_page_different' ? normalizeHeaderFooterVariantEntry(type, entry.first_page) : null;
  const showFirstPagePreview = firstPageEntry && headerFooterEntryHasVisibleContent(type, firstPageEntry);
  const showRemainingPreview = Boolean(
    showHeaderText
      || showHeaderRightText
      || showHeaderImage
      || (type === 'footer' && entry.dynamic_page_number.enabled)
      || (type === 'footer' && normalizedFooterSeparator.enabled)
  );

  if (!showRemainingPreview && !showFirstPagePreview) {
    return null;
  }

  return (
    <div className={type === 'header' ? 'preview-header-strip' : 'preview-footer-strip'} style={separatorStyle}>
      {type === 'footer' && separatorLines}
      <div className="preview-header-footer-meta">{type === 'header' ? 'Header Preview' : 'Footer Preview'}</div>
      <div className="preview-header-footer-variant-stack">
        {showFirstPagePreview && (
          <PreviewHeaderFooterVariant type={type} entry={firstPageEntry} label="First Page" />
        )}
        {showRemainingPreview && (
          <PreviewHeaderFooterVariant
            type={type}
            entry={entry}
            label={entry.page_mode === 'first_page_different' ? 'Remaining Pages' : 'All Pages'}
          />
        )}
      </div>
      {type === 'header' && separatorLines}
    </div>
  );
}

function PreviewSeparatorLines({ type, config, color, cssStyle }) {
  const isDouble = config.style === 'double';
  const upperWidth = isDouble ? config.upper_width_pt : config.width_pt;
  const lowerWidth = isDouble ? config.lower_width_pt : config.width_pt;
  const lineStyle = cssStyle(isDouble ? 'single' : config.style);
  return (
    <div
      className={`preview-separator-lines preview-separator-lines-${type}`}
      style={{
        marginTop: `${type === 'header' ? config.margin_above_pt : config.margin_above_pt}pt`,
        marginBottom: `${type === 'header' ? config.margin_below_pt : config.margin_below_pt}pt`,
      }}
    >
      <div style={{ width: '100%', borderTop: `${upperWidth}pt ${lineStyle} ${color}` }} />
      {isDouble && <div style={{ height: `${config.double_gap_pt}pt` }} />}
      {isDouble && <div style={{ width: '100%', borderTop: `${lowerWidth}pt solid ${color}` }} />}
    </div>
  );
}

function PreviewHeaderFooterVariant({ type, entry, label }) {
  const headerLayout = entry.layout || 'text_only';
  const showDualHeaderText = type === 'header' && headerLayout === 'text_left_text_right';
  const showHeaderImage = type === 'header'
    && (entry.image.name || entry.image.src)
    && headerLayout !== 'text_only'
    && headerLayout !== 'text_left_text_right';
  const showHeaderText = type !== 'header'
    ? Boolean(entry.text)
    : Boolean(entry.text) && headerLayout !== 'image_only';
  const showHeaderRightText = showDualHeaderText && Boolean(entry.right_text);

  return (
    <div className="preview-header-footer-variant-block">
      <div className="preview-header-footer-variant-label">{label}</div>
      <div
        className="preview-header-footer-content"
        style={{
          ...buildHeaderFooterInlineStyle(entry),
          ...(type === 'header' && headerLayout === 'text_left_text_right'
            ? {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              alignItems: 'start',
              columnGap: '24px',
            }
            : {
              flexDirection: type === 'header' ? getHeaderLayoutDirection(headerLayout) : 'column',
            }),
        }}
      >
        {showHeaderImage && (
          <div className="preview-header-footer-image-wrap" style={buildHeaderImagePreviewStyle(entry)}>
            {entry.image.src?.startsWith('data:image/') ? (
              <img
                className="preview-header-footer-inline-image"
                src={entry.image.src}
                alt={entry.image.name || 'Header image'}
              />
            ) : (
              <div className="preview-header-footer-image">
                Image: {entry.image.name || entry.image.src}
              </div>
            )}
          </div>
        )}
        {showHeaderText && (
          <div style={{ whiteSpace: 'pre-line', textAlign: showDualHeaderText ? 'left' : undefined }}>
            {entry.text}
          </div>
        )}
        {showHeaderRightText && (
          <div style={{ whiteSpace: 'pre-line', textAlign: 'right' }}>
            {entry.right_text}
          </div>
        )}
        {type === 'footer' && entry.dynamic_page_number.enabled && (
          <div
            className="preview-footer-page-number"
            style={{ textAlign: normalizePageNumberAlignment(entry.dynamic_page_number.alignment) }}
          >
            {entry.dynamic_page_number.prefix}
            {resolveNumberingPreview({ enabled: true, style: entry.dynamic_page_number.format, raw: '' }, 0) || '1'}
            {entry.dynamic_page_number.suffix}
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewSectionGroup({
  group,
  allBlocks,
  rules,
  onChange,
  onBlocksChange,
  onBlockRemove,
  onAddSectionBelow,
  onRuleChange,
  appliedRuleRows = [],
  selectedBlockId,
  selectedBlockIds = [],
  onSelect,
  registerRef,
}) {
  const frontMatterBox = normalizeFrontMatterLines(rules?.front_matter_lines);
  const isCombinedBoxSection = frontMatterBox.box_enabled
    && group.blocks.some((block) => block.type === 'abstract')
    && group.blocks.some((block) => block.type === 'keywords');
  const isBoxSection = frontMatterBox.box_enabled && (isCombinedBoxSection || ['abstract', 'keywords'].includes(group.type));
  const boxLineStyle = frontMatterBox.box_style === 'double'
    ? 'double'
    : frontMatterBox.box_style === 'dotted'
      ? 'dotted'
      : frontMatterBox.box_style === 'dashed'
        ? 'dashed'
        : 'solid';
  const boxSectionStyle = isBoxSection
    ? {
        '--front-box-color': frontMatterBox.color,
        '--front-box-width': `${frontMatterBox.width_pt}pt`,
        '--front-box-style': boxLineStyle,
        '--front-box-radius': `${frontMatterBox.box_corner_radius_pt}pt`,
        '--front-box-padding': `${frontMatterBox.box_padding_pt}pt`,
      }
    : undefined;

  function handleGroupRuleChange(event) {
    const nextBlockType = event.target.value;
    const blockIds = group.blocks.map((block) => block.id).filter(Boolean);
    if (
      !nextBlockType
      || nextBlockType === group.type
      || blockIds.length === 0
      || !onBlocksChange
      || ['table', 'figure'].includes(group.type)
    ) {
      return;
    }

    onBlocksChange(
      blockIds,
      (block) => buildPreviewRuleTypePatch(block, nextBlockType, rules),
      {
        selectedBlockId: blockIds[blockIds.length - 1],
        selectedBlockIds: blockIds,
        disableCascade: true,
      }
    );
  }

  return (
    <section
      className={`preview-section-group${isBoxSection ? ` preview-front-matter-box ${isCombinedBoxSection ? 'preview-front-matter-box-combined' : `preview-front-matter-box-${group.type}`}` : ''}`}
      style={boxSectionStyle}
    >
      <div className="preview-block-top">
        <select
          className="preview-block-type preview-block-type-select"
          value={group.type || 'content'}
          disabled={['table', 'figure'].includes(group.type)}
          onMouseDown={(event) => event.stopPropagation()}
          onChange={handleGroupRuleChange}
          aria-label="Change rule name"
        >
          {getPreviewRuleTypeOptions(rules, group.type).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="preview-section-body">
        {group.blocks.map((block, index) => {
          const blockAppliedRuleRows = appliedRuleRows.filter((row) => row.blockId === block.id);
          const blockAppliedRuleRow = blockAppliedRuleRows.length === 1 ? blockAppliedRuleRows[0] : null;

          return (
            <PreviewBlockCard
              key={block.id}
              block={block}
              allBlocks={allBlocks}
              onChange={onChange}
              onRuleChange={onRuleChange}
              onRemove={onBlockRemove}
              onAddSectionBelow={onAddSectionBelow}
              appliedRuleOptions={getAppliedRuleOptions(rules)}
              appliedRuleRow={blockAppliedRuleRow}
              isSelected={selectedBlockId === block.id}
              isMultiSelected={selectedBlockIds.includes(block.id)}
              onSelect={onSelect}
              registerRef={(node) => registerRef(node, block.id)}
            />
          );
        })}
      </div>
    </section>
  );
}

function PreviewBlockCard({ block, allBlocks, onChange, onRuleChange, onRemove, onAddSectionBelow, appliedRuleOptions = APPLIED_RULE_OPTIONS, appliedRuleRow, isSelected, isMultiSelected, onSelect, registerRef }) {
  const [newSectionType, setNewSectionType] = useState('content');
  const renderedPrefix = getHeadingRenderPrefix(allBlocks, block);
  const isHeadingBlock = block.type?.startsWith('heading_');
  const cardStyle = buildPreviewBlockCardStyle(block.format);
  const headingAlign = block.format?.align || 'left';
  const cardClassName = [
    'preview-block-card',
    isSelected ? 'selected' : '',
    isMultiSelected ? 'multi-selected' : '',
  ].filter(Boolean).join(' ');
  const headingRowStyle = isHeadingBlock
    ? {
        justifyContent: headingAlign === 'center'
          ? 'center'
          : headingAlign === 'right'
            ? 'flex-end'
            : 'flex-start',
      }
    : undefined;
  const headingRowClassName = isHeadingBlock
    ? `preview-heading-row preview-heading-row-${headingAlign === 'center' ? 'center' : headingAlign === 'right' ? 'right' : 'left'}`
    : '';
  function handleMultiSelectToggle(event) {
    if (!(event.ctrlKey || event.metaKey)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onSelect(block.id, { toggle: true });
  }

  function handleCardClick(event) {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    const targetElement = event.target instanceof Element ? event.target : null;
    if (targetElement?.closest('button, select, input, textarea, [data-ignore-block-select="true"]')) {
      return;
    }

    const selection = typeof window !== 'undefined' ? window.getSelection() : null;
    if (
      selection
      && !selection.isCollapsed
      && targetElement?.closest('[contenteditable="true"]')
    ) {
      return;
    }

    onSelect(block.id, { skipSync: true });
  }

  function handleTextMouseUp() {
    const selectionInfo = getSelectionInfo();
    if (selectionInfo.hasSelection) {
      return;
    }
  }

  function handleTextKeyUp() {
    const selectionInfo = getSelectionInfo();
    if (!selectionInfo.hasSelection) {
      return;
    }
  }

  function handlePreviewRuleChange(event) {
    event.stopPropagation();
    if (!appliedRuleRow || !onRuleChange) {
      return;
    }

    onSelect(block.id);
    onRuleChange(appliedRuleRow, event.target.value);
  }

  function handleRemoveBlock(event) {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.(block.id);
  }

  return (
    <article className={cardClassName} style={cardStyle} onMouseDownCapture={handleMultiSelectToggle} onClick={handleCardClick}>
      {(isSelected || isMultiSelected) && (
        <div className="preview-card-action-row">
          {isMultiSelected && (
            <div className="preview-block-selection-indicator">Selected</div>
          )}
          {onRemove && (
            <button
              className="preview-block-remove-button"
              type="button"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={handleRemoveBlock}
              aria-label="Remove section"
              title="Remove section"
            >
              ×
            </button>
          )}
          {isSelected && onAddSectionBelow && (
            <div className="preview-add-section-control" data-ignore-block-select="true">
              <select className="preview-add-section-select" value={newSectionType} onChange={(event) => setNewSectionType(event.target.value)} aria-label="New section type">
                <option value="content">Content</option>
                <option value="title">Title</option>
                <option value="heading_1">Heading 1</option>
                <option value="heading_2">Heading 2</option>
                <option value="heading_3">Heading 3</option>
              </select>
              <button className="ghost-button preview-add-section-button" type="button" onClick={(event) => { event.stopPropagation(); onAddSectionBelow(block.id, newSectionType); }}>
                + Add below
              </button>
            </div>
          )}
        </div>
      )}
      {(isSelected || isMultiSelected) && appliedRuleRow && onRuleChange && (
        <div className="preview-card-rule-control" onMouseDown={(event) => event.stopPropagation()}>
          <span>Rule Name</span>
          <select
            className="applied-rules-rule-select preview-card-rule-select"
            value={appliedRuleRow.ruleType}
            onChange={handlePreviewRuleChange}
          >
            {appliedRuleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {block.type === 'table' ? (
        <PreviewTableBlock
          block={block}
          onChange={onChange}
          onSelect={onSelect}
        />
      ) : block.type === 'figure' ? (
        <PreviewFigureBlock
          block={block}
          onSelect={onSelect}
          onChange={onChange}
          isSelected={isSelected}
        />
      ) : (
        <div className={headingRowClassName} style={headingRowStyle}>
          {isHeadingBlock && renderedPrefix && (
            <span className="preview-heading-prefix" style={buildPreviewInlineStyle(block.format, { includeSpacing: false })}>
              {renderedPrefix}
            </span>
          )}
          <div
            ref={registerRef}
            className={isHeadingBlock ? 'preview-block-text preview-block-text-heading rich-preview-block doc-surface-block' : 'preview-block-text rich-preview-block doc-surface-block'}
            data-block-id={block.id}
            contentEditable
            suppressContentEditableWarning
            spellCheck
            onFocus={() => {}}
            onMouseUp={handleTextMouseUp}
            onKeyUp={handleTextKeyUp}
            onKeyDown={handleEditableKeyDown}
            onBlur={(event) =>
              onChange(block.id, getNormalizedBlockContentFromElement(block, event.currentTarget, allBlocks))
            }
            onInput={() => {}}
            onPaste={handlePlainTextPaste}
            style={buildPreviewInlineStyle(block.format, { includeSpacing: false })}
            dangerouslySetInnerHTML={{ __html: renderPreviewBlockEditorHtml(block, allBlocks) }}
          />
        </div>
      )}
    </article>
  );
}

function PreviewTableBlock({ block, onChange, onSelect }) {
  const rows = block.rows || [];

  function updateCell(rowIndex, cellIndex, nextValue) {
    const nextRows = rows.map((row, currentRowIndex) =>
      currentRowIndex === rowIndex
        ? row.map((cell, currentCellIndex) => (currentCellIndex === cellIndex ? nextValue : cell))
        : row
    );

    onChange(block.id, { rows: nextRows });
  }

  return (
    <div className="preview-table-wrap" onClick={() => onSelect(block.id)}>
      <table className="preview-doc-table">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`${block.id}-row-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`${block.id}-cell-${rowIndex}-${cellIndex}`}>
                  <div
                    className={rowIndex === 0 ? 'preview-table-cell header-cell' : 'preview-table-cell'}
                    contentEditable
                    suppressContentEditableWarning
                    onFocus={() => onSelect(block.id)}
                    onInput={(event) => updateCell(rowIndex, cellIndex, event.currentTarget.innerText)}
                  >
                    {cell}
                  </div>
                  {((block.cell_images || {})[String(rowIndex)] || {})[String(cellIndex)]?.map((image, imageIndex) => (
                    <img
                      key={`${block.id}-cell-image-${rowIndex}-${cellIndex}-${imageIndex}`}
                      className="preview-table-cell-image"
                      src={image.src}
                      alt={image.alt || 'Figure'}
                    />
                  ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DataTable({ rows, showActions }) {
  return (
    <div className="table-wrap">
      <table className="rules-table">
        <thead>
          <tr>
            <th>Rule Set</th>
            <th>Field</th>
            <th>Value</th>
            {showActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.ruleSetLabel}</td>
              <td>{row.fieldLabel}</td>
              <td>
                <ExpandableText className="table-value" text={row.displayValue || 'No data'} maxLength={220} />
              </td>
              {showActions && <td />}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ManageTable({ rows, saving, activeRow, onFieldChange, onHeadingNumberingUpdate, onUpdate, onDelete }) {
  const [openNumberingRow, setOpenNumberingRow] = useState('');
  const [numberingSelections, setNumberingSelections] = useState({});

  function toggleNumberingPanel(row) {
    setOpenNumberingRow((currentRow) => {
      if (currentRow === row.key) {
        return '';
      }

      setNumberingSelections((currentSelections) => ({
        ...currentSelections,
        [row.key]: row.numberingStyle,
      }));
      return row.key;
    });
  }

  return (
    <div className="table-wrap">
      <table className="rules-table manage-table">
        <thead>
          <tr>
            <th>Rule Set</th>
            <th>Field</th>
            <th>Rule</th>
            <th>Example</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.ruleSetLabel}</td>
              <td>{row.fieldLabel}</td>
              <td>
                {row.isHeaderFooterField ? (
                  <pre className="rule-json-preview">{row.rulePattern || 'No rule extracted yet.'}</pre>
                ) : (
                  <FieldRuleEditor
                    row={row}
                    saving={saving}
                    onFieldChange={onFieldChange}
                  />
                )}
              </td>
              <td>
                {row.isHeaderFooterField ? (
                  <HeaderFooterRuleForm
                    row={row}
                    saving={saving}
                    onFieldChange={onFieldChange}
                  />
                ) : row.isHeadingField ? (
                  <HeadingExamplePreview
                    fieldKey={row.fieldKey}
                    value={row.originalValue}
                    ruleConfig={row.ruleConfig}
                  />
                ) : (
                  <ExpandableText
                    className="example-preview"
                    text={row.exampleValue || 'No example available.'}
                    maxLength={180}
                  />
                )}
              </td>
              <td>
                <div className="action-stack">
                  {row.isNumberingField && (
                    <div className="numbering-action-group">
                      <button
                        className="mini-button ghost-mini-button"
                        type="button"
                        onClick={() => toggleNumberingPanel(row)}
                        disabled={saving}
                      >
                        {openNumberingRow === row.key ? 'Close Numbering' : 'Edit Numbering'}
                      </button>

                      {openNumberingRow === row.key && (
                        <div className="numbering-panel">
                          <label className="numbering-panel-label" htmlFor={`${row.key}-numbering-style`}>
                            Numbering Style
                          </label>
                          <select
                            id={`${row.key}-numbering-style`}
                            className="heading-select"
                            value={numberingSelections[row.key] || row.numberingStyle}
                            onChange={(event) =>
                              setNumberingSelections((currentSelections) => ({
                                ...currentSelections,
                                [row.key]: event.target.value,
                              }))
                            }
                          >
                            {getNumberingStyleOptionsForField(row.fieldKey).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>

                          <button
                            className="mini-button update-button"
                            type="button"
                            onClick={async () => {
                              await onHeadingNumberingUpdate(
                                row.ruleSetKey,
                                row.fieldKey,
                                numberingSelections[row.key] || row.numberingStyle
                              );
                              setOpenNumberingRow('');
                            }}
                            disabled={saving}
                          >
                            {saving && activeRow === row.key ? 'Updating...' : 'Apply Numbering'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    className="mini-button update-button"
                    type="button"
                    onClick={() => onUpdate(row.ruleSetKey, row.fieldKey)}
                    disabled={saving}
                  >
                    {saving && activeRow === row.key ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    className="mini-button delete-button"
                    type="button"
                    onClick={() => onDelete(row.ruleSetKey, row.fieldKey, row.originalValue)}
                    disabled={saving}
                  >
                    {saving && activeRow === row.key ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PreviewFigureBlock({ block, onSelect, onChange, isSelected }) {
  const [cropTop, setCropTop] = useState(Number(block.crop_top_percent || 0));
  const [cropBottom, setCropBottom] = useState(Number(block.crop_bottom_percent || 0));
  const [cropLeft, setCropLeft] = useState(Number(block.crop_left_percent || 0));
  const [cropRight, setCropRight] = useState(Number(block.crop_right_percent || 0));
  const [activeHandle, setActiveHandle] = useState(null);
  const interactionRef = useRef(null);
  const cropEditorRef = useRef(null);
  const imageAlign = block.format?.align || 'center';
  const imageScale = Math.max(20, Math.min(100, Number(block.image_scale_percent || 100) || 100));
  const justifyContentMap = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
    justify: 'center',
  };

  useEffect(() => {
    if (!activeHandle) return undefined;
    function moveHandle(event) {
      const start = interactionRef.current;
      const bounds = cropEditorRef.current?.getBoundingClientRect();
      if (!start || !bounds || !onChange) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (start.kind === 'move') {
        const nextX = Math.max(-bounds.width / 2, Math.min(bounds.width / 2, start.offsetX + dx));
        const nextY = Math.max(-bounds.height / 2, Math.min(bounds.height / 2, start.offsetY + dy));
        onChange(block.id, { image_offset_x: nextX, image_offset_y: nextY });
        return;
      }
      if (start.kind === 'resize') {
        const delta = Math.max(dx * start.signX, dy * start.signY);
        onChange(block.id, { image_scale_percent: Math.max(20, Math.min(150, start.scale + (delta / Math.max(1, bounds.width)) * 100)) });
        return;
      }
      const xPercent = Math.max(0, Math.min(100, (event.clientX - bounds.left) / bounds.width * 100));
      const yPercent = Math.max(0, Math.min(100, (event.clientY - bounds.top) / bounds.height * 100));
      if (start.kind === 'left') { const value = Math.min(45, Math.max(0, xPercent)); setCropLeft(value); onChange(block.id, { crop_left_percent: value }); }
      if (start.kind === 'right') { const value = Math.min(45, Math.max(0, 100 - xPercent)); setCropRight(value); onChange(block.id, { crop_right_percent: value }); }
      if (start.kind === 'top') { const value = Math.min(45, Math.max(0, yPercent)); setCropTop(value); onChange(block.id, { crop_top_percent: value }); }
      if (start.kind === 'bottom') { const value = Math.min(45, Math.max(0, 100 - yPercent)); setCropBottom(value); onChange(block.id, { crop_bottom_percent: value }); }
    }
    function stopHandle() { interactionRef.current = null; setActiveHandle(null); }
    window.addEventListener('pointermove', moveHandle);
    window.addEventListener('pointerup', stopHandle);
    return () => { window.removeEventListener('pointermove', moveHandle); window.removeEventListener('pointerup', stopHandle); };
  }, [activeHandle, block.id, block.image_offset_x, block.image_offset_y, block.image_scale_percent, onChange]);

  function beginInteraction(event, kind, signX = 1, signY = 1) {
    event.preventDefault();
    event.stopPropagation();
    interactionRef.current = {
      kind,
      x: event.clientX,
      y: event.clientY,
      offsetX: Number(block.image_offset_x || 0),
      offsetY: Number(block.image_offset_y || 0),
      scale: Number(block.image_scale_percent || 100),
      signX,
      signY,
    };
    setActiveHandle(kind);
  }

  function replaceImage(event) {
    const file = event.target.files?.[0];
    if (!file || !onChange) return;
    const reader = new FileReader();
    reader.onload = () => onChange(block.id, {
      src: String(reader.result || ''),
      alt: file.name,
      original_src: block.original_src || block.src,
    });
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  function restoreOriginalImage() {
    if (block.original_src && onChange) {
      onChange(block.id, {
        src: block.original_src,
        original_src: undefined,
        crop_top_percent: 0,
        crop_bottom_percent: 0,
        crop_left_percent: 0,
        crop_right_percent: 0,
        image_offset_x: 0,
        image_offset_y: 0,
        image_scale_percent: 100,
      });
      setCropTop(0);
      setCropBottom(0);
      setCropLeft(0);
      setCropRight(0);
    }
  }


  return (
    <>
      <div
        className="preview-figure-wrap"
        onClick={() => onSelect(block.id)}
        style={{ justifyContent: justifyContentMap[imageAlign] || 'center' }}
      >
        {block.src ? (
          <div className="preview-crop-editor" ref={cropEditorRef} style={{ maxWidth: `${imageScale}%`, transform: `translate(${Number(block.image_offset_x || 0)}px, ${Number(block.image_offset_y || 0)}px)` }}>
            <img
              className="preview-figure-image"
              src={block.src}
              alt={block.alt || 'Figure'}
              style={{ clipPath: `inset(${cropTop}% ${cropRight}% ${cropBottom}% ${cropLeft}%)` }}
              onPointerDown={(event) => isSelected && beginInteraction(event, 'move')}
            />
            {isSelected && onChange && (
              <>
                <button type="button" aria-label="Resize top left" className="crop-handle crop-handle-corner crop-handle-tl" onPointerDown={(event) => beginInteraction(event, 'resize', -1, -1)} />
                <button type="button" aria-label="Resize top right" className="crop-handle crop-handle-corner crop-handle-tr" onPointerDown={(event) => beginInteraction(event, 'resize', 1, -1)} />
                <button type="button" aria-label="Resize bottom left" className="crop-handle crop-handle-corner crop-handle-bl" onPointerDown={(event) => beginInteraction(event, 'resize', -1, 1)} />
                <button type="button" aria-label="Resize bottom right" className="crop-handle crop-handle-corner crop-handle-br" onPointerDown={(event) => beginInteraction(event, 'resize', 1, 1)} />
                <button type="button" aria-label="Crop left" className="crop-handle crop-handle-left" onPointerDown={(event) => beginInteraction(event, 'left')} />
                <button type="button" aria-label="Crop right" className="crop-handle crop-handle-right" onPointerDown={(event) => beginInteraction(event, 'right')} />
                <button type="button" aria-label="Crop top" className="crop-handle crop-handle-top" onPointerDown={(event) => beginInteraction(event, 'top')} />
                <button type="button" aria-label="Crop bottom" className="crop-handle crop-handle-bottom" onPointerDown={(event) => beginInteraction(event, 'bottom')} />
              </>
            )}
          </div>
        ) : (
          <div className="preview-header-footer-image">Figure image unavailable.</div>
        )}
      </div>
      {isSelected && onChange && (
        <div className="preview-image-controls" onMouseDown={(event) => event.stopPropagation()}>
          <label>
            <span>Image size (%)</span>
            <input
              type="number"
              min="20"
              max="100"
              value={block.image_scale_percent || '100'}
              onChange={(event) => onChange(block.id, { image_scale_percent: event.target.value })}
            />
          </label>
          <label>
            <span>Alignment</span>
            <select
              value={imageAlign}
              onChange={(event) => onChange(block.id, { format: { align: event.target.value } })}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>
          <label className="preview-image-replace-button">
            Replace image
            <input type="file" accept="image/*" onChange={replaceImage} />
          </label>
          {block.original_src && (
            <button type="button" className="ghost-button" onClick={restoreOriginalImage}>
              Restore original
            </button>
          )}
        </div>
      )}
    </>
  );
}

function ExpandableText({ text, className = '', maxLength = 180 }) {
  const [expanded, setExpanded] = useState(false);
  const safeText = String(text || '');
  const shouldCollapse = safeText.length > maxLength;
  const visibleText = shouldCollapse && !expanded
    ? `${safeText.slice(0, maxLength).trimEnd()}...`
    : safeText;

  return (
    <div className="expandable-text-block">
      <div className={className}>{visibleText}</div>
      {shouldCollapse && (
        <button
          className="expandable-text-button"
          type="button"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

function HeadingExamplePreview({ fieldKey, value, ruleConfig }) {
  const entries = normalizeHeadingEntries(getStructuredFieldValue(fieldKey, value));

  if (entries.length === 0) {
    return <div className="example-preview">No example available.</div>;
  }

  return (
    <div className="heading-example-list">
      {entries.map((entry, index) => {
        const previewText = getHeadingPreview(entry, index);
        if (!previewText) {
          return null;
        }

        return (
          <div key={`${fieldKey}-${index}`} className="heading-example-row">
            <span className="heading-example-label">{FIELD_LABELS[fieldKey] || formatBlockLabel(fieldKey)}</span>
            <div className="example-preview heading-example-text" style={buildPreviewInlineStyle(ruleConfig)}>
              {applyTextTransformToText(previewText, ruleConfig.text_transform)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FieldRuleEditor({ row, saving, onFieldChange }) {
  const ruleConfig = row.ruleConfig || getDefaultFieldRule(row.fieldKey);
  const supportsTitlePosition = row.fieldKey === 'table_title' || row.fieldKey === 'figure_title';
  const draftNamespace = 'field_rules';

  function updateRuleConfig(patch) {
    const nextConfig = {
      ...ruleConfig,
      ...patch,
    };

    onFieldChange(draftNamespace, row.fieldKey, JSON.stringify(nextConfig, null, 2));
  }

  return (
    <div className="field-rule-editor">
      <div className="field-rule-grid">
        <label className="header-footer-form-field">
          <span>Font Family</span>
          <input
            className="heading-input"
            type="text"
            value={ruleConfig.font_family}
            onChange={(event) => updateRuleConfig({ font_family: event.target.value })}
            disabled={saving}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Font Size</span>
          <input
            className="heading-input"
            type="text"
            value={ruleConfig.font_size}
            onChange={(event) => updateRuleConfig({ font_size: event.target.value })}
            disabled={saving}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Alignment</span>
          <select
            className="heading-select"
            value={ruleConfig.alignment}
            onChange={(event) => updateRuleConfig({ alignment: event.target.value })}
            disabled={saving}
          >
            {ALIGNMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="header-footer-form-field">
          <span>Line Spacing</span>
          <input
            className="heading-input"
            type="text"
            value={ruleConfig.line_spacing}
            onChange={(event) => updateRuleConfig({ line_spacing: event.target.value })}
            disabled={saving}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Text Case</span>
          <select
            className="heading-select"
            value={normalizeTextTransform(ruleConfig.text_transform)}
            onChange={(event) => updateRuleConfig({ text_transform: event.target.value })}
            disabled={saving}
          >
            {TEXT_TRANSFORM_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="header-footer-form-field">
          <span>Spacing Before</span>
          <input
            className="heading-input"
            type="text"
            value={ruleConfig.spacing_before}
            onChange={(event) => updateRuleConfig({ spacing_before: event.target.value })}
            disabled={saving}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Spacing After</span>
          <input
            className="heading-input"
            type="text"
            value={ruleConfig.spacing_after}
            onChange={(event) => updateRuleConfig({ spacing_after: event.target.value })}
            disabled={saving}
          />
        </label>

        {supportsTitlePosition && (
          <label className="header-footer-form-field">
            <span>Title Position</span>
            <select
              className="heading-select"
              value={ruleConfig.title_position || getDefaultFieldRule(row.fieldKey).title_position || 'above'}
              onChange={(event) => updateRuleConfig({ title_position: event.target.value })}
              disabled={saving}
            >
              {TITLE_POSITION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="toggle-row">
        <label className="toggle-chip">
          <input
            type="checkbox"
            checked={ruleConfig.bold}
            onChange={(event) => updateRuleConfig({ bold: event.target.checked })}
            disabled={saving}
          />
          <span>Bold</span>
        </label>
        <label className="toggle-chip">
          <input
            type="checkbox"
            checked={ruleConfig.italic}
            onChange={(event) => updateRuleConfig({ italic: event.target.checked })}
            disabled={saving}
          />
          <span>Italic</span>
        </label>
        <label className="toggle-chip">
          <input
            type="checkbox"
            checked={ruleConfig.underline}
            onChange={(event) => updateRuleConfig({ underline: event.target.checked })}
            disabled={saving}
          />
          <span>Underline</span>
        </label>
      </div>
    </div>
  );
}

function HeaderFooterEntryFields({ fieldKey, entry, saving, label, onChangeEntry }) {
  const isHeader = fieldKey === 'header';
  const usesHeaderImage = isHeader && [
    'image_only',
    'image_top_text_bottom',
    'text_top_image_bottom',
    'image_left_text_right',
    'text_left_image_right',
  ].includes(entry.layout);
  const usesDualHeaderText = isHeader && entry.layout === 'text_left_text_right';

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const { dataUrl, fileName } = await readImageFileForDocx(file);
    onChangeEntry({
      image: {
        name: fileName,
        src: dataUrl,
      },
    });
  }

  return (
    <div className="header-footer-variant-card">
      <div className="header-footer-variant-title">{label}</div>

      <label className="header-footer-form-field">
        <span>{isHeader ? 'Header Text' : 'Footer Text'}</span>
        <textarea
          className="table-editor"
          rows={3}
          value={entry.text}
          onChange={(event) => onChangeEntry({ text: event.target.value })}
          disabled={saving}
        />
      </label>

      {usesDualHeaderText && (
        <label className="header-footer-form-field">
          <span>Header Right Text</span>
          <textarea
            className="table-editor"
            rows={3}
            value={entry.right_text}
            onChange={(event) => onChangeEntry({ right_text: event.target.value })}
            disabled={saving}
          />
        </label>
      )}

      <div className="header-footer-form-grid">
        <label className="header-footer-form-field">
          <span>{isHeader ? 'Header Layout' : 'Footer Layout'}</span>
          <select
            className="heading-select"
            value={entry.layout}
            onChange={(event) => onChangeEntry({ layout: event.target.value })}
            disabled={saving}
          >
            {HEADER_LAYOUT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="header-footer-form-field">
          <span>{isHeader ? 'Header from Top (cm)' : 'Footer from Bottom (cm)'}</span>
          <input
            className="heading-input"
            type="text"
            value={isHeader ? entry.header_from_top_cm : entry.footer_from_bottom_cm}
            onChange={(event) =>
              onChangeEntry(
                isHeader
                  ? { header_from_top_cm: event.target.value }
                  : { footer_from_bottom_cm: event.target.value }
              )
            }
            disabled={saving}
            placeholder={isHeader ? 'e.g. 2.6' : 'e.g. 4.1'}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Font Size</span>
          <input
            className="heading-input"
            type="text"
            value={entry.font_size}
            onChange={(event) => onChangeEntry({ font_size: event.target.value })}
            disabled={saving}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Font Family</span>
          <input
            className="heading-input"
            type="text"
            value={entry.font_family}
            onChange={(event) => onChangeEntry({ font_family: event.target.value })}
            disabled={saving}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Text Align</span>
          <select
            className="heading-select"
            value={entry.text_align}
            onChange={(event) => onChangeEntry({ text_align: event.target.value })}
            disabled={saving}
          >
            {ALIGNMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="header-footer-toggle-row">
        <label className="toggle-chip">
          <input
            type="checkbox"
            checked={entry.bold}
            onChange={(event) => onChangeEntry({ bold: event.target.checked })}
            disabled={saving}
          />
          <span>Bold</span>
        </label>
        <label className="toggle-chip">
          <input
            type="checkbox"
            checked={entry.italic}
            onChange={(event) => onChangeEntry({ italic: event.target.checked })}
            disabled={saving}
          />
          <span>Italic</span>
        </label>
      </div>

      {usesHeaderImage && (
        <div className="header-footer-form-grid">
          <label className="header-footer-form-field">
            <span>Image Upload</span>
            <input
              className="heading-input"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={saving}
            />
          </label>

          <label className="header-footer-form-field">
            <span>Image Name</span>
            <input
              className="heading-input"
              type="text"
              value={entry.image.name}
              onChange={(event) => onChangeEntry({ image: { name: event.target.value } })}
              disabled={saving}
            />
          </label>

          <label className="header-footer-form-field">
            <span>Image Align</span>
            <select
              className="heading-select"
              value={entry.image_align}
              onChange={(event) => onChangeEntry({ image_align: event.target.value })}
              disabled={saving}
            >
              {ALIGNMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="header-footer-form-field">
            <span>Image Width (cm)</span>
            <input
              className="heading-input"
              type="text"
              value={entry.image_width_cm}
              onChange={(event) => onChangeEntry({ image_width_cm: event.target.value })}
              disabled={saving}
              placeholder="e.g. 4"
            />
          </label>

          <label className="header-footer-form-field">
            <span>Image Height (cm)</span>
            <input
              className="heading-input"
              type="text"
              value={entry.image_height_cm}
              onChange={(event) => onChangeEntry({ image_height_cm: event.target.value })}
              disabled={saving}
              placeholder="e.g. 2.5"
            />
          </label>

          {entry.image.src && (
            <div className="header-footer-image-preview-card">
              <span className="header-footer-image-preview-label">Image Preview</span>
              <img
                className="header-footer-image-preview"
                src={entry.image.src}
                alt={entry.image.name || `${isHeader ? 'Header' : 'Footer'} preview`}
              />
            </div>
          )}
        </div>
      )}

      {!isHeader && (
        <div className="header-footer-form-grid">
          <label className="toggle-chip">
            <input
              type="checkbox"
              checked={entry.dynamic_page_number.enabled}
              onChange={(event) =>
                onChangeEntry({
                  dynamic_page_number: { enabled: event.target.checked },
                })
              }
              disabled={saving}
            />
            <span>Page Number</span>
          </label>

          <label className="header-footer-form-field">
            <span>Numbering Format</span>
            <select
              className="heading-select"
              value={entry.dynamic_page_number.format}
              onChange={(event) =>
                onChangeEntry({
                  dynamic_page_number: { format: event.target.value },
                })
              }
              disabled={saving}
            >
              {PAGE_NUMBER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="header-footer-form-field">
            <span>Page Number Align</span>
            <select
              className="heading-select"
              value={entry.dynamic_page_number.alignment}
              onChange={(event) =>
                onChangeEntry({
                  dynamic_page_number: { alignment: event.target.value },
                })
              }
              disabled={saving || !entry.dynamic_page_number.enabled}
            >
              {PAGE_NUMBER_ALIGNMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

        </div>
      )}
    </div>
  );
}

function HeaderFooterRuleForm({ row, saving, onFieldChange }) {
  const entries = normalizeHeaderFooterEntries(row.fieldKey, getStructuredFieldValue(row.fieldKey, row.editValue));
  const entry = entries[0] || createHeaderFooterEntry(row.fieldKey, 1);
  const isDifferentFirstPage = entry.page_mode === 'first_page_different';

  function commitEntry(nextEntry) {
    onFieldChange(row.ruleSetKey, row.fieldKey, JSON.stringify([nextEntry], null, 2));
  }

  function updateEntry(patch) {
    commitEntry(normalizeHeaderFooterEntry(row.fieldKey, mergeHeaderFooterEntry(entry, patch), entry.section || 1));
  }

  function updateFirstPageEntry(patch) {
    updateEntry({
      first_page: mergeHeaderFooterVariantEntry(entry.first_page, patch),
    });
  }

  return (
    <div className="header-footer-form">
      <label className="header-footer-form-field">
        <span>Page Setup</span>
        <select
          className="heading-select"
          value={entry.page_mode}
          onChange={(event) => updateEntry({ page_mode: event.target.value })}
          disabled={saving}
        >
          {HEADER_FOOTER_PAGE_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className="header-footer-variant-grid">
        {isDifferentFirstPage && (
          <HeaderFooterEntryFields
            fieldKey={row.fieldKey}
            entry={entry.first_page}
            saving={saving}
            label={`First Page ${row.fieldKey === 'header' ? 'Header' : 'Footer'}`}
            onChangeEntry={updateFirstPageEntry}
          />
        )}
        <HeaderFooterEntryFields
          fieldKey={row.fieldKey}
          entry={entry}
          saving={saving}
          label={isDifferentFirstPage ? `Remaining Pages ${row.fieldKey === 'header' ? 'Header' : 'Footer'}` : `All Pages ${row.fieldKey === 'header' ? 'Header' : 'Footer'}`}
          onChangeEntry={updateEntry}
        />
      </div>
    </div>
  );
}

function SpecialPreviewHeaderFooterChrome({ type, section }) {
  const normalizedSection = normalizeSpecialSectionConfig(section);
  const firstPageSection = normalizedSection.page_mode === 'first_page_different' ? normalizedSection.first_page : null;
  const showFirstPage = hasSpecialSectionVariantContent(firstPageSection);
  const showRemaining = hasSpecialSectionVariantContent(normalizedSection);

  return (
    <div
      className={type === 'header' ? 'preview-header-strip' : 'preview-footer-strip'}
      style={type === 'header'
        ? { paddingTop: `${Number(normalizedSection.distance_cm || 0) * 10}px` }
        : { paddingBottom: `${Number(normalizedSection.distance_cm || 0) * 10}px` }}
    >
      <div className="preview-header-footer-meta">{type === 'header' ? 'Header Preview' : 'Footer Preview'}</div>
      <div className="preview-header-footer-variant-stack">
        {showFirstPage && (
          <SpecialPreviewHeaderFooterVariant type={type} section={firstPageSection} label="First Page" />
        )}
        {showRemaining && (
          <SpecialPreviewHeaderFooterVariant
            type={type}
            section={normalizedSection}
            label={normalizedSection.page_mode === 'first_page_different' ? 'Remaining Pages' : 'All Pages'}
          />
        )}
      </div>
    </div>
  );
}

function SpecialPreviewHeaderFooterVariant({ type, section, label }) {
  const normalizedSection = normalizeSpecialSectionVariantConfig(section);
  const items = getSpecialSectionItems(normalizedSection).slice(0, normalizedSection.columns);
  const showPageNumber = type === 'footer' && normalizedSection.page_number?.enabled;
  const previewColumnWidths = getSpecialPreviewColumnWidths(normalizedSection);
  const previewMarginStyle = buildSpecialPreviewSectionStyle(normalizedSection);

  return (
    <div className="preview-header-footer-variant-block">
      <div className="preview-header-footer-variant-label">{label}</div>
      <div
        className="preview-special-header-footer-grid"
        style={{
          ...previewMarginStyle,
          gridTemplateColumns: previewColumnWidths.map((width) => `minmax(0, ${width}fr)`).join(' '),
        }}
      >
        {items.map((item, index) => (
          <div key={`${type}-special-${label}-${index}`} className="preview-special-header-footer-cell" style={buildSpecialPreviewColumnStyle(item)}>
            {(item.type === 'image' || item.type === 'text_image') && item.image.src && (
              <img
                className="preview-header-footer-inline-image"
                src={item.image.src}
                alt={item.image.name || `${type} image ${index + 1}`}
              />
            )}
            {(item.type === 'text' || item.type === 'text_image') && item.text && (
              <div className="preview-special-text-lines">
                {getSpecialTextLines(item.text).map((line, lineIndex) => (
                  <div
                    key={`${type}-text-line-${label}-${index}-${lineIndex}`}
                    style={{ color: getSpecialLineColor(item, lineIndex) }}
                  >
                    {line || '\u00A0'}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {showPageNumber && (
        <div
          className="preview-special-footer-page-number"
          style={{
            textAlign: normalizePageNumberAlignment(normalizedSection.page_number?.alignment),
            marginTop: `${normalizedSection.page_number?.content_gap_pt || 0}pt`,
          }}
        >
          {resolveNumberingPreview({ enabled: true, style: normalizedSection.page_number.format, raw: '' }, 0) || '1'}
        </div>
      )}
    </div>
  );
}

function SpecialHeaderFooterPage({
  rules,
  config,
  onChange,
  onRulesChange,
  onSave,
  onClear,
  isSaved,
  saving,
  formats,
  selectedFormatId,
  onFormatChange,
  getFormatLabel,
}) {
  const normalizedConfig = normalizeSpecialHeaderConfig(config);

  function updateSection(sectionKey, patch) {
    onChange({
      ...normalizedConfig,
      [sectionKey]: normalizeSpecialSectionConfig({
        ...normalizedConfig[sectionKey],
        ...patch,
      }),
    });
  }

  function updateSectionFirstPage(sectionKey, patch) {
    const baseFirstPage = normalizedConfig[sectionKey].first_page
      ? normalizeSpecialSectionVariantConfig(normalizedConfig[sectionKey].first_page)
      : normalizeSpecialSectionVariantConfig(createDefaultSpecialSectionConfig());

    onChange({
      ...normalizedConfig,
      [sectionKey]: normalizeSpecialSectionConfig({
        ...normalizedConfig[sectionKey],
        first_page: normalizeSpecialSectionVariantConfig({
          ...baseFirstPage,
          ...patch,
        }),
      }),
    });
  }

  function clearConfig() {
    if (typeof onClear === 'function') {
      onClear();
      return;
    }

    onChange(createDefaultSpecialHeaderConfig());
  }

  return (
    <section className="table-panel manage-panel">
      <div className="panel-header">
        <div>
          <span className="eyebrow">Special Header/Footer</span>
        </div>
        <button className="secondary-button" type="button" onClick={clearConfig} disabled={saving}>
          {saving ? 'Saving...' : 'Clear Special Config'}
        </button>
      </div>

      <div className="format-selector-row">
        <label className="file-picker" htmlFor="saved-format-special">
          <span>Select Format</span>
          <select
            id="saved-format-special"
            className="format-select"
            value={selectedFormatId}
            onChange={onFormatChange}
            disabled={formats.length === 0}
          >
            <option value="">Select saved format</option>
            {formats.map((format) => (
              <option key={format.format_id} value={format.format_id}>
                {getFormatLabel(format)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="special-builder-grid">
        <SpecialHeaderFooterSectionBuilder
          label="Header Builder"
          sectionKey="header"
          value={normalizedConfig.header}
          onChange={(patch) => updateSection('header', patch)}
          onFirstPageChange={(patch) => updateSectionFirstPage('header', patch)}
        />
        <SpecialHeaderFooterSectionBuilder
          label="Footer Builder"
          sectionKey="footer"
          value={normalizedConfig.footer}
          onChange={(patch) => updateSection('footer', patch)}
          onFirstPageChange={(patch) => updateSectionFirstPage('footer', patch)}
        />
      </div>

      {rules && (
        <HeaderFooterSeparatorSettings rules={rules} onChange={onRulesChange} />
      )}

      <div className="special-save-actions">
        <button className="primary-button" type="button" onClick={onSave} disabled={!rules || saving}>
          {saving ? 'Saving...' : isSaved ? 'Update & Save' : 'Save'}
        </button>
      </div>
    </section>
  );
}

function HeaderFooterSeparatorSettings({ rules, onChange }) {
  const header = normalizeHeaderSeparator(rules?.header_separator);
  const footer = normalizeFooterSeparator(rules?.footer_separator);
  const updateHeader = (key, value) => onChange({ header_separator: { ...header, [key]: value } });
  const updateFooter = (key, value) => onChange({ footer_separator: { ...footer, [key]: value } });
  const numberFields = [
    ['margin_above_pt', 'Line Margin Above (pt)', 0, 36, 1],
    ['margin_below_pt', 'Line Margin Below (pt)', 0, 36, 1],
  ];

  return (
    <div className="preview-format-toolbar special-layout-grid special-header-footer-separator-settings">
      <h3 className="special-layout-section-title">Header Separator</h3>
      <label className="toggle-chip">
        <input type="checkbox" checked={header.enabled} onChange={(event) => updateHeader('enabled', event.target.checked)} />
        <span>Line below Header</span>
      </label>
      <label className="header-footer-form-field">
        <span>Header Line Style</span>
        <select className="heading-select" value={header.style} onChange={(event) => updateHeader('style', event.target.value)}>
          <option value="single">──────── Single</option>
          <option value="double">════ Double (separate)</option>
          <option value="dotted">········ Dotted</option>
          <option value="dashed">— — — — Dashed</option>
        </select>
      </label>
      <SeparatorPageDisplayFields value={header} prefix="Header" onChange={updateHeader} />
      <label className="header-footer-form-field">
        <span>Header Line Color</span>
        <input className="heading-input layout-color-input" type="color" value={header.color} onChange={(event) => updateHeader('color', event.target.value)} />
      </label>
      {header.style === 'double' ? (
        <>
          <LineThicknessField label="Header Upper Line Thickness (pt)" value={header.upper_width_pt} onChange={(value) => updateHeader('upper_width_pt', value)} />
          <LineThicknessField label="Header Lower Line Thickness (pt)" value={header.lower_width_pt} onChange={(value) => updateHeader('lower_width_pt', value)} />
          <LineThicknessField label="Header Double-Line Gap (pt)" value={header.double_gap_pt} onChange={(value) => updateHeader('double_gap_pt', value)} min="0" max="12" />
        </>
      ) : (
        <LineThicknessField label="Header Line Thickness (pt)" value={header.width_pt} onChange={(value) => updateHeader('width_pt', value)} />
      )}
      {numberFields.map(([key, label, min, max, step]) => (
        <label className="header-footer-form-field" key={`header-${key}`}>
          <span>{key === 'margin_above_pt'
            ? 'Header Content to Line Gap (pt)'
            : key === 'margin_below_pt'
              ? 'Header Line to Body Gap (pt)'
              : `Header ${label}`}</span>
          <input className="heading-input" type="number" min={min} max={max} step={step} value={header[key]} onChange={(event) => updateHeader(key, event.target.value)} />
        </label>
      ))}

      <h3 className="special-layout-section-title">Footer Separator</h3>
      <label className="toggle-chip">
        <input type="checkbox" checked={footer.enabled} onChange={(event) => updateFooter('enabled', event.target.checked)} />
        <span>Line above Footer</span>
      </label>
      <label className="header-footer-form-field">
        <span>Footer Line Style</span>
        <select className="heading-select" value={footer.style} onChange={(event) => updateFooter('style', event.target.value)}>
          <option value="single">──────── Single</option>
          <option value="double">════ Double (separate)</option>
          <option value="dotted">········ Dotted</option>
          <option value="dashed">— — — — Dashed</option>
        </select>
      </label>
      <SeparatorPageDisplayFields value={footer} prefix="Footer" onChange={updateFooter} />
      <label className="header-footer-form-field">
        <span>Footer Line Color</span>
        <input className="heading-input layout-color-input" type="color" value={footer.color} onChange={(event) => updateFooter('color', event.target.value)} />
      </label>
      {footer.style === 'double' ? (
        <>
          <LineThicknessField label="Footer Upper Line Thickness (pt)" value={footer.upper_width_pt} onChange={(value) => updateFooter('upper_width_pt', value)} />
          <LineThicknessField label="Footer Lower Line Thickness (pt)" value={footer.lower_width_pt} onChange={(value) => updateFooter('lower_width_pt', value)} />
          <LineThicknessField label="Footer Double-Line Gap (pt)" value={footer.double_gap_pt} onChange={(value) => updateFooter('double_gap_pt', value)} min="0" max="12" />
        </>
      ) : (
        <LineThicknessField label="Footer Line Thickness (pt)" value={footer.width_pt} onChange={(value) => updateFooter('width_pt', value)} />
      )}
      {numberFields.map(([key, label, min, max, step]) => (
        <label className="header-footer-form-field" key={`footer-${key}`}>
          <span>{key === 'margin_above_pt'
            ? 'Body to Footer Line Gap (pt)'
            : key === 'margin_below_pt'
              ? 'Footer Line to Content Gap (pt)'
              : `Footer ${label}`}</span>
          <input className="heading-input" type="number" min={min} max={max} step={step} value={footer[key]} onChange={(event) => updateFooter(key, event.target.value)} />
        </label>
      ))}
    </div>
  );
}

function SeparatorPageDisplayFields({ value, prefix, onChange }) {
  return (
    <>
      <label className="header-footer-form-field">
        <span>{prefix} Line Pages</span>
        <select className="heading-select" value={value.page_scope} onChange={(event) => onChange('page_scope', event.target.value)}>
          <option value="all_pages">All pages</option>
          <option value="first_page_only">First page only</option>
          <option value="remaining_pages">Remaining pages only</option>
        </select>
      </label>
      <label className="header-footer-form-field">
        <span>{prefix} Line Appearance</span>
        <select className="heading-select" value={value.tone} onChange={(event) => onChange('tone', event.target.value)}>
          <option value="dark">Dark</option>
          <option value="faded">Faded</option>
        </select>
      </label>
    </>
  );
}

function LineThicknessField({ label, value, onChange, min = '0.5', max = '4' }) {
  return (
    <label className="header-footer-form-field">
      <span>{label}</span>
      <input className="heading-input" type="number" min={min} max={max} step="0.5" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SpecialLayoutSettingPage({
  rules,
  saving,
  formats,
  selectedFormatId,
  onFormatChange,
  onChange,
  onSave,
  getFormatLabel,
}) {
  const templateColumns = rules?.template_columns === 2 ? 2 : 1;
  const layout = normalizeOptimizeLayout(rules?.document_layout, templateColumns);
  const frontMatterLines = normalizeFrontMatterLines(rules?.front_matter_lines);
  const headerSeparator = normalizeHeaderSeparator(rules?.header_separator);
  const footerSeparator = normalizeFooterSeparator(rules?.footer_separator);

  function updateLayoutField(fieldKey, value) {
    onChange({
      document_layout: {
        ...layout,
        [fieldKey]: value,
      },
    });
  }

  function updateFrontMatterLine(fieldKey, value) {
    onChange({
      front_matter_lines: {
        ...frontMatterLines,
        [fieldKey]: value,
      },
    });
  }

  function updateHeaderSeparator(fieldKey, value) {
    onChange({ header_separator: { ...headerSeparator, [fieldKey]: value } });
  }

  function updateFooterSeparator(fieldKey, value) {
    onChange({ footer_separator: { ...footerSeparator, [fieldKey]: value } });
  }

  return (
    <section className="table-panel manage-panel">
      <div className="panel-header">
        <div>
          <span className="eyebrow">Special Layout Setting</span>
          <h2>Document layout and separator lines</h2>
        </div>
        <button className="primary-button" type="button" onClick={onSave} disabled={!rules || saving}>
          {saving ? 'Saving...' : 'Save Layout'}
        </button>
      </div>

      <div className="format-selector-row">
        <label className="file-picker" htmlFor="saved-format-layout">
          <span>Select Format</span>
          <select
            id="saved-format-layout"
            className="format-select"
            value={selectedFormatId}
            onChange={onFormatChange}
            disabled={formats.length === 0}
          >
            <option value="">Select saved format</option>
            {formats.map((format) => (
              <option key={format.format_id} value={format.format_id}>
                {getFormatLabel(format)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!rules ? (
        <div className="empty-state">No format selected yet.</div>
      ) : (
        <>
          <div className="preview-format-toolbar special-layout-grid">
            {false && <>
            <h3 className="special-layout-section-title">Header Separator</h3>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={headerSeparator.enabled}
                onChange={(event) => updateHeaderSeparator('enabled', event.target.checked)}
              />
              <span>Line below Header</span>
            </label>

            <label className="header-footer-form-field">
              <span>Header Line Color</span>
              <input
                className="heading-input layout-color-input"
                type="color"
                value={headerSeparator.color}
                onChange={(event) => updateHeaderSeparator('color', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Header Line Thickness (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0.5"
                max="4"
                step="0.5"
                value={headerSeparator.width_pt}
                onChange={(event) => updateHeaderSeparator('width_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Header Line: Margin Above (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={headerSeparator.margin_above_pt}
                onChange={(event) => updateHeaderSeparator('margin_above_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Header Line: Margin Below (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={headerSeparator.margin_below_pt}
                onChange={(event) => updateHeaderSeparator('margin_below_pt', event.target.value)}
              />
            </label>

            <h3 className="special-layout-section-title">Footer Content and Separator</h3>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={footerSeparator.enabled}
                onChange={(event) => updateFooterSeparator('enabled', event.target.checked)}
              />
              <span>Line above Footer</span>
            </label>

            <label className="header-footer-form-field">
              <span>Footer Line Color</span>
              <input
                className="heading-input layout-color-input"
                type="color"
                value={footerSeparator.color}
                onChange={(event) => updateFooterSeparator('color', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Footer Line Thickness (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0.5"
                max="4"
                step="0.5"
                value={footerSeparator.width_pt}
                onChange={(event) => updateFooterSeparator('width_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Footer Line: Margin Above (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={footerSeparator.margin_above_pt}
                onChange={(event) => updateFooterSeparator('margin_above_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Footer Line: Margin Below (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={footerSeparator.margin_below_pt}
                onChange={(event) => updateFooterSeparator('margin_below_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field special-layout-wide-field">
              <span>Custom Footer Text</span>
              <input
                className="heading-input"
                type="text"
                value={footerSeparator.text}
                placeholder="Example: © 2026 IJSRET"
                onChange={(event) => updateFooterSeparator('text', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Footer Text Alignment</span>
              <select
                className="heading-select"
                value={footerSeparator.text_align}
                onChange={(event) => updateFooterSeparator('text_align', event.target.value)}
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </label>

            <label className="header-footer-form-field">
              <span>Footer Font Size (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="6"
                max="24"
                step="1"
                value={footerSeparator.font_size}
                onChange={(event) => updateFooterSeparator('font_size', event.target.value)}
              />
            </label>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={footerSeparator.bold}
                onChange={(event) => updateFooterSeparator('bold', event.target.checked)}
              />
              <span>Bold Footer Text</span>
            </label>

            </>}

            <h3 className="special-layout-section-title">Abstract and Keywords Box / Separators</h3>

            <label className="toggle-chip">
              <input type="checkbox" checked={frontMatterLines.box_enabled} onChange={(event) => updateFrontMatterLine('box_enabled', event.target.checked)} />
              <span>Box around Abstract &amp; Keywords</span>
            </label>

            <label className="header-footer-form-field">
              <span>Box Line Style</span>
              <select className="heading-select" value={frontMatterLines.box_style} onChange={(event) => updateFrontMatterLine('box_style', event.target.value)}>
                <option value="single">Single</option>
                <option value="double">Double</option>
                <option value="dotted">Dotted</option>
                <option value="dashed">Dashed</option>
              </select>
            </label>

            <label className="header-footer-form-field">
              <span>Corner Roundness (pt)</span>
              <input className="heading-input" type="number" min="0" max="36" step="1" value={frontMatterLines.box_corner_radius_pt} onChange={(event) => updateFrontMatterLine('box_corner_radius_pt', event.target.value)} />
            </label>

            <label className="header-footer-form-field">
              <span>Box Inner Padding (pt)</span>
              <input className="heading-input" type="number" min="0" max="36" step="1" value={frontMatterLines.box_padding_pt} onChange={(event) => updateFrontMatterLine('box_padding_pt', event.target.value)} />
            </label>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={frontMatterLines.above_abstract}
                onChange={(event) => updateFrontMatterLine('above_abstract', event.target.checked)}
              />
              <span>Line above Abstract</span>
            </label>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={frontMatterLines.below_keywords}
                onChange={(event) => updateFrontMatterLine('below_keywords', event.target.checked)}
              />
              <span>Line below Keywords</span>
            </label>

            <label className="header-footer-form-field">
              <span>Line Color</span>
              <input
                className="heading-input layout-color-input"
                type="color"
                value={frontMatterLines.color}
                onChange={(event) => updateFrontMatterLine('color', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Line Thickness (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0.5"
                max="4"
                step="0.5"
                value={frontMatterLines.width_pt}
                onChange={(event) => updateFrontMatterLine('width_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Upper Line: Margin Above (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={frontMatterLines.above_line_outer_margin_pt}
                onChange={(event) => updateFrontMatterLine('above_line_outer_margin_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Upper Line: Margin Below (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={frontMatterLines.above_line_inner_margin_pt}
                onChange={(event) => updateFrontMatterLine('above_line_inner_margin_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Lower Line: Margin Above (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={frontMatterLines.below_line_inner_margin_pt}
                onChange={(event) => updateFrontMatterLine('below_line_inner_margin_pt', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Lower Line: Margin Below (pt)</span>
              <input
                className="heading-input"
                type="number"
                min="0"
                max="36"
                step="1"
                value={frontMatterLines.below_line_outer_margin_pt}
                onChange={(event) => updateFrontMatterLine('below_line_outer_margin_pt', event.target.value)}
              />
            </label>
          </div>

          {templateColumns === 2 ? (
            <div className="preview-format-toolbar special-layout-grid">
          <label className="header-footer-form-field">
            <span>Left Margin (cm)</span>
            <input
              className="heading-input"
              type="number"
              min="0.5"
              max="5"
              step="0.1"
              value={layout.left_margin_cm}
              onChange={(event) => updateLayoutField('left_margin_cm', event.target.value)}
            />
          </label>

          <label className="header-footer-form-field">
            <span>Right Margin (cm)</span>
            <input
              className="heading-input"
              type="number"
              min="0.5"
              max="5"
              step="0.1"
              value={layout.right_margin_cm}
              onChange={(event) => updateLayoutField('right_margin_cm', event.target.value)}
            />
          </label>

          <label className="header-footer-form-field">
            <span>Column Gap / Gutter (cm)</span>
            <input
              className="heading-input"
              type="number"
              min="0.5"
              max="5"
              step="0.1"
              value={layout.column_gap_cm}
              onChange={(event) => updateLayoutField('column_gap_cm', event.target.value)}
            />
          </label>
            </div>
          ) : (
            <p className="helper-text">Column spacing controls are available only for double-column formats.</p>
          )}

          <div className="preview-format-toolbar special-layout-grid">
            <h3 className="special-layout-section-title">Visual Pagination</h3>
            <label className="header-footer-form-field">
              <span>Pagination Mode</span>
              <select
                className="heading-select"
                value={layout.pagination_mode}
                onChange={(event) => updateLayoutField('pagination_mode', event.target.value)}
              >
                <option value="balanced">Balanced (recommended)</option>
                <option value="compact">Compact</option>
                <option value="strict">Strict keep-together</option>
              </select>
            </label>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={layout.auto_fit_images}
                onChange={(event) => updateLayoutField('auto_fit_images', event.target.checked)}
              />
              <span>Auto-fit oversized images</span>
            </label>

            <label className="header-footer-form-field">
              <span>Maximum Figure Height (% of page)</span>
              <input
                className="heading-input"
                type="number"
                min="15"
                max="60"
                step="1"
                value={layout.max_figure_height_percent}
                onChange={(event) => updateLayoutField('max_figure_height_percent', event.target.value)}
              />
            </label>

            <label className="header-footer-form-field">
              <span>Keep Small Tables Together (max rows)</span>
              <input
                className="heading-input"
                type="number"
                min="1"
                max="12"
                step="1"
                value={layout.keep_small_table_rows}
                onChange={(event) => updateLayoutField('keep_small_table_rows', event.target.value)}
              />
            </label>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={layout.allow_large_table_split}
                onChange={(event) => updateLayoutField('allow_large_table_split', event.target.checked)}
              />
              <span>Allow large tables to split by row</span>
            </label>

            <label className="toggle-chip">
              <input
                type="checkbox"
                checked={layout.repeat_table_header}
                onChange={(event) => updateLayoutField('repeat_table_header', event.target.checked)}
              />
              <span>Repeat table header after split</span>
            </label>
          </div>
        </>
      )}
    </section>
  );
}

function SpecialSectionVariantEditor({ label, sectionKey, value, onChange }) {
  const normalizedValue = normalizeSpecialSectionVariantConfig(value);
  const items = getSpecialSectionItems(normalizedValue);
  const isFooter = sectionKey === 'footer';

  function updateItem(index, patch) {
    onChange({
      ...normalizedValue,
      items: items.map((item, currentIndex) =>
        currentIndex === index
          ? normalizeSpecialColumnConfig({
              ...item,
              ...patch,
              image: {
                ...item.image,
                ...(patch.image || {}),
              },
            })
          : item
      ),
    });
  }

  return (
    <div className="header-footer-variant-card">
      <div className="header-footer-variant-title">{label}</div>
      <div className="header-footer-form-grid">
        <label className="header-footer-form-field">
          <span>Columns</span>
          <select
            className="heading-select"
            value={normalizedValue.columns}
            onChange={(event) => onChange({ columns: Number(event.target.value) || 1 })}
          >
            {SPECIAL_COLUMN_COUNT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="header-footer-form-field">
          <span>Left Margin (cm)</span>
          <input
            className="heading-input"
            type="number"
            min="0.5"
            max="5"
            step="0.1"
            value={normalizedValue.left_margin_cm}
            onChange={(event) => onChange({ left_margin_cm: event.target.value })}
          />
        </label>

        <label className="header-footer-form-field">
          <span>Right Margin (cm)</span>
          <input
            className="heading-input"
            type="number"
            min="0.5"
            max="5"
            step="0.1"
            value={normalizedValue.right_margin_cm}
            onChange={(event) => onChange({ right_margin_cm: event.target.value })}
          />
        </label>

        <label className="header-footer-form-field">
          <span>{sectionKey === 'header' ? 'Header from Top (cm)' : 'Footer from Bottom (cm)'}</span>
          <input
            className="heading-input"
            type="number"
            min="0"
            max="10"
            step="0.1"
            value={normalizedValue.distance_cm}
            onChange={(event) => onChange({ distance_cm: event.target.value })}
          />
        </label>
      </div>

      <div className={`special-columns-grid special-columns-grid-${normalizedValue.columns}`}>
        {items.slice(0, normalizedValue.columns).map((item, index) => (
          <SpecialHeaderFooterColumnEditor
            key={`${sectionKey}-column-${index}`}
            label={`Column ${index + 1}`}
            value={item}
            onChange={(patch) => updateItem(index, patch)}
          />
        ))}
      </div>

      {isFooter && (
        <div className="special-footer-options">
          <label className="toggle-chip special-page-number-toggle">
            <input
              type="checkbox"
              checked={Boolean(normalizedValue.page_number?.enabled)}
              onChange={(event) =>
                onChange({
                  page_number: {
                    ...normalizedValue.page_number,
                    enabled: event.target.checked,
                  },
                })
              }
            />
            <span>Page Number</span>
          </label>

          <label className="header-footer-form-field">
            <span>Numbering Format</span>
            <select
              className="heading-select"
              value={normalizedValue.page_number?.format || 'decimal'}
              onChange={(event) =>
                onChange({
                  page_number: {
                    ...normalizedValue.page_number,
                    format: event.target.value,
                  },
                })
              }
              disabled={!normalizedValue.page_number?.enabled}
            >
              {PAGE_NUMBER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="header-footer-form-field">
            <span>Page Number Align</span>
            <select
              className="heading-select"
              value={normalizePageNumberAlignment(normalizedValue.page_number?.alignment)}
              onChange={(event) =>
                onChange({
                  page_number: {
                    ...normalizedValue.page_number,
                    alignment: event.target.value,
                  },
                })
              }
              disabled={!normalizedValue.page_number?.enabled}
            >
              {PAGE_NUMBER_ALIGNMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="header-footer-form-field">
            <span>Footer Content to Page Number Gap (pt)</span>
            <input
              className="heading-input"
              type="number"
              min="0"
              max="72"
              step="1"
              value={normalizedValue.page_number?.content_gap_pt || '0'}
              onChange={(event) => onChange({
                page_number: {
                  ...normalizedValue.page_number,
                  content_gap_pt: event.target.value,
                },
              })}
              disabled={!normalizedValue.page_number?.enabled}
            />
          </label>
        </div>
      )}
    </div>
  );
}

function SpecialHeaderFooterSectionBuilder({ label, sectionKey, value, onChange, onFirstPageChange }) {
  const normalizedValue = normalizeSpecialSectionConfig(value);
  const isDifferentFirstPage = normalizedValue.page_mode === 'first_page_different';

  return (
    <section className={`special-builder-card special-builder-card-${sectionKey}`}>
      <div className="panel-header">
        <div>
          <span className="eyebrow">{label}</span>
          <h2>{sectionKey === 'header' ? 'Preview override for document header' : 'Preview override for document footer'}</h2>
        </div>
      </div>

      <label className="header-footer-form-field">
        <span>Page Setup</span>
        <select
          className="heading-select"
          value={normalizedValue.page_mode}
          onChange={(event) =>
            onChange({
              page_mode: event.target.value,
              first_page: event.target.value === 'first_page_different'
                ? (normalizedValue.first_page || normalizeSpecialSectionVariantConfig(createDefaultSpecialSectionConfig()))
                : null,
            })
          }
        >
          {HEADER_FOOTER_PAGE_MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className="header-footer-variant-grid">
        {isDifferentFirstPage && (
          <SpecialSectionVariantEditor
            label={`First Page ${sectionKey === 'header' ? 'Header' : 'Footer'}`}
            sectionKey={sectionKey}
            value={normalizedValue.first_page || normalizeSpecialSectionVariantConfig(createDefaultSpecialSectionConfig())}
            onChange={onFirstPageChange}
          />
        )}
        <SpecialSectionVariantEditor
          label={isDifferentFirstPage ? `Remaining Pages ${sectionKey === 'header' ? 'Header' : 'Footer'}` : `All Pages ${sectionKey === 'header' ? 'Header' : 'Footer'}`}
          sectionKey={sectionKey}
          value={normalizedValue}
          onChange={onChange}
        />
      </div>
    </section>
  );
}

function SpecialHeaderFooterColumnEditor({ label, value, onChange }) {
  const usesText = value.type === 'text' || value.type === 'text_image';
  const usesImage = value.type === 'image' || value.type === 'text_image';
  const textLines = getSpecialTextLines(value.text);

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const { dataUrl, fileName } = await readImageFileForDocx(file);
    onChange({
      image: {
        name: fileName,
        src: dataUrl,
      },
    });
  }

  return (
    <article className="special-column-card">
      <div className="special-column-title">{label}</div>

      <label className="header-footer-form-field">
        <span>Content Type</span>
        <select
          className="heading-select"
          value={value.type}
          onChange={(event) => onChange({ type: event.target.value })}
        >
          {SPECIAL_COLUMN_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <label className="header-footer-form-field">
        <span>Width (%)</span>
        <input
          className="heading-input"
          type="text"
          value={value.width_percent}
          onChange={(event) => onChange({ width_percent: event.target.value })}
          placeholder="33"
        />
      </label>

      {usesText && (
        <>
          <label className="header-footer-form-field">
            <span>Text</span>
            <textarea
              className="table-editor"
              rows={5}
              value={value.text}
              onChange={(event) =>
                onChange({
                  text: event.target.value,
                  line_colors: syncSpecialLineColors(event.target.value, value.line_colors, value.text_color),
                })
              }
            />
          </label>

          <div className="header-footer-form-grid">
            {textLines.map((line, lineIndex) => (
              <label className="header-footer-form-field" key={`line-color-${lineIndex}`}>
                <span>{`Line ${lineIndex + 1} Color`}</span>
                <div className="color-control-row">
                  <input
                    className="heading-input color-input"
                    type="color"
                    value={getSpecialLineColor(value, lineIndex)}
                    onChange={(event) =>
                      onChange({
                        line_colors: textLines.map((_, currentIndex) =>
                          currentIndex === lineIndex ? event.target.value : getSpecialLineColor(value, currentIndex)
                        ),
                      })
                    }
                  />
                  <input
                    className="heading-input color-hex-input"
                    type="text"
                    value={getSpecialLineColor(value, lineIndex)}
                    onChange={(event) =>
                      onChange({
                        line_colors: textLines.map((_, currentIndex) =>
                          currentIndex === lineIndex
                            ? resolveHexInputValue(event.target.value, getSpecialLineColor(value, currentIndex))
                            : getSpecialLineColor(value, currentIndex)
                        ),
                      })
                    }
                    placeholder="#000000"
                  />
                </div>
              </label>
            ))}
          </div>
        </>
      )}

      {usesImage && (
        <div className="header-footer-form-grid">
          <label className="header-footer-form-field">
            <span>Upload Image</span>
            <input className="heading-input" type="file" accept="image/*" onChange={handleImageUpload} />
          </label>

          <label className="header-footer-form-field">
            <span>Image Width (cm)</span>
            <input
              className="heading-input"
              type="text"
              value={value.image.width_cm}
              onChange={(event) => onChange({ image: { width_cm: event.target.value } })}
              placeholder="e.g. 3.5"
            />
          </label>

          <label className="header-footer-form-field">
            <span>Image Height (cm)</span>
            <input
              className="heading-input"
              type="text"
              value={value.image.height_cm}
              onChange={(event) => onChange({ image: { height_cm: event.target.value } })}
              placeholder="e.g. 2"
            />
          </label>

          <label className="header-footer-form-field">
            <span>Image Position Mode</span>
            <select className="heading-select" value={value.image.position_mode} onChange={(event) => onChange({ image: { position_mode: event.target.value } })}>
              <option value="inline">Inline with text</option>
              <option value="floating">Absolute / Floating</option>
            </select>
          </label>

          {value.image.position_mode === 'floating' && (
            <>
              <label className="header-footer-form-field">
                <span>Horizontal Position (cm)</span>
                <input className="heading-input" type="number" step="0.01" value={value.image.horizontal_position_cm} onChange={(event) => onChange({ image: { horizontal_position_cm: event.target.value } })} />
              </label>
              <label className="header-footer-form-field">
                <span>Horizontal Relative To</span>
                <select className="heading-select" value={value.image.horizontal_relative_to} onChange={(event) => onChange({ image: { horizontal_relative_to: event.target.value } })}>
                  <option value="column">Column</option><option value="margin">Margin</option><option value="page">Page</option>
                </select>
              </label>
              <label className="header-footer-form-field">
                <span>Vertical Position (cm)</span>
                <input className="heading-input" type="number" step="0.01" value={value.image.vertical_position_cm} onChange={(event) => onChange({ image: { vertical_position_cm: event.target.value } })} />
              </label>
              <label className="header-footer-form-field">
                <span>Vertical Relative To</span>
                <select className="heading-select" value={value.image.vertical_relative_to} onChange={(event) => onChange({ image: { vertical_relative_to: event.target.value } })}>
                  <option value="page">Page</option><option value="margin">Margin</option><option value="paragraph">Paragraph</option>
                </select>
              </label>
              <label className="header-footer-form-field">
                <span>Text Wrapping</span>
                <select className="heading-select" value={value.image.wrap_style} onChange={(event) => onChange({ image: { wrap_style: event.target.value } })}>
                  <option value="square">Square</option><option value="top_bottom">Top and bottom</option><option value="behind_text">Behind text</option><option value="in_front_of_text">In front of text</option>
                </select>
              </label>
              {[
                ['distance_top_cm', 'Distance from Text: Top (cm)'], ['distance_bottom_cm', 'Distance from Text: Bottom (cm)'],
                ['distance_left_cm', 'Distance from Text: Left (cm)'], ['distance_right_cm', 'Distance from Text: Right (cm)'],
              ].map(([key, fieldLabel]) => (
                <label className="header-footer-form-field" key={key}>
                  <span>{fieldLabel}</span>
                  <input className="heading-input" type="number" min="0" step="0.01" value={value.image[key]} onChange={(event) => onChange({ image: { [key]: event.target.value } })} />
                </label>
              ))}
              <label className="toggle-chip"><input type="checkbox" checked={value.image.move_with_text} onChange={(event) => onChange({ image: { move_with_text: event.target.checked } })} /><span>Move with text</span></label>
              <label className="toggle-chip"><input type="checkbox" checked={value.image.allow_overlap} onChange={(event) => onChange({ image: { allow_overlap: event.target.checked } })} /><span>Allow overlap</span></label>
              <label className="toggle-chip"><input type="checkbox" checked={value.image.lock_anchor} onChange={(event) => onChange({ image: { lock_anchor: event.target.checked } })} /><span>Lock anchor</span></label>
            </>
          )}

          {value.image.src && (
            <div className="header-footer-image-preview-card">
              <span className="header-footer-image-preview-label">Image Preview</span>
              <img className="header-footer-image-preview" src={value.image.src} alt={value.image.name || 'Special preview'} />
            </div>
          )}
        </div>
      )}

      <div className="header-footer-form-grid">
        <label className="header-footer-form-field">
          <span>Font Size</span>
          <input
            className="heading-input"
            type="text"
            value={value.font_size}
            onChange={(event) => onChange({ font_size: event.target.value })}
            placeholder="10"
          />
        </label>

        <label className="header-footer-form-field">
          <span>Text Color</span>
          <div className="color-control-row">
            <input
              className="heading-input color-input"
              type="color"
              value={normalizeColorValue(value.text_color)}
              onChange={(event) => onChange({ text_color: event.target.value })}
            />
            <input
              className="heading-input color-hex-input"
              type="text"
              value={normalizeColorValue(value.text_color)}
              onChange={(event) =>
                onChange({ text_color: resolveHexInputValue(event.target.value, normalizeColorValue(value.text_color)) })
              }
              placeholder="#000000"
            />
          </div>
        </label>

        <label className="header-footer-form-field">
          <span>Alignment</span>
          <select
            className="heading-select"
            value={value.alignment}
            onChange={(event) => onChange({ alignment: event.target.value })}
          >
            {ALIGNMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="header-footer-toggle-row">
        <label className="toggle-chip">
          <input
            type="checkbox"
            checked={value.bold}
            onChange={(event) => onChange({ bold: event.target.checked })}
          />
          <span>Bold</span>
        </label>
        <label className="toggle-chip">
          <input
            type="checkbox"
            checked={value.italic}
            onChange={(event) => onChange({ italic: event.target.checked })}
          />
          <span>Italic</span>
        </label>
      </div>
    </article>
  );
}

function LandingPage({ currentRole }) {
  return (
    <main className="page-shell auth-shell">
      <section className="auth-card">
        <span className="eyebrow">Private Access</span>
        <h1 className="auth-title">AutoFormatter</h1>
        <p className="auth-copy">Please open your assigned login URL to continue.</p>
        {currentRole && <p className="helper-text">Current session detected. Open your panel URL to continue.</p>}
      </section>
    </main>
  );
}

function ExternalLoginPage({ loading, error, message }) {
  return (
    <main className="page-shell auth-shell">
      <section className="auth-card">
        <span className="eyebrow">External Login</span>
        <h1 className="auth-title">Connecting Account</h1>
        <p className="auth-copy">
          {loading ? 'Please wait while we confirm your abcdindex session.' : 'External login is ready.'}
        </p>
        {(message || error) && (
          <section className={`status-banner ${error ? 'error' : 'success'}`}>
            {error || message}
          </section>
        )}
      </section>
    </main>
  );
}

function RestrictedLoginPage({ onAdminOpen }) {
  return (
    <main className="page-shell auth-shell">
      <section className="auth-card">
        <span className="eyebrow">Restricted Access</span>
        <h1 className="auth-title">Login Link Required</h1>
        <p className="auth-copy">Please use the private access link shared by the team.</p>
        <div className="auth-actions">
          <button className="secondary-button" type="button" onClick={onAdminOpen}>
            Admin Panel
          </button>
        </div>
      </section>
    </main>
  );
}

function AuthPage({ panel, mode, form, loading, error, message, onModeChange, onFormChange, onSubmit }) {
  const isAdmin = panel === 'admin';
  const isRegister = !isAdmin && mode === 'register';

  function updateField(field, value) {
    onFormChange((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  return (
    <main className="page-shell auth-shell">
      <form className="auth-card" onSubmit={onSubmit}>
        <span className="eyebrow">{isAdmin ? 'Admin Panel' : 'User Panel'}</span>
        <h1 className="auth-title">{isAdmin ? 'Admin Login' : isRegister ? 'Create User Account' : 'User Login'}</h1>

        {(message || error) && (
          <section className={`status-banner ${error ? 'error' : 'success'}`}>
            {error || message}
          </section>
        )}

        {isRegister && (
          <label className="auth-field">
            <span>Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => updateField('name', event.target.value)}
              autoComplete="name"
            />
          </label>
        )}

        {isAdmin ? (
          <label className="auth-field">
            <span>Email / Username</span>
            <input
              type="text"
              value={form.identifier}
              onChange={(event) => updateField('identifier', event.target.value)}
              autoComplete="username"
            />
          </label>
        ) : (
          <label className="auth-field">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => updateField('email', event.target.value)}
              autoComplete="email"
            />
          </label>
        )}

        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            value={form.password}
            onChange={(event) => updateField('password', event.target.value)}
            autoComplete={isRegister ? 'new-password' : 'current-password'}
          />
        </label>

        {isAdmin && (
          <label className="auth-field">
            <span>Secret Key</span>
            <input
              type="text"
              value={form.secret_key}
              onChange={(event) => updateField('secret_key', event.target.value)}
              autoComplete="off"
            />
          </label>
        )}

        <button className="primary-button" type="submit" disabled={loading}>
          {loading ? 'Please wait...' : isRegister ? 'Sign Up' : 'Sign In'}
        </button>

        {!isAdmin && (
          <button
            className="ghost-button"
            type="button"
            onClick={() => onModeChange(isRegister ? 'login' : 'register')}
          >
            {isRegister ? 'Already registered? Sign in' : 'New user? Sign up'}
          </button>
        )}

      </form>
    </main>
  );
}

function loadAuthSession() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || 'null');
    if (parsed?.token && parsed?.user?.role) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
}

function cloneIndexPreviewDocument(document) {
  return JSON.parse(JSON.stringify(document));
}

function getPageFromHash() {
  const routeParts = getRouteParts();
  const panel = routeParts[0] === 'admin' || routeParts[0] === 'user' ? routeParts[0] : '';
  const scopedPage = panel ? routeParts[1] || '' : routeParts[0] || '';

  if (scopedPage === 'manage') {
    return 'manage';
  }

  if (scopedPage === DOCUMENT_MANAGE_PAGE || scopedPage === 'manage-new') {
    return DOCUMENT_MANAGE_PAGE;
  }

  if (scopedPage === DOCUMENT_UPLOAD_PAGE || scopedPage === 'new-upload') {
    return DOCUMENT_UPLOAD_PAGE;
  }

  if (scopedPage === 'special-header-footer') {
    return 'special-header-footer';
  }

  if (scopedPage === 'special-layout-setting') {
    return 'special-layout-setting';
  }

  if (scopedPage === 'registered-users') {
    return 'registered-users';
  }

  if (scopedPage === 'detection-rules') {
    return 'detection-rules';
  }

  if (scopedPage === 'apply') {
    return 'apply';
  }

  if (scopedPage === DOCUMENT_APPLY_PAGE || scopedPage === 'apply-new') {
    return DOCUMENT_APPLY_PAGE;
  }

  if (scopedPage === 'optimize') {
    return 'optimize';
  }

  if (scopedPage === 'index') {
    return 'index';
  }

  if (!scopedPage && panel === 'user') {
    return 'apply';
  }

  return 'home';
}

function getPanelFromHash() {
  const firstSegment = getRouteParts()[0] || '';

  if (firstSegment === 'external-login') {
    return 'external-login';
  }

  if (firstSegment === 'admin') {
    return 'admin';
  }

  if (firstSegment === 'user') {
    return 'user';
  }

  return 'public';
}

function getRouteParts() {
  const hashPath = String(window.location.hash || '')
    .replace(/^#\/?/, '')
    .replace(/^\/?/, '')
    .split('?')[0]
    .trim();
  const browserPath = String(window.location.pathname || '')
    .replace(/^\/?/, '')
    .split('?')[0]
    .trim();
  const routePath = hashPath || browserPath;
  return routePath.split('/').filter(Boolean);
}

function getExternalLoginCode() {
  const searchParams = new URLSearchParams(window.location.search || '');
  const directCode = searchParams.get('code') || searchParams.get('token');
  if (directCode) {
    return directCode;
  }

  const hashValue = String(window.location.hash || '');
  const queryIndex = hashValue.indexOf('?');
  if (queryIndex === -1) {
    return '';
  }

  const hashParams = new URLSearchParams(hashValue.slice(queryIndex + 1));
  return hashParams.get('code') || hashParams.get('token') || '';
}

function getRequestedFormatId() {
  const searchParams = new URLSearchParams(window.location.search || '');
  const directFormatId = searchParams.get('format_id') || searchParams.get('formatId');
  if (directFormatId) {
    return directFormatId;
  }

  const hashValue = String(window.location.hash || '');
  const queryIndex = hashValue.indexOf('?');
  if (queryIndex === -1) {
    return '';
  }

  const hashParams = new URLSearchParams(hashValue.slice(queryIndex + 1));
  return hashParams.get('format_id') || hashParams.get('formatId') || '';
}

function isAllowedUserLoginRoute() {
  const routeParts = getRouteParts();
  return routeParts[0] === 'user' && routeParts[1] === 'login' && routeParts[2] === USER_LOGIN_ACCESS_CODE;
}

function isAllowedAdminLoginRoute() {
  const routeParts = getRouteParts();
  return routeParts[0] === 'admin' && routeParts[1] === 'login' && routeParts[2] === ADMIN_LOGIN_ACCESS_CODE;
}

function navigateTo(nextPage) {
  const updatePath = (pathValue) => {
    if (window.location.pathname === pathValue && !window.location.hash) {
      window.dispatchEvent(new Event('popstate'));
      return;
    }

    window.history.pushState({}, '', pathValue);
    window.dispatchEvent(new Event('popstate'));
  };

  updatePath(getPathForPage(nextPage));
}

function openPageInNewTab(nextPage, options = {}) {
  const pathValue = getPathForPage(nextPage, options);
  window.open(pathValue, '_blank', 'noopener,noreferrer');
}

function getPathForPage(nextPage, options = {}) {
  const pagePaths = {
    'admin-login': ADMIN_LOGIN_PATH,
    'user-login': USER_LOGIN_PATH,
    manage: '/admin/manage',
    [DOCUMENT_MANAGE_PAGE]: '/admin/manage-document-rules',
    [DOCUMENT_UPLOAD_PAGE]: '/admin/upload-document-format',
    'special-header-footer': '/admin/special-header-footer',
    'special-layout-setting': '/admin/special-layout-setting',
    'registered-users': '/admin/registered-users',
    'detection-rules': '/admin/detection-rules',
    apply: '/user/apply',
    [DOCUMENT_APPLY_PAGE]: '/user/apply-document-rules',
    optimize: '/user/optimize',
    index: '/user/index',
  };

  const pathValue = pagePaths[nextPage] || '/admin/upload';
  if (!options.formatId) {
    return pathValue;
  }

  const searchParams = new URLSearchParams();
  searchParams.set('format_id', options.formatId);
  return `${pathValue}?${searchParams.toString()}`;
}

function flattenRuleRows(payload, drafts, useDrafts) {
  if (!payload?.rule_sets) {
    return [];
  }

  const fieldOrder = getFieldOrderForRules(payload);

  return RULE_SET_ORDER.flatMap((ruleSetKey) => {
    const fields = payload.rule_sets[ruleSetKey];
    if (!fields) {
      return [];
    }

    return fieldOrder[ruleSetKey].filter((fieldKey) => fieldKey in fields).map((fieldKey) => {
      const originalValue = fields[fieldKey];
      const draftKey = `${ruleSetKey}.${fieldKey}`;
      const ruleDraftKey = `field_rules.${fieldKey}`;
      const editValue = drafts[draftKey] ?? serializeEditableValue(payload, fieldKey, originalValue);
      const displaySource = useDrafts ? editValue : serializeValue(originalValue);
      const ruleDraftValue = drafts[ruleDraftKey] ?? JSON.stringify(getPersistedFieldRule(payload, fieldKey), null, 2);
      const ruleConfig = getFieldRuleDraftValue(payload, fieldKey, ruleDraftValue);

      return {
        key: draftKey,
        ruleDraftKey,
        ruleSetKey,
        fieldKey,
        ruleSetLabel: RULE_SET_LABELS[ruleSetKey] || ruleSetKey,
        fieldLabel: getFieldLabelForRules(payload, fieldKey),
        originalValue,
        editValue,
        ruleDraftValue,
        isHeadingField: isHeadingField(fieldKey),
        isNumberingField: isNumberingField(fieldKey),
        isHeaderFooterField: isHeaderFooterField(fieldKey),
        isString: typeof originalValue === 'string',
        numberingStyle: isNumberingField(fieldKey)
          ? getNumberingStyle(payload, fieldKey, editValue, ruleDraftValue)
          : 'none',
        ruleConfig,
        displayValue: formatDisplayValue(displaySource, fieldKey),
        rulePattern: getRulePattern(fieldKey, ruleConfig),
        ruleSummary: getRuleSummary(fieldKey, useDrafts ? editValue : originalValue),
        exampleValue: getExampleValue(fieldKey, originalValue),
      };
    });
  });
}

function getFieldLabelForRules(payload, fieldKey) {
  if (fieldKey === 'title' && isNewUploadFormat(payload)) {
    return 'Chapter Title';
  }

  return FIELD_LABELS[fieldKey] || fieldKey;
}

function createDraftMap(payload) {
  if (!payload?.rule_sets) {
    return {};
  }

  const nextDrafts = {};
  const fieldOrder = getFieldOrderForRules(payload);

  RULE_SET_ORDER.forEach((ruleSetKey) => {
    const fields = payload.rule_sets[ruleSetKey];
    if (!fields) {
      return;
    }

    fieldOrder[ruleSetKey].filter((fieldKey) => fieldKey in fields).forEach((fieldKey) => {
      const value = fields[fieldKey];
      nextDrafts[`${ruleSetKey}.${fieldKey}`] = serializeEditableValue(payload, fieldKey, value);
      nextDrafts[`field_rules.${fieldKey}`] = JSON.stringify(getPersistedFieldRule(payload, fieldKey), null, 2);
    });
  });

  return nextDrafts;
}

function buildPayloadFromDrafts(baseRules, drafts) {
  const nextRuleSets = {};
  const nextFieldRules = {
    ...(baseRules.field_rules || {}),
  };
  const fieldOrder = getFieldOrderForRules(baseRules);

  RULE_SET_ORDER.forEach((ruleSetKey) => {
    const fields = baseRules.rule_sets[ruleSetKey];
    if (!fields) {
      return;
    }

    nextRuleSets[ruleSetKey] = {};

    fieldOrder[ruleSetKey].filter((fieldKey) => fieldKey in fields).forEach((fieldKey) => {
      const value = fields[fieldKey];
      const draftValue = drafts[`${ruleSetKey}.${fieldKey}`];
      const fieldRuleDraftValue = drafts[`field_rules.${fieldKey}`] ?? JSON.stringify(getPersistedFieldRule(baseRules, fieldKey));

      nextFieldRules[fieldKey] = parseFieldRuleDraft(baseRules, fieldKey, fieldRuleDraftValue);

      if (isHeadingField(fieldKey)) {
        nextRuleSets[ruleSetKey][fieldKey] = normalizeHeadingEntries(parseStructuredDraft(fieldKey, draftValue));
        return;
      }

      if (isHeaderFooterField(fieldKey)) {
        nextRuleSets[ruleSetKey][fieldKey] = normalizeHeaderFooterEntries(
          fieldKey,
          parseStructuredDraft(fieldKey, draftValue)
        );
        return;
      }

      nextRuleSets[ruleSetKey][fieldKey] = value;
    });
  });

  return {
    ...baseRules,
    field_rules: nextFieldRules,
    rule_sets: nextRuleSets,
  };
}

function getFieldOrderForRules(rules) {
  return isNewUploadFormat(rules) ? NEW_UPLOAD_FIELD_ORDER : FIELD_ORDER;
}

function isNewUploadFormat(value) {
  return String(value?.source || '').trim().toLowerCase() === 'new_upload';
}

function serializeValue(value) {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

function serializeEditableValue(payload, fieldKey, value) {
  if (isHeadingField(fieldKey)) {
    return JSON.stringify(normalizeHeadingEntries(value), null, 2);
  }

  if (isHeaderFooterField(fieldKey)) {
    return JSON.stringify(normalizeHeaderFooterEntries(fieldKey, value), null, 2);
  }

  return JSON.stringify(getPersistedFieldRule(payload, fieldKey), null, 2);
}

function parseStructuredDraft(fieldKey, draftValue) {
  try {
    return JSON.parse(draftValue ?? JSON.stringify([]));
  } catch {
    throw new Error(`Invalid JSON in ${FIELD_LABELS[fieldKey] || fieldKey}.`);
  }
}

function formatDisplayValue(value, fieldKey = '') {
  if (Array.isArray(value) && value.every(isHeadingEntryLike)) {
    return value.map((entry, index) => getHeadingPreview(normalizeHeadingEntry(entry), index)).join(' | ');
  }

  if (Array.isArray(value) && value.every(isHeaderFooterEntryLike)) {
    return value.map((entry, index) => formatHeaderFooterPreview('header', normalizeHeaderFooterEntry('header', entry, index + 1), index)).join(' | ');
  }

  if (!value) {
    return '';
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      if (fieldKey === 'references') {
        return formatReferenceList(parsed);
      }

      if (parsed.every(isHeaderFooterEntryLike)) {
        const inferredFieldKey = parsed.some((entry) => entry.dynamic_page_number) ? 'footer' : 'header';
        return parsed
          .map((entry, index) =>
            formatHeaderFooterPreview(inferredFieldKey, normalizeHeaderFooterEntry(inferredFieldKey, entry, index + 1), index)
          )
          .join(' | ');
      }

      return parsed.length === 0 ? '' : parsed.map(formatAnyValue).join(' | ');
    }

    if (typeof parsed === 'object' && parsed !== null) {
      return formatAnyValue(parsed);
    }
  } catch {
    return value;
  }

  return value;
}

function formatAnyValue(value) {
  if (Array.isArray(value)) {
    return value.map(formatAnyValue).join(', ');
  }

  if (typeof value === 'object' && value !== null) {
    if (isHeadingEntryLike(value)) {
      return getHeadingPreview(normalizeHeadingEntry(value), 0);
    }

    if (isHeaderFooterEntryLike(value)) {
      const fieldKey = value.dynamic_page_number ? 'footer' : 'header';
      return formatHeaderFooterPreview(fieldKey, normalizeHeaderFooterEntry(fieldKey, value, value.section || 1), 0);
    }

    return Object.entries(value)
      .map(([key, item]) => `${key}: ${formatAnyValue(item)}`)
      .join(' | ');
  }

  return String(value);
}

function createEmptyValue(value) {
  if (Array.isArray(value) && value.every(isHeadingEntryLike)) {
    return [];
  }

  if (Array.isArray(value) && value.every(isHeaderFooterEntryLike)) {
    return [];
  }

  if (typeof value === 'string') {
    return '';
  }

  if (Array.isArray(value)) {
    return [];
  }

  if (value && typeof value === 'object') {
    return {};
  }

  return '';
}

function getFriendlyErrorMessage(error) {
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return 'Cannot connect to the server right now. Please try again in a moment.';
  }

  return error.message || 'Something went wrong.';
}

async function readJsonResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.toLowerCase().includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  const normalizedText = String(text || '').trim();

  if (!response.ok) {
    return {
      error: normalizedText || 'Server error. Please try again.',
    };
  }

  throw new Error('Backend response was not valid JSON.');
}

function isHeadingField(fieldKey) {
  return HEADING_FIELD_KEYS.has(fieldKey);
}

function formatAdminDate(value) {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value).split('T')[0].split(' ')[0] || value;
  }

  return parsedDate.toLocaleDateString();
}

function getCurrentDateInputValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isNumberingField(fieldKey) {
  return NUMBERING_FIELD_KEYS.has(fieldKey);
}

function getNumberingStyleOptionsForField(fieldKey) {
  return NUMBERING_STYLE_OPTIONS.filter((option) => (
    option.value !== 'custom' && (!option.referencesOnly || fieldKey === 'references')
  ));
}

function isHeaderFooterField(fieldKey) {
  return HEADER_FOOTER_FIELD_KEYS.has(fieldKey);
}

function normalizeHeadingEntries(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(normalizeHeadingEntry);
}

function getHeadingNumberingStyle(fieldKey, value) {
  const entries = normalizeHeadingEntries(getStructuredFieldValue(fieldKey, value));
  if (entries.length === 0) {
    return 'none';
  }

  const firstStyle = entries[0]?.numbering?.style || 'none';
  const hasDifferentStyle = entries.some((entry) => (entry.numbering?.style || 'none') !== firstStyle);
  return hasDifferentStyle ? firstStyle : firstStyle;
}

function getNumberingStyle(payload, fieldKey, value, ruleDraftValue) {
  if (isHeadingField(fieldKey)) {
    return getHeadingNumberingStyle(fieldKey, value);
  }

  const numbering = getFieldRuleDraftValue(payload, fieldKey, ruleDraftValue)?.numbering || {};
  return numbering.enabled ? numbering.style || 'none' : 'none';
}

function normalizeHeadingEntry(entry) {
  if (typeof entry === 'string') {
    return createHeadingEntry(entry);
  }

  if (!entry || typeof entry !== 'object') {
    return createHeadingEntry();
  }

  const numbering = entry.numbering && typeof entry.numbering === 'object' ? entry.numbering : {};

  return {
    text: typeof entry.text === 'string' ? entry.text : '',
    numbering: {
      raw: typeof numbering.raw === 'string' ? numbering.raw : '',
      style: typeof numbering.style === 'string' ? numbering.style : 'none',
      enabled: Boolean(numbering.enabled),
    },
  };
}

function createHeadingEntry(text = '') {
  return {
    text,
    numbering: {
      raw: '',
      style: 'none',
      enabled: false,
    },
  };
}

function isHeadingEntryLike(value) {
  return Boolean(value) && typeof value === 'object' && ('text' in value || 'numbering' in value);
}

function normalizeHeaderFooterEntries(fieldKey, value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((entry, index) => normalizeHeaderFooterEntry(fieldKey, entry, index + 1));
}

function formatReferenceList(value) {
  const items = Array.isArray(value) ? value.map(formatAnyValue).map((item) => item.trim()).filter(Boolean) : [];
  if (items.length === 0) {
    return '';
  }

  return items.map((item, index) => {
    if (/^(\[\d+\]|\d+[.)])\s+/.test(item)) {
      return item;
    }

    return `${index + 1}. ${item}`;
  }).join('\n');
}

function getSpecialHeaderConfigForFormat(drafts, formatId, rules) {
  const directConfig = formatId && drafts && typeof drafts === 'object' ? drafts[formatId] : null;
  if (directConfig) {
    return normalizeSpecialHeaderConfig(directConfig);
  }

  if (hasSpecialHeaderFooterConfig(rules?.special_header_config)) {
    return normalizeSpecialHeaderConfig(rules.special_header_config);
  }

  return createDefaultSpecialHeaderConfig();
}

function isSpecialHeaderConfigPersisted(activeConfig, rules) {
  if (!hasSpecialHeaderFooterConfig(activeConfig) || !hasSpecialHeaderFooterConfig(rules?.special_header_config)) {
    return false;
  }

  return JSON.stringify(normalizeSpecialHeaderConfig(activeConfig)) === JSON.stringify(normalizeSpecialHeaderConfig(rules.special_header_config));
}

function createDefaultSpecialHeaderConfig() {
  return {
    header: createDefaultSpecialSectionConfig(),
    footer: createDefaultSpecialSectionConfig(),
  };
}

function createDefaultSpecialSectionConfig() {
  return {
    columns: 1,
    left_margin_cm: '0.6',
    right_margin_cm: '0.6',
    distance_cm: '0',
    items: [createDefaultSpecialColumnConfig(), createDefaultSpecialColumnConfig(), createDefaultSpecialColumnConfig()],
    page_number: {
      enabled: false,
      format: 'decimal',
      alignment: 'right',
      content_gap_pt: '6',
    },
    page_mode: 'all_pages_same',
    first_page: null,
  };
}

function createDefaultSpecialColumnConfig() {
  return {
    type: 'text',
    text: '',
    width_percent: '100',
    font_size: '10',
    text_color: '#000000',
    line_colors: [],
    alignment: 'left',
    bold: false,
    italic: false,
    image: {
      name: '',
      src: '',
      width_cm: '',
      height_cm: '',
      position_mode: 'inline',
      horizontal_position_cm: '0',
      horizontal_relative_to: 'column',
      vertical_position_cm: '0',
      vertical_relative_to: 'page',
      wrap_style: 'square',
      distance_top_cm: '0',
      distance_bottom_cm: '0',
      distance_left_cm: '0.32',
      distance_right_cm: '0.32',
      move_with_text: false,
      allow_overlap: false,
      lock_anchor: false,
    },
  };
}

function normalizeSpecialHeaderConfig(value) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    header: normalizeSpecialSectionConfig(source.header),
    footer: normalizeSpecialSectionConfig(source.footer),
  };
}

function normalizeSpecialSectionConfig(value) {
  const base = createDefaultSpecialSectionConfig();
  const source = value && typeof value === 'object' ? value : {};
  const columns = Math.max(1, Math.min(3, Number(source.columns) || 1));
  const items = Array.from({ length: 3 }, (_, index) => normalizeSpecialColumnConfig(source.items?.[index]));
  const normalizedFirstPage = source.first_page && typeof source.first_page === 'object'
    ? normalizeSpecialSectionVariantConfig(source.first_page)
    : null;

  return {
    ...base,
    columns,
    left_margin_cm: normalizeSpecialMarginValue(source.left_margin_cm, base.left_margin_cm),
    right_margin_cm: normalizeSpecialMarginValue(source.right_margin_cm, base.right_margin_cm),
    distance_cm: normalizeSpecialDistanceValue(source.distance_cm, base.distance_cm),
    items,
    page_number: {
      enabled: Boolean(source.page_number?.enabled),
      format: typeof source.page_number?.format === 'string' ? source.page_number.format : base.page_number.format,
      alignment: normalizePageNumberAlignment(source.page_number?.alignment),
      content_gap_pt: normalizeSpecialPointValue(source.page_number?.content_gap_pt, base.page_number.content_gap_pt),
    },
    page_mode: source.page_mode === 'first_page_different' ? 'first_page_different' : 'all_pages_same',
    first_page: normalizedFirstPage,
  };
}

function normalizeSpecialSectionVariantConfig(value) {
  const normalized = normalizeSpecialSectionConfig({
    ...value,
    page_mode: 'all_pages_same',
    first_page: null,
  });

  return {
    ...normalized,
    page_mode: 'all_pages_same',
    first_page: null,
  };
}

function normalizeSpecialDistanceValue(value, fallback = '0') {
  const parsed = Number.parseFloat(value);
  return String(Number.isFinite(parsed) ? Math.max(0, Math.min(10, parsed)) : fallback);
}

function normalizeSpecialPointValue(value, fallback = '6') {
  const parsed = Number.parseFloat(value);
  return String(Number.isFinite(parsed) ? Math.max(0, Math.min(72, parsed)) : fallback);
}

function normalizePageNumberAlignment(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return PAGE_NUMBER_ALIGNMENT_OPTIONS.some((option) => option.value === normalized) ? normalized : 'right';
}

function normalizeSpecialColumnConfig(value) {
  const base = createDefaultSpecialColumnConfig();
  const source = value && typeof value === 'object' ? value : {};
  const type = SPECIAL_COLUMN_TYPE_OPTIONS.some((option) => option.value === source.type) ? source.type : base.type;

  return {
    ...base,
    type,
    text: typeof source.text === 'string' ? source.text : base.text,
    width_percent:
      typeof source.width_percent === 'string'
        ? source.width_percent
        : String(source.width_percent ?? base.width_percent),
    font_size: typeof source.font_size === 'string' ? source.font_size : String(source.font_size ?? base.font_size),
    text_color: normalizeColorValue(source.text_color || base.text_color),
    line_colors: syncSpecialLineColors(
      typeof source.text === 'string' ? source.text : base.text,
      Array.isArray(source.line_colors) ? source.line_colors : [],
      source.text_color || base.text_color
    ),
    alignment: typeof source.alignment === 'string' ? source.alignment : base.alignment,
    bold: Boolean(source.bold),
    italic: Boolean(source.italic),
    image: {
      name: typeof source.image?.name === 'string' ? source.image.name : base.image.name,
      src: typeof source.image?.src === 'string' ? source.image.src : base.image.src,
      width_cm: typeof source.image?.width_cm === 'string' ? source.image.width_cm : String(source.image?.width_cm ?? base.image.width_cm),
      height_cm: typeof source.image?.height_cm === 'string' ? source.image.height_cm : String(source.image?.height_cm ?? base.image.height_cm),
      position_mode: source.image?.position_mode === 'floating' ? 'floating' : 'inline',
      horizontal_position_cm: String(source.image?.horizontal_position_cm ?? base.image.horizontal_position_cm),
      horizontal_relative_to: ['column', 'margin', 'page'].includes(source.image?.horizontal_relative_to) ? source.image.horizontal_relative_to : base.image.horizontal_relative_to,
      vertical_position_cm: String(source.image?.vertical_position_cm ?? base.image.vertical_position_cm),
      vertical_relative_to: ['page', 'margin', 'paragraph'].includes(source.image?.vertical_relative_to) ? source.image.vertical_relative_to : base.image.vertical_relative_to,
      wrap_style: ['square', 'top_bottom', 'behind_text', 'in_front_of_text'].includes(source.image?.wrap_style) ? source.image.wrap_style : base.image.wrap_style,
      distance_top_cm: String(source.image?.distance_top_cm ?? base.image.distance_top_cm),
      distance_bottom_cm: String(source.image?.distance_bottom_cm ?? base.image.distance_bottom_cm),
      distance_left_cm: String(source.image?.distance_left_cm ?? base.image.distance_left_cm),
      distance_right_cm: String(source.image?.distance_right_cm ?? base.image.distance_right_cm),
      move_with_text: Boolean(source.image?.move_with_text),
      allow_overlap: Boolean(source.image?.allow_overlap),
      lock_anchor: Boolean(source.image?.lock_anchor),
    },
  };
}

function getSpecialSectionItems(section) {
  return normalizeSpecialSectionConfig(section).items;
}

function hasSpecialSectionContent(section) {
  const normalizedSection = normalizeSpecialSectionConfig(section);
  return normalizedSection.items.some((item) => {
    const hasText = (item.type === 'text' || item.type === 'text_image') && item.text.trim();
    const hasImage = (item.type === 'image' || item.type === 'text_image') && item.image.src;
    return Boolean(hasText || hasImage);
  }) || Boolean(normalizedSection.page_number?.enabled)
    || (normalizedSection.page_mode === 'first_page_different' && hasSpecialSectionVariantContent(normalizedSection.first_page));
}

function hasSpecialSectionVariantContent(section) {
  if (!section) {
    return false;
  }
  const normalizedSection = normalizeSpecialSectionVariantConfig(section);
  return normalizedSection.items.some((item) => {
    const hasText = (item.type === 'text' || item.type === 'text_image') && item.text.trim();
    const hasImage = (item.type === 'image' || item.type === 'text_image') && item.image.src;
    return Boolean(hasText || hasImage);
  }) || Boolean(normalizedSection.page_number?.enabled);
}

function hasSpecialHeaderFooterConfig(value) {
  const normalized = normalizeSpecialHeaderConfig(value);
  return hasSpecialSectionContent(normalized.header) || hasSpecialSectionContent(normalized.footer);
}

function getPreviewDocumentWithSpecialOverride(doc, specialHeaderConfig) {
  const normalizedSpecialConfig = hasSpecialHeaderFooterConfig(specialHeaderConfig)
    ? normalizeSpecialHeaderConfig(specialHeaderConfig)
    : null;

  return {
    ...doc,
    special_header_config: normalizedSpecialConfig,
  };
}

function normalizeColorValue(value) {
  const normalized = String(value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : '#000000';
}

function resolveHexInputValue(nextValue, currentValue) {
  const rawValue = String(nextValue || '').trim();
  if (!rawValue) {
    return '#000000';
  }

  const withHash = rawValue.startsWith('#') ? rawValue : `#${rawValue}`;
  return /^#[0-9a-f]{6}$/i.test(withHash) ? withHash.toUpperCase() : normalizeColorValue(currentValue);
}

function getSpecialTextLines(text) {
  return String(text || '').split(/\r?\n/);
}

function getSpecialLineColor(item, lineIndex) {
  return normalizeColorValue(item.line_colors?.[lineIndex] || item.text_color);
}

function syncSpecialLineColors(text, lineColors, fallbackColor) {
  const lines = getSpecialTextLines(text);
  const sourceColors = Array.isArray(lineColors) ? lineColors : [];
  return lines.map((_, index) => normalizeColorValue(sourceColors[index] || fallbackColor));
}

function normalizeSpecialWidthPercent(value) {
  const parsed = Number.parseFloat(String(value ?? '').trim());
  if (!Number.isFinite(parsed)) {
    return 100;
  }

  return Math.max(5, Math.min(100, parsed));
}

function getSpecialPreviewColumnWidths(section) {
  const normalizedSection = normalizeSpecialSectionConfig(section);
  const activeItems = normalizedSection.items.slice(0, normalizedSection.columns);
  const rawWidths = activeItems.map((item) => normalizeSpecialWidthPercent(item.width_percent));
  const totalWidth = rawWidths.reduce((sum, value) => sum + value, 0);

  if (totalWidth <= 0) {
    return activeItems.map(() => 1);
  }

  return rawWidths.map((value) => value / totalWidth);
}

function normalizeHeaderFooterEntry(fieldKey, entry, sectionNumber) {
  const base = createHeaderFooterEntry(fieldKey, sectionNumber);

  if (!entry || typeof entry !== 'object') {
    return base;
  }

  if ('content' in entry && !('text' in entry)) {
    return {
      ...base,
      section: Number(entry.section) || sectionNumber,
      text: Array.isArray(entry.content) ? entry.content.join('\n') : '',
    };
  }

  return {
    ...base,
    section: Number(entry.section) || sectionNumber,
    text: typeof entry.text === 'string' ? entry.text : base.text,
    font_size: typeof entry.font_size === 'string' ? entry.font_size : base.font_size,
    font_family: typeof entry.font_family === 'string' ? entry.font_family : base.font_family,
    bold: Boolean(entry.bold),
    italic: Boolean(entry.italic),
    underline: Boolean(entry.underline),
    text_align: typeof entry.text_align === 'string' ? entry.text_align : base.text_align,
    layout: typeof entry.layout === 'string' ? entry.layout : base.layout,
    right_text: typeof entry.right_text === 'string' ? entry.right_text : base.right_text,
    header_from_top_cm:
      typeof entry.header_from_top_cm === 'string'
        ? entry.header_from_top_cm
        : String(entry.header_from_top_cm ?? base.header_from_top_cm ?? ''),
    footer_from_bottom_cm:
      typeof entry.footer_from_bottom_cm === 'string'
        ? entry.footer_from_bottom_cm
        : String(entry.footer_from_bottom_cm ?? base.footer_from_bottom_cm ?? ''),
    image: {
      name: typeof entry.image?.name === 'string' ? entry.image.name : base.image.name,
      src: typeof entry.image?.src === 'string' ? entry.image.src : base.image.src,
    },
    image_align: typeof entry.image_align === 'string' ? entry.image_align : base.image_align,
    image_width_cm: typeof entry.image_width_cm === 'string' ? entry.image_width_cm : base.image_width_cm,
    image_height_cm: typeof entry.image_height_cm === 'string' ? entry.image_height_cm : base.image_height_cm,
    dynamic_page_number: {
      enabled: Boolean(entry.dynamic_page_number?.enabled),
      format:
        typeof entry.dynamic_page_number?.format === 'string'
          ? entry.dynamic_page_number.format
          : base.dynamic_page_number.format,
      prefix:
        typeof entry.dynamic_page_number?.prefix === 'string'
          ? entry.dynamic_page_number.prefix
          : base.dynamic_page_number.prefix,
      suffix:
        typeof entry.dynamic_page_number?.suffix === 'string'
          ? entry.dynamic_page_number.suffix
          : base.dynamic_page_number.suffix,
      alignment: normalizePageNumberAlignment(entry.dynamic_page_number?.alignment),
    },
    page_mode: entry.page_mode === 'first_page_different' ? 'first_page_different' : 'all_pages_same',
    first_page: normalizeHeaderFooterVariantEntry(fieldKey, entry.first_page),
  };
}

function createHeaderFooterVariantEntry(fieldKey, sectionNumber) {
  return {
    section: sectionNumber,
    text: '',
    font_size: '',
    font_family: '',
    bold: false,
    italic: false,
    underline: false,
    text_align: 'left',
    layout: 'text_only',
    right_text: '',
    header_from_top_cm: '',
    footer_from_bottom_cm: '',
    image: {
      name: '',
      src: '',
    },
    image_align: 'center',
    image_width_cm: '',
    image_height_cm: '',
    dynamic_page_number: {
      enabled: false,
      format: 'decimal',
      prefix: '',
      suffix: '',
      alignment: 'right',
    },
    kind: fieldKey,
  };
}

function createHeaderFooterEntry(fieldKey, sectionNumber) {
  return {
    ...createHeaderFooterVariantEntry(fieldKey, sectionNumber),
    page_mode: 'all_pages_same',
    first_page: createHeaderFooterVariantEntry(fieldKey, sectionNumber),
  };
}

function normalizeHeaderFooterVariantEntry(fieldKey, entry) {
  const base = createHeaderFooterVariantEntry(fieldKey, Number(entry?.section) || 1);

  if (!entry || typeof entry !== 'object') {
    return base;
  }

  return {
    ...base,
    section: Number(entry.section) || base.section,
    text: typeof entry.text === 'string' ? entry.text : base.text,
    font_size: typeof entry.font_size === 'string' ? entry.font_size : base.font_size,
    font_family: typeof entry.font_family === 'string' ? entry.font_family : base.font_family,
    bold: Boolean(entry.bold),
    italic: Boolean(entry.italic),
    underline: Boolean(entry.underline),
    text_align: typeof entry.text_align === 'string' ? entry.text_align : base.text_align,
    layout: typeof entry.layout === 'string' ? entry.layout : base.layout,
    right_text: typeof entry.right_text === 'string' ? entry.right_text : base.right_text,
    header_from_top_cm:
      typeof entry.header_from_top_cm === 'string'
        ? entry.header_from_top_cm
        : String(entry.header_from_top_cm ?? base.header_from_top_cm ?? ''),
    footer_from_bottom_cm:
      typeof entry.footer_from_bottom_cm === 'string'
        ? entry.footer_from_bottom_cm
        : String(entry.footer_from_bottom_cm ?? base.footer_from_bottom_cm ?? ''),
    image: {
      name: typeof entry.image?.name === 'string' ? entry.image.name : base.image.name,
      src: typeof entry.image?.src === 'string' ? entry.image.src : base.image.src,
    },
    image_align: typeof entry.image_align === 'string' ? entry.image_align : base.image_align,
    image_width_cm: typeof entry.image_width_cm === 'string' ? entry.image_width_cm : base.image_width_cm,
    image_height_cm: typeof entry.image_height_cm === 'string' ? entry.image_height_cm : base.image_height_cm,
    dynamic_page_number: {
      enabled: Boolean(entry.dynamic_page_number?.enabled),
      format:
        typeof entry.dynamic_page_number?.format === 'string'
          ? entry.dynamic_page_number.format
          : base.dynamic_page_number.format,
      prefix:
        typeof entry.dynamic_page_number?.prefix === 'string'
          ? entry.dynamic_page_number.prefix
          : base.dynamic_page_number.prefix,
      suffix:
        typeof entry.dynamic_page_number?.suffix === 'string'
          ? entry.dynamic_page_number.suffix
          : base.dynamic_page_number.suffix,
      alignment: normalizePageNumberAlignment(entry.dynamic_page_number?.alignment),
    },
  };
}

function mergeHeaderFooterVariantEntry(currentEntry, patch) {
  const safeCurrent = currentEntry && typeof currentEntry === 'object' ? currentEntry : {};
  const safePatch = patch && typeof patch === 'object' ? patch : {};
  return {
    ...safeCurrent,
    ...safePatch,
    image: {
      ...(safeCurrent.image || {}),
      ...(safePatch.image || {}),
    },
    dynamic_page_number: {
      ...(safeCurrent.dynamic_page_number || {}),
      ...(safePatch.dynamic_page_number || {}),
    },
  };
}

function mergeHeaderFooterEntry(currentEntry, patch) {
  const merged = mergeHeaderFooterVariantEntry(currentEntry, patch);
  if ('first_page' in patch) {
    merged.first_page = patch.first_page;
  } else {
    merged.first_page = currentEntry?.first_page;
  }
  return merged;
}

function headerFooterEntryHasVisibleContent(fieldKey, entry) {
  const normalizedEntry = normalizeHeaderFooterVariantEntry(fieldKey, entry);
  const headerLayout = normalizedEntry.layout || 'text_only';
  const hasImage = fieldKey === 'header'
    && Boolean(normalizedEntry.image.name || normalizedEntry.image.src)
    && headerLayout !== 'text_only'
    && headerLayout !== 'text_left_text_right';
  const hasText = fieldKey !== 'header'
    ? Boolean(normalizedEntry.text)
    : Boolean(normalizedEntry.text) && headerLayout !== 'image_only';
  const hasRightText = fieldKey === 'header'
    && headerLayout === 'text_left_text_right'
    && Boolean(normalizedEntry.right_text);
  const hasPageNumber = fieldKey === 'footer' && Boolean(normalizedEntry.dynamic_page_number.enabled);
  return Boolean(hasImage || hasText || hasRightText || hasPageNumber);
}

function isHeaderFooterEntryLike(value) {
  return Boolean(value) && typeof value === 'object' && ('section' in value || 'content' in value || 'text_align' in value);
}

function formatHeaderFooterPreview(fieldKey, entry, index) {
  const summary = [];
  summary.push(`Section ${entry.section || index + 1}`);

  if (entry.text) {
    summary.push(entry.text.replace(/\s+/g, ' ').trim());
  }
  if (fieldKey === 'header' && entry.right_text) {
    summary.push(`right: ${entry.right_text.replace(/\s+/g, ' ').trim()}`);
  }

  if (entry.font_family || entry.font_size) {
    summary.push([entry.font_family, entry.font_size].filter(Boolean).join(' '));
  }

  const styles = ['bold', 'italic', 'underline'].filter((key) => entry[key]);
  if (styles.length > 0) {
    summary.push(styles.join(', '));
  }

  if (fieldKey === 'header') {
    summary.push(`layout: ${entry.layout}`);
    if (entry.header_from_top_cm) {
      summary.push(`top: ${entry.header_from_top_cm} cm`);
    }
    if (entry.image.name || entry.image.src) {
      summary.push(`image: ${entry.image.name || entry.image.src}`);
    }
  } else {
    if (entry.footer_from_bottom_cm) {
      summary.push(`bottom: ${entry.footer_from_bottom_cm} cm`);
    }
    if (entry.dynamic_page_number.enabled) {
      summary.push(`page no: ${entry.dynamic_page_number.format}, ${entry.dynamic_page_number.alignment}`);
    }
  }

  return summary.join(' | ');
}

function getRuleSummary(fieldKey, value) {
  if (isHeadingField(fieldKey)) {
    const entries = normalizeHeadingEntries(getStructuredFieldValue(fieldKey, value));
    if (entries.length === 0) {
      return 'Heading numbering rule not configured.';
    }

    return entries
      .map((entry, index) => {
        const styleLabel = NUMBERING_STYLE_OPTIONS.find((option) => option.value === entry.numbering.style)?.label
          || entry.numbering.style
          || 'No numbering';
        return `Item ${index + 1}: ${styleLabel}`;
      })
      .join(' | ');
  }

  if (isHeaderFooterField(fieldKey)) {
    const entries = normalizeHeaderFooterEntries(fieldKey, getStructuredFieldValue(fieldKey, value));
    if (entries.length === 0) {
      return `${FIELD_LABELS[fieldKey]} rule not configured.`;
    }

    return entries
      .map((entry, index) => formatHeaderFooterRuleSummary(fieldKey, entry, index))
      .join(' | ');
  }

  if (fieldKey === 'keywords') {
    return 'Keyword list extracted from the sample document.';
  }

  if (fieldKey === 'content') {
    return 'Body content sample extracted. Formatting rule editor for body paragraphs can be added next.';
  }

  if (fieldKey === 'table' || fieldKey === 'figure') {
    return 'Structured object extracted from the sample document.';
  }

  return 'Sample rule captured from the current format.';
}

function getRulePattern(fieldKey, ruleConfig) {
  return JSON.stringify(buildRulePatternValue(fieldKey, ruleConfig), null, 2);
}

function buildRulePatternValue(fieldKey, ruleConfig) {
  if (isHeadingField(fieldKey)) {
    return {
      [fieldKey]: {
        format: ruleConfig,
      },
    };
  }

  if (isHeaderFooterField(fieldKey)) {
    return {
      [fieldKey]: {
        format: ruleConfig,
      },
    };
  }

  return {
    [fieldKey]: {
      ...ruleConfig,
    },
  };
}

function getDefaultFieldRule(fieldKey) {
  return {
    ...(DEFAULT_FIELD_RULES[fieldKey] || {
      font_family: 'Times New Roman',
      font_size: '',
      bold: false,
      italic: false,
      underline: false,
      alignment: 'left',
    }),
  };
}

function getPersistedFieldRule(payload, fieldKey) {
  return normalizeFieldRuleConfig(fieldKey, payload?.field_rules?.[fieldKey]);
}

function getFieldRuleDraftValue(payload, fieldKey, editValue) {
  return parseFieldRuleDraft(payload, fieldKey, editValue);
}

function parseFieldRuleDraft(payload, fieldKey, draftValue) {
  try {
    const parsedDraft = JSON.parse(draftValue ?? JSON.stringify(getPersistedFieldRule(payload, fieldKey)));
    return normalizeFieldRuleConfig(fieldKey, parsedDraft);
  } catch {
    throw new Error(`Invalid rule config in ${FIELD_LABELS[fieldKey] || fieldKey}.`);
  }
}

function normalizeFieldRuleConfig(fieldKey, value) {
  const baseRule = getDefaultFieldRule(fieldKey);
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

  const numbering = source.numbering && typeof source.numbering === 'object' ? source.numbering : baseRule.numbering;
  const textTransform = normalizeTextTransform(source.text_transform ?? baseRule.text_transform);

  return {
    ...baseRule,
    ...source,
    font_family: typeof source.font_family === 'string' ? source.font_family : baseRule.font_family,
    font_size: typeof source.font_size === 'string' ? source.font_size : String(source.font_size ?? baseRule.font_size ?? ''),
    bold: Boolean(source.bold ?? baseRule.bold),
    italic: Boolean(source.italic ?? baseRule.italic),
    underline: Boolean(source.underline ?? baseRule.underline),
    alignment: typeof source.alignment === 'string' ? source.alignment : baseRule.alignment,
    line_spacing: typeof source.line_spacing === 'string' ? source.line_spacing : String(source.line_spacing ?? baseRule.line_spacing ?? ''),
    text_transform: ['author_name', 'author_details'].includes(fieldKey) && textTransform === 'none' ? 'capitalize_words' : textTransform,
    spacing_before: typeof source.spacing_before === 'string' ? source.spacing_before : String(source.spacing_before ?? baseRule.spacing_before ?? ''),
    spacing_after: typeof source.spacing_after === 'string' ? source.spacing_after : String(source.spacing_after ?? baseRule.spacing_after ?? ''),
    title_position: typeof source.title_position === 'string' ? source.title_position : baseRule.title_position,
    ...(numbering
      ? {
          numbering: {
            raw: typeof numbering.raw === 'string' ? numbering.raw : '',
            style: typeof numbering.style === 'string' ? numbering.style : 'none',
            enabled: Boolean(numbering.enabled),
          },
        }
      : {}),
  };
}

function getExampleValue(fieldKey, value) {
  if (isHeadingField(fieldKey)) {
    return normalizeHeadingEntries(value).map((entry) => entry.text).filter(Boolean).join(' | ');
  }

  if (isHeaderFooterField(fieldKey)) {
    return normalizeHeaderFooterEntries(fieldKey, value)
      .map((entry) => {
        if (fieldKey === 'header' && entry.layout === 'text_left_text_right') {
          return [entry.text, entry.right_text].filter(Boolean).join(' | ') || `Section ${entry.section}`;
        }

        return entry.text || `Section ${entry.section}`;
      })
      .join(' | ');
  }

  return formatDisplayValue(serializeExampleValue(value), fieldKey);
}

function serializeExampleValue(value) {
  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value);
}

function getStructuredFieldValue(fieldKey, value) {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return isHeaderFooterField(fieldKey) || isHeadingField(fieldKey) ? [] : value;
  }
}

function formatHeaderFooterRuleSummary(fieldKey, entry, index) {
  const parts = [`Section ${entry.section || index + 1}`];

  if (entry.font_family || entry.font_size) {
    parts.push([entry.font_family, entry.font_size].filter(Boolean).join(' '));
  }

  parts.push(`align: ${entry.text_align}`);

  const styles = ['bold', 'italic', 'underline'].filter((key) => entry[key]);
  if (styles.length > 0) {
    parts.push(styles.join(', '));
  }

  if (fieldKey === 'header') {
    parts.push(`layout: ${entry.layout}`);
    if (entry.header_from_top_cm) {
      parts.push(`top: ${entry.header_from_top_cm} cm`);
    }
    if (entry.right_text) {
      parts.push('right text set');
    }
    if (entry.image.name || entry.image.src) {
      parts.push(`image align: ${entry.image_align}`);
    }
  }

  if (fieldKey === 'footer') {
    if (entry.footer_from_bottom_cm) {
      parts.push(`bottom: ${entry.footer_from_bottom_cm} cm`);
    }
    if (entry.dynamic_page_number.enabled) {
      parts.push(`page number: ${entry.dynamic_page_number.format}, ${entry.dynamic_page_number.alignment}`);
    }
  }

  return parts.join(' | ');
}

function buildHeaderFooterInlineStyle(entry) {
  return {
    fontFamily: entry.font_family || 'Times New Roman, serif',
    fontSize: entry.font_size ? `${String(entry.font_size).replace('pt', '')}pt` : '10pt',
    fontWeight: entry.bold ? 700 : 400,
    fontStyle: entry.italic ? 'italic' : 'normal',
    textDecoration: entry.underline ? 'underline' : 'none',
    textAlign: entry.text_align || 'left',
  };
}

function buildSpecialPreviewColumnStyle(item) {
  return {
    color: normalizeColorValue(item.text_color),
    fontSize: item.font_size ? `${String(item.font_size).replace('pt', '')}pt` : '10pt',
    fontWeight: item.bold ? 700 : 400,
    fontStyle: item.italic ? 'italic' : 'normal',
    textAlign: item.alignment || 'left',
    alignItems:
      item.alignment === 'center'
        ? 'center'
        : item.alignment === 'right'
          ? 'flex-end'
          : 'flex-start',
  };
}

function buildSpecialPreviewSectionStyle(section) {
  const normalizedSection = normalizeSpecialSectionConfig(section);
  const leftMargin = Number.parseFloat(normalizedSection.left_margin_cm) || 0.6;
  const rightMargin = Number.parseFloat(normalizedSection.right_margin_cm) || 0.6;
  const pxPerCm = 24;

  return {
    paddingLeft: `${Math.max(12, leftMargin * pxPerCm)}px`,
    paddingRight: `${Math.max(12, rightMargin * pxPerCm)}px`,
  };
}

function getHeaderLayoutDirection(layout) {
  switch (layout) {
    case 'image_left_text_right':
      return 'row';
    case 'text_left_image_right':
      return 'row-reverse';
    case 'text_top_image_bottom':
      return 'column-reverse';
    default:
      return 'column';
  }
}

function buildHeaderImagePreviewStyle(entry) {
  const layout = entry.layout || 'text_only';
  const imageAlign = entry.image_align || 'center';
  const justifyContentMap = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
    justify: 'space-between',
  };

  return {
    width: layout === 'image_left_text_right' || layout === 'text_left_image_right' ? 'auto' : '100%',
    display: 'flex',
    justifyContent: justifyContentMap[imageAlign] || 'center',
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Unable to read the selected image.'));
    reader.readAsDataURL(file);
  });
}

async function readImageFileForDocx(file) {
  const originalDataUrl = await readFileAsDataUrl(file);
  const mimeType = String(file.type || '').toLowerCase();
  const supportedMimeTypes = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/bmp',
    'image/tiff',
  ]);

  if (supportedMimeTypes.has(mimeType)) {
    return {
      dataUrl: originalDataUrl,
      fileName: file.name,
    };
  }

  const pngDataUrl = await convertDataUrlToPng(originalDataUrl);
  return {
    dataUrl: pngDataUrl,
    fileName: replaceFileExtension(file.name, 'png'),
  };
}

function convertDataUrlToPng(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = window.document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width || 1;
      canvas.height = image.naturalHeight || image.height || 1;
      const context = canvas.getContext('2d');

      if (!context) {
        reject(new Error('Unable to prepare the selected image for DOCX export.'));
        return;
      }

      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => reject(new Error('Unsupported image format. Use PNG or JPG for header image.'));
    image.src = dataUrl;
  });
}

function replaceFileExtension(fileName, nextExtension) {
  const baseName = String(fileName || 'header-image').replace(/\.[^.]+$/, '');
  return `${baseName}.${nextExtension}`;
}

function buildPreviewInlineStyle(format, options = {}) {
  const includeSpacing = options.includeSpacing !== false;
  return {
    fontFamily: format.font_family || 'Times New Roman, serif',
    fontSize: format.font_size ? `${String(format.font_size).replace('pt', '')}pt` : '10pt',
    fontWeight: format.bold ? 700 : 400,
    fontStyle: format.italic ? 'italic' : 'normal',
    textDecoration: format.underline ? 'underline' : 'none',
    textAlign: format.align || 'left',
    lineHeight: format.line_spacing || 1.6,
    marginTop: includeSpacing && format.spacing_before ? `${String(format.spacing_before).replace('pt', '')}pt` : '0pt',
    marginBottom: includeSpacing && format.spacing_after ? `${String(format.spacing_after).replace('pt', '')}pt` : '0pt',
  };
}

function buildPreviewBlockCardStyle(format) {
  const topBorder = format?.border_top;
  const bottomBorder = format?.border_bottom;
  const spacingBefore = Number.parseFloat(format?.spacing_before) || 0;
  const spacingAfter = Number.parseFloat(format?.spacing_after) || 0;
  const topOuterMargin = Number.parseFloat(topBorder?.outer_margin_pt) || 0;
  const bottomOuterMargin = Number.parseFloat(bottomBorder?.outer_margin_pt) || 0;
  return {
    marginTop: `${spacingBefore + topOuterMargin}pt`,
    marginBottom: `${spacingAfter + bottomOuterMargin}pt`,
    ...(topBorder
      ? {
          borderTop: `${topBorder.width_pt || 1.5}pt solid ${topBorder.color || '#17365D'}`,
          paddingTop: `${topBorder.inner_margin_pt || 6}pt`,
        }
      : {}),
    ...(bottomBorder
      ? {
          borderBottom: `${bottomBorder.width_pt || 1.5}pt solid ${bottomBorder.color || '#17365D'}`,
          paddingBottom: `${bottomBorder.inner_margin_pt || 6}pt`,
        }
      : {}),
  };
}

function renderPreviewBlockHtml(block) {
  const transformedHtml = applyTextTransformToHtml(block.html || block.text || '', block?.format?.text_transform);
  const normalizedHtml = normalizeOperatorPlaceholderSymbolsInHtml(transformedHtml);
  if (block?.type === 'references') {
    return normalizeReferenceHeadingHtml(normalizedHtml);
  }
  if (shouldSuppressContentListFormatting(block)) {
    return removeLeadingListMarkerFromHtmlPreservingFormatting(normalizedHtml) || normalizedHtml;
  }
  return block?.type === 'author_name' ? renderAuthorNameSuperscripts(normalizedHtml) : normalizedHtml;
}

function normalizeOperatorPlaceholderSymbolsInHtml(html) {
  let normalized = String(html || '').replace(
    /<span([^>]*)>\s*([\u00ac\u25a1\u25a0\u25fb\u25fc\u25fd\u25fe\uf020-\uf0ff])\s*<\/span>/gi,
    (match, attrs, symbol) => {
      const styleText = String(attrs || '').toLowerCase();
      if (!styleText.includes('symbol') && !styleText.includes('wingdings')) {
        return match;
      }
      return symbolToOperator(symbol);
    }
  );

  normalized = normalized.replace(
    /([A-Za-z0-9)])\s*(?:<[^>]+>\s*)*([\u00ac\u25a1\u25a0\u25fb\u25fc\u25fd\u25fe\uf020-\uf0ff])\s*(?:<\/[^>]+>\s*)*(?=[A-Za-z0-9_(])/g,
    (_match, prefix, symbol) => `${prefix}${symbolToOperator(symbol)}`
  );

  return normalized;
}

function symbolToOperator(symbol) {
  const code = String(symbol || '').charCodeAt(0) & 0xff;
  const map = {
    0x9f: '←',
    0xac: '←',
    0xae: '→',
    0xad: '↑',
    0xaf: '↓',
  };
  return map[code] || '←';
}

function isListFormattingEnabled(block) {
  if (shouldSuppressContentListFormatting(block)) {
    return false;
  }
  const listConfig = block?.format?.list || {};
  return Boolean(listConfig.enabled) && (listConfig.type === 'bullet' || listConfig.type === 'number');
}

function shouldSuppressContentListFormatting(block) {
  if (block?.format?.list?.enabled) {
    return false;
  }
  return block?.type === 'content' && block?.source === 'numbered-list' && looksLikeAccidentalBodyNumbering(block);
}

function normalizeContentListFormatting(block) {
  if (!shouldSuppressContentListFormatting(block)) {
    return block;
  }

  const nextFormat = { ...(block.format || {}) };
  delete nextFormat.list;

  return {
    ...block,
    text: removeLeadingListMarkerFromText(block.text || stripHtmlTagsPreservingLineBreaks(block.html || '')),
    format: nextFormat,
  };
}

function looksLikeAccidentalBodyNumbering(block) {
  const text = String(block?.text || stripHtmlTagsPreservingLineBreaks(block?.html || '') || '').trim();
  const firstLine = text.split(/\n+/).find((line) => line.trim()) || text;
  const body = removeLeadingListMarkerFromText(firstLine);

  if (!body || body === firstLine.trim()) {
    return false;
  }

  const words = body.match(/\b[\w'-]+\b/g) || [];
  return body.length > 180 || words.length > 22;
}

function getListRenderPrefix(allBlocks, currentBlock) {
  const listConfig = currentBlock?.format?.list || {};
  if (!listConfig.enabled) {
    return '';
  }

  if (listConfig.type === 'bullet') {
    return resolveBulletGlyph(listConfig.style);
  }

  const currentIndex = Array.isArray(allBlocks) ? allBlocks.findIndex((block) => block.id === currentBlock.id) : -1;
  if (currentIndex === -1) {
    return formatNumber(listConfig.style || 'upper-alpha', 1);
  }

  let count = 0;
  for (let index = currentIndex; index >= 0; index -= 1) {
    const block = allBlocks[index];
    const blockList = block?.format?.list || {};
    if (!blockList.enabled || blockList.type !== 'number' || (blockList.style || 'upper-alpha') !== (listConfig.style || 'upper-alpha')) {
      break;
    }
    count += 1;
  }

  return formatNumber(listConfig.style || 'upper-alpha', count || 1);
}

function resolveBulletGlyph(style) {
  switch (style) {
    case 'circle':
      return '○';
    case 'square':
      return '■';
    default:
      return '•';
  }
}

function getStandaloneListRenderPrefix(block) {
  const listConfig = block?.format?.list || {};
  if (!listConfig.enabled) {
    return '';
  }
  return listConfig.type === 'bullet'
    ? resolveBulletGlyph(listConfig.style)
    : formatNumber(listConfig.style || 'upper-alpha', 1);
}

function removeLeadingListMarkerFromText(value) {
  return String(value || '')
    .split(/\n+/)
    .map((line) => line.replace(/^\s*(?:[-–—]|\u2022|\u25e6|\u25aa|\u25cf|\(?\d+(?:\.\d+)*[.)]?|[A-Za-z][.)]|[IVXLCDMivxlcdm]+[.)])(?:\s+|$)/u, '').trimEnd())
    .join('\n')
    .trim();
}

function stripHtmlTagsPreservingLineBreaks(value) {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/div>\s*<div[^>]*>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .trim();
}

function removeLeadingListMarkerFromHtml(value) {
  const normalizedText = removeLeadingListMarkerFromText(stripHtmlTagsPreservingLineBreaks(value));
  return normalizedText ? escapeHtml(normalizedText).replace(/\n/g, '<br>') : '';
}

function removeLeadingListMarkerFromHtmlPreservingFormatting(value) {
  const sourceHtml = String(value || '');
  if (!sourceHtml) {
    return '';
  }

  if (typeof window === 'undefined' || !window.document) {
    return removeLeadingListMarkerFromHtml(sourceHtml);
  }

  const paragraphBreakPattern = /(?:<br\s*\/?>\s*){2,}/i;
  const parts = sourceHtml.split(paragraphBreakPattern);
  const normalizedParts = (parts.length > 0 ? parts : [sourceHtml]).map((part) => {
    const container = window.document.createElement('div');
    container.innerHTML = part;
    removeLeadingListMarkerFromFirstTextNode(container);
    return container.innerHTML.trim();
  });

  return normalizedParts.filter(Boolean).join('<br><br>');
}

function removeLeadingListMarkerFromFirstTextNode(root) {
  if (!root) {
    return false;
  }

  const markerPattern = /^\s*(?:[-–—]|\u2022|\u25e6|\u25aa|\u25cf|\(?\d+(?:\.\d+)*[.)]?|[A-Za-z][.)]|[IVXLCDMivxlcdm]+[.)])(?:\s+|$)/u;
  const walker = window.document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    const value = String(textNode.nodeValue || '');
    if (!value.trim()) {
      textNode = walker.nextNode();
      continue;
    }

    const nextValue = value.replace(markerPattern, '');
    if (nextValue !== value) {
      textNode.nodeValue = nextValue;
    }
    return true;
  }

  return false;
}

function renderListFormattedEditorHtml(block) {
  const listConfig = block?.format?.list || {};
  let lines = splitReferenceHtmlIntoLines(renderPreviewBlockHtml(block));
  if (lines.length === 0) {
    lines = String(block?.text || stripHtmlTagsPreservingLineBreaks(block?.html || '') || '')
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
  }
  if (lines.length === 0) {
    return renderPreviewBlockHtml(block) || escapeHtml(block?.text || '');
  }
  const paragraphStyle = buildPreviewParagraphStyle(block.format, block);
  let listIndex = 0;

  const renderedLines = lines
    .map((line, index) => {
      const trimmedLine = String(line || '').trim();
      if (!trimmedLine) {
        return null;
      }

      listIndex += 1;
      const cleanedLine = (stripReferenceNumberingFromHtml(trimmedLine)
        .replace(/^\s*(?:[-–—]|\u2022|\u25e6|\u25aa|\u25cf|[A-Za-z][.)]|[IVXLCDMivxlcdm]+[.)])\s+/u, '')
        .trimStart()) || stripHtmlTagsPreservingLineBreaks(trimmedLine) || trimmedLine;
      const prefix = listConfig.type === 'bullet'
        ? resolveBulletGlyph(listConfig.style)
        : formatNumber(listConfig.style || 'upper-alpha', listIndex);

      return { index, html: `${escapeHtml(prefix)} ${cleanedLine}` };
    })
    .filter(Boolean)
    .map(({ index, html }, renderedIndex, renderedLines) => {
      const finalParagraphStyle = renderedIndex === renderedLines.length - 1
        ? buildLastPreviewParagraphStyle(paragraphStyle)
        : paragraphStyle;
      return `<div data-preview-paragraph="true" data-preview-paragraph-index="${index}" style="${finalParagraphStyle}">${html}</div>`;
    })
    .join('');

  return renderedLines;
}

function renderPreviewBlockEditorHtml(block, allBlocks = []) {
  if (isReferenceNumberingEnabled(block)) {
    return renderNumberedReferenceEditorHtml(block, allBlocks);
  }
  if (isListFormattingEnabled(block)) {
    return renderListFormattedEditorHtml(block);
  }

  const html = renderPreviewBlockHtml(block);
  const parts = splitHtmlIntoEditorParagraphs(html);
  const paragraphStyle = buildPreviewParagraphStyle(block.format, block);

  return parts
    .map(
      (part, index) => {
        const nextParagraphStyle = getPreviewEditorParagraphStyle(block, part, paragraphStyle);
        const finalParagraphStyle = index === parts.length - 1
          ? buildLastPreviewParagraphStyle(nextParagraphStyle)
          : nextParagraphStyle;
        return `<div data-preview-paragraph="true" data-preview-paragraph-index="${index}" style="${finalParagraphStyle}">${part}</div>`;
      }
    )
    .join('');
}

function renderNumberedReferenceEditorHtml(block, allBlocks = []) {
  const lines = splitReferenceHtmlIntoLines(renderPreviewBlockHtml(block));
  const paragraphStyle = buildPreviewParagraphStyle(block.format, block);
  let referenceIndex = getReferenceNumberBeforeBlock(allBlocks, block);

  const renderedLines = lines
    .map((line, index) => {
      const trimmedLine = String(line || '').trim();
      if (!trimmedLine) {
        return '';
      }

      let renderedLine = trimmedLine;
      if (!isReferenceHeadingHtml(trimmedLine)) {
        if (isStandaloneReferenceNumberHtml(trimmedLine)) {
          return '';
        }
        referenceIndex += 1;
        renderedLine = `${escapeHtml(formatNumber(block.format.numbering.style, referenceIndex))} ${stripReferenceNumberingFromHtml(trimmedLine)}`;
      } else {
        renderedLine = escapeHtml(normalizeReferenceHeadingText(trimmedLine));
      }

      const nextParagraphStyle = getPreviewEditorParagraphStyle(block, trimmedLine, paragraphStyle);
      return { index, renderedLine, style: nextParagraphStyle };
    })
    .filter(Boolean);

  return renderedLines
    .map(({ index, renderedLine, style }, renderedIndex) => {
      const finalParagraphStyle = renderedIndex === renderedLines.length - 1
        ? buildLastPreviewParagraphStyle(style)
        : style;
      return `<div data-preview-paragraph="true" data-preview-paragraph-index="${index}" style="${finalParagraphStyle}">${renderedLine}</div>`;
    })
    .join('');
}

function getReferenceNumberBeforeBlock(allBlocks, currentBlock) {
  if (!Array.isArray(allBlocks) || !currentBlock?.id || !isReferenceNumberingEnabled(currentBlock)) {
    return 0;
  }

  let count = 0;
  for (const block of allBlocks) {
    if (block?.id === currentBlock.id) {
      break;
    }
    if (!isReferenceNumberingEnabled(block)) {
      continue;
    }
    const referenceLines = splitReferenceHtmlIntoLines(renderPreviewBlockHtml(block));
    for (const line of referenceLines) {
      const trimmedLine = String(line || '').trim();
      if (trimmedLine && !isReferenceHeadingHtml(trimmedLine) && !isStandaloneReferenceNumberHtml(trimmedLine)) {
        count += 1;
      }
    }
  }
  return count;
}

function getPreviewEditorParagraphStyle(block, paragraphHtml, fallbackStyle) {
  if (block?.type !== 'references' || !isReferenceHeadingHtml(paragraphHtml)) {
    return fallbackStyle;
  }

  return `${fallbackStyle} text-align: center; font-weight: 700;`;
}

function isReferenceNumberingEnabled(block) {
  const numbering = block?.format?.numbering || {};
  return block?.type === 'references' && Boolean(numbering.enabled) && !['none', 'custom'].includes(numbering.style);
}

function splitReferenceHtmlIntoLines(html) {
  const normalizedHtml = splitUnnumberedReferenceEntries(String(html || ''));
  return normalizedHtml
    .replace(/(<br\s*\/?>\s*){2,}/gi, '<br>')
    .split(/<br\s*\/?>/i)
    .map((line) => line.trim())
    .filter(Boolean);
}

function splitUnnumberedReferenceEntries(html) {
  return String(html || '')
    .replace(
      /(?<!\b[A-Z])([.!?])\s+((?:[A-Z][A-Za-z&'\-]+,\s+(?=.{0,120}\(\d{4}[a-z]?\)).{1,140}?|[A-Z][A-Za-z&'\-]+(?:\s+(?:[A-Z][A-Za-z&'\-]+|of|and|for|the|in)){1,12}\.\s*)\(\d{4}[a-z]?\))/g,
      '$1<br>$2'
    )
    .replace(
      /(https?:\/\/\S+)\s+((?:[A-Z][A-Za-z&'\-]+,\s+(?=.{0,120}\(\d{4}[a-z]?\)).{1,140}?|[A-Z][A-Za-z&'\-]+(?:\s+(?:[A-Z][A-Za-z&'\-]+|of|and|for|the|in)){1,12}\.\s*)\(\d{4}[a-z]?\))/g,
      '$1<br>$2'
    );
}

function htmlToPlainText(html) {
  return String(html || '').replace(/<[^>]+>/g, '').trim();
}

function isReferenceHeadingHtml(html) {
  return /^(references?|bibliography)\s*:?\s*$/i.test(stripReferenceNumberingFromText(htmlToPlainText(html)));
}

function startsWithReferenceNumberHtml(html) {
  return /^(\[\d+\]|\(?\d+[.)])\s+/i.test(htmlToPlainText(html));
}

function isStandaloneReferenceNumberHtml(html) {
  return /^(?:\[\d+\]|\(?\d+[.)])$/i.test(htmlToPlainText(html));
}

function stripReferenceNumberingFromHtml(html) {
  const sourceHtml = String(html || '');
  if (!sourceHtml) {
    return '';
  }

  if (typeof window === 'undefined' || !window.document) {
    return stripReferenceNumberingFromText(sourceHtml);
  }

  const container = window.document.createElement('div');
  container.innerHTML = sourceHtml;
  stripReferenceNumberingFromFirstTextNode(container);
  return container.innerHTML.trimStart();
}

function stripReferenceNumberingFromText(text) {
  return String(text || '').replace(/^\s*(?:(?:\[\d+\]|\(?\d+[.)]|[-–—]|\u2022|\u25e6|\u25aa|\u25cf)(?:\s+|$)\s*)+/iu, '').trimStart();
}

function stripReferenceNumberingFromFirstTextNode(root) {
  if (!root) {
    return false;
  }

  const markerPattern = /^\s*(?:(?:\[\d+\]|\(?\d+[.)]|[-–—]|\u2022|\u25e6|\u25aa|\u25cf)(?:\s+|$)\s*)+/iu;
  const walker = window.document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  let strippedAnyMarker = false;

  while (textNode) {
    const value = String(textNode.nodeValue || '');
    if (!value.trim()) {
      textNode = walker.nextNode();
      continue;
    }

    const nextValue = value.replace(markerPattern, '');
    if (nextValue !== value) {
      textNode.nodeValue = nextValue;
      strippedAnyMarker = true;
      if (!nextValue.trim()) {
        textNode = walker.nextNode();
        continue;
      }
    }
    return strippedAnyMarker;
  }

  return strippedAnyMarker;
}

function normalizeReferenceHeadingText(html) {
  const plainText = stripReferenceNumberingFromText(htmlToPlainText(html)).replace(/[:.\-–—]\s*$/, '').trim();
  return /^bibliography$/i.test(plainText) ? 'Bibliography' : 'References';
}

function normalizeReferenceHeadingHtml(html) {
  const lines = splitReferenceHtmlIntoLines(html);
  if (!lines.length) {
    return html;
  }

  return lines.map((line) => (
    isReferenceHeadingHtml(line)
      ? escapeHtml(normalizeReferenceHeadingText(line))
      : line
  )).join('<br><br>');
}

function renderAuthorNameSuperscripts(html) {
  return String(html || '').replace(/([A-Za-z])\s*(\d+)(?=(?:\s*(?:,|&)\s*|\s+and\s+|\s*$))/gi, '$1<sup>$2</sup>');
}

function splitHtmlIntoEditorParagraphs(html) {
  const normalized = String(html || '')
    .replace(/\r\n/g, '\n')
    .replace(/(<br\s*\/?>\s*){2,}/gi, '__PARA_BREAK__')
    .replace(/\n{2,}/g, '__PARA_BREAK__');
  const parts = normalized.split('__PARA_BREAK__').map((part) => part.trim());
  return parts.length > 0 ? parts : [''];
}

function buildPreviewParagraphStyle(format, block = null) {
  const spacingBefore = parsePointValue(format?.spacing_before);
  const spacingAfter = parsePointValue(format?.spacing_after);
  const minimumGap = block?.type === 'content' ? 6 : 0;
  const paragraphGap = `${Math.max(minimumGap, spacingBefore + spacingAfter)}pt`;
  const lineHeight = format?.line_spacing ? String(format.line_spacing) : '1.6';
  return `margin: 0 0 ${paragraphGap} 0; line-height: ${lineHeight};`;
}

function buildLastPreviewParagraphStyle(paragraphStyle) {
  return `${paragraphStyle} margin-bottom: 0;`;
}

function parsePointValue(value) {
  const parsed = Number.parseFloat(String(value ?? '').replace('pt', ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function stripRenderedPrefix(value, prefix) {
  if (!prefix || !value) {
    return value;
  }

  let nextValue = String(value);
  const normalizedPrefix = `${prefix} `;

  while (nextValue.startsWith(normalizedPrefix) || nextValue.startsWith(prefix)) {
    if (nextValue.startsWith(normalizedPrefix)) {
      nextValue = nextValue.slice(normalizedPrefix.length);
      continue;
    }

    nextValue = nextValue.slice(prefix.length).trimStart();
  }

  return nextValue;
}

function getNormalizedBlockContentFromElement(block, element, allBlocks) {
  const renderedPrefix = getHeadingRenderPrefix(allBlocks, block);
  const editableText = getEditableElementText(element);
  const editableHtml = getEditableElementHtml(element);
  let nextText = sanitizeHeadingBlockValue(block, stripRenderedPrefix(editableText, renderedPrefix));
  let nextHtml = sanitizeHeadingBlockValue(block, stripRenderedPrefix(editableHtml || textToPreviewHtml(editableText), renderedPrefix), true);

  if (isListFormattingEnabled(block)) {
    nextText = removeLeadingListMarkerFromText(nextText);
    nextHtml = removeLeadingListMarkerFromHtmlPreservingFormatting(nextHtml);
  }

  if (block?.type === 'references') {
    nextText = normalizeReferenceBlockText(nextText);
    nextHtml = textToPreviewHtml(nextText);
  }

  if (block?.type?.startsWith('heading_') && !nextText && block.text) {
    return {
      text: block.text,
      html: block.html || buildHeadingHtmlFromText(block.text),
    };
  }

  return {
    text: nextText,
    html: nextHtml,
  };
}

function getEditableElementText(element) {
  const paragraphText = getEditableElementParagraphText(element);
  if (paragraphText) {
    return paragraphText;
  }

  const domText = getNodeTextPreservingBreaks(element);
  const fallbackText = String(element?.innerText || element?.textContent || '');
  const normalizedText = String(domText || fallbackText)
    .replace(/\r/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n+$/g, '');

  return preserveEditableBlankLines(normalizedText);
}

function getEditableElementHtml(element) {
  const paragraphElements = Array.from(element?.querySelectorAll?.('[data-preview-paragraph="true"]') || []);
  if (paragraphElements.length > 0) {
    return paragraphElements
      .map((paragraphElement) => String(paragraphElement?.innerHTML || '').trim())
      .filter(Boolean)
      .join('<br><br>');
  }

  return String(element?.innerHTML || '').trim();
}

function getEditableElementParagraphText(element) {
  const paragraphElements = Array.from(element?.querySelectorAll?.('[data-preview-paragraph="true"]') || []);
  if (paragraphElements.length <= 1) {
    return '';
  }

  return paragraphElements
    .map((paragraphElement) => {
      const domText = getNodeTextPreservingBreaks(paragraphElement);
      const fallbackText = String(paragraphElement?.innerText || paragraphElement?.textContent || '');
      return String(domText || fallbackText)
        .replace(/\r/g, '')
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n+$/g, '')
        .trim();
    })
    .filter(Boolean)
    .join('\n\n');
}

function preserveEditableBlankLines(value) {
  return String(value || '')
    .split('\n')
    .map((line) => (line.trim() ? line : '\u00a0'))
    .join('\n');
}

function removeLeadingHeadingNumber(value) {
  if (!value) {
    return value;
  }

  return String(value)
    .replace(/^\s*(?:\d+(?:\.\d+)*|[IVXLCDMivxlcdm]+|[A-Za-z])(?:[.)])\s+/u, '')
    .trimStart();
}

function sanitizeHeadingText(value) {
  return normalizeHeadingContent(value || '');
}

function sanitizeHeadingHtml(value) {
  return buildHeadingHtmlFromText(normalizeHeadingContent(value || ''));
}

function normalizeHeadingHtmlPreservingInline(value, normalizedText) {
  const sourceHtml = String(value || '');
  if (!sourceHtml) {
    return buildHeadingHtmlFromText(normalizedText || '');
  }

  const cleanedHtml = removeLeadingHeadingNumberFromHtmlPreservingFormatting(sourceHtml);
  if (normalizeHeadingContent(cleanedHtml) === normalizedText) {
    return cleanedHtml;
  }

  return buildHeadingHtmlFromText(normalizedText || '');
}

function removeLeadingHeadingNumberFromHtmlPreservingFormatting(value) {
  const sourceHtml = String(value || '');
  if (!sourceHtml) {
    return '';
  }

  if (typeof window === 'undefined' || !window.document) {
    return buildHeadingHtmlFromText(normalizeHeadingContent(sourceHtml));
  }

  const container = window.document.createElement('div');
  container.innerHTML = sourceHtml;

  const paragraphElements = Array.from(container.querySelectorAll('[data-preview-paragraph="true"], div, p'));
  paragraphElements.forEach((element) => {
    if (isStandaloneHeadingNumber(element.textContent || '')) {
      element.remove();
    }
  });

  removeLeadingHeadingNumberFromFirstTextNode(container);
  return container.innerHTML.trim();
}

function removeLeadingHeadingNumberFromFirstTextNode(root) {
  if (!root || typeof window === 'undefined' || !window.document) {
    return false;
  }

  const markerPattern = /^\s*(?:\d+(?:\.\d+)*|[IVXLCDMivxlcdm]+|[A-Za-z])(?:[.)])?\s+/u;
  const walker = window.document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    const value = String(textNode.nodeValue || '');
    if (!value.trim()) {
      textNode = walker.nextNode();
      continue;
    }

    const nextValue = value.replace(markerPattern, '');
    if (nextValue !== value) {
      textNode.nodeValue = nextValue;
    }
    return true;
  }

  return false;
}

function sanitizeHeadingBlockValue(block, value, isHtml = false) {
  if (!block?.type?.startsWith('heading_')) {
    return value;
  }

  return isHtml ? sanitizeHeadingHtml(value) : sanitizeHeadingText(value);
}

function normalizePreviewHeadingBlock(block) {
  if (!block?.type?.startsWith('heading_')) {
    return block;
  }

  const normalizedText = sanitizeHeadingText(block.text || block.html || '');

  return {
    ...block,
    level: getHeadingLevel(block.type),
    source: block.source || 'rule',
    text: normalizedText,
    html: normalizedText
      ? normalizeHeadingHtmlPreservingInline(block.html || block.text || '', normalizedText)
      : buildHeadingHtmlFromText(block.text || ''),
  };
}

function getRuleDrivenBlockFormat(rules, fieldKey) {
  const ruleConfig = getPersistedFieldRule(rules, fieldKey);
  const blockFormat = {
    font_family: ruleConfig.font_family,
    font_size: ruleConfig.font_size,
    bold: Boolean(ruleConfig.bold),
    italic: Boolean(ruleConfig.italic),
    underline: Boolean(ruleConfig.underline),
    align: ruleConfig.alignment || ruleConfig.align || 'left',
    line_spacing: ruleConfig.line_spacing,
    spacing_before: ruleConfig.spacing_before,
    spacing_after: ruleConfig.spacing_after,
    text_transform: normalizeTextTransform(ruleConfig.text_transform),
  };

  if (fieldKey === 'table_title' || fieldKey === 'figure_title') {
    blockFormat.title_position = ruleConfig.title_position;
  }

  if (isNumberingField(fieldKey)) {
    blockFormat.numbering = getConfiguredHeadingNumbering(rules, fieldKey);
  }

  return blockFormat;
}

function getRuleDrivenInlineStyle(rules, fieldKey) {
  return buildPreviewInlineStyle(getRuleDrivenBlockFormat(rules, fieldKey));
}

function normalizeOptimizePreviewDocument(doc) {
  if (!doc || !Array.isArray(doc.blocks)) {
    return doc;
  }

  const normalizedBlocks = doc.blocks.map((block) => {
    const normalizedBlock = normalizeContentListFormatting(normalizePreviewHeadingBlock(block));
    const safeType = normalizedBlock.type || 'content';

    return {
      ...normalizedBlock,
      type: safeType,
      source: normalizedBlock.source || (safeType.startsWith('heading_') ? 'rule' : 'fallback'),
      format: {
        ...(normalizedBlock.format || {}),
      },
    };
  });

  return {
    ...doc,
    template_columns: doc.template_columns === 2 ? 2 : 1,
    document_layout: normalizeOptimizeLayout(doc.document_layout, doc.template_columns === 2 ? 2 : 1),
    blocks: assignPreviewHeadingHierarchy(normalizedBlocks),
  };
}

function reconcilePreviewDocument(doc, rules, changedBlockId = '', options = {}) {
  if (!doc || !doc.blocks) return doc;

  const normalizedBlocks = doc.blocks.map((block) => {
    const normalizedBlock = normalizeContentListFormatting(normalizePreviewHeadingBlock(block));
    const safeType = getPreviewSafeBlockType(normalizedBlock.type, rules);
    const ruleDrivenFormat = getRuleDrivenBlockFormat(rules, safeType) || {};
    const resolvedFormat = {
      ...ruleDrivenFormat,
      ...normalizedBlock.format,
    };

    if (isNumberingField(safeType)) {
      const configuredNumbering = getConfiguredNumbering(rules, safeType);
      resolvedFormat.numbering = {
        ...configuredNumbering,
        enabled: Boolean(configuredNumbering.enabled),
        style: configuredNumbering.style || 'none',
        raw: configuredNumbering.raw || '',
      };
    }

    return {
      ...normalizedBlock,
      type: safeType,
      label: formatBlockLabel(safeType),
      source: normalizedBlock.source || (safeType.startsWith('heading_') ? 'rule' : 'fallback'),
      format: resolvedFormat,
    };
  });

  const blocks = options.disableCascade
    ? normalizedBlocks
    : mergeAdjacentPreviewBlocks(
        promoteSectionChildHeadings(
          enforceSingleInstancePreviewBlocks(normalizedBlocks, rules, changedBlockId),
          rules,
          changedBlockId
        ),
        changedBlockId
      );

  const orderedBlocks = orderAuthorFrontMatterBlocks(blocks);
  const numberedBlocks = applyObjectiveNumberingToPreviewBlocks(orderedBlocks);

  return {
    ...doc,
    template_columns: doc.template_columns === 2 ? 2 : (rules?.template_columns === 2 ? 2 : 1),
    blocks: assignPreviewHeadingHierarchy(numberedBlocks),
  };
/*

  return {
    ...doc,
    blocks: doc.blocks.map((block) => {
      // ✅ preserve original type (DO NOT CHANGE)
      const safeType = block.type || "content";

      return {
        ...block,
        type: safeType,
        level: getHeadingLevel(safeType),

        format: {
          ...block.format,
          ...(getRuleDrivenBlockFormat(rules, safeType) || {}),
        },
      };
    }),
  };
*/
}

function getPreviewSafeBlockType(blockType, rules) {
  const safeType = blockType || 'content';
  if (isNewUploadFormat(rules) && DOCUMENT_EXCLUDED_RULE_TYPES.has(safeType)) {
    return 'content';
  }

  return safeType;
}

function orderAuthorFrontMatterBlocks(blocks) {
  if (!Array.isArray(blocks) || blocks.length < 2) {
    return blocks;
  }

  const frontMatterTypes = new Set(['title', 'author_name', 'author_details', 'abstract', 'keywords']);
  let frontMatterEnd = 0;
  while (frontMatterEnd < blocks.length && frontMatterTypes.has(blocks[frontMatterEnd]?.type)) {
    frontMatterEnd += 1;
  }

  if (frontMatterEnd <= 1) {
    return blocks;
  }

  const frontMatter = blocks.slice(0, frontMatterEnd);
  const authorNames = frontMatter.filter((block) => block?.type === 'author_name');
  const authorDetails = frontMatter.filter((block) => block?.type === 'author_details');
  if (authorNames.length === 0 || authorDetails.length === 0) {
    return blocks;
  }

  const withoutAuthorBlocks = frontMatter.filter((block) => !['author_name', 'author_details'].includes(block?.type));
  const insertIndex = withoutAuthorBlocks.findIndex((block) => ['abstract', 'keywords'].includes(block?.type));
  const beforeAuthors = insertIndex === -1 ? withoutAuthorBlocks : withoutAuthorBlocks.slice(0, insertIndex);
  const afterAuthors = insertIndex === -1 ? [] : withoutAuthorBlocks.slice(insertIndex);

  return [
    ...beforeAuthors,
    ...authorNames,
    ...authorDetails,
    ...afterAuthors,
    ...blocks.slice(frontMatterEnd),
  ];
}

function applyObjectiveNumberingToPreviewBlocks(blocks) {
  let numberedSection = '';
  let sectionCounter = 1;

  return (Array.isArray(blocks) ? blocks : []).map((block) => {
    const rawText = String(block?.text || htmlToPlainText(block?.html || '') || '');
    if (!rawText.trim()) {
      return block;
    }

    const paragraphs = splitPreviewTextIntoParagraphs(rawText);
    let changed = false;
    const nextParagraphs = paragraphs.map((paragraph) => {
      const trimmed = paragraph.trim();
      const sectionKey = getAutoNumberedSectionKey(trimmed);

      if (sectionKey) {
        numberedSection = sectionKey;
        sectionCounter = 1;
        return paragraph;
      }

      if (numberedSection && isAutoNumberedSectionItemText(trimmed, numberedSection)) {
        if (hasLeadingNumber(trimmed)) {
          sectionCounter += 1;
          return paragraph;
        }

        changed = true;
        const indent = paragraph.match(/^\s*/)?.[0] || '';
        const numberedParagraph = `${indent}${sectionCounter}. ${paragraph.trimStart()}`;
        sectionCounter += 1;
        return numberedParagraph;
      }

      if (numberedSection && trimmed && !isAutoNumberedSectionContinuationText(trimmed, numberedSection)) {
        numberedSection = '';
      }

      return paragraph;
    });

    if (!changed) {
      return block;
    }

    const nextText = nextParagraphs.join('\n\n');
    return {
      ...block,
      text: nextText,
      html: textToPreviewHtml(nextText),
    };
  });
}

function splitPreviewTextIntoParagraphs(value) {
  return String(value || '')
    .replace(/\r/g, '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function getAutoNumberedSectionKey(value) {
  const normalized = String(value || '').toLowerCase().replace(/[^a-z]+/g, ' ').trim();
  if (['objective', 'objectives', 'objective of the study', 'objectives of the study'].includes(normalized)) {
    return 'objectives';
  }
  if (['finding', 'findings', 'finding of the study', 'findings of the study'].includes(normalized)) {
    return 'findings';
  }
  if (['suggestion', 'suggestions'].includes(normalized)) {
    return 'suggestions';
  }
  return '';
}

function isAutoNumberedSectionItemText(value, sectionKey) {
  const text = String(value || '').trim();
  if (sectionKey === 'objectives') {
    return /^(?:\d+[.)]\s*)?to\s+\w+/i.test(text);
  }
  if (sectionKey === 'findings' || sectionKey === 'suggestions') {
    return /^(?:\d+[.)]\s*)?[A-Z0-9][\s\S]{12,}/.test(text) && !getAutoNumberedSectionKey(text);
  }
  return false;
}

function hasLeadingNumber(value) {
  return /^\d+[.)]\s+/.test(String(value || '').trim());
}

function isAutoNumberedSectionContinuationText(value, sectionKey) {
  return isAutoNumberedSectionItemText(value, sectionKey) || hasLeadingNumber(value);
}

function mergeAdjacentPreviewBlocks(blocks, preferredBlockId = '') {
  const mergeableTypes = new Set(['content', 'abstract', 'author_details', 'keywords']);
  const mergedBlocks = [];

  blocks.forEach((block) => {
    const lastBlock = mergedBlocks[mergedBlocks.length - 1];

    if (!lastBlock || lastBlock.type !== block.type || !mergeableTypes.has(block.type) || getPreviewListMergeKey(lastBlock) !== getPreviewListMergeKey(block)) {
      mergedBlocks.push(block);
      return;
    }

    const preferredBlockIsCurrent = block.id === preferredBlockId;
    const preferredBlockIsPrevious = lastBlock.id === preferredBlockId;
    const baseBlock = preferredBlockIsCurrent && !preferredBlockIsPrevious ? block : lastBlock;

    mergedBlocks[mergedBlocks.length - 1] = {
      ...baseBlock,
      text: joinPreviewBlockText(lastBlock.text, block.text),
      html: joinPreviewBlockHtml(lastBlock.html, block.html),
    };
  });

  return mergedBlocks;
}

function getPreviewListMergeKey(block) {
  const listConfig = block?.format?.list || {};
  if (!listConfig.enabled) {
    return 'no-list';
  }
  return `list:${listConfig.type || ''}:${listConfig.style || ''}`;
}

function mergeManualSplitHeadingsBackToContent(doc, rows, rules, preferredBlockId = '') {
  const targetIds = new Set((rows || []).map((row) => row?.blockId).filter(Boolean));
  if (!doc?.blocks?.length || targetIds.size === 0) {
    return null;
  }

  const consumedIndexes = new Set();
  let didMerge = false;
  const nextBlocks = [];

  doc.blocks.forEach((block, index) => {
    if (consumedIndexes.has(index)) {
      return;
    }

    const splitParts = getManualSplitBlockIdParts(block?.id);
    const isTargetManualHeading = targetIds.has(block?.id) && block?.source === 'manual' && block?.type?.startsWith('heading_');
    if (!isTargetManualHeading || !splitParts) {
      nextBlocks.push(block);
      return;
    }

    const groupIndexes = collectManualSplitGroupIndexes(doc.blocks, index, splitParts);
    if (groupIndexes.length <= 1) {
      nextBlocks.push(createSplitPreviewBlock(
        block,
        getPreviewBlockMergeText(block),
        'content',
        getRuleDrivenBlockFormat(rules, 'content'),
        block.id,
        'manual'
      ));
      didMerge = true;
      return;
    }

    const previousGroupCount = groupIndexes.filter((groupIndex) => groupIndex < index).length;
    if (previousGroupCount > 0) {
      nextBlocks.splice(Math.max(0, nextBlocks.length - previousGroupCount), previousGroupCount);
    }

    groupIndexes.forEach((groupIndex) => consumedIndexes.add(groupIndex));
    const groupBlocks = groupIndexes.map((groupIndex) => doc.blocks[groupIndex]);
    const targetBlock = groupBlocks.find((groupBlock) => groupBlock.id === block.id) || block;
    const mergedText = mergeManualSplitGroupText(groupBlocks, block.id);
    const mergedBlock = createSplitPreviewBlock(
      targetBlock,
      mergedText,
      'content',
      getRuleDrivenBlockFormat(rules, 'content'),
      splitParts.baseId,
      'manual'
    );

    nextBlocks.push({
      ...mergedBlock,
      source_debug_entries: groupBlocks.flatMap((groupBlock) =>
        Array.isArray(groupBlock?.source_debug_entries) ? groupBlock.source_debug_entries : []
      ),
    });
    didMerge = true;
  });

  if (!didMerge) {
    return null;
  }

  return reconcilePreviewDocument({
    ...doc,
    blocks: nextBlocks,
  }, rules, preferredBlockId, { disableCascade: true });
}

function mergeConvertedManualSplitGroups(blocks, targetIds, rules) {
  const nextBlocks = [];
  let index = 0;

  while (index < blocks.length) {
    const block = blocks[index];
    const splitParts = getManualSplitBlockIdParts(block?.id);
    if (!splitParts) {
      nextBlocks.push(block);
      index += 1;
      continue;
    }

    const groupBlocks = [];
    let groupIndex = index;
    while (groupIndex < blocks.length && isSameManualSplitGroup(blocks[groupIndex], splitParts)) {
      groupBlocks.push(blocks[groupIndex]);
      groupIndex += 1;
    }

    const shouldMergeGroup = groupBlocks.length > 1 && groupBlocks.some((groupBlock) => targetIds.has(groupBlock.id));
    if (!shouldMergeGroup) {
      nextBlocks.push(block);
      index += 1;
      continue;
    }

    const targetBlock = groupBlocks.find((groupBlock) => targetIds.has(groupBlock.id)) || groupBlocks[0];
    const mergedText = mergeManualSplitGroupText(groupBlocks, targetBlock.id);
    const mergedBlock = createSplitPreviewBlock(
      targetBlock,
      mergedText,
      'content',
      getRuleDrivenBlockFormat(rules, 'content'),
      splitParts.baseId,
      'manual'
    );

    nextBlocks.push({
      ...mergedBlock,
      source_debug_entries: groupBlocks.flatMap((groupBlock) =>
        Array.isArray(groupBlock?.source_debug_entries) ? groupBlock.source_debug_entries : []
      ),
    });
    index = groupIndex;
  }

  return nextBlocks;
}

function collectManualSplitGroupIndexes(blocks, targetIndex, targetParts) {
  const groupIndexes = [targetIndex];

  for (let index = targetIndex - 1; index >= 0; index -= 1) {
    if (!isSameManualSplitGroup(blocks[index], targetParts)) {
      break;
    }
    groupIndexes.unshift(index);
  }

  for (let index = targetIndex + 1; index < blocks.length; index += 1) {
    if (!isSameManualSplitGroup(blocks[index], targetParts)) {
      break;
    }
    groupIndexes.push(index);
  }

  return groupIndexes;
}

function isSameManualSplitGroup(block, targetParts) {
  const parts = getManualSplitBlockIdParts(block?.id);
  return Boolean(
    parts &&
    targetParts &&
    parts.baseId === targetParts.baseId &&
    parts.splitId === targetParts.splitId &&
    (block?.type === 'content' || block?.type?.startsWith('heading_'))
  );
}

function getManualSplitBlockIdParts(blockId) {
  const match = String(blockId || '').match(/^(.*)-(before|after|heading_1|heading_2|heading_3)-(\d+)$/);
  if (!match) {
    return null;
  }

  return {
    baseId: match[1],
    partType: match[2],
    splitId: match[3],
  };
}

function mergeManualSplitGroupText(groupBlocks, targetBlockId) {
  const beforeBlocks = [];
  const afterBlocks = [];
  let selectedBlock = null;

  groupBlocks.forEach((block) => {
    if (block.id === targetBlockId) {
      selectedBlock = block;
      return;
    }

    if (selectedBlock) {
      afterBlocks.push(block);
    } else {
      beforeBlocks.push(block);
    }
  });

  const beforeText = beforeBlocks.map(getPreviewBlockMergeText).filter(Boolean).join('\n\n');
  const selectedText = getPreviewBlockMergeText(selectedBlock);
  const afterText = afterBlocks.map(getPreviewBlockMergeText).filter(Boolean).join('\n\n');

  return joinManualSplitTextInline(beforeText, selectedText, afterText);
}

function joinManualSplitTextInline(beforeText, selectedText, afterText) {
  const beforeParagraphs = splitPreviewTextParagraphs(beforeText);
  const afterParagraphs = splitPreviewTextParagraphs(afterText);
  const selected = String(selectedText || '').trim();

  let mergedParagraph = selected;
  if (beforeParagraphs.length > 0) {
    mergedParagraph = joinInlineText(beforeParagraphs.pop(), mergedParagraph);
  }
  if (afterParagraphs.length > 0) {
    mergedParagraph = joinInlineText(mergedParagraph, afterParagraphs.shift());
  }

  return [...beforeParagraphs, mergedParagraph, ...afterParagraphs]
    .map((paragraph) => String(paragraph || '').trim())
    .filter(Boolean)
    .join('\n\n');
}

function splitPreviewTextParagraphs(value) {
  return String(value || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function joinInlineText(firstValue, secondValue) {
  const firstText = String(firstValue || '').trim();
  const secondText = String(secondValue || '').trim();
  if (!firstText) {
    return secondText;
  }
  if (!secondText) {
    return firstText;
  }
  if (/[-/([{]$/.test(firstText) || /^[,.;:!?%)\]}]/.test(secondText)) {
    return `${firstText}${secondText}`;
  }
  return `${firstText} ${secondText}`;
}

function joinPreviewBlockText(firstValue, secondValue) {
  const firstText = String(firstValue || '').trim();
  const secondText = String(secondValue || '').trim();
  if (!firstText) {
    return secondText;
  }
  if (!secondText) {
    return firstText;
  }
  return `${firstText}\n\n${secondText}`;
}

function joinPreviewBlockHtml(firstValue, secondValue) {
  const firstHtml = String(firstValue || '').trim();
  const secondHtml = String(secondValue || '').trim();
  if (!firstHtml) {
    return secondHtml;
  }
  if (!secondHtml) {
    return firstHtml;
  }
  return `${firstHtml}<br><br>${secondHtml}`;
}

function getPreviewBlockMergeText(block) {
  if (Array.isArray(block?.rows)) {
    return block.rows
      .map((row) => (Array.isArray(row) ? row.join('\t') : String(row || '')))
      .join('\n');
  }

  return String(block?.text || htmlToPlainText(block?.html || '') || '').trim();
}

function getPreviewBlockMergeHtml(block) {
  if (Array.isArray(block?.rows)) {
    return escapeHtml(getPreviewBlockMergeText(block)).replace(/\n/g, '<br>');
  }

  const html = String(block?.html || '').trim();
  return html || escapeHtml(String(block?.text || '').trim()).replace(/\n/g, '<br>');
}

function hasPreviewKeywordMarker(block) {
  return block?.type === 'keywords' || Boolean(block?.containsKeywords);
}

function enforceSingleInstancePreviewBlocks(blocks, rules, changedBlockId) {
  const changedBlock = blocks.find((block) => block.id === changedBlockId);
  if (!changedBlock || changedBlock.source !== 'manual' || !SINGLE_INSTANCE_BLOCK_TYPES.has(changedBlock.type)) {
    return blocks;
  }

  const contentFormat = getRuleDrivenBlockFormat(rules, 'content');

  return blocks.map((block) => {
    if (block.id === changedBlockId || block.type !== changedBlock.type) {
      return block;
    }

    return {
      ...block,
      type: 'content',
      level: 0,
      label: formatBlockLabel('content'),
      source: 'manual-reset',
      format: {
        ...contentFormat,
      },
    };
  });
}

function promoteSectionChildHeadings(blocks, rules, changedBlockId) {
  const changedBlock = blocks.find((block) => block.id === changedBlockId);
  if (!changedBlock || changedBlock.type !== 'heading_1' || changedBlock.source !== 'manual') {
    return blocks;
  }

  const heading1Format = getRuleDrivenBlockFormat(rules, 'heading_1');
  const changedIndex = blocks.findIndex((block) => block.id === changedBlockId);

  const nextBlocks = [];

  blocks.forEach((block, index) => {
    if (index <= changedIndex) {
      nextBlocks.push(block);
      return;
    }

    if (block.type === 'table' || block.type === 'figure') {
      nextBlocks.push(block);
      return;
    }

    if (block.type === 'heading_1') {
      nextBlocks.push(block);
      return;
    }

    if (block.type?.startsWith('heading_') || looksLikeHeadingSequenceCandidate(block.text)) {
      nextBlocks.push(convertBlockToHeading1(block, heading1Format));
      return;
    }

    const splitBlocks = splitContentBlockIntoHeadingSequence(block, heading1Format);
    splitBlocks.forEach((splitBlock) => nextBlocks.push(splitBlock));
  });

  return nextBlocks;
}

function convertBlockToHeading1(block, heading1Format) {
  return {
    ...block,
    type: 'heading_1',
    level: 1,
    label: formatBlockLabel('heading_1'),
    source: 'manual',
    text: sanitizeHeadingText(block.text || block.html || ''),
    html: sanitizeHeadingHtml(block.html || block.text || ''),
    format: heading1Format,
  };
}

function splitContentBlockIntoHeadingSequence(block, heading1Format) {
  if (block.type !== 'content') {
    return [block];
  }

  const paragraphs = String(block.text || '')
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (paragraphs.length <= 1) {
    return [block];
  }

   // Keep body content together instead of breaking one content block into many UI sections.
   // We only split when every paragraph strongly looks like a heading-like sequence.
  const allParagraphsLookLikeHeadings = paragraphs.every((paragraphText) => looksLikeHeadingSequenceCandidate(paragraphText));
  if (!allParagraphsLookLikeHeadings) {
    return [block];
  }

  return paragraphs.map((paragraphText, index) => {
    const nextBlock = {
      ...block,
      id: `${block.id}-split-${index + 1}`,
      text: paragraphText,
      html: buildHeadingHtmlFromText(paragraphText),
    };

    return looksLikeHeadingSequenceCandidate(paragraphText)
      ? convertBlockToHeading1(nextBlock, heading1Format)
      : nextBlock;
  });
}

function looksLikeHeadingSequenceCandidate(value) {
  const text = String(value || '').trim();
  if (!text) {
    return false;
  }

  if (text.length > 120) {
    return false;
  }

  if (/\.$/.test(text) && text.split(/\s+/).length > 8) {
    return false;
  }

  return (
    /^\d+(?:\.\d+)*(?:[.)]|\s)\s*\S/.test(text)
    || /^[A-Za-z][.)]\s+\S/.test(text)
  );
}
function assignPreviewHeadingHierarchy(blocks) {
  const headingStack = {};
  const headingCounters = { 1: 0, 2: 0, 3: 0 };

  return blocks.map((block) => {
    const level = getHeadingLevel(block.type);
    const nextBlock = {
      ...block,
      level,
      parentId: '',
      renderPrefix: '',
      source: block.source || (level ? 'rule' : 'fallback'),
    };

    if (!level) {
      return nextBlock;
    }

    for (let staleLevel = level; staleLevel <= 3; staleLevel += 1) {
      delete headingStack[staleLevel];
    }

    headingCounters[level] += 1;
    for (let nextLevel = level + 1; nextLevel <= 3; nextLevel += 1) {
      headingCounters[nextLevel] = 0;
    }

    for (let parentLevel = level - 1; parentLevel >= 1; parentLevel -= 1) {
      if (headingStack[parentLevel]) {
        nextBlock.parentId = headingStack[parentLevel];
        break;
      }
    }

    headingStack[level] = block.id;
    nextBlock.renderPrefix = resolveHeadingPrefixFromCounters(
      nextBlock.format?.numbering || {},
      level,
      headingCounters
    );
    return nextBlock;
  });
}

function normalizeHeadingContent(value) {
  const plainText = String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/div>\s*<div[^>]*>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\r/g, '\n');

  const lines = plainText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !isStandaloneHeadingNumber(line));

  if (lines.length === 0) {
    return '';
  }

  const normalizedLines = [...lines];
  normalizedLines[0] = removeLeadingHeadingNumber(normalizedLines[0]);

  return normalizedLines
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function isStandaloneHeadingNumber(value) {
  const token = String(value || '').trim();
  if (!token) {
    return false;
  }

  const decimalPattern = /^\d+(?:\.\d+)*[.)]?$/;
  const romanPattern = /^(?:M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})|m{0,4}(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3}))[.)]?$/;
  const alphaPattern = /^[A-Za-z][.)]$/;

  return decimalPattern.test(token) || romanPattern.test(token) || alphaPattern.test(token);
}

function buildHeadingHtmlFromText(text) {
  return escapeHtml(String(text || '')).replace(/\n/g, '<br>');
}

function getHeadingLevel(blockType) {
  if (blockType === 'heading_1') {
    return 1;
  }
  if (blockType === 'heading_2') {
    return 2;
  }
  if (blockType === 'heading_3') {
    return 3;
  }
  return 0;
}

function getHeadingCountersForBlock(blocks, currentBlock) {
  const counters = { 1: 0, 2: 0, 3: 0, chapter: 1, chapterCount: 0 };

  for (const block of blocks) {
    if (block.type === 'title') {
      if (isChapterTitleBlock(block)) {
        counters.chapterCount += 1;
        counters.chapter = resolveChapterNumberFromTitle(block, counters.chapterCount);
      }
      counters[1] = 0;
      counters[2] = 0;
      counters[3] = 0;
      if (block.id === currentBlock?.id) {
        break;
      }
      continue;
    }

    const level = getHeadingLevel(block.type);
    if (!level) {
      if (block.id === currentBlock?.id) {
        break;
      }
      continue;
    }

    counters[level] += 1;
    for (let nextLevel = level + 1; nextLevel <= 3; nextLevel += 1) {
      counters[nextLevel] = 0;
    }

    if (block.id === currentBlock?.id) {
      return counters;
    }
  }

  return counters;
}

function isChapterTitleBlock(block) {
  return looksLikeChapterTitle(block?.text) || looksLikeChapterTitle(stripHtmlTagsPreservingLineBreaks(block?.html || ''));
}

function resolveChapterNumberFromTitle(block, fallback) {
  const sources = [block?.text, stripHtmlTagsPreservingLineBreaks(block?.html || '')];
  for (const source of sources) {
    const match = String(source || '').match(/\bchapter\s*-?\s*(\d+)\b/i);
    if (match) {
      return Math.max(1, Number.parseInt(match[1], 10) || fallback || 1);
    }
  }
  return Math.max(1, fallback || 1);
}

function looksLikeChapterTitle(value) {
  const normalized = String(value || '').replace(/\u00a0/g, ' ').trim();
  if (!normalized) {
    return false;
  }
  if (/^chapter\s*-?\s*(?:\d+|[ivxlcdm]+)\s*$/i.test(normalized)) {
    return true;
  }
  const match = normalized.match(/^chapter\s*-?\s*(?:\d+|[ivxlcdm]+)\b(.*)$/i);
  if (!match) {
    return false;
  }
  const rest = String(match[1] || '').trim().replace(/^[:.\-\s]+|[:.\-\s]+$/g, '');
  if (!rest) {
    return true;
  }
  const words = rest.match(/[A-Za-z0-9]+/g) || [];
  if (words.length > 8) {
    return false;
  }
  if (/\b(gives|explains|discusses|describes|presents|shows|covers|introduces)\b/i.test(rest)) {
    return false;
  }
  return !/[.!?]\s*$/.test(rest);
}

function getHeadingRenderPrefix(blocks, currentBlock) {
  if (!currentBlock || !currentBlock.type?.startsWith('heading_')) {
    return '';
  }

  return resolveHeadingPrefixForBlock(blocks, currentBlock);
}

function resolveHeadingPrefixForBlock(blocks, currentBlock) {
  const level = getHeadingLevel(currentBlock?.type);
  if (!level) {
    return '';
  }

  const numbering = currentBlock.format?.numbering || {};
  if (!numbering.enabled || numbering.style === 'none') {
    return '';
  }

  if (numbering.style === 'custom') {
    return numbering.raw || '';
  }

  const counters = getHeadingCountersForBlock(blocks, currentBlock);
  const value = counters[level] || 0;
  if (!value) {
    return '';
  }

  return resolveHeadingPrefixFromCounters(numbering, level, counters);
}

function resolveHeadingPrefixFromCounters(numbering, level, counters) {
  if (!numbering?.enabled || numbering.style === 'none') {
    return '';
  }

  if (numbering.style === 'custom') {
    return numbering.raw || '';
  }

  if (numbering.style === 'decimal-nested') {
    const parts = [];
    for (let currentLevel = 1; currentLevel <= level; currentLevel += 1) {
      if (counters[currentLevel]) {
        parts.push(counters[currentLevel]);
      }
    }
    return parts.join('.') || '';
  }

  const value = counters[level] || 0;
  return value ? formatNumber(numbering.style, value) : '';
}

function isHeadingAfterKeywords(blocks, currentBlock) {
  if (!Array.isArray(blocks) || !currentBlock?.id) {
    return false;
  }

  let keywordsSeen = false;
  for (const block of blocks) {
    if (hasPreviewKeywordMarker(block)) {
      keywordsSeen = true;
    }

    if (block.id === currentBlock.id) {
      return keywordsSeen;
    }
  }

  return false;
}

function groupPreviewBlocks(blocks, combineFrontMatterBox = false) {
  const groups = [];

  blocks.forEach((block) => {
    const lastGroup = groups[groups.length - 1];

    if (combineFrontMatterBox && block.type === 'keywords' && lastGroup?.type === 'abstract') {
      lastGroup.blocks.push(block);
    } else if (!lastGroup || lastGroup.type !== block.type) {
      groups.push({
        type: block.type,
        blocks: [block],
      });
    } else {
      lastGroup.blocks.push(block);
    }
  });

  return groups;
}

function getPreviewLayoutGroups(blocks, templateColumns, combineFrontMatterBox = false) {
  const groupedBlocks = groupPreviewBlocks(blocks, combineFrontMatterBox);
  if (templateColumns !== 2) {
    return {
      singleColumnGroups: groupedBlocks,
      twoColumnGroups: [],
    };
  }

  const twoColumnStartIndex = getTwoColumnStartIndex(blocks);
  if (twoColumnStartIndex <= 0) {
    return {
      singleColumnGroups: [],
      twoColumnGroups: groupedBlocks,
    };
  }

  if (twoColumnStartIndex >= blocks.length) {
    return {
      singleColumnGroups: groupedBlocks,
      twoColumnGroups: [],
    };
  }

  return {
    singleColumnGroups: groupPreviewBlocks(blocks.slice(0, twoColumnStartIndex), combineFrontMatterBox),
    twoColumnGroups: groupPreviewBlocks(blocks.slice(twoColumnStartIndex), combineFrontMatterBox),
  };
}

function getAppliedRuleTableRows(previewDocument) {
  const blocks = Array.isArray(previewDocument?.blocks) ? previewDocument.blocks : [];
  const debugBlocks = Array.isArray(previewDocument?.debug_raw_blocks) ? previewDocument.debug_raw_blocks : [];
  const usedDebugBlockIndexes = new Set();
  const rows = [];

  blocks.forEach((block, blockIndex) => {
    if (block?.type === 'figure' || block?.type === 'references' || block?.type === 'table') {
      return;
    }

    const originalEntry = findOriginalAppliedRuleEntry(block, debugBlocks, usedDebugBlockIndexes);
    getAppliedRuleTextLines(block, originalEntry?.text || '').forEach((line, lineIndex) => {
      rows.push({
        id: `${block.id || blockIndex}-${lineIndex}`,
        blockId: block.id,
        ruleType: block.type || 'content',
        ruleName: getAppliedRuleName(block.type),
        text: line,
        patternStyle: originalEntry
          ? getAppliedRulePatternStyle(line, originalEntry.formatSignature)
          : 'Not detected',
        formatSignature: originalEntry?.formatSignature || null,
        originalIndex: originalEntry?.debugIndex ?? Number.POSITIVE_INFINITY,
        sourceDebugIndex: Number.isFinite(originalEntry?.debugIndex) ? originalEntry.debugIndex + 1 : null,
      });
    });
  });

  appendUnmatchedOriginalRuleRows(rows, debugBlocks, usedDebugBlockIndexes, blocks);

  return rows.sort((left, right) => {
    const leftIndex = Number.isFinite(left.originalIndex) ? left.originalIndex : Number.POSITIVE_INFINITY;
    const rightIndex = Number.isFinite(right.originalIndex) ? right.originalIndex : Number.POSITIVE_INFINITY;
    return leftIndex - rightIndex;
  });
}

function findOriginalAppliedRuleEntry(block, debugBlocks, usedDebugBlockIndexes) {
  if (!block || !Array.isArray(debugBlocks) || debugBlocks.length === 0) {
    return null;
  }

  const blockText = normalizeAppliedRuleComparableText(block.text || htmlToPlainText(block.html || ''));
  if (!blockText) {
    return null;
  }

  const blockType = String(block.type || '').toLowerCase();
  const isUnusedDebugBlock = (_debugBlock, debugIndex) => !usedDebugBlockIndexes.has(debugIndex);
  const isSameType = (debugBlock) => String(debugBlock?.type || '').toLowerCase() === blockType;
  const hasExactTextMatch = (debugBlock) => {
    const { rawText, finalText } = getOriginalDebugComparableTexts(debugBlock);
    return stripAppliedRuleNumbering(rawText) === blockText || stripAppliedRuleNumbering(finalText) === blockText;
  };
  const hasPartialTextMatch = (debugBlock) => {
    const { rawText, finalText } = getOriginalDebugComparableTexts(debugBlock);
    return [rawText, finalText].some((candidate) => {
      const comparable = stripAppliedRuleNumbering(candidate);
      return Boolean(
        comparable &&
        blockText &&
        (comparable.includes(blockText) || blockText.includes(comparable))
      );
    });
  };

  let matchedIndex = debugBlocks.findIndex((debugBlock, debugIndex) => {
    if (usedDebugBlockIndexes.has(debugIndex)) {
      return false;
    }
    return isSameType(debugBlock) && hasExactTextMatch(debugBlock);
  });

  if (matchedIndex === -1) {
    matchedIndex = debugBlocks.findIndex((debugBlock, debugIndex) => isUnusedDebugBlock(debugBlock, debugIndex) && hasExactTextMatch(debugBlock));
  }

  if (matchedIndex === -1) {
    matchedIndex = debugBlocks.findIndex((debugBlock, debugIndex) => isUnusedDebugBlock(debugBlock, debugIndex) && isSameType(debugBlock) && hasPartialTextMatch(debugBlock));
  }

  if (matchedIndex === -1) {
    matchedIndex = debugBlocks.findIndex((debugBlock, debugIndex) => isUnusedDebugBlock(debugBlock, debugIndex) && hasPartialTextMatch(debugBlock));
  }

  if (matchedIndex === -1) {
    return null;
  }

  usedDebugBlockIndexes.add(matchedIndex);
  return {
    text: debugBlocks[matchedIndex]?.raw_text || debugBlocks[matchedIndex]?.final_text || '',
    formatSignature: debugBlocks[matchedIndex]?.format_signature || null,
    debugIndex: matchedIndex,
  };
}

function appendUnmatchedOriginalRuleRows(rows, debugBlocks, usedDebugBlockIndexes, blocks = []) {
  if (!Array.isArray(debugBlocks) || debugBlocks.length === 0) {
    return;
  }

  debugBlocks.forEach((debugBlock, debugIndex) => {
    if (usedDebugBlockIndexes.has(debugIndex)) {
      return;
    }

    const blockType = String(debugBlock?.type || '').toLowerCase();
    if (!blockType || blockType === 'figure' || blockType === 'references' || blockType === 'table') {
      return;
    }

    const originalText = debugBlock?.raw_text || debugBlock?.final_text || '';
    const originalLines = getAppliedRuleTextLines({ text: originalText }, originalText);
    originalLines.forEach((line, lineIndex) => {
      const matchedBlockId = findPreviewBlockIdForOriginalLine(blocks, line);
      rows.push({
        id: `original-${debugIndex}-${lineIndex}`,
        blockId: matchedBlockId,
        ruleType: blockType || 'content',
        ruleName: getAppliedRuleName(blockType),
        text: line,
        patternStyle: getAppliedRulePatternStyle(line, debugBlock?.format_signature || null),
        formatSignature: debugBlock?.format_signature || null,
        originalIndex: debugIndex,
        sourceDebugIndex: debugIndex + 1,
      });
    });
  });
}

function findPreviewBlockIdForOriginalLine(blocks, line, options = {}) {
  const targetText = normalizeAppliedRuleComparableText(line);
  if (!targetText || !Array.isArray(blocks)) {
    return '';
  }

  const sourceDebugIndex = Number(options.sourceDebugIndex);
  if (Number.isFinite(sourceDebugIndex)) {
    const sourceMatchedBlock = blocks.find((block) =>
      Array.isArray(block?.source_debug_entries) &&
      block.source_debug_entries.some((entry) => Number(entry?.index) === sourceDebugIndex)
    );
    if (sourceMatchedBlock?.id) {
      return sourceMatchedBlock.id;
    }
  }

  const targetRawText = normalizeAppliedRuleText(line);
  const targetWithoutNumbering = stripAppliedRuleNumbering(targetText);
  const allowPartialMatch = isSafeAppliedRulePartialMatchText(targetText);
  const allowHeadingPhraseMatch = options.allowHeadingPhraseMatch && looksLikeAppliedRuleHeadingText(targetRawText);
  const matchedBlock = blocks.find((block) => {
    if (block?.type === 'figure' || block?.type === 'references') {
      return false;
    }

    const blockRawText = normalizeAppliedRuleText(block?.text || htmlToPlainText(block?.html || ''));
    const blockText = normalizeAppliedRuleComparableText(blockRawText);
    const blockWithoutNumbering = stripAppliedRuleNumbering(blockText);
    const hasNumberingStrippedText = Boolean(blockWithoutNumbering && targetWithoutNumbering);
    return Boolean(
      blockText &&
      (
        blockText === targetText ||
        (hasNumberingStrippedText && blockWithoutNumbering === targetWithoutNumbering) ||
        (allowPartialMatch && blockText.includes(targetText)) ||
        (allowPartialMatch && targetText.includes(blockText)) ||
        (allowPartialMatch && hasNumberingStrippedText && blockWithoutNumbering.includes(targetWithoutNumbering)) ||
        (allowPartialMatch && hasNumberingStrippedText && targetWithoutNumbering.includes(blockWithoutNumbering)) ||
        (allowHeadingPhraseMatch && findAppliedRuleShortHeadingPhraseMatch(blockRawText, targetRawText, 'case-sensitive-heading'))
      )
    );
  });

  return matchedBlock?.id || '';
}

function isSafeAppliedRulePartialMatchText(value) {
  const text = normalizeAppliedRuleText(value);
  if (!text) {
    return false;
  }

  const wordCount = (text.match(/[A-Za-z0-9]+/g) || []).length;
  return text.length >= 20 || wordCount >= 4;
}

function getOriginalDebugComparableTexts(debugBlock) {
  return {
    rawText: normalizeAppliedRuleComparableText(debugBlock?.raw_text || ''),
    finalText: normalizeAppliedRuleComparableText(debugBlock?.final_text || ''),
  };
}

function dedupeAppliedRuleRows(rows) {
  const seen = new Set();

  return (rows || []).filter((row) => {
    const key = `${row?.blockId || ''}::${row?.id || ''}::${row?.text || ''}`;
    if (!row?.blockId || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function resolveAppliedRuleRowsForBlocks(rows, blocks, nextBlockType = '') {
  return (rows || []).map((row) => {
    if (row?.blockId) {
      return row;
    }

    const resolvedBlockId = findPreviewBlockIdForOriginalLine(blocks, row?.text || '', {
      allowHeadingPhraseMatch: Boolean(nextBlockType?.startsWith('heading_')),
      sourceDebugIndex: row?.sourceDebugIndex,
    });
    return resolvedBlockId ? { ...row, blockId: resolvedBlockId } : row;
  });
}

function areAppliedRuleRowsSimilarForChange(candidateRow, sourceRow, nextBlockType) {
  if (!candidateRow || !sourceRow) {
    return false;
  }

  if (nextBlockType?.startsWith('heading_')) {
    return areAppliedRuleRowsSameExactHeadingStyle(candidateRow, sourceRow);
  }

  if (candidateRow.patternStyle !== sourceRow.patternStyle) {
    return false;
  }

  return true;
}

function isPreviewNumberedListRow(row, previewDocument) {
  const block = (previewDocument?.blocks || []).find((candidate) => candidate?.id === row?.blockId);
  if (!block) {
    return false;
  }

  if (block.source === 'numbered-list' || block.format?.list?.enabled) {
    return true;
  }

  return (block.source_debug_entries || []).some((entry) => (
    entry?.source === 'numbered-list' ||
    entry?.list_label ||
    (entry?.list_level !== undefined && entry?.list_level !== null && entry?.list_level !== '') ||
    entry?.format_signature?.list_level
  ));
}

function areAppliedRuleRowsSameExactHeadingStyle(candidateRow, sourceRow) {
  const candidateText = normalizeAppliedRuleText(candidateRow?.text || '');
  const sourceText = normalizeAppliedRuleText(sourceRow?.text || '');
  if (!looksLikeAppliedRuleHeadingText(candidateText) || !looksLikeAppliedRuleHeadingText(sourceText)) {
    return false;
  }

  if (isAppliedRuleHeadingFalsePositiveText(candidateText) || isAppliedRuleHeadingFalsePositiveText(sourceText)) {
    return false;
  }

  const candidateNumbering = getAppliedRuleLeadingNumberingKind(candidateText);
  const sourceNumbering = getAppliedRuleLeadingNumberingKind(sourceText);
  if (candidateNumbering !== sourceNumbering) {
    return false;
  }

  const candidateSignature = candidateRow?.formatSignature || null;
  const sourceSignature = sourceRow?.formatSignature || null;
  if (candidateSignature && sourceSignature) {
    return areAppliedRuleFormatSignaturesSame(candidateSignature, sourceSignature);
  }

  return candidateRow.patternStyle === sourceRow.patternStyle;
}

function areAppliedRuleFormatSignaturesSame(candidateSignature, sourceSignature) {
  const keys = [
    'font_family',
    'font_size',
    'bold',
    'italic',
    'underline',
    'align',
    'spacing_before',
    'spacing_after',
    'line_spacing',
    'left_indent',
    'first_line_indent',
    'list_level',
    'list_format',
    'style_name',
  ];

  return keys.every((key) => normalizeAppliedRuleSignatureValue(candidateSignature?.[key]) === normalizeAppliedRuleSignatureValue(sourceSignature?.[key]));
}

function normalizeAppliedRuleSignatureValue(value) {
  return String(value ?? '').trim().toLowerCase();
}

function isAppliedRuleHeadingFalsePositiveText(value) {
  const text = normalizeAppliedRuleText(value);
  if (!text) {
    return true;
  }

  const headingText = stripAppliedRuleNumbering(text);
  const words = headingText.match(/[A-Za-z0-9]+/g) || [];
  const firstWordWithLetter = words.find((word) => /[A-Za-z]/.test(word)) || '';
  if (firstWordWithLetter && !/^[A-Z]/.test(firstWordWithLetter)) {
    return true;
  }

  if (/^(input|output|table|figure|fig|algorithm|step)\s*[:.]/i.test(text)) {
    return true;
  }

  if (/[-=]{2,}|←|->|=>|≤|≥|[{}[\]]/.test(text)) {
    return true;
  }

  if (/\b(eq|equation)\.?\s*\d+/i.test(text)) {
    return true;
  }

  return false;
}

function getAppliedRuleLeadingNumberingKind(value) {
  const text = normalizeAppliedRuleText(value);
  if (/^\d+(?:\.\d+)*(?:[.)]|\s)\s+\S/.test(text)) {
    return 'decimal';
  }
  if (/^[IVXLCDM]+(?:[.)]|\s)\s+\S/i.test(text)) {
    return 'roman';
  }
  if (/^[A-Za-z][.)]\s+\S/.test(text)) {
    return 'alpha';
  }
  return '';
}

function looksLikeAppliedRuleHeadingText(value) {
  const text = normalizeAppliedRuleText(value);
  if (!text || text.length > 120) {
    return false;
  }

  return looksLikeHeadingSequenceCandidate(text);
}

function applyAppliedRuleRowsToBlock(block, rows, nextBlockType, rules) {
  if (!block || !Array.isArray(rows) || rows.length === 0 || !nextBlockType) {
    return [block];
  }

  const blockText = String(block.text || htmlToPlainText(block.html || '') || '');
  if (!blockText.trim()) {
    return [block];
  }

  const nextFormat = getRuleDrivenBlockFormat(rules, nextBlockType);
  const sourceSplitBlocks = applyAppliedRuleRowsToSourceDebugEntries(block, rows, nextBlockType, nextFormat);
  if (sourceSplitBlocks) {
    return sourceSplitBlocks;
  }
  if (rows.some((row) => Number.isFinite(Number(row?.sourceDebugIndex)))) {
    return [block];
  }

  let fragments = [{
    id: block.id,
    text: blockText,
    type: block.type || 'content',
    format: block.format,
    source: block.source,
  }];

  rows.forEach((row, rowIndex) => {
    const rowText = normalizeAppliedRuleText(row?.text || '');
    if (!rowText) {
      return;
    }

    fragments = fragments.flatMap((fragment, fragmentIndex) => {
      if (fragment.type === nextBlockType) {
        return [fragment];
      }

      const match = findAppliedRuleRowTextMatch(fragment.text, rowText, {
        phraseMatchMode: nextBlockType?.startsWith('heading_') ? 'case-sensitive-heading' : 'case-insensitive',
      });
      if (!match) {
        return [fragment];
      }

      const beforeText = fragment.text.slice(0, match.start).trim();
      const matchedText = fragment.text.slice(match.start, match.end).trim();
      const selectedText = nextBlockType?.startsWith('heading_') && !nextFormat?.numbering?.enabled
        ? stripAppliedRuleNumbering(matchedText)
        : matchedText;
      const afterText = fragment.text.slice(match.end).trim();
      const splitBaseId = `${block.id}-rule-${rowIndex + 1}-${fragmentIndex + 1}`;

      return [
        beforeText ? { ...fragment, id: `${splitBaseId}-before`, text: beforeText } : null,
        selectedText
          ? {
              id: `${splitBaseId}-${nextBlockType}`,
              text: selectedText,
              type: nextBlockType,
              format: nextFormat,
              source: 'manual',
            }
          : null,
        afterText ? { ...fragment, id: `${splitBaseId}-after`, text: afterText } : null,
      ].filter(Boolean);
    });
  });

  return fragments.map((fragment) =>
    createSplitPreviewBlock(
      block,
      fragment.text,
      fragment.type,
      fragment.type === nextBlockType ? nextFormat : fragment.format,
      fragment.id,
      fragment.source
    )
  );
}

function applyAppliedRuleRowsToSourceDebugEntries(block, rows, nextBlockType, nextFormat) {
  const sourceEntries = Array.isArray(block?.source_debug_entries) ? block.source_debug_entries : [];
  const targetSourceIndexes = new Set(
    (rows || [])
      .map((row) => Number(row?.sourceDebugIndex))
      .filter((index) => Number.isFinite(index))
  );

  if (sourceEntries.length === 0 || targetSourceIndexes.size === 0) {
    return null;
  }

  const matchedEntries = sourceEntries.filter((entry) => targetSourceIndexes.has(Number(entry?.index)));
  if (matchedEntries.length === 0) {
    return null;
  }

  return sourceEntries.map((entry, entryIndex) => {
    const entrySourceText = normalizeAppliedRuleText(
      nextBlockType === 'content'
        ? (entry?.raw_text || entry?.text || entry?.final_text || '')
        : (entry?.raw_text || entry?.text || entry?.final_text || '')
    );
    const isTargetEntry = targetSourceIndexes.has(Number(entry?.index));
    const originalEntryType = entry?.type || block.type || 'content';
    const entryType = isTargetEntry ? nextBlockType : originalEntryType;
    const entryText = isTargetEntry && nextBlockType === 'content' && String(originalEntryType || '').startsWith('heading_')
      ? normalizeHeadingConvertedToContentText(entrySourceText)
      : isTargetEntry && nextBlockType?.startsWith('heading_') && !nextFormat?.numbering?.enabled
        ? stripAppliedRuleNumbering(entrySourceText)
        : entrySourceText;
    const entryFormat = isTargetEntry
      ? nextFormat
      : (entry?.format || block.format);
    return {
      ...createSplitPreviewBlock(
        block,
        entryText,
        entryType,
        entryFormat,
        `${block.id}-source-${entry?.index || entryIndex + 1}-${entryType}`,
        isTargetEntry ? 'manual' : (entry?.source || block.source)
      ),
      source_debug_entries: [entry],
    };
  });
}

function findAppliedRuleRowTextMatch(blockText, rowText, options = {}) {
  const sourceText = String(blockText || '');
  const targetText = String(rowText || '').trim();
  if (!sourceText.trim() || !targetText) {
    return null;
  }

  const sourceComparable = normalizeAppliedRuleText(sourceText).toLowerCase();
  const targetComparable = normalizeAppliedRuleText(targetText).toLowerCase();
  if (sourceComparable === targetComparable) {
    return { start: 0, end: sourceText.length };
  }

  const sourceWithoutNumbering = stripAppliedRuleNumbering(sourceComparable);
  const targetWithoutNumbering = stripAppliedRuleNumbering(targetComparable);
  if (sourceWithoutNumbering && targetWithoutNumbering && sourceWithoutNumbering === targetWithoutNumbering) {
    return { start: 0, end: sourceText.length };
  }

  const exactLineMatch = findAppliedRuleExactLineMatch(sourceText, targetText);
  if (exactLineMatch) {
    return exactLineMatch;
  }

  if (options.phraseMatchMode) {
    const shortHeadingPhraseMatch = findAppliedRuleShortHeadingPhraseMatch(sourceText, targetText, options.phraseMatchMode);
    if (shortHeadingPhraseMatch) {
      return shortHeadingPhraseMatch;
    }
  }

  if (!isSafeAppliedRulePartialMatchText(targetText)) {
    return null;
  }

  const directIndex = sourceText.toLowerCase().indexOf(targetText.toLowerCase());
  if (directIndex !== -1) {
    return { start: directIndex, end: directIndex + targetText.length };
  }

  const whitespaceFlexiblePattern = targetText.split(/\s+/).map(escapeRegExp).join('\\s+');
  const flexibleMatch = new RegExp(whitespaceFlexiblePattern, 'i').exec(sourceText);
  if (flexibleMatch?.index !== undefined) {
    return { start: flexibleMatch.index, end: flexibleMatch.index + flexibleMatch[0].length };
  }

  return null;
}

function findAppliedRuleShortHeadingPhraseMatch(sourceText, targetText, mode = 'case-insensitive') {
  const source = String(sourceText || '');
  const target = normalizeAppliedRuleText(targetText);
  if (!source.trim() || !looksLikeAppliedRuleHeadingText(target)) {
    return null;
  }

  if (mode === 'case-sensitive-heading' && isAppliedRuleHeadingFalsePositiveText(target)) {
    return null;
  }

  const flags = mode === 'case-sensitive-heading' ? '' : 'i';
  const phrasePattern = new RegExp(`(^|[^A-Za-z0-9])(${escapeRegExp(target)})(?=$|[^A-Za-z0-9])`, flags);
  const match = phrasePattern.exec(source);
  if (!match?.[2]) {
    return null;
  }

  const prefixLength = match[1]?.length || 0;
  const start = match.index + prefixLength;
  const end = start + match[2].length;
  if (mode === 'case-sensitive-heading' && !isStandaloneHeadingPhraseOccurrence(source, start, end)) {
    return null;
  }

  return {
    start,
    end,
  };
}

function isStandaloneHeadingPhraseOccurrence(sourceText, start, end) {
  const source = String(sourceText || '');
  const lineStart = Math.max(source.lastIndexOf('\n', start - 1), source.lastIndexOf('\r', start - 1)) + 1;
  const nextNewlineIndexes = [source.indexOf('\n', end), source.indexOf('\r', end)].filter((index) => index !== -1);
  const lineEnd = nextNewlineIndexes.length > 0 ? Math.min(...nextNewlineIndexes) : source.length;
  const beforeOnLine = source.slice(lineStart, start).trim();
  const afterOnLine = source.slice(end, lineEnd).trim();

  const hasOnlyOptionalNumberingBefore = !beforeOnLine || /^(?:\d+(?:\.\d+)*|[A-Za-z]|[IVXLCDMivxlcdm]+)[.)]?$/.test(beforeOnLine);
  return hasOnlyOptionalNumberingBefore && !afterOnLine;
}

function findAppliedRuleExactLineMatch(sourceText, targetText) {
  const source = String(sourceText || '');
  const targetComparable = normalizeAppliedRuleComparableText(targetText);
  if (!source.trim() || !targetComparable) {
    return null;
  }

  const linePattern = /[^\r\n]+/g;
  let match;
  while ((match = linePattern.exec(source)) !== null) {
    const lineText = match[0];
    const trimmedLineText = lineText.trim();
    if (!trimmedLineText) {
      continue;
    }

    if (normalizeAppliedRuleComparableText(trimmedLineText) === targetComparable) {
      const leadingWhitespaceLength = lineText.search(/\S/);
      const start = match.index + (leadingWhitespaceLength === -1 ? 0 : leadingWhitespaceLength);
      return {
        start,
        end: start + trimmedLineText.length,
      };
    }
  }

  return null;
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeAppliedRuleComparableText(value) {
  return stripAppliedRuleNumbering(normalizeAppliedRuleText(value)).toLowerCase();
}

function getAppliedRuleTextLines(block, originalText = '') {
  if (Array.isArray(block?.rows)) {
    return block.rows
      .map((row) => (Array.isArray(row) ? row.join(' | ') : String(row || '')))
      .map(normalizeAppliedRuleText)
      .filter(isAppliedRuleSingleLineText)
      .filter(Boolean);
  }

  const rawText = String(originalText || block?.text || htmlToPlainText(block?.html || ''));
  return rawText
    .split(/\r?\n+/)
    .map(normalizeAppliedRuleText)
    .filter(isAppliedRuleSingleLineText)
    .filter(Boolean);
}

function normalizeAppliedRuleText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function isAppliedRuleSingleLineText(value) {
  const text = normalizeAppliedRuleText(value);
  if (!text) {
    return false;
  }

  return text.length <= 80;
}

function getAppliedRuleName(blockType) {
  const normalized = String(blockType || '').trim().toLowerCase();
  if (normalized === 'heading_1') return 'H1';
  if (normalized === 'heading_2') return 'H2';
  if (normalized === 'heading_3') return 'H3';
  if (normalized === 'content') return 'Content';
  if (normalized === 'table_title') return 'Table Title';
  if (normalized === 'figure_title') return 'Figure Title';
  return formatBlockLabel(normalized || 'content');
}

function getAppliedRulePatternStyle(text, originalFormatSignature = null) {
  const parts = [];
  const normalizedText = normalizeAppliedRuleText(text);
  const numberingLabel = getNumberingPatternLabel(normalizedText);
  const contentText = stripAppliedRuleNumbering(normalizedText);
  const styleSource = originalFormatSignature || {};

  if (numberingLabel) {
    parts.push(numberingLabel);
  }

  if (isAppliedRuleStyleEnabled(styleSource.bold)) {
    parts.push('Bold');
  }
  if (isAppliedRuleStyleEnabled(styleSource.italic)) {
    parts.push('Italic');
  }
  if (isAppliedRuleStyleEnabled(styleSource.underline)) {
    parts.push('Underline');
  }

  const caseLabel = getTextCasePatternLabel(contentText);
  if (caseLabel) {
    parts.push(caseLabel);
  }

  return parts.length > 0 ? parts.join(', ') : 'Not detected';
}

function isAppliedRuleStyleEnabled(value) {
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return Boolean(value);
}

function getNumberingPatternLabel(text) {
  if (/^\s*\d+\.\d+(?:\.\d+)*(?:[.)]\s*|\s+)/u.test(text)) {
    return 'Decimal Number with dot number';
  }
  if (/^\s*\d+(?:[.)]\s*|\s+)/u.test(text)) {
    return 'Decimal Number';
  }
  if (/^\s*[IVXLCDMivxlcdm]+(?:[.)]\s*|\s+)/u.test(text)) {
    return 'Roman Number';
  }
  if (/^\s*[A-Za-z](?:[.)]\s*|\s+)/u.test(text)) {
    return 'Alphabet Number';
  }

  return '';
}

function stripAppliedRuleNumbering(text) {
  return String(text || '').replace(/^\s*(?:\d+(?:\.\d+)*|[IVXLCDMivxlcdm]+|[A-Za-z])(?:[.)]\s*|\s+)/u, '').trim();
}

function getTextCasePatternLabel(text) {
  const letters = String(text || '').match(/[A-Za-z]+/g) || [];
  if (letters.length === 0) {
    return '';
  }

  const firstWord = letters[0] || '';
  const allWordsStartCapital = letters.every((word) => /^[A-Z]/.test(word));
  if (allWordsStartCapital) {
    return 'First Letter Capital';
  }
  if (/^[A-Z]/.test(firstWord)) {
    return 'Sentence case';
  }

  return '';
}

function normalizeSpecialMarginValue(value, fallbackValue) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return fallbackValue;
  }

  return String(Math.max(0.5, Math.min(5, parsed)));
}

function getTwoColumnStartIndex(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return -1;
  }

  const keywordIndex = blocks.findIndex((block) => block.type === 'keywords');
  if (keywordIndex !== -1 && keywordIndex < blocks.length - 1) {
    return keywordIndex + 1;
  }

  const abstractIndex = blocks.findIndex((block) => block.type === 'abstract');
  if (abstractIndex !== -1 && abstractIndex < blocks.length - 1) {
    return abstractIndex + 1;
  }

  const authorDetailsIndex = blocks.findIndex((block) => block.type === 'author_details');
  if (authorDetailsIndex !== -1 && authorDetailsIndex < blocks.length - 1) {
    return authorDetailsIndex + 1;
  }

  const headingIndex = blocks.findIndex((block) => block.type?.startsWith('heading_'));
  if (headingIndex > 0) {
    return headingIndex;
  }

  const contentIndex = blocks.findIndex((block) => block.type === 'content');
  if (contentIndex > 0) {
    return contentIndex;
  }

  return 0;
}

function getRulePresetGroups(rules) {
  const isNewFlow = isNewUploadFormat(rules);
  const titleLabel = isNewUploadFormat(rules) ? 'Chapter Title' : 'Title';
  const baseGroups = [
    {
      id: 'rule_set_1',
      label: 'Rule Set 1',
      presets: [
        createRulePreset('title', titleLabel, {
          blockType: 'title',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'title'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'title'),
        }),
        ...(!isNewFlow ? [
          createRulePreset('author_name', 'Author Name', {
            blockType: 'author_name',
            inlineStyle: getRuleDrivenInlineStyle(rules, 'author_name'),
            blockFormat: getRuleDrivenBlockFormat(rules, 'author_name'),
          }),
          createRulePreset('author_details', 'Author Details', {
            blockType: 'author_details',
            inlineStyle: getRuleDrivenInlineStyle(rules, 'author_details'),
            blockFormat: getRuleDrivenBlockFormat(rules, 'author_details'),
          }),
        ] : []),
        createRulePreset('abstract', 'Abstract', {
          blockType: 'abstract',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'abstract'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'abstract'),
        }),
        createRulePreset('keywords', 'Keywords', {
          blockType: 'keywords',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'keywords'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'keywords'),
        }),
      ],
    },
    {
      id: 'rule_set_2',
      label: 'Rule Set 2',
      presets: [
        createRulePreset('heading_1', 'Heading 1', {
          blockType: 'heading_1',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'heading_1'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'heading_1'),
          numbering: getConfiguredHeadingNumbering(rules, 'heading_1'),
        }),
        createRulePreset('heading_2', 'Heading 2', {
          blockType: 'heading_2',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'heading_2'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'heading_2'),
          numbering: getConfiguredHeadingNumbering(rules, 'heading_2'),
        }),
        createRulePreset('heading_3', 'Heading 3', {
          blockType: 'heading_3',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'heading_3'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'heading_3'),
          numbering: getConfiguredHeadingNumbering(rules, 'heading_3'),
        }),
        createRulePreset('content', 'Content', {
          blockType: 'content',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'content'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'content'),
        }),
      ],
    },
    {
      id: 'rule_set_3',
      label: 'Rule Set 3',
      presets: [
        createRulePreset('table', 'Table', {
          blockType: 'table',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'table'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'table'),
        }),
        createRulePreset('table_title', 'Table Title', {
          blockType: 'table_title',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'table_title'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'table_title'),
        }),
        createRulePreset('figure', 'Figure', {
          blockType: 'figure',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'figure'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'figure'),
        }),
        createRulePreset('figure_title', 'Figure Title', {
          blockType: 'figure_title',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'figure_title'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'figure_title'),
        }),
        createRulePreset('references', 'References', {
          blockType: 'references',
          inlineStyle: getRuleDrivenInlineStyle(rules, 'references'),
          blockFormat: getRuleDrivenBlockFormat(rules, 'references'),
        }),
      ],
    },
  ];

  return baseGroups.filter((group) => group.presets.length > 0);
}

function getPreviewRuleTypeOptions(rules, currentType = '') {
  const options = isNewUploadFormat(rules)
    ? DOCUMENT_DETECTION_RULE_OPTIONS
    : DETECTION_RULE_OPTIONS;

  const filteredOptions = options.filter((option) => !['table', 'figure', 'header', 'footer'].includes(option.value));
  if (isNewUploadFormat(rules) && DOCUMENT_EXCLUDED_RULE_TYPES.has(currentType)) {
    return filteredOptions;
  }

  if (!currentType || filteredOptions.some((option) => option.value === currentType)) {
    return filteredOptions;
  }

  return [
    { value: currentType, label: formatBlockLabel(currentType) },
    ...filteredOptions,
  ];
}

function getAppliedRuleOptions(rules) {
  if (!isNewUploadFormat(rules)) {
    return APPLIED_RULE_OPTIONS;
  }

  return APPLIED_RULE_OPTIONS
    .filter((option) => !DOCUMENT_EXCLUDED_RULE_TYPES.has(option.value))
    .map((option) => option.value === 'title' ? { ...option, label: 'Chapter Title' } : option);
}

function buildPreviewRuleTypePatch(block, nextBlockType, rules) {
  const safeType = getPreviewSafeBlockType(nextBlockType, rules);
  const blockFormat = {
    ...getRuleDrivenBlockFormat(rules, safeType),
    ...(isNumberingField(safeType) ? { numbering: getConfiguredNumbering(rules, safeType) } : {}),
  };
  const sourceText = getPreviewRuleTypeSourceText(block, safeType);
  const patch = {
    type: safeType,
    level: getHeadingLevel(safeType),
    label: formatBlockLabel(safeType),
    source: 'manual',
    format: blockFormat,
  };

  if (safeType?.startsWith('heading_')) {
    const normalizedText = sanitizeHeadingText(sourceText);
    return {
      ...patch,
      text: normalizedText,
      html: sanitizeHeadingHtml(normalizedText),
    };
  }

  if (safeType === 'references') {
    const normalizedText = normalizeReferenceBlockText(sourceText);
    return {
      ...patch,
      text: normalizedText,
      html: textToPreviewHtml(normalizedText),
    };
  }

  if (block?.type?.startsWith('heading_') && safeType === 'content') {
    const normalizedText = normalizeHeadingConvertedToContentText(sourceText);
    return {
      ...patch,
      text: normalizedText,
      html: textToPreviewHtml(normalizedText),
    };
  }

  return patch;
}

function getPreviewRuleTypeSourceText(block, nextBlockType = '') {
  const sourceEntries = Array.isArray(block?.source_debug_entries) ? block.source_debug_entries : [];
  if (block?.type?.startsWith('heading_') && nextBlockType === 'content' && sourceEntries.length === 1) {
    const originalText = sourceEntries[0]?.raw_text || sourceEntries[0]?.text || sourceEntries[0]?.final_text || '';
    if (String(originalText || '').trim()) {
      return String(originalText || '');
    }
  }

  return block?.text || htmlToPlainText(block?.html || '');
}

function normalizeHeadingConvertedToContentText(value) {
  const strippedText = stripAppliedRuleNumbering(value).trim();
  if (!shouldTitleCaseConvertedHeading(strippedText)) {
    return strippedText;
  }

  return strippedText
    .toLowerCase()
    .replace(/\b[a-z][a-z&'-]*/g, (word, offset) => {
      const upperWord = word.toUpperCase();
      if (CONVERTED_CONTENT_ACRONYMS.has(upperWord)) {
        return upperWord;
      }
      if (offset > 0 && CONVERTED_CONTENT_MINOR_WORDS.has(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
}

const CONVERTED_CONTENT_ACRONYMS = new Set(['HDFC', 'ICICI', 'SBI', 'PNB', 'IDBI', 'IDFC', 'RBI', 'HSBC', 'UCO', 'LIC']);
const CONVERTED_CONTENT_MINOR_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'or', 'the', 'to']);

function shouldTitleCaseConvertedHeading(value) {
  const text = String(value || '').trim();
  const letters = text.match(/[A-Za-z]/g) || [];
  if (letters.length < 3 || text.length > 90) {
    return false;
  }

  const lowercaseLetters = letters.filter((character) => character >= 'a' && character <= 'z').length;
  return lowercaseLetters === 0;
}

function createRulePreset(id, label, config) {
  return { id, label, ...config };
}

function getConfiguredHeadingNumbering(rules, fieldKey) {
  return getConfiguredNumbering(rules, fieldKey);
}

function normalizeTextTransform(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return TEXT_TRANSFORM_OPTIONS.some((option) => option.value === normalized) ? normalized : 'none';
}

function applyTextTransformToText(value, transform) {
  const mode = normalizeTextTransform(transform);
  const text = String(value || '');

  if (!text || mode === 'none') {
    return text;
  }

  if (mode === 'uppercase') {
    return text.toUpperCase();
  }

  if (mode === 'lowercase') {
    return text.toLowerCase();
  }

  if (mode === 'capitalize_words') {
    return text
      .toLowerCase()
      .replace(/\b([a-z])/g, (match) => match.toUpperCase());
  }

  if (mode === 'sentence_case') {
    const lowered = text.toLowerCase();
    let shouldCapitalize = true;
    return lowered.replace(/[a-z]/g, (character) => {
      if (!shouldCapitalize) {
        return character;
      }
      shouldCapitalize = false;
      return character.toUpperCase();
    }).replace(/([.!?]\s+)([a-z])/g, (_, prefix, character) => `${prefix}${character.toUpperCase()}`);
  }

  return text;
}

function applyTextTransformToHtml(html, transform) {
  const mode = normalizeTextTransform(transform);
  const source = String(html || '');
  if (!source || mode === 'none' || typeof document === 'undefined') {
    return mode === 'none' ? source : applyTextTransformToText(source, mode);
  }

  const container = document.createElement('div');
  container.innerHTML = source;

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      node.textContent = applyTextTransformToText(node.textContent || '', mode);
      return;
    }
    node.childNodes.forEach(walk);
  };

  container.childNodes.forEach(walk);
  return container.innerHTML;
}

function getConfiguredNumbering(rules, fieldKey) {
  if (!isHeadingField(fieldKey)) {
    const numbering = getPersistedFieldRule(rules, fieldKey)?.numbering || {};
    return {
      raw: typeof numbering.raw === 'string' ? numbering.raw : '',
      style: typeof numbering.style === 'string' ? numbering.style : 'none',
      enabled: Boolean(numbering.enabled),
    };
  }

  const entries = normalizeHeadingEntries(rules?.rule_sets?.rule_set_2?.[fieldKey] || []);
  const firstEnabledEntry = entries.find((entry) => entry.numbering?.enabled && entry.numbering?.style !== 'none');
  if (firstEnabledEntry) {
    return {
      raw: firstEnabledEntry.numbering.raw || '',
      style: firstEnabledEntry.numbering.style || 'none',
      enabled: Boolean(firstEnabledEntry.numbering.enabled),
    };
  }

  if (entries.length === 0) {
    return getDefaultHeadingNumbering(fieldKey);
  }

  return {
    raw: '',
    style: 'none',
    enabled: false,
  };
}

function getDefaultHeadingNumbering(fieldKey) {
  if (fieldKey === 'heading_1') {
    return { raw: '', style: 'upper-roman', enabled: true };
  }
  if (fieldKey === 'heading_2' || fieldKey === 'heading_3') {
    return { raw: '', style: 'decimal-nested', enabled: true };
  }
  return { raw: '', style: 'none', enabled: false };
}

function formatBlockLabel(blockType) {
  const normalized = String(blockType || '').trim().toLowerCase();
  if (normalized === 'title' && typeof window !== 'undefined') {
    const path = String(window.location?.pathname || '').toLowerCase();
    if (
      path.includes('/apply-document-rules')
      || path.includes('/manage-document-rules')
      || path.includes('/apply-new')
      || path.includes('/manage-new')
    ) {
      return 'Chapter Title';
    }
  }

  return String(blockType)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSelectedBlockElement() {
  const selection = window.getSelection();
  const anchorNode = selection?.anchorNode;
  if (!anchorNode) {
    return null;
  }

  const element = anchorNode.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode.parentElement;
  return element?.closest('[data-block-id]') || null;
}

function getPreviewBlockElementById(blockId) {
  if (!blockId) {
    return null;
  }

  return window.document.querySelector(`[data-block-id="${blockId}"]`);
}

function getSelectedParagraphElement() {
  const selection = window.getSelection();
  const anchorNode = selection?.anchorNode;
  if (!anchorNode) {
    return null;
  }

  const element = anchorNode.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode.parentElement;
  return element?.closest('[data-preview-paragraph="true"]') || null;
}

function handlePlainTextPaste(event) {
  const text = event.clipboardData?.getData('text/plain');
  if (typeof text !== 'string') {
    return;
  }

  event.preventDefault();

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();
  range.insertNode(window.document.createTextNode(text));
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);

  lastPreviewSelectionInfo = null;
}

function handleEditableKeyDown(event) {
  if (event.key !== 'Tab') {
    return;
  }

  event.preventDefault();
  insertTextAtSelection('    ');
}

function insertTextAtSelection(text) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();
  range.insertNode(window.document.createTextNode(text));
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

let lastPreviewSelectionInfo = null;

function getSelectionInfo(options = {}) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    if (options.allowStored && lastPreviewSelectionInfo?.blockId && Date.now() - (lastPreviewSelectionInfo.storedAt || 0) < 10000) {
      return lastPreviewSelectionInfo;
    }
    return { hasSelection: false, blockId: '' };
  }

  const selectedText = selection.toString();
  const blockElement = getSelectedBlockElement();
  const hasSelection = Boolean(selectedText.trim()) && !selection.isCollapsed;
  const range = selection.getRangeAt(0);

  if (options.allowStored && (!hasSelection || !blockElement?.dataset.blockId) && lastPreviewSelectionInfo?.blockId && Date.now() - (lastPreviewSelectionInfo.storedAt || 0) < 10000) {
    return lastPreviewSelectionInfo;
  }

  const selectionInfo = {
    hasSelection,
    selectedText,
    blockId: blockElement?.dataset.blockId || '',
    blockElement,
    range,
  };

  if (hasSelection && blockElement?.dataset.blockId) {
    lastPreviewSelectionInfo = {
      ...selectionInfo,
      range: range.cloneRange(),
      storedAt: Date.now(),
    };
  } else if (!options.allowStored) {
    lastPreviewSelectionInfo = null;
  }

  return selectionInfo;
}

function restorePreviewSelection(selectionInfo) {
  if (!selectionInfo?.range) {
    return false;
  }

  const selection = window.getSelection();
  if (!selection) {
    return false;
  }

  try {
    selection.removeAllRanges();
    selection.addRange(selectionInfo.range.cloneRange ? selectionInfo.range.cloneRange() : selectionInfo.range);
    return true;
  } catch {
    return false;
  }
}

function isSelectionWithinCurrentBlock(selectionInfo, blockId) {
  return Boolean(selectionInfo?.hasSelection && selectionInfo.blockId && selectionInfo.blockId === blockId);
}

function isFullBlockSelection(selectionInfo, blockId) {
  if (!isSelectionWithinCurrentBlock(selectionInfo, blockId)) {
    return false;
  }

  const blockElement = selectionInfo.blockElement || getPreviewBlockElementById(blockId);
  const blockText = String(blockElement?.innerText || '').replace(/\s+/g, ' ').trim();
  const selectedText = String(selectionInfo.selectedText || '').replace(/\s+/g, ' ').trim();

  return Boolean(blockText && selectedText && blockText === selectedText);
}

function createPartialSelectionRuleSplit(block, selectionInfo, preset, rules) {
  if (!block || !selectionInfo?.range || !selectionInfo.blockElement || !preset?.blockType) {
    return null;
  }

  const textParts = getSelectionTextParts(selectionInfo.blockElement, selectionInfo.range);
  if (!textParts || !textParts.selectedText.trim()) {
    return null;
  }

  const renderedPrefix = getHeadingRenderPrefix([], block);
  const sourceHasListPrefix = isListFormattingEnabled(block);
  const normalizeSourceSelectionPart = (value) => {
    const withoutHeadingPrefix = stripRenderedPrefix(value, renderedPrefix);
    return sourceHasListPrefix ? removeLeadingListMarkerFromText(withoutHeadingPrefix) : withoutHeadingPrefix;
  };
  const beforeText = sanitizeHeadingBlockValue(block, normalizeSourceSelectionPart(textParts.beforeText)).trim();
  let selectedText = preset.blockType?.startsWith('heading_')
    ? sanitizeHeadingText(removeLeadingListMarkerFromText(textParts.selectedText)).trim()
    : sanitizeHeadingBlockValue(block, normalizeSourceSelectionPart(textParts.selectedText)).trim();
  let afterText = sanitizeHeadingBlockValue(block, normalizeSourceSelectionPart(textParts.afterText)).trim();

  if (!selectedText) {
    return null;
  }

  if (shouldMergeTrailingReferenceText(preset.blockType, selectedText, afterText)) {
    selectedText = `${selectedText}\n${afterText}`.trim();
    afterText = '';
  }

  const splitId = Date.now();
  const selectedBlockId = buildSplitBlockId(block.id, preset.blockType, splitId);
  const replacementBlocks = [
    beforeText ? createSplitPreviewBlock(block, beforeText, block.type, block.format, buildSplitBlockId(block.id, 'before', splitId)) : null,
    createSplitPreviewBlock(
      block,
      selectedText,
      preset.blockType,
      {
        ...getRuleDrivenBlockFormat(rules, preset.blockType),
        ...(isNumberingField(preset.blockType) ? { numbering: getConfiguredNumbering(rules, preset.blockType) } : {}),
      },
      selectedBlockId,
      'manual'
    ),
    afterText ? createSplitPreviewBlock(block, afterText, block.type, block.format, buildSplitBlockId(block.id, 'after', splitId)) : null,
  ].filter(Boolean);

  return {
    __replaceBlocks: replacementBlocks,
    __selectBlockId: selectedBlockId,
  };
}

function getSelectionTextParts(blockElement, range, options = {}) {
  if (!blockElement || !range) {
    return null;
  }

  try {
    if (options.preferRangeText) {
      const rangeParts = getSelectionTextPartsFromRanges(blockElement, range);
      if (rangeParts?.selectedText?.trim()) {
        return rangeParts;
      }
    }

    const markerParts = getSelectionTextPartsFromClonedMarkers(blockElement, range);
    if (markerParts?.selectedText?.trim()) {
      return markerParts;
    }

    const paragraphElements = Array.from(blockElement.querySelectorAll('[data-preview-paragraph="true"]'));
    if (paragraphElements.length > 0) {
      const paragraphParts = getSelectionTextPartsFromParagraphs(paragraphElements, range);
      if (paragraphParts?.selectedText?.trim()) {
        return paragraphParts;
      }
    }

    return getSelectionTextPartsFromRanges(blockElement, range);
  } catch {
    return null;
  }
}

function getSelectionTextPartsFromRanges(blockElement, range) {
  const beforeRange = range.cloneRange();
  beforeRange.selectNodeContents(blockElement);
  beforeRange.setEnd(range.startContainer, range.startOffset);

  const afterRange = range.cloneRange();
  afterRange.selectNodeContents(blockElement);
  afterRange.setStart(range.endContainer, range.endOffset);

  const selectedRange = range.cloneRange();
  const parts = {
    beforeText: getRangeText(beforeRange),
    selectedText: getRangeText(selectedRange),
    afterText: getRangeText(afterRange),
  };

  beforeRange.detach?.();
  afterRange.detach?.();
  selectedRange.detach?.();

  return parts;
}

function getSelectionTextPartsFromClonedMarkers(blockElement, range) {
  const startMarker = `__DOCX_SELECTION_START_${Date.now()}_${Math.random()}__`;
  const endMarker = `__DOCX_SELECTION_END_${Date.now()}_${Math.random()}__`;

  try {
    const startPath = getNodePathWithinRoot(blockElement, range.startContainer);
    const endPath = getNodePathWithinRoot(blockElement, range.endContainer);
    if (!startPath || !endPath) {
      return null;
    }

    const clonedBlock = blockElement.cloneNode(true);
    const clonedStartNode = getNodeByPath(clonedBlock, startPath);
    const clonedEndNode = getNodeByPath(clonedBlock, endPath);
    if (!clonedStartNode || !clonedEndNode) {
      return null;
    }

    const clonedRange = window.document.createRange();
    clonedRange.setStart(clonedStartNode, Math.min(range.startOffset, getMaxRangeOffset(clonedStartNode)));
    clonedRange.setEnd(clonedEndNode, Math.min(range.endOffset, getMaxRangeOffset(clonedEndNode)));
    clonedRange.collapse(false);
    clonedRange.insertNode(window.document.createTextNode(endMarker));

    const clonedStartRange = window.document.createRange();
    clonedStartRange.setStart(clonedStartNode, Math.min(range.startOffset, getMaxRangeOffset(clonedStartNode)));
    clonedStartRange.collapse(true);
    clonedStartRange.insertNode(window.document.createTextNode(startMarker));

    const paragraphElements = Array.from(clonedBlock.querySelectorAll('[data-preview-paragraph="true"]'));
    const paragraphTexts = (paragraphElements.length > 0 ? paragraphElements : [clonedBlock])
      .map((element) => String(element.textContent || '').replace(/\r/g, ''))
      .filter((text) => text.trim() || text.includes(startMarker) || text.includes(endMarker));

    clonedRange.detach?.();
    clonedStartRange.detach?.();

    return splitMarkedParagraphTexts(paragraphTexts, startMarker, endMarker);
  } catch {
    return null;
  }
}

function splitMarkedParagraphTexts(paragraphTexts, startMarker, endMarker) {
  const beforeParagraphs = [];
  const selectedParagraphs = [];
  const afterParagraphs = [];
  let mode = 'before';

  paragraphTexts.forEach((paragraphText) => {
    let remainingText = String(paragraphText || '');

    while (remainingText) {
      const nextStartIndex = remainingText.indexOf(startMarker);
      const nextEndIndex = remainingText.indexOf(endMarker);

      if (nextStartIndex === -1 && nextEndIndex === -1) {
        pushParagraphTextByMode(mode, beforeParagraphs, selectedParagraphs, afterParagraphs, remainingText);
        remainingText = '';
        continue;
      }

      const markerIsStart = nextStartIndex !== -1 && (nextEndIndex === -1 || nextStartIndex < nextEndIndex);
      const markerIndex = markerIsStart ? nextStartIndex : nextEndIndex;
      pushParagraphTextByMode(mode, beforeParagraphs, selectedParagraphs, afterParagraphs, remainingText.slice(0, markerIndex));
      mode = markerIsStart ? 'selected' : 'after';
      remainingText = remainingText.slice(markerIndex + (markerIsStart ? startMarker.length : endMarker.length));
    }
  });

  return {
    beforeText: beforeParagraphs.join('\n\n'),
    selectedText: selectedParagraphs.join('\n\n'),
    afterText: afterParagraphs.join('\n\n'),
  };
}

function pushParagraphTextByMode(mode, beforeParagraphs, selectedParagraphs, afterParagraphs, text) {
  if (mode === 'selected') {
    pushParagraphText(selectedParagraphs, text);
    return;
  }
  if (mode === 'after') {
    pushParagraphText(afterParagraphs, text);
    return;
  }
  pushParagraphText(beforeParagraphs, text);
}

function getNodePathWithinRoot(rootNode, targetNode) {
  if (!rootNode || !targetNode || !(rootNode === targetNode || rootNode.contains(targetNode))) {
    return null;
  }

  const path = [];
  let currentNode = targetNode;
  while (currentNode && currentNode !== rootNode) {
    const parentNode = currentNode.parentNode;
    if (!parentNode) {
      return null;
    }
    path.unshift(Array.prototype.indexOf.call(parentNode.childNodes, currentNode));
    currentNode = parentNode;
  }
  return path;
}

function getNodeByPath(rootNode, path) {
  return (path || []).reduce((node, childIndex) => node?.childNodes?.[childIndex] || null, rootNode);
}

function getMaxRangeOffset(node) {
  if (!node) {
    return 0;
  }
  return node.nodeType === Node.TEXT_NODE ? String(node.nodeValue || '').length : node.childNodes.length;
}

function getSelectionTextPartsFromParagraphs(paragraphElements, range) {
  const beforeParagraphs = [];
  const selectedParagraphs = [];
  const afterParagraphs = [];
  let hasSeenSelection = false;

  try {
    paragraphElements.forEach((paragraphElement) => {
      const paragraphRange = window.document.createRange();
      paragraphRange.selectNodeContents(paragraphElement);

      const paragraphText = getRangeText(paragraphRange);
      if (!paragraphText.trim()) {
        paragraphRange.detach?.();
        return;
      }

      const intersectsSelection = range.intersectsNode(paragraphElement);
      if (!intersectsSelection) {
        pushParagraphText(hasSeenSelection ? afterParagraphs : beforeParagraphs, paragraphText);
        paragraphRange.detach?.();
        return;
      }

      hasSeenSelection = true;
      const startsInParagraph = isRangeBoundaryInsideElement(paragraphElement, range.startContainer);
      const endsInParagraph = isRangeBoundaryInsideElement(paragraphElement, range.endContainer);

      if (startsInParagraph) {
        const beforeRange = paragraphRange.cloneRange();
        beforeRange.setEnd(range.startContainer, range.startOffset);
        pushParagraphText(beforeParagraphs, getRangeText(beforeRange));
        beforeRange.detach?.();
      }

      const selectedRange = paragraphRange.cloneRange();
      if (startsInParagraph) {
        selectedRange.setStart(range.startContainer, range.startOffset);
      }
      if (endsInParagraph) {
        selectedRange.setEnd(range.endContainer, range.endOffset);
      }
      pushParagraphText(selectedParagraphs, getRangeText(selectedRange));
      selectedRange.detach?.();

      if (endsInParagraph) {
        const afterRange = paragraphRange.cloneRange();
        afterRange.setStart(range.endContainer, range.endOffset);
        pushParagraphText(afterParagraphs, getRangeText(afterRange));
        afterRange.detach?.();
      }

      paragraphRange.detach?.();
    });

    return {
      beforeText: beforeParagraphs.join('\n\n'),
      selectedText: selectedParagraphs.join('\n\n'),
      afterText: afterParagraphs.join('\n\n'),
    };
  } catch {
    return null;
  }
}

function getRangeText(range) {
  if (!range) {
    return '';
  }

  try {
    const fragment = range.cloneContents();
    const fragmentText = getNodeTextPreservingBreaks(fragment);
    if (fragmentText.trim()) {
      return fragmentText.replace(/\r/g, '').replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n').trim();
    }
  } catch {
    // Fall back to the browser selection text.
  }

  return String(range.toString?.() || '').replace(/\r/g, '');
}

function getNodeTextPreservingBreaks(node) {
  if (!node) {
    return '';
  }

  if (node.nodeType === Node.TEXT_NODE) {
    return String(node.nodeValue || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
    return '';
  }

  const tagName = node.nodeType === Node.ELEMENT_NODE ? String(node.tagName || '').toLowerCase() : '';
  if (tagName === 'br') {
    return '\n';
  }

  const childText = Array.from(node.childNodes || []).map(getNodeTextPreservingBreaks).join('');
  if (['div', 'p', 'li', 'tr'].includes(tagName)) {
    return `${childText}\n`;
  }

  return childText;
}

function pushParagraphText(paragraphs, text) {
  const normalizedText = String(text || '').trim();
  if (normalizedText) {
    paragraphs.push(normalizedText);
  }
}

function isRangeBoundaryInsideElement(element, boundaryNode) {
  return Boolean(element && boundaryNode && (element === boundaryNode || element.contains(boundaryNode)));
}

function shouldMergeTrailingReferenceText(blockType, selectedText, afterText) {
  if (blockType !== 'references' || !afterText) {
    return false;
  }

  const normalizedSelected = String(selectedText || '').trim();
  const normalizedAfter = String(afterText || '').trim();
  const selectedLooksLikeReferences = /(^|\n)\s*(references\s*:?\s*)?(\[\d+\]|\d+\.)/i.test(normalizedSelected);
  const afterStartsNewReference = /^(\[\d+\]|\d+\.)\s+/i.test(normalizedAfter);
  const afterLooksLikeContinuation = normalizedAfter.length < 220 && !afterStartsNewReference;

  return selectedLooksLikeReferences && afterLooksLikeContinuation;
}

function createSplitPreviewBlock(sourceBlock, text, blockType, format, id, source = sourceBlock.source) {
  const safeType = blockType || sourceBlock.type || 'content';
  const normalizedText = safeType === 'references'
    ? normalizeReferenceBlockText(text)
    : safeType?.startsWith('heading_')
      ? sanitizeHeadingText(text)
      : text;

  return {
    ...sourceBlock,
    id,
    type: safeType,
    level: getHeadingLevel(safeType),
    label: formatBlockLabel(safeType),
    source,
    text: normalizedText,
    html: safeType?.startsWith('heading_') ? sanitizeHeadingHtml(normalizedText) : textToPreviewHtml(normalizedText),
    format: {
      ...(format || sourceBlock.format || {}),
    },
  };
}

function createPartialSelectionListSplit(block, selectionInfo, listPatch, rules) {
  if (!block || !selectionInfo?.range || !selectionInfo.blockElement) {
    return null;
  }

  const textParts = getSelectionTextParts(selectionInfo.blockElement, selectionInfo.range, { preferRangeText: true });
  if (!textParts || !textParts.selectedText.trim()) {
    return null;
  }

  const renderedPrefix = getHeadingRenderPrefix([], block);
  const sourceHasListPrefix = isListFormattingEnabled(block);
  const normalizeSourceSelectionPart = (value) => {
    const withoutHeadingPrefix = stripRenderedPrefix(value, renderedPrefix);
    return sourceHasListPrefix ? removeLeadingListMarkerFromText(withoutHeadingPrefix) : withoutHeadingPrefix;
  };

  const beforeText = sanitizeHeadingBlockValue(block, normalizeSourceSelectionPart(textParts.beforeText)).trim();
  const selectedText = removeLeadingListMarkerFromText(textParts.selectedText).trim();
  const afterText = sanitizeHeadingBlockValue(block, normalizeSourceSelectionPart(textParts.afterText)).trim();

  if (!selectedText) {
    return null;
  }

  const splitId = Date.now();
  const selectedBlockId = buildSplitBlockId(block.id, 'content', splitId);
  const selectedFormat = {
    ...getRuleDrivenBlockFormat(rules, 'content'),
    list: listPatch,
  };
  const replacementBlocks = [
    beforeText ? createSplitPreviewBlock(block, beforeText, block.type, block.format, buildSplitBlockId(block.id, 'before', splitId)) : null,
    createSplitPreviewBlock(block, selectedText, 'content', selectedFormat, selectedBlockId, 'manual'),
    afterText ? createSplitPreviewBlock(block, afterText, block.type, block.format, buildSplitBlockId(block.id, 'after', splitId)) : null,
  ].filter(Boolean);

  return {
    __replaceBlocks: replacementBlocks,
    __selectBlockId: selectedBlockId,
    __disableCascade: true,
  };
}

function normalizeReferenceBlockText(value) {
  let normalized = String(value || '')
    .replace(/\r/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim();

  normalized = normalized.replace(/^(references?|bibliography)\s*[:.\-–—]?\s*/i, (match) => `${match.trim().replace(/[:.\-–—]?$/, ':')}\n`);
  normalized = normalized.replace(/\s*(?:\[(\d{1,3})\]|(?<!\d)(\d{1,3})[.)])\s+/g, (_match, bracketNumber, decimalNumber) => {
    const number = bracketNumber || decimalNumber;
    return `\n${number}. `;
  });

  const lines = normalized
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^(?:\[\d+\]|\d+[.)])$/.test(line));

  let headingSeen = false;
  const cleanedLines = lines.map((line) => {
    const withoutNumber = stripReferenceNumberingFromText(line).replace(/[:.\-–—]\s*$/, '').trim();
    if (/^(references?|bibliography)$/i.test(withoutNumber)) {
      if (headingSeen) {
        return '';
      }
      headingSeen = true;
      return /^bibliography$/i.test(withoutNumber) ? 'Bibliography' : 'References';
    }
    return stripReferenceNumberingFromText(line);
  }).filter(Boolean);

  return mergeFragmentedReferenceLines(cleanedLines).join('\n\n');
}

function mergeFragmentedReferenceLines(lines) {
  const mergedLines = [];
  for (let index = 0; index < lines.length; index += 1) {
    const currentLine = String(lines[index] || '').trim();
    const nextLine = String(lines[index + 1] || '').trim();
    if (nextLine && isReferenceAuthorFragment(currentLine) && lineContainsReferenceYear(nextLine)) {
      mergedLines.push(`${currentLine} ${stripReferenceNumberingFromText(nextLine)}`.trim());
      index += 1;
      continue;
    }
    mergedLines.push(currentLine);
  }
  return mergedLines;
}

function isReferenceAuthorFragment(value) {
  const text = stripReferenceNumberingFromText(value).trim();
  if (!text || /^(references?|bibliography)$/i.test(text) || lineContainsReferenceYear(text)) {
    return false;
  }
  if (text.length > 70) {
    return false;
  }
  const words = text.match(/[A-Za-z]+/g) || [];
  if (words.length === 0 || words.length > 6) {
    return false;
  }
  return text.includes(',') || /\b[A-Z]\.\s*$/.test(text);
}

function lineContainsReferenceYear(value) {
  return /\(\d{4}[a-z]?\)/.test(stripReferenceNumberingFromText(value));
}

function buildSplitBlockId(blockId, blockType, splitId) {
  return `${blockId}-${blockType}-${splitId}`;
}

function textToPreviewHtml(value) {
  return String(value || '')
    .split('\n')
    .map((line) => (line === '\u00a0' ? '&nbsp;' : escapeHtml(line)))
    .join('<br>');
}

function applyInlineStyleToSelection(styleMap) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return false;
  }

  const range = selection.getRangeAt(0);
  const span = window.document.createElement('span');
  Object.entries(styleMap || {}).forEach(([key, value]) => {
    if (value) {
      span.style[key] = value;
    }
  });

  try {
    span.appendChild(range.extractContents());
    range.insertNode(span);
    selection.removeAllRanges();
    const nextRange = window.document.createRange();
    nextRange.selectNodeContents(span);
    selection.addRange(nextRange);
    return true;
  } catch {
    return false;
  }
}

function toggleInlineStyleOnSelection(command, fallbackStyleMap) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return false;
  }

  if (typeof window.document.execCommand === 'function') {
    try {
      window.document.execCommand('styleWithCSS', false, false);
      const didToggle = window.document.execCommand(command, false);
      if (didToggle) {
        return true;
      }
    } catch {
      // Fall through to manual wrapper styling when execCommand is unavailable.
    }
  }

  return applyInlineStyleToSelection(fallbackStyleMap);
}

function getHeadingPreview(entry, index) {
  const prefix = resolveNumberingPreview(entry.numbering, index);
  const title = entry.text?.trim() || '';
  return [prefix, title].filter(Boolean).join(' ').trim();
}

function resolveNumberingPreview(numbering, index) {
  if (!numbering?.enabled || numbering.style === 'none') {
    return '';
  }

  if (numbering.style === 'custom') {
    return numbering.raw || '';
  }

  return formatNumber(numbering.style, index + 1);
}

function formatNumber(type, value) {
  switch (type) {
    case 'decimal':
      return `${value}.`;
    case 'decimal-bracket':
      return `[${value}]`;
    case 'decimal-nested':
      return `1.${value}`;
    case 'decimal-paren':
      return `${value})`;
    case 'upper-alpha':
      return `${toAlphabet(value).toUpperCase()}.`;
    case 'lower-alpha':
      return `${toAlphabet(value).toLowerCase()}.`;
    case 'lower-alpha-paren':
      return `${toAlphabet(value).toLowerCase()})`;
    case 'upper-roman':
      return `${toRoman(value)}.`;
    case 'lower-roman':
      return `${toRoman(value).toLowerCase()}.`;
    default:
      return String(value || '');
  }
}

function toAlphabet(value) {
  let current = value;
  let result = '';

  while (current > 0) {
    current -= 1;
    result = String.fromCharCode(65 + (current % 26)) + result;
    current = Math.floor(current / 26);
  }

  return result || 'A';
}

function toRoman(value) {
  const numerals = [
    ['M', 1000],
    ['CM', 900],
    ['D', 500],
    ['CD', 400],
    ['C', 100],
    ['XC', 90],
    ['L', 50],
    ['XL', 40],
    ['X', 10],
    ['IX', 9],
    ['V', 5],
    ['IV', 4],
    ['I', 1],
  ];

  let remaining = value;
  let result = '';

  numerals.forEach(([symbol, amount]) => {
    while (remaining >= amount) {
      result += symbol;
      remaining -= amount;
    }
  });

  return result || 'I';
}

export default App;
